import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import './App.css'

type TaskStatus = 'backlog' | 'doing' | 'done'
type Priority = 'P1' | 'P2' | 'P3'
type Owner = 'You' | 'Head Bot' | 'Shared'
type TabKey =
  | 'projectDashboards'
  | 'projects'
  | 'officeOverview'
  | 'consultantCoordinationTracking'
  | 'drawingVersionHistory'
  | 'decisionLogs'
  | 'caWorkflows'
  | 'staffingAllocation'
  | 'teamTasks'
  | 'teamOrganization'
  | 'meetingActionTracking'
  | 'calendar'
  | 'projectStandards'
  | 'directory'
  | 'consultantList'
  | 'activity'
  | 'settings'

type Task = {
  id: string
  title: string
  notes?: string
  status: TaskStatus
  priority: Priority
  owner: Owner
  dueDate?: string
  progress: number
  blocked: boolean
  blockerReason?: string
  createdAt: string
  updatedAt: string
  completedAt?: string
}

type TaskEvent = {
  id: string
  taskId: string
  taskTitle: string
  at: string
  message: string
}

type ActivityEntry = {
  id: string
  timestamp: string
  actor: string
  module: string
  action: string
  entity_type: string
  entity_id: string
  source: string
  severity: 'info' | 'important' | 'critical'
  summary: string
}

type StartupHealthCheck = {
  label: string
  ok: boolean
  status: number | 'ERR'
  detail?: string
}

type DocumentItem = {
  id: string
  title: string
  kind: 'upload' | 'link' | 'note'
  source: 'You' | 'Head Bot'
  description: string
  url?: string
  fileName?: string
  folderPath?: string
  folders?: Array<{ name: string; path: string }>
  createdAt: string
}

type CalendarDeadline = {
  title: string
  description?: string
  assignTo?: string
  notes?: string
  dateKey: string
  source: string
  status?: 'not-selected' | 'overdue' | 'on-track' | 'completed'
}

type DirectoryContact = {
  name: string
  role: string
  team: string
  company?: string
  companyAbbreviation?: string
  phone?: string
  phoneType?: 'Cell' | 'Office'
  altPhone?: string
  altPhoneType?: 'Cell' | 'Office'
  officeExtension?: string
  notes?: string
  email?: string
  autoFromOffice?: boolean
  primaryContact?: boolean
  archived?: boolean
}

type DirectorySavedView = {
  id: string
  name: string
  search: string
  contactFilter: 'all' | 'key' | 'archived'
  sortBy: 'company' | 'consultantType'
  contactKeys: string[]
  createdAt: string
  updatedAt: string
}

type OfficeEmployeeRow = {
  name: string
  position: string
  project: string
  location: string
}

type OfficeContactCardDraft = {
  name: string
  position: string
  accessTier: string
  project: string
  projects: string[]
  location: string
  reportsTo: string
  pastProjects: string
  email: string
  phone: string
  extension: string
  cell: string
}

type NotificationTopic = 'taskUpdates' | 'assignmentChanges' | 'meetingReminders' | 'consultantUpdates' | 'projectChanges'
type NotificationLevel = 'none' | 'all' | 'mine'
type AppNotification = { id: string; topic: NotificationTopic; text: string; unread: boolean; ts: number }

// Executive Sync Map
// Purpose: define the read-only aggregation contract for Executive-level views.
// Executive can monitor progress without direct visibility/edit access to every source tab.
// Keep this map updated whenever dashboard cards or source tabs change.
const EXECUTIVE_SYNC_MAP = {
  projectOverview: ['Project Overview (Project Dashboard)'],
  deadlinesMilestones: ['Calendar → Deliverables List'],
  calendarMini: ['Calendar → Day/Month/Year data'],
  meetingTopicsActions: ['Meeting Minutes → action items'],
  taskHealth: ['Task List / Staffing Allocation'],
  consultantCoordination: ['Consultant Coordination Tracking'],
  teamMembers: ['Directory + Employee/Project assignments'],
  projectsDirectoryCards: ['Project Overview (per-project) + Employee assignments'],
} as const

const COMPANY_TIERS = ['Executive', 'Principal', 'Project Manager', 'Drafter'] as const

type ConsultantSectionKey = 'toConsultant' | 'fromConsultant' | 'nextDeliverable'
type ConsultantAttachment = { id: string; name: string; dataUrl: string; createdAt: string; addedBy?: string }
type ConsultantItem = { id: string; title: string; assignee?: string; dueDate: string; sentAt?: string; responseReceivedAt?: string; waitingOn?: 'consultant' | 'internal'; slaDays?: number; sourceCompany?: string; attachments?: ConsultantAttachment[] }
type ConsultantItemsBySection = Record<ConsultantSectionKey, ConsultantItem[]>
type MeetingAttendee = { id: string; name: string; company: string; present: boolean }
type StaffingRow = { id: string; status: string; priority: string; start: string; due: string; task: string; assignee: string; notes: string; updatedAt?: string }
type MeetingActionItem = { id: string; number?: string; parentId?: string; date: string; item: string; owner: string; due: string; status: string }
type MeetingTabKey = string

type ConsultantReview = {
  id: string
  authorName: string
  authorUsername: string
  rating: number
  notes: string
  createdAt: string
  updatedAt: string
}

const ALL_CONSULTANT_TAB = 'All'

const parseConsultantTrackerName = (value: string) => {
  const raw = (value || '').trim()
  if (!raw || raw === ALL_CONSULTANT_TAB) {
    return { discipline: raw || ALL_CONSULTANT_TAB, company: '' }
  }

  const parts = raw
    .split(/\s+[—–-]\s+/)
    .map((p) => p.trim())
    .filter(Boolean)
  if (parts.length >= 2) {
    return {
      company: parts[0],
      discipline: parts.slice(1).join(' — '),
    }
  }

  return { discipline: raw, company: '' }
}

type TaskStore = {
  tasks: Task[]
  history: TaskEvent[]
  documents: DocumentItem[]
  addTask: (payload: { title: string; notes?: string; priority: Priority; owner: Owner; dueDate?: string }) => void
  queueAgentTask: (payload: { title: string }) => void
  moveTask: (id: string, status: TaskStatus) => void
  setPriority: (id: string, priority: Priority) => void
  setOwner: (id: string, owner: Owner) => void
  setDueDate: (id: string, dueDate?: string) => void
  setProgress: (id: string, progress: number) => void
  toggleBlocked: (id: string, blockerReason?: string) => void
  removeTask: (id: string) => void
  addDocument: (payload: Omit<DocumentItem, 'id' | 'createdAt'>) => void
  removeDocument: (id: string) => void
}

const now = () => new Date().toISOString()
const todayIso = () => new Date().toISOString().slice(0, 10)
const makeId = () =>
  globalThis.crypto && 'randomUUID' in globalThis.crypto
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

const directoryContactKey = (c: DirectoryContact) => [c.name, c.company, c.email, c.phone, c.role].map((x) => (x || '').trim().toLowerCase()).join('|')
const officeContactKey = (name: string) => name.trim().toLowerCase()
const normalizeNameKey = (value: string) => value.trim().toLowerCase().replace(/\s+/g, ' ')
const uniqueTrimmedCaseInsensitive = (values: string[]) => values
  .map((value) => String(value || '').trim())
  .filter(Boolean)
  .filter((value, index, list) => list.findIndex((candidate) => candidate.toLowerCase() === value.toLowerCase()) === index)
const uniqueSortedTrimmed = (values: string[]) => Array.from(new Set(values.map((value) => (value || '').trim()).filter(Boolean))).sort((left, right) => left.localeCompare(right))
const areNumberSetsEqual = (a: Set<number>, b: Set<number>) => a.size === b.size && [...a].every((item) => b.has(item))

const makeDefaultConsultantSections = (): ConsultantItemsBySection => ({
  toConsultant: [
    { id: makeId(), title: 'Updated reflected ceiling plan set', dueDate: 'Mar 14' },
    { id: makeId(), title: 'Door schedule + hardware revisions', dueDate: 'Mar 16' },
    { id: makeId(), title: 'RFI response package (RFI-021 to RFI-028)', dueDate: 'Drafting' },
    { id: makeId(), title: 'Interior finish legend updates', dueDate: 'Apr 09' },
    { id: makeId(), title: 'Furniture plan revision cloud set', dueDate: 'Apr 11' },
    { id: makeId(), title: 'Elevator lobby dimension check', dueDate: 'Apr 14' },
    { id: makeId(), title: 'Signage package redline comments', dueDate: 'Apr 16' },
    { id: makeId(), title: 'Site detail keynote coordination', dueDate: 'Apr 18' },
  ],
  fromConsultant: [
    { id: makeId(), title: 'MEP coordination markups', dueDate: 'Mar 15' },
    { id: makeId(), title: 'Lighting fixture cut sheets', dueDate: 'Mar 18' },
    { id: makeId(), title: 'Structural beam penetration confirmation', dueDate: 'Pending' },
    { id: makeId(), title: 'Mechanical equipment clearance matrix', dueDate: 'Apr 10' },
    { id: makeId(), title: 'Electrical panel load updates', dueDate: 'Apr 12' },
    { id: makeId(), title: 'Plumbing riser coordination notes', dueDate: 'Apr 15' },
    { id: makeId(), title: 'Fire alarm device spacing review', dueDate: 'Apr 17' },
    { id: makeId(), title: 'Structural slab edge detail revisions', dueDate: 'Apr 19' },
  ],
  nextDeliverable: [{ id: makeId(), title: '50% DD Coordination Package', dueDate: 'Mar 20' }],
})

const makeEmptyConsultantSections = (): ConsultantItemsBySection => ({
  toConsultant: [],
  fromConsultant: [],
  nextDeliverable: [],
})


const makeDefaultMeetingAttendees = (): MeetingAttendee[] => [
  { id: makeId(), name: 'Alex Morgan', company: 'AOR Studio', present: true },
  { id: makeId(), name: 'Jamie Chen', company: 'Client Team', present: true },
  { id: makeId(), name: 'Marco Ruiz', company: 'MEP Partners', present: true },
  { id: makeId(), name: 'Rina Patel', company: 'Core Team', present: true },
  { id: makeId(), name: 'Evan Brooks', company: 'Structural Group', present: true },
  { id: makeId(), name: 'Nora Diaz', company: 'AOR Studio', present: true },
  { id: makeId(), name: 'Liam Foster', company: 'Client Team', present: true },
  { id: makeId(), name: 'Priya Nair', company: 'MEP Partners', present: true },
  { id: makeId(), name: 'Owen Clarke', company: 'Cost Consultant', present: true },
  { id: makeId(), name: 'Sofia Kim', company: 'Landscape', present: true },
  { id: makeId(), name: 'Dylan Hart', company: 'Civil', present: true },
  { id: makeId(), name: 'Maya Scott', company: 'Interiors', present: true },
  { id: makeId(), name: 'Noah Grant', company: 'GC Team', present: true },
]

const defaultOfficeEmployees: OfficeEmployeeRow[] = [
  { name: 'Alex Morgan', position: 'Project Manager', project: 'Tower 36', location: 'Austin' },
  { name: 'Rina Patel', position: 'Lead Architect', project: 'Cloud One', location: 'Austin' },
  { name: 'Marco Ruiz', position: 'MEP Coordinator', project: 'Tower 36', location: 'Dallas' },
  { name: 'Nora Diaz', position: 'Project Architect', project: 'Cloud One', location: 'Houston' },
  { name: 'Liam Foster', position: 'Design Lead', project: 'Tower 36', location: 'Austin' },
  { name: 'Ethan Cole', position: 'Principal', project: 'Project North', location: 'Austin' },
  { name: 'Mia Bennett', position: 'Project Manager', project: 'Project North', location: 'Dallas' },
  { name: 'Jordan Price', position: 'Drafter', project: 'Project North', location: 'Houston' },
  { name: 'Sophie Turner', position: 'Senior Designer', project: 'Cloud One', location: 'Austin' },
  { name: 'Caleb Morris', position: 'BIM Specialist', project: 'Tower 36', location: 'Dallas' },
  { name: 'Isla Romero', position: 'Project Coordinator', project: 'Skyline East', location: 'Houston' },
  { name: 'Noel Hayes', position: 'Technical Architect', project: 'Skyline East', location: 'Austin' },
  { name: 'Tessa Nguyen', position: 'Drafter', project: 'Skyline East', location: 'Dallas' },
  { name: 'Victor Shah', position: 'Project Manager', project: 'Harbor Point', location: 'Houston' },
  { name: 'Leah Brooks', position: 'Interior Designer', project: 'Harbor Point', location: 'Austin' },
]

const makeDefaultStaffingRows = (): StaffingRow[] => [
  { id: makeId(), status: 'In Progress', priority: 'High', start: '2026-03-11', due: '2026-03-15', task: 'Issue DD plan set updates', assignee: 'Alex Morgan', notes: 'Coordinate with MEP before issue' },
  { id: makeId(), status: 'Not Started', priority: 'Medium', start: '2026-03-12', due: '2026-03-18', task: 'Compile consultant responses', assignee: 'Rina Patel', notes: 'Await structural markups' },
  { id: makeId(), status: 'On Hold', priority: 'Low', start: '2026-03-10', due: '2026-03-20', task: 'Finalize finish schedule', assignee: 'Team', notes: 'Client material confirmation pending' },
  { id: makeId(), status: 'Complete', priority: 'High', start: '2026-03-08', due: '2026-03-10', task: 'Publish meeting minutes', assignee: 'Alex Morgan', notes: 'Distributed to all consultants' },
  { id: makeId(), status: 'In Progress', priority: 'Medium', start: '2026-03-11', due: '2026-03-17', task: 'Update permit comment log', assignee: 'Rina Patel', notes: 'Track open items by discipline' },
]

const defaultMeetingAgendaItems = [
  'Permit timeline and review comments status',
  'MEP clash resolution at Level 02 corridor',
  'Pending RFI responses impacting millwork release',
]

const DEMO_CALENDAR_DEADLINES: CalendarDeadline[] = [
  { title: 'Example Deliverable — Permit Submission', dateKey: '2026-03-10', source: 'Demo' },
  { title: 'Demo Deadline — Site Survey Coordination', dateKey: '2026-04-04', source: 'Demo', status: 'on-track' },
  { title: 'Demo Deadline — Schematic Set Issue', dateKey: '2026-04-08', source: 'Demo', status: 'overdue' },
  { title: 'Demo Deadline — Consultant RFI Roundup', dateKey: '2026-04-11', source: 'Demo', status: 'not-selected' },
  { title: 'Demo Deadline — Structural Markup Review', dateKey: '2026-04-15', source: 'Demo', status: 'completed' },
  { title: 'Demo Deadline — MEP Clash Resolution', dateKey: '2026-04-18', source: 'Demo', status: 'on-track' },
  { title: 'Demo Deadline — Interior Finish Schedule', dateKey: '2026-04-21', source: 'Demo', status: 'not-selected' },
  { title: 'Demo Deadline — Client Presentation Deck', dateKey: '2026-04-24', source: 'Demo', status: 'on-track' },
  { title: 'Demo Deadline — Permit Resubmission', dateKey: '2026-04-26', source: 'Demo', status: 'overdue' },
  { title: 'Demo Deadline — QA/QC Redline Pass', dateKey: '2026-04-28', source: 'Demo', status: 'completed' },
  { title: 'Demo Deadline — Issue Coordination Set', dateKey: '2026-04-30', source: 'Demo', status: 'on-track' },
]

const mergeDemoCalendarDeadlines = (items: CalendarDeadline[]) => {
  const normalized = items.map(normalizeCalendarDeadline)
  const seen = new Set(normalized.map((d) => `${d.source}|${d.dateKey}|${d.title}`))
  const extras = DEMO_CALENDAR_DEADLINES
    .map(normalizeCalendarDeadline)
    .filter((d) => !seen.has(`${d.source}|${d.dateKey}|${d.title}`))
  return [...normalized, ...extras]
}

const makeBlankMeetingDraft = () => ({
  date: '',
  type: '',
  preparedBy: '',
  location: '',
  attendees: [] as MeetingAttendee[],
  agendaItems: [] as string[],
  actionItems: [] as MeetingActionItem[],
})

const defaultMeetingActionItems: MeetingActionItem[] = (() => {
  const topic1 = makeId()
  const topic2 = makeId()
  return [
    { id: topic1, number: '1', date: '2026-03-10', item: 'Ceiling coordination', owner: 'AOR', due: '2026-03-14', status: 'open' },
    { id: makeId(), parentId: topic1, date: '2026-03-10', item: 'Issue revised reflected ceiling plan', owner: 'AOR', due: '2026-03-14', status: 'open' },
    { id: topic2, number: '2', date: '2026-03-10', item: 'MEP updates', owner: 'MEP', due: '2026-03-15', status: 'in progress' },
    { id: makeId(), parentId: topic2, date: '2026-03-10', item: 'Submit updated load calcs', owner: 'MEP', due: '2026-03-15', status: 'in progress' },
  ]
})()

const normalizeIsoDate = (value: string | undefined) => (/^\d{4}-\d{2}-\d{2}$/.test(value || '') ? (value as string) : '')

type SiteTheme = {
  sapphire: string
  royalBlue: string
  quicksand: string
  primaryAccent: string
  secondaryAccent: string
  swanWing: string
  shellstone: string
  bodyText: string
  line: string
  textSoft: string
  statusSuccess: string
  statusWarning: string
  statusDanger: string
  statusInfo: string
  statusSuccessFg: string
  statusSuccessBg: string
  statusWarningFg: string
  statusWarningBg: string
  statusDangerFg: string
  statusDangerBg: string
  statusInfoFg: string
  statusInfoBg: string
}

type SiteTypography = {
  uiFont: string
  technicalFont: string
  pageTitle: number
  sectionTitle: number
  cardTitle: number
  bodyText: number
  tableText: number
  labels: number
}

type SiteSettings = {
  schemaVersion: 3
  companyName: string
  projectName: string
  companyLogo: string
  companyLogoCropped: string
  companyIcon: string
  companyIconCropped: string
  theme: SiteTheme
  typography: SiteTypography
}

type ProjectOverview = {
  location: string
  buildingPhoto: string
  startDate: string
  endDate: string
  client: string
  projectType: string
  storyCount: string
  totalSquareFootage: string
  topOff: string
  tco: string
  co: string
  phaseDeadlines: {
    Concept: string
    SD: string
    DD: string
    CC: string
  }
}

const blankProjectOverview = (): ProjectOverview => ({
  location: '',
  buildingPhoto: '',
  startDate: '',
  endDate: '',
  client: '',
  projectType: '',
  storyCount: '',
  totalSquareFootage: '',
  topOff: '',
  tco: '',
  co: '',
  phaseDeadlines: { Concept: '', SD: '', DD: '', CC: '' },
})

const defaultTheme: SiteTheme = {
  sapphire: '#1F1F1F',
  royalBlue: '#F9F9F9',
  quicksand: '#C8B89A',
  primaryAccent: '#5F7C68',
  secondaryAccent: '#6F7C86',
  swanWing: '#F0ECE6',
  shellstone: '#1F1F1F',
  bodyText: '#1F1F1F',
  line: '#A3A3A3',
  textSoft: '#6D6D6D',
  statusSuccess: '#456A52',
  statusWarning: '#9A6A2F',
  statusDanger: '#A24E45',
  statusInfo: '#5E6D78',
  statusSuccessFg: '#456A52',
  statusSuccessBg: '#E8F1EB',
  statusWarningFg: '#9A6A2F',
  statusWarningBg: '#F7EEDF',
  statusDangerFg: '#A24E45',
  statusDangerBg: '#F6E8E5',
  statusInfoFg: '#5E6D78',
  statusInfoBg: '#EAF0F4',
}

const defaultSiteSettings: SiteSettings = {
  schemaVersion: 3,
  companyName: 'ODP',
  projectName: 'Tower 36',
  companyLogo: '/archops-logo-transparent.png',
  companyLogoCropped: '',
  companyIcon: '/vite.svg',
  companyIconCropped: '',
  theme: defaultTheme,
  typography: {
    uiFont: 'Inter, system-ui, Arial, sans-serif',
    technicalFont: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
    pageTitle: 32,
    sectionTitle: 20,
    cardTitle: 18,
    bodyText: 14,
    tableText: 14,
    labels: 12,
  },
}

const getInitialSiteSettings = (): SiteSettings => {
  try {
    const raw = localStorage.getItem('site-settings-default-v1')
    if (!raw) return defaultSiteSettings
    const parsed = JSON.parse(raw) as Partial<SiteSettings>
    return {
      ...defaultSiteSettings,
      ...parsed,
      schemaVersion: 3,
      theme: { ...defaultTheme, ...(parsed?.theme || {}) },
      typography: { ...defaultSiteSettings.typography, ...(parsed?.typography || {}) },
    }
  } catch {
    return defaultSiteSettings
  }
}

const hexToRgb = (hex: string) => {
  const cleaned = hex.replace('#', '').trim()
  const full = cleaned.length === 3 ? cleaned.split('').map((c) => c + c).join('') : cleaned
  const int = Number.parseInt(full, 16)
  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  }
}

const tintSymbolImage = (src: string, hexColor: string, fitTarget = 0.82) => new Promise<string>((resolve) => {
  const img = new Image()
  img.onload = () => {
    const srcCanvas = document.createElement('canvas')
    srcCanvas.width = img.naturalWidth || 64
    srcCanvas.height = img.naturalHeight || 64
    const srcCtx = srcCanvas.getContext('2d')
    if (!srcCtx) return resolve(src)
    srcCtx.drawImage(img, 0, 0)

    const imageData = srcCtx.getImageData(0, 0, srcCanvas.width, srcCanvas.height)
    const data = imageData.data
    const { r, g, b } = hexToRgb(hexColor)

    let minX = srcCanvas.width
    let minY = srcCanvas.height
    let maxX = 0
    let maxY = 0
    let hasInk = false

    for (let i = 0; i < data.length; i += 4) {
      const x = (i / 4) % srcCanvas.width
      const y = Math.floor(i / 4 / srcCanvas.width)
      const lum = (0.299 * data[i]) + (0.587 * data[i + 1]) + (0.114 * data[i + 2])
      const alpha = Math.max(0, Math.min(255, 255 - lum))

      data[i] = r
      data[i + 1] = g
      data[i + 2] = b
      data[i + 3] = alpha

      if (alpha > 18) {
        hasInk = true
        if (x < minX) minX = x
        if (y < minY) minY = y
        if (x > maxX) maxX = x
        if (y > maxY) maxY = y
      }
    }

    srcCtx.putImageData(imageData, 0, 0)

    if (!hasInk) return resolve(srcCanvas.toDataURL('image/png'))

    const cropW = maxX - minX + 1
    const cropH = maxY - minY + 1
    const outSize = 96
    const outCanvas = document.createElement('canvas')
    outCanvas.width = outSize
    outCanvas.height = outSize
    const outCtx = outCanvas.getContext('2d')
    if (!outCtx) return resolve(srcCanvas.toDataURL('image/png'))

    const fit = Math.min((outSize * fitTarget) / cropW, (outSize * fitTarget) / cropH)
    const drawW = cropW * fit
    const drawH = cropH * fit
    const dx = (outSize - drawW) / 2
    const dy = (outSize - drawH) / 2

    outCtx.drawImage(srcCanvas, minX, minY, cropW, cropH, dx, dy, drawW, drawH)
    resolve(outCanvas.toDataURL('image/png'))
  }
  img.onerror = () => resolve(src)
  img.src = src
})

const daysUntil = (isoDate?: string) => {
  const normalized = normalizeIsoDate(isoDate)
  if (!normalized) return null
  const today = new Date()
  const target = new Date(`${normalized}T00:00:00`)
  const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const diff = target.getTime() - startToday.getTime()
  return Math.floor(diff / 86400000)
}

const normalizeCalendarDeadline = (d: CalendarDeadline): CalendarDeadline => {
  const assignMatch = d.title.match(/\(Assign to:\s*([^)]*)\)$/i)
  const assignFromTitle = (assignMatch?.[1] || '').trim()
  const baseTitle = d.title.replace(/\s*\(Assign to:[^)]*\)\s*$/i, '').trim()
  const [mainTitle, ...descParts] = baseTitle.split(' — ')
  return {
    ...d,
    title: (mainTitle || d.title || '').trim(),
    description: (d.description || descParts.join(' — ') || '').trim() || undefined,
    assignTo: (d.assignTo || assignFromTitle || '').trim() || undefined,
    status: d.status === 'not-selected' || d.status === 'overdue' || d.status === 'completed' || d.status === 'on-track' ? d.status : 'not-selected',
  }
}

const normalizeMeetingActionItems = (items: MeetingActionItem[]): MeetingActionItem[] => {
  const topics = items.filter((x) => !x.parentId)
  const topicIdByNumber = new Map<string, string>()
  topics.forEach((t, i) => topicIdByNumber.set(String(t.number || i + 1), t.id))
  return items.map((item) => {
    const rawStatus = (item.status || '').toLowerCase().trim()
    const normalizedStatus = rawStatus === 'complete' || rawStatus === 'completed' || rawStatus === 'done'
      ? 'closed'
      : (['open', 'in progress', 'closed'].includes(rawStatus) ? rawStatus : 'open')

    if (!item.parentId) {
      return {
        ...item,
        number: String(item.number || topics.findIndex((t) => t.id === item.id) + 1),
        date: normalizeIsoDate(item.date),
        due: normalizeIsoDate(item.due),
        status: normalizedStatus,
      }
    }

    return {
      ...item,
      parentId: topicIdByNumber.get(String(item.parentId)) || item.parentId,
      date: normalizeIsoDate(item.date),
      due: normalizeIsoDate(item.due),
      status: normalizedStatus,
    }
  })
}

const useTaskStore = create<TaskStore>()(
  persist(
    (set) => ({
      tasks: [
        {
          id: makeId(),
          title: 'OpenClaw dashboard v3 planning',
          status: 'doing',
          priority: 'P1',
          owner: 'Head Bot',
          progress: 35,
          blocked: false,
          createdAt: now(),
          updatedAt: now(),
        },
      ],
      history: [],
      documents: [
        {
          id: makeId(),
          title: 'Project Vault',
          kind: 'note',
          source: 'You',
          description: 'Project workspace initialized.',
          folderPath: 'http://187.124.92.179:8791/',
          folders: [
            { name: 'inbox', path: 'http://187.124.92.179:8791/inbox/' },
            { name: 'outbound', path: 'http://187.124.92.179:8791/outbound/' },
          ],
          createdAt: now(),
        },
      ],
      addTask: ({ title, notes, priority, owner, dueDate }) => {
        const id = makeId()
        const stamp = now()
        set((s) => ({
          tasks: [
            {
              id,
              title,
              notes,
              status: 'backlog',
              priority,
              owner,
              dueDate,
              progress: 0,
              blocked: false,
              createdAt: stamp,
              updatedAt: stamp,
            },
            ...s.tasks,
          ],
          history: [{ id: makeId(), taskId: id, taskTitle: title, at: stamp, message: `Created in backlog • ${priority} • ${owner}` }, ...s.history].slice(0, 300),
        }))
      },
      queueAgentTask: ({ title }) => {
        const id = makeId()
        const stamp = now()
        const projectName = 'Project'
        set((s) => ({
          tasks: [
            {
              id,
              title: `${projectName}: ${title}`,
              notes: 'Queued from Vault RedLine',
              status: 'doing',
              priority: 'P1',
              owner: 'Head Bot',
              progress: 10,
              blocked: false,
              createdAt: stamp,
              updatedAt: stamp,
            },
            ...s.tasks,
          ],
          history: [
            { id: makeId(), taskId: id, taskTitle: `${projectName}: ${title}`, at: stamp, message: `Queued to ${projectName} agent` },
            ...s.history,
          ].slice(0, 300),
        }))
      },
      moveTask: (id, status) =>
        set((s) => {
          const t = s.tasks.find((x) => x.id === id)
          if (!t || t.status === status) return s
          const stamp = now()
          return {
            tasks: s.tasks.map((x) =>
              x.id === id
                ? {
                    ...x,
                    status,
                    progress: status === 'done' ? 100 : status === 'doing' ? Math.max(x.progress, 10) : x.progress,
                    updatedAt: stamp,
                    completedAt: status === 'done' ? stamp : undefined,
                  }
                : x,
            ),
            history: [{ id: makeId(), taskId: id, taskTitle: t.title, at: stamp, message: `Moved ${t.status} → ${status}` }, ...s.history].slice(0, 300),
          }
        }),
      setPriority: (id, priority) =>
        set((s) => ({
          tasks: s.tasks.map((x) => (x.id === id ? { ...x, priority, updatedAt: now() } : x)),
          history: (() => {
            const t = s.tasks.find((x) => x.id === id)
            return t ? [{ id: makeId(), taskId: id, taskTitle: t.title, at: now(), message: `Priority set to ${priority}` }, ...s.history].slice(0, 300) : s.history
          })(),
        })),
      setOwner: (id, owner) =>
        set((s) => ({
          tasks: s.tasks.map((x) => (x.id === id ? { ...x, owner, updatedAt: now() } : x)),
          history: (() => {
            const t = s.tasks.find((x) => x.id === id)
            return t ? [{ id: makeId(), taskId: id, taskTitle: t.title, at: now(), message: `Owner set to ${owner}` }, ...s.history].slice(0, 300) : s.history
          })(),
        })),
      setDueDate: (id, dueDate) =>
        set((s) => ({
          tasks: s.tasks.map((x) => (x.id === id ? { ...x, dueDate, updatedAt: now() } : x)),
        })),
      setProgress: (id, progress) =>
        set((s) => ({
          tasks: s.tasks.map((x) =>
            x.id === id ? { ...x, progress: Math.max(0, Math.min(100, progress)), updatedAt: now() } : x,
          ),
        })),
      toggleBlocked: (id, blockerReason) =>
        set((s) => {
          const t = s.tasks.find((x) => x.id === id)
          if (!t) return s
          const blocked = !t.blocked
          return {
            tasks: s.tasks.map((x) =>
              x.id === id ? { ...x, blocked, blockerReason: blocked ? blockerReason || 'Waiting on input' : undefined, updatedAt: now() } : x,
            ),
            history: [{ id: makeId(), taskId: id, taskTitle: t.title, at: now(), message: blocked ? 'Marked blocked' : 'Unblocked' }, ...s.history].slice(0, 300),
          }
        }),
      removeTask: (id) =>
        set((s) => {
          const t = s.tasks.find((x) => x.id === id)
          return {
            tasks: s.tasks.filter((x) => x.id !== id),
            history: t ? [{ id: makeId(), taskId: id, taskTitle: t.title, at: now(), message: 'Deleted task' }, ...s.history].slice(0, 300) : s.history,
          }
        }),
      addDocument: (payload) =>
        set((s) => ({
          documents: [{ id: makeId(), createdAt: now(), ...payload }, ...s.documents],
        })),
      removeDocument: (id) =>
        set((s) => ({
          documents: s.documents.filter((d) => d.id !== id),
        })),
    }),
    {
      name: 'headbot-mission-control-style-lab-v1',
      version: 6,
      migrate: (persistedState: unknown) => {
        const state = persistedState as Partial<TaskStore> | undefined
        const docs = state?.documents ?? []

        const ensureProject = (title: string, baseUrl: string) => {
          const existing = docs.find((d) => d.title === title)
          if (!existing) {
            docs.unshift({
              id: makeId(),
              title,
              kind: 'note',
              source: 'You',
              description: 'Project workspace initialized.',
              folderPath: `${baseUrl}/`,
              folders: [
                { name: 'inbox', path: `${baseUrl}/inbox/` },
                { name: 'outbound', path: `${baseUrl}/outbound/` },
              ],
              createdAt: now(),
            })
          } else {
            if (!existing.folderPath) existing.folderPath = `${baseUrl}/`
            existing.folders = [
              { name: 'inbox', path: `${baseUrl}/inbox/` },
              { name: 'outbound', path: `${baseUrl}/outbound/` },
            ]
          }
        }

        ensureProject('Project Vault', 'http://187.124.92.179:8791')

        return { ...(state as object), documents: docs } as TaskStore
      },
    },
  ),
)

function App() {
  const store = useTaskStore()
  const apiBase = (((import.meta.env.VITE_API_BASE as string | undefined) || window.location.origin).replace(/\/$/, ''))
  const getInitialRoute = () => {
    const rawPath = (window.location.pathname || '/').toLowerCase()
    const path = rawPath.endsWith('/') && rawPath !== '/' ? rawPath.slice(0, -1) : rawPath
    if (path === '/login') return 'login' as const
    if (path === '/app') return 'app' as const
    return 'landing' as const
  }
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      const token = localStorage.getItem('odp-auth-token-v1')
      const expires = Number(localStorage.getItem('odp-auth-exp-v1') || '0')
      return !!token && Date.now() < expires
    } catch {
      return false
    }
  })
  const [routeView, setRouteView] = useState<'landing' | 'login' | 'app'>(() => getInitialRoute())
  const [tenantId, setTenantId] = useState(() => {
    try {
      return localStorage.getItem('odp-tenant-id-v1') || 'default'
    } catch {
      return 'default'
    }
  })
  const [currentUsername, setCurrentUsername] = useState(() => {
    try {
      return localStorage.getItem('odp-auth-user-v1') || 'Operator'
    } catch {
      return 'Operator'
    }
  })
  const normalizeRole = (role: string) => {
    const r = String(role || '').trim().toLowerCase()
    if (r === 'admin') return 'Admin'
    if (r === 'executive') return 'Executive'
    if (r === 'principal' || r === 'principle') return 'Principal'
    if (r === 'project manager' || r === 'project_manager') return 'Project Manager'
    if (r === 'drafter') return 'Drafter'
    return 'Executive'
  }
  const [currentRole, setCurrentRole] = useState(() => {
    try {
      return normalizeRole(localStorage.getItem('odp-auth-role-v1') || 'Executive')
    } catch {
      return 'Executive'
    }
  })
  const [currentProjectAccess, setCurrentProjectAccess] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('odp-auth-projects-v1')
      const parsed = raw ? JSON.parse(raw) : ['*']
      return Array.isArray(parsed) ? parsed.map((x) => String(x)) : ['*']
    } catch {
      return ['*']
    }
  })
  const [loginUsername, setLoginUsername] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [loginRemember, setLoginRemember] = useState(true)
  const [localFontFamilies, setLocalFontFamilies] = useState<string[]>([])
  const [loginPending, setLoginPending] = useState(false)
  const [loginError, setLoginError] = useState('')

  const navigateTo = (next: 'landing' | 'login' | 'app', replace = false) => {
    setRouteView(next)
    const target = next === 'landing' ? '/' : `/${next}`
    if (replace) window.history.replaceState({}, '', target)
    else window.history.pushState({}, '', target)
  }

  const apiFetch = useCallback((url: string, init: RequestInit = {}) => {
    const token = localStorage.getItem('odp-auth-token-v1') || ''
    const baseHeaders: Record<string, string> = { 'X-Tenant-Id': tenantId }
    if (token) baseHeaders.Authorization = `Bearer ${token}`
    return fetch(url, {
      ...init,
      headers: {
        ...baseHeaders,
        ...(init.headers as Record<string, string> | undefined),
      },
    })
  }, [tenantId])

  const [activityModuleFilter, setActivityModuleFilter] = useState('')
  const [activitySeverityFilter, setActivitySeverityFilter] = useState('')

  const loadActivityFeed = useCallback(async () => {
    setActivityLoading(true)
    setActivityError('')
    try {
      const params = new URLSearchParams({ limit: '150' })
      if (activityModuleFilter) params.set('module', activityModuleFilter)
      if (activitySeverityFilter) params.set('severity', activitySeverityFilter)
      const res = await apiFetch(`${apiBase}/api/activity?${params.toString()}`)
      const raw = await res.text()
      let data: { ok?: boolean; rows?: ActivityEntry[]; error?: string } | null = null
      try {
        data = JSON.parse(raw)
      } catch {
        throw new Error('Activity API not reachable (received HTML instead of JSON).')
      }

      if (!res.ok || !data?.ok) throw new Error(data?.error || 'Activity load failed')
      setActivityRows(Array.isArray(data.rows) ? data.rows : [])
    } catch (err) {
      setActivityError(err instanceof Error ? err.message : 'Activity load failed')
    } finally {
      setActivityLoading(false)
    }
  }, [activityModuleFilter, activitySeverityFilter, apiBase, apiFetch])

  const runStartupHealthChecks = useCallback(async () => {
    setStartupHealthLoading(true)
    const endpoints: Array<{ label: string; path: string }> = [
      { label: 'Auth Verify', path: '/api/auth/verify' },
      { label: 'App State', path: '/api/app-state' },
      { label: 'Calendar State', path: '/api/calendar-state' },
      { label: 'Directory Contacts', path: '/api/directory-contacts' },
      { label: 'Tasks State', path: '/api/tasks-state' },
    ]

    const results = await Promise.all(endpoints.map(async ({ label, path }) => {
      try {
        const res = await apiFetch(`${apiBase}${path}`)
        return { label, ok: res.ok, status: res.status, detail: res.ok ? 'OK' : `HTTP ${res.status}` } as StartupHealthCheck
      } catch (err) {
        return { label, ok: false, status: 'ERR', detail: err instanceof Error ? err.message : 'Request failed' } as StartupHealthCheck
      }
    }))

    setStartupHealthChecks(results)
    setStartupHealthLoading(false)
  }, [apiBase, apiFetch])

  const allTabs: TabKey[] = ['projectDashboards', 'projects', 'officeOverview', 'consultantCoordinationTracking', 'drawingVersionHistory', 'decisionLogs', 'caWorkflows', 'staffingAllocation', 'teamTasks', 'teamOrganization', 'meetingActionTracking', 'calendar', 'projectStandards', 'directory', 'consultantList', 'activity', 'settings']
  const isTabKey = (value: string | null | undefined): value is TabKey => !!value && allTabs.includes(value as TabKey)

  const [activeTab, setActiveTab] = useState<TabKey>(() => {
    try {
      const tabFromUrl = new URLSearchParams(window.location.search).get('tab')
      if (isTabKey(tabFromUrl)) return tabFromUrl
    } catch {
      // ignore URL parse failures
    }

    try {
      const saved = localStorage.getItem('active-tab-v1')
      if (isTabKey(saved)) return saved
    } catch {
      // ignore storage access errors
    }

    return 'projectDashboards'
  })
  const appStateHydratedRef = useRef(false)
  const roleTabAccess: Record<string, { view: TabKey[]; edit: TabKey[] }> = {
    Admin: {
      view: allTabs,
      edit: allTabs,
    },
    Executive: {
      view: ['officeOverview', 'teamTasks', 'consultantList', 'projects', 'activity', 'settings'],
      edit: ['officeOverview', 'teamTasks', 'consultantList', 'projects', 'activity', 'settings'],
    },
    Principal: {
      view: ['projectDashboards', 'directory', 'calendar', 'consultantCoordinationTracking', 'teamOrganization', 'staffingAllocation', 'meetingActionTracking', 'projectStandards', 'activity'],
      edit: ['projectDashboards', 'directory', 'calendar', 'consultantCoordinationTracking', 'teamOrganization', 'staffingAllocation', 'meetingActionTracking', 'projectStandards', 'activity'],
    },
    'Project Manager': {
      view: ['projectDashboards', 'directory', 'calendar', 'consultantCoordinationTracking', 'teamOrganization', 'staffingAllocation', 'meetingActionTracking', 'projectStandards', 'activity'],
      edit: ['projectDashboards', 'directory', 'calendar', 'consultantCoordinationTracking', 'staffingAllocation', 'meetingActionTracking', 'projectStandards', 'activity'],
    },
    Drafter: {
      view: ['projectDashboards', 'directory', 'calendar', 'consultantCoordinationTracking', 'staffingAllocation', 'meetingActionTracking', 'projectStandards', 'activity'],
      edit: ['directory', 'calendar', 'consultantCoordinationTracking', 'staffingAllocation', 'meetingActionTracking', 'activity'],
    },
  }
  const [rolePreview, setRolePreview] = useState<string | null>(null)
  const effectiveRole = currentRole === 'Admin' && rolePreview ? rolePreview : currentRole
  const allowedTabs = roleTabAccess[effectiveRole]?.view || roleTabAccess.Principal.view
  const editableTabs = roleTabAccess[effectiveRole]?.edit || roleTabAccess.Principal.edit
  const canViewTab = (tab: TabKey) => allowedTabs.includes(tab)
  const canEditTab = (tab: TabKey) => editableTabs.includes(tab)

  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [navFlyoutGroup, setNavFlyoutGroup] = useState<'people' | 'projects' | 'settings' | 'corePeople' | 'coreProjects' | 'coreConsultants' | null>(null)
  const [navFlyoutTop, setNavFlyoutTop] = useState(64)
  const phaseDateInputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const companyHolidayUploadRef = useRef<HTMLInputElement | null>(null)
  const companyLogoUploadRef = useRef<HTMLInputElement | null>(null)
  const companyIconUploadRef = useRef<HTMLInputElement | null>(null)
  const [showSiteSettings, setShowSiteSettings] = useState(false)
  const [showProfileSettings, setShowProfileSettings] = useState(false)
  const [consultantCompanyReviews, setConsultantCompanyReviews] = useState<Record<string, ConsultantReview[]>>(() => {
    try {
      const raw = localStorage.getItem('consultant-company-reviews-v1')
      const parsed = raw ? JSON.parse(raw) : {}
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  })
  const [showImageCropModal, setShowImageCropModal] = useState(false)
  const [imageCropTarget, setImageCropTarget] = useState<'companyLogo' | 'companyIcon' | null>(null)
  const [imageCropSource, setImageCropSource] = useState('')
  const [imageCropRect, setImageCropRect] = useState({ x: 10, y: 10, w: 80 })
  const imageCropDragRef = useRef<{ mode: 'move' | 'nw' | 'ne' | 'sw' | 'se'; startX: number; startY: number; startRect: { x: number; y: number; w: number } } | null>(null)
  const [profileSettingsTab, setProfileSettingsTab] = useState<string | null>(() => {
    try {
      return localStorage.getItem('profile-settings-tab-v1') || null
    } catch {
      return null
    }
  })
  const [showNotifications, setShowNotifications] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const settingsGearBtnRef = useRef<HTMLButtonElement | null>(null)
  const settingsDropdownRef = useRef<HTMLDivElement | null>(null)
  const settingsDetailRef = useRef<HTMLDivElement | null>(null)
  const notificationsBtnRef = useRef<HTMLButtonElement | null>(null)
  const notificationsDropdownRef = useRef<HTMLDivElement | null>(null)
  const [notificationPrefs, setNotificationPrefs] = useState<Record<NotificationTopic, NotificationLevel>>({
    taskUpdates: 'all',
    assignmentChanges: 'all',
    meetingReminders: 'all',
    consultantUpdates: 'all',
    projectChanges: 'all',
  })

  useEffect(() => {
    if (!sidebarCollapsed) setNavFlyoutGroup(null)
  }, [sidebarCollapsed])
  const [appNotifications, setAppNotifications] = useState<AppNotification[]>([])
  const [activityRows, setActivityRows] = useState<ActivityEntry[]>([])
  const [activityLoading, setActivityLoading] = useState(false)
  const [activityError, setActivityError] = useState('')
  const [startupHealthLoading, setStartupHealthLoading] = useState(false)
  const [startupHealthChecks, setStartupHealthChecks] = useState<StartupHealthCheck[]>([])
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)
  const [mustChangePassword, setMustChangePassword] = useState(false)
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false)
  const [twoFactorChallengeId, setTwoFactorChallengeId] = useState('')
  const [twoFactorCodeInput, setTwoFactorCodeInput] = useState('')
  const [twoFactorRecoveryInput, setTwoFactorRecoveryInput] = useState('')
  const [twoFactorError, setTwoFactorError] = useState('')
  const [twoFactorEnrollmentSecret, setTwoFactorEnrollmentSecret] = useState('')
  const [twoFactorEnrollmentQr, setTwoFactorEnrollmentQr] = useState('')
  const [twoFactorEnrollmentCode, setTwoFactorEnrollmentCode] = useState('')
  const [twoFactorRecoveryCodes, setTwoFactorRecoveryCodes] = useState<string[]>([])
  const [showInviteUserModal, setShowInviteUserModal] = useState(false)
  const [showDeactivateUserModal, setShowDeactivateUserModal] = useState(false)
  const [deactivateUserTarget, setDeactivateUserTarget] = useState('')
  const [inviteUserPassword, setInviteUserPassword] = useState('')
  const [inviteUserPasswordConfirm, setInviteUserPasswordConfirm] = useState('')
  const [profileAccounts, setProfileAccounts] = useState<Array<{ username: string; email: string; tier: string; name: string; project: string; active: boolean }>>(() => {
    try {
      const raw = localStorage.getItem('odp-profile-accounts-v1')
      const parsed = raw ? JSON.parse(raw) : []
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  })
  const [inviteUserDraft, setInviteUserDraft] = useState<OfficeContactCardDraft>({
    name: '',
    position: '',
    accessTier: 'Drafter',
    project: '',
    projects: [''],
    location: '',
    reportsTo: '',
    pastProjects: '',
    email: '',
    phone: '',
    extension: '',
    cell: '',
  })
  const [currentPasswordInput, setCurrentPasswordInput] = useState('')
  const [newPasswordInput, setNewPasswordInput] = useState('')
  const [confirmPasswordInput, setConfirmPasswordInput] = useState('')
  const [passwordUpdateError, setPasswordUpdateError] = useState('')
  const [profileDashboardVisibility, setProfileDashboardVisibility] = useState(() => {
    try {
      const raw = localStorage.getItem('profile-dashboard-visibility-v1')
      if (!raw) {
        return { deadlinesMilestones: true, calendar: true, meetingTopicsActions: true, taskHealth: true, consultantCoordination: true }
      }
      const parsed = JSON.parse(raw)
      return {
        deadlinesMilestones: parsed?.deadlinesMilestones !== false,
        calendar: parsed?.calendar !== false,
        meetingTopicsActions: parsed?.meetingTopicsActions !== false,
        taskHealth: parsed?.taskHealth !== false,
        consultantCoordination: parsed?.consultantCoordination !== false,
      }
    } catch {
      return { deadlinesMilestones: true, calendar: true, meetingTopicsActions: true, taskHealth: true, consultantCoordination: true }
    }
  })
  const [siteSettings, setSiteSettings] = useState<SiteSettings>(() => getInitialSiteSettings())
  const [savedDefaultSiteSettings, setSavedDefaultSiteSettings] = useState<SiteSettings>(() => {
    try {
      const raw = localStorage.getItem('site-settings-default-v1')
      if (!raw) return getInitialSiteSettings()
      const parsed = JSON.parse(raw) as Partial<SiteSettings>
      return {
        ...defaultSiteSettings,
        ...parsed,
        schemaVersion: 3,
        theme: { ...defaultTheme, ...(parsed?.theme || {}) },
        typography: { ...defaultSiteSettings.typography, ...(parsed?.typography || {}) },
      }
    } catch {
      return getInitialSiteSettings()
    }
  })
  const [hasSavedDefaultSiteSettings, setHasSavedDefaultSiteSettings] = useState<boolean>(() => {
    try {
      return !!localStorage.getItem('site-settings-default-v1')
    } catch {
      return false
    }
  })
  const [siteSettingsHydrated, setSiteSettingsHydrated] = useState(false)

  useEffect(() => {
    const onPopState = () => setRouteView(getInitialRoute())
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const clearAuth = () => {
    setIsAuthenticated(false)
    setMustChangePassword(false)
    try {
      localStorage.removeItem('odp-auth-token-v1')
      localStorage.removeItem('odp-auth-exp-v1')
      localStorage.removeItem('odp-auth-user-v1')
      localStorage.removeItem('odp-auth-role-v1')
      localStorage.removeItem('odp-auth-projects-v1')
      // Keep tenant id so company context doesn't silently reset to "default" on next login.
    } catch {
      // ignore storage failures
    }
  }

  useEffect(() => {
    const token = localStorage.getItem('odp-auth-token-v1')
    const expires = Number(localStorage.getItem('odp-auth-exp-v1') || '0')
    if (!token || !expires || Date.now() >= expires) {
      if (token || expires) clearAuth()
      return
    }
    ;(async () => {
      try {
        const res = await apiFetch(`${apiBase}/api/auth/verify`, {
          headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenantId },
        })
        const data = await res.json()
        if (!res.ok || !data?.ok) clearAuth()
        else if (data?.profile?.accessTier) {
          const normalized = normalizeRole(String(data.profile.accessTier))
          const projects = Array.isArray(data.profile.projects) ? data.profile.projects.map((x: unknown) => String(x)) : ['*']
          const requirePasswordChange = Boolean(data?.mustChangePassword)
          setCurrentRole(normalized)
          setCurrentProjectAccess(projects)
          setMustChangePassword(requirePasswordChange)
          if (requirePasswordChange) setShowChangePasswordModal(true)
          localStorage.setItem('odp-auth-role-v1', normalized)
          localStorage.setItem('odp-auth-projects-v1', JSON.stringify(projects))
        }
      } catch {
        clearAuth()
      }
    })()
  }, [apiBase, tenantId])

  useEffect(() => {
    if (!isAuthenticated && routeView === 'app') {
      navigateTo('login', true)
    }
  }, [isAuthenticated, routeView])

  useEffect(() => {
    if (!isAuthenticated || activeTab !== 'activity') return
    void loadActivityFeed()
  }, [isAuthenticated, activeTab, activityModuleFilter, activitySeverityFilter])

  useEffect(() => {
    // Avoid refresh flicker: wait until app-state hydration settles before
    // enforcing role-based tab fallback.
    if (!appStateHydratedRef.current) return
    if (!canViewTab(activeTab)) {
      setActiveTab(allowedTabs[0] || 'projectDashboards')
    }
  }, [activeTab, effectiveRole])

  useEffect(() => {
    let active = true
    const loadLocalFonts = async () => {
      try {
        const fn = (window as unknown as { queryLocalFonts?: () => Promise<Array<{ family?: string }>> }).queryLocalFonts
        if (!fn) return
        const fonts = await fn()
        const families = Array.from(new Set(fonts.map((f) => (f.family || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b))
        if (active) setLocalFontFamilies(families)
      } catch {
        // Local Font Access API may be unavailable/denied; keep fallback options.
      }
    }
    void loadLocalFonts()
    return () => { active = false }
  }, [])

  useEffect(() => {
    const onDocPointerDown = (event: MouseEvent) => {
      const target = event.target as Node

      if (showProfileSettings) {
        const insideSettings =
          settingsGearBtnRef.current?.contains(target)
          || settingsDropdownRef.current?.contains(target)
          || settingsDetailRef.current?.contains(target)

        if (!insideSettings) {
          setShowProfileSettings(false)
          setProfileSettingsTab(null)
        }
      }

      if (showNotifications) {
        const insideNotifications =
          notificationsBtnRef.current?.contains(target)
          || notificationsDropdownRef.current?.contains(target)

        if (!insideNotifications) setShowNotifications(false)
      }
    }

    document.addEventListener('mousedown', onDocPointerDown)
    return () => document.removeEventListener('mousedown', onDocPointerDown)
  }, [showProfileSettings, showNotifications])

  const [docDescription, setDocDescription] = useState('')
  const [projectOverviewByProject, setProjectOverviewByProject] = useState<Record<string, ProjectOverview>>(() => {
    try {
      const rawByProject = localStorage.getItem('project-overview-by-project-v1')
      if (rawByProject) {
        const parsed = JSON.parse(rawByProject)
        if (parsed && typeof parsed === 'object') return parsed as Record<string, ProjectOverview>
      }

      const rawLegacy = localStorage.getItem('project-overview-v1')
      if (!rawLegacy) return {}
      const parsedLegacy = JSON.parse(rawLegacy)
      return {
        default: {
          location: parsedLegacy?.location || '',
          buildingPhoto: parsedLegacy?.buildingPhoto || '',
          startDate: parsedLegacy?.startDate || '',
          endDate: parsedLegacy?.endDate || '',
          client: parsedLegacy?.client || '',
          projectType: parsedLegacy?.projectType || '',
          storyCount: parsedLegacy?.storyCount || '',
          totalSquareFootage: parsedLegacy?.totalSquareFootage || '',
          topOff: parsedLegacy?.topOff || '',
          tco: parsedLegacy?.tco || '',
          co: parsedLegacy?.co || '',
          phaseDeadlines: {
            Concept: parsedLegacy?.phaseDeadlines?.Concept || '',
            SD: parsedLegacy?.phaseDeadlines?.SD || '',
            DD: parsedLegacy?.phaseDeadlines?.DD || '',
            CC: parsedLegacy?.phaseDeadlines?.CC || parsedLegacy?.phaseDeadlines?.NCD || '',
          },
        },
      }
    } catch {
      return {}
    }
  })
  const projectOverviewKey = (siteSettings.projectName || 'default').trim().toLowerCase()
  const projectOverview = projectOverviewByProject[projectOverviewKey] || blankProjectOverview()
  const updateProjectOverview = (updater: ProjectOverview | ((prev: ProjectOverview) => ProjectOverview)) => {
    setProjectOverviewByProject((prev) => {
      const current = prev[projectOverviewKey] || blankProjectOverview()
      const next = typeof updater === 'function'
        ? (updater as (p: ProjectOverview) => ProjectOverview)(current)
        : updater
      return { ...prev, [projectOverviewKey]: next }
    })
  }
  const [projectProgress] = useState(35)
  const calculateTimelineProgress = (overview: ProjectOverview) => {
    const parseDate = (value?: string) => {
      if (!value) return NaN
      const dt = new Date(`${value}T00:00:00`)
      return Number.isNaN(dt.getTime()) ? NaN : dt.getTime()
    }

    const phaseDates = (['Concept', 'SD', 'DD', 'CC'] as const)
      .map((key) => parseDate(overview.phaseDeadlines[key]))
      .filter((x) => !Number.isNaN(x))

    if (!phaseDates.length) return projectProgress

    const configuredStart = parseDate(overview.startDate)
    const configuredEnd = parseDate(overview.endDate)
    const inferredStart = Math.min(...phaseDates)
    const inferredEnd = Math.max(...phaseDates)

    const timelinePoints = [
      Number.isNaN(configuredStart) ? inferredStart : configuredStart,
      ...phaseDates,
      Number.isNaN(configuredEnd) ? inferredEnd : configuredEnd,
    ]
      .filter((x, idx, arr) => arr.indexOf(x) === idx)
      .sort((a, b) => a - b)

    if (timelinePoints.length < 2) return projectProgress

    const now = Date.now()
    const first = timelinePoints[0]
    const last = timelinePoints[timelinePoints.length - 1]
    if (now <= first) return 0
    if (now >= last) return 100

    const segmentCount = timelinePoints.length - 1
    for (let i = 0; i < segmentCount; i += 1) {
      const segStart = timelinePoints[i]
      const segEnd = timelinePoints[i + 1]
      if (now >= segStart && now <= segEnd) {
        const fraction = segEnd === segStart ? 0 : (now - segStart) / (segEnd - segStart)
        return Math.round(((i + fraction) / segmentCount) * 100)
      }
    }

    return projectProgress
  }
  const timelineProjectProgress = useMemo(() => calculateTimelineProgress(projectOverview), [projectOverview, projectProgress])
  const getCurrentPhaseStatus = (overview: ProjectOverview): { phase: 'Concept' | 'SD' | 'DD' | 'CC'; progress: number } => {
    const parseDate = (value?: string) => {
      if (!value) return NaN
      const dt = new Date(`${value}T00:00:00`)
      return Number.isNaN(dt.getTime()) ? NaN : dt.getTime()
    }

    const milestone = {
      Concept: parseDate(overview.phaseDeadlines.Concept),
      SD: parseDate(overview.phaseDeadlines.SD),
      DD: parseDate(overview.phaseDeadlines.DD),
      CC: parseDate(overview.phaseDeadlines.CC),
    }

    const configuredStart = parseDate(overview.startDate)
    const configuredEnd = parseDate(overview.endDate)
    const allKnownDates = [configuredStart, configuredEnd, milestone.Concept, milestone.SD, milestone.DD, milestone.CC]
      .filter((x) => !Number.isNaN(x))

    if (!allKnownDates.length) return { phase: 'Concept', progress: 0 }

    const inferredStart = Math.min(...allKnownDates)
    const inferredEnd = Math.max(...allKnownDates)
    const timelineStart = Number.isNaN(configuredStart) ? inferredStart : configuredStart
    const timelineEnd = Number.isNaN(configuredEnd) ? inferredEnd : configuredEnd

    const conceptStart = timelineStart
    const conceptEnd = !Number.isNaN(milestone.Concept) ? milestone.Concept
      : !Number.isNaN(milestone.SD) ? milestone.SD
      : !Number.isNaN(milestone.DD) ? milestone.DD
      : !Number.isNaN(milestone.CC) ? milestone.CC
      : timelineEnd

    const sdStart = !Number.isNaN(milestone.Concept) ? milestone.Concept : conceptStart
    const sdEnd = !Number.isNaN(milestone.SD) ? milestone.SD
      : !Number.isNaN(milestone.DD) ? milestone.DD
      : !Number.isNaN(milestone.CC) ? milestone.CC
      : timelineEnd

    const ddStart = !Number.isNaN(milestone.SD) ? milestone.SD : sdStart
    const ddEnd = !Number.isNaN(milestone.DD) ? milestone.DD
      : !Number.isNaN(milestone.CC) ? milestone.CC
      : timelineEnd

    const ccStart = !Number.isNaN(milestone.DD) ? milestone.DD : ddStart
    const ccEnd = !Number.isNaN(milestone.CC) ? milestone.CC : timelineEnd

    const now = Date.now()
    const activePhase: 'Concept' | 'SD' | 'DD' | 'CC' = now < sdStart
      ? 'Concept'
      : now < ddStart
        ? 'SD'
        : now < ccStart
          ? 'DD'
          : 'CC'

    const bounds = activePhase === 'Concept'
      ? [conceptStart, conceptEnd]
      : activePhase === 'SD'
        ? [sdStart, sdEnd]
        : activePhase === 'DD'
          ? [ddStart, ddEnd]
          : [ccStart, ccEnd]

    const [phaseStart, phaseEnd] = bounds
    if (!Number.isFinite(phaseStart) || !Number.isFinite(phaseEnd) || phaseEnd <= phaseStart) {
      return { phase: activePhase, progress: now >= phaseEnd ? 100 : 0 }
    }

    const raw = ((now - phaseStart) / (phaseEnd - phaseStart)) * 100
    const progress = Math.max(0, Math.min(100, Math.round(raw)))
    return { phase: activePhase, progress }
  }
  const [projectsSearch, setProjectsSearch] = useState('')
  const [headerProjectSearch, setHeaderProjectSearch] = useState('')
  const [headerProjectScope, setHeaderProjectScope] = useState<'all' | 'current' | 'past'>('current')
  const [projectsSubTab, setProjectsSubTab] = useState<'current' | 'past'>(() => {
    try {
      const saved = localStorage.getItem('projects-subtab-v1')
      return saved === 'past' ? 'past' : 'current'
    } catch {
      return 'current'
    }
  })
  const [pastProjectKeys, setPastProjectKeys] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem('past-project-keys-v1')
      const parsed = raw ? JSON.parse(raw) : {}
      return parsed && typeof parsed === 'object' ? parsed as Record<string, boolean> : {}
    } catch {
      return {}
    }
  })
  const [deletedProjectKeys, setDeletedProjectKeys] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem('deleted-project-keys-v1')
      const parsed = raw ? JSON.parse(raw) : {}
      return parsed && typeof parsed === 'object' ? parsed as Record<string, boolean> : {}
    } catch {
      return {}
    }
  })
  const [projectCardDraft, setProjectCardDraft] = useState<null | { name: string; client: string; projectType: string; storyCount: string; totalSquareFootage: string; topOff: string; tco: string; co: string; isCustom: boolean }>(null)
  const [showAddProjectModal, setShowAddProjectModal] = useState(false)
  const [addProjectAttempted, setAddProjectAttempted] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [newProjectLocation, setNewProjectLocation] = useState('')
  const [customProjectsOverviewCards, setCustomProjectsOverviewCards] = useState<Array<{ name: string; location: string; employeeCount: number; buildingPhoto: string; phaseDeadlines: { Concept: string; SD: string; DD: string; CC: string }; progress: number }>>([])
  const [officeEmployeeSortKey, setOfficeEmployeeSortKey] = useState<keyof OfficeEmployeeRow>('name')
  const [officeEmployeeSortDir, setOfficeEmployeeSortDir] = useState<'asc' | 'desc'>('asc')
  const [officeEmployeeSearch, setOfficeEmployeeSearch] = useState('')
  const [showOfficeContactCard, setShowOfficeContactCard] = useState(false)
  const [officeContactCardDraft, setOfficeContactCardDraft] = useState<OfficeContactCardDraft | null>(null)
  const [officeCardIsNew, setOfficeCardIsNew] = useState(false)
  const [officeContactSaveAttempted, setOfficeContactSaveAttempted] = useState(false)
  const [officeContactOverrides, setOfficeContactOverrides] = useState<Record<string, OfficeContactCardDraft>>(() => {
    try {
      const raw = localStorage.getItem('office-contact-overrides-v1')
      if (!raw) return {}
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? parsed as Record<string, OfficeContactCardDraft> : {}
    } catch {
      return {}
    }
  })
  const [officeCustomEmployees, setOfficeCustomEmployees] = useState<OfficeEmployeeRow[]>(() => {
    try {
      const raw = localStorage.getItem('office-custom-employees-v1')
      if (!raw) return []
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed as OfficeEmployeeRow[] : []
    } catch {
      return []
    }
  })
  const [officeRemovedEmployees, setOfficeRemovedEmployees] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem('office-removed-employees-v1')
      if (!raw) return {}
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? parsed as Record<string, boolean> : {}
    } catch {
      return {}
    }
  })
  const officeEmployeesUploadRef = useRef<HTMLInputElement | null>(null)
  const [, setTintedSymbols] = useState({ aor: '/symbols/aor.png', consultants: '/symbols/consultants.png', client: '/symbols/client.png' })
  const transparentPixel = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs='
  const [tintedOfficeKpiSymbols, setTintedOfficeKpiSymbols] = useState(() => {
    try {
      const raw = localStorage.getItem('office-kpi-symbols-tinted-v1')
      const parsed = raw ? JSON.parse(raw) : null
      if (parsed && typeof parsed === 'object' && parsed.color === siteSettings.theme.quicksand && parsed.employees && parsed.projects && parsed.locations) {
        return { employees: parsed.employees, projects: parsed.projects, locations: parsed.locations }
      }
    } catch {
      // ignore bad local cache
    }
    return { employees: transparentPixel, projects: transparentPixel, locations: transparentPixel }
  })
  const [docFileName, setDocFileName] = useState('')
  const [docFile, setDocFile] = useState<File | null>(null)
  const docProject = 'tower36' as const // legacy backend slug; this site is project-scoped.
  const [directorySearch, setDirectorySearch] = useState(() => {
    try { return localStorage.getItem('directory-search-v1') || '' } catch { return '' }
  })
  const [directoryContactFilter, setDirectoryContactFilter] = useState<'all' | 'key' | 'archived'>(() => {
    try {
      const v = localStorage.getItem('directory-contact-filter-v1')
      return v === 'key' || v === 'archived' ? v : 'all'
    } catch { return 'all' }
  })
  const [directorySortBy, setDirectorySortBy] = useState<'company' | 'consultantType'>(() => {
    try {
      const v = localStorage.getItem('directory-sort-by-v1')
      return v === 'consultantType' ? 'consultantType' : 'company'
    } catch { return 'company' }
  })
  const [redlineStatus, setRedlineStatus] = useState('')
  const [docSearch, setDocSearch] = useState('')
  const [calendarListSearch, setCalendarListSearch] = useState('')
  const [, setNowTick] = useState(Date.now())
  const isCalendarViewValue = (value: unknown): value is 'day' | 'week' | 'month' | 'year' | 'list' =>
    value === 'day' || value === 'week' || value === 'month' || value === 'year' || value === 'list'
  const [calendarView, setCalendarView] = useState<'day' | 'week' | 'month' | 'year' | 'list'>(() => {
    try {
      const saved = localStorage.getItem('calendar-view-v1')
      return isCalendarViewValue(saved) ? saved : 'month'
    } catch {
      return 'month'
    }
  })
  const [calendarFeedFilters, setCalendarFeedFilters] = useState(() => {
    try {
      const raw = localStorage.getItem('calendar-feed-filters-v1')
      const parsed = raw ? JSON.parse(raw) : null
      if (
        parsed &&
        typeof parsed === 'object' &&
        typeof parsed.deliverables === 'boolean' &&
        typeof parsed.teamTasks === 'boolean' &&
        typeof parsed.myTasks === 'boolean' &&
        typeof parsed.consultantTasks === 'boolean'
      ) {
        return {
          deliverables: parsed.deliverables,
          teamTasks: parsed.teamTasks,
          myTasks: parsed.myTasks,
          consultantTasks: parsed.consultantTasks,
        }
      }
    } catch {
      // ignore bad local storage data
    }

    return {
      deliverables: true,
      teamTasks: false,
      myTasks: false,
      consultantTasks: false,
    }
  })
  const [consultantView, setConsultantView] = useState(() => ALL_CONSULTANT_TAB)
  const [showAddConsultantTrackerModal, setShowAddConsultantTrackerModal] = useState(false)
  const [showDeleteConsultantTrackerConfirm, setShowDeleteConsultantTrackerConfirm] = useState(false)
  const [pendingDeleteConfirm, setPendingDeleteConfirm] = useState<null | { title: string; body: string; onConfirm: () => void }>(null)
  const [deletedConsultantTrackers, setDeletedConsultantTrackers] = useState<string[]>([])
  const [newConsultantTrackerCompany, setNewConsultantTrackerCompany] = useState('')
  const [newConsultantTrackerDiscipline, setNewConsultantTrackerDiscipline] = useState('')
  const [newConsultantTrackerAbbreviation, setNewConsultantTrackerAbbreviation] = useState('')
  const [lastConsultantTrackerCompany, setLastConsultantTrackerCompany] = useState('')
  const [allConsultantFilter, setAllConsultantFilter] = useState<'all' | 'overdue' | 'thisWeek' | 'noDue'>('all')
  const [allConsultantSort, setAllConsultantSort] = useState<'due' | 'company'>('due')
  const [consultantColumnSort, setConsultantColumnSort] = useState<{ key: 'item' | 'assignee' | 'dueDate'; direction: 'asc' | 'desc' }>({ key: 'dueDate', direction: 'asc' })
  const [consultantSendExpanded, setConsultantSendExpanded] = useState(false)
  const [consultantReceiveExpanded, setConsultantReceiveExpanded] = useState(false)
  const [consultantFeedExpanded, setConsultantFeedExpanded] = useState(false)
  const [consultantRiskFilter, setConsultantRiskFilter] = useState<'all' | 'atRisk' | 'waiting' | 'overdue' | 'noDue'>('all')
  const [selectedConsultantFeedId, setSelectedConsultantFeedId] = useState<string | null>(null)
  const [selectedConsultantDetailKey, setSelectedConsultantDetailKey] = useState<string | null>(null)
  const [consultantSelectedDetailKeys, setConsultantSelectedDetailKeys] = useState<Set<string>>(new Set())
  const [consultantJumpSourceKey, setConsultantJumpSourceKey] = useState<string | null>(null)
  const [savedConsultantViews, setSavedConsultantViews] = useState<Array<{ name: string; risk: 'all' | 'atRisk' | 'waiting' | 'overdue' | 'noDue'; allFilter: 'all' | 'overdue' | 'thisWeek' | 'noDue'; allSort: 'due' | 'company' }>>([])
  const [consultantAttachmentPreviewKey, setConsultantAttachmentPreviewKey] = useState<string | null>(null)
  const [consultantAttachmentPinnedKey, setConsultantAttachmentPinnedKey] = useState<string | null>(null)
  const consultantAttachmentHoverTimerRef = useRef<number | null>(null)
  const [projectStandardsView, setProjectStandardsView] = useState<'sheetIndex' | 'sheetGraphics' | 'bep' | 'uploads'>('sheetIndex')
  const [sheetIndexFileName, setSheetIndexFileName] = useState('')
  const [sheetIndexColumns, setSheetIndexColumns] = useState<string[]>(['Assign To', 'Sheet Number', 'Sheet Name', 'Scale'])
  const [sheetIndexMarkerColumns, setSheetIndexMarkerColumns] = useState<string[]>([])
  const [sheetIndexRows, setSheetIndexRows] = useState<Array<Record<string, string>>>([])
  const [sheetIndexDraftRow, setSheetIndexDraftRow] = useState<Record<string, string>>({})
  const [sheetIndexNewColumn, setSheetIndexNewColumn] = useState('')
  const drawingScaleOptions = [
    'NTS',
    '3" = 1\'-0"',
    '1 1/2" = 1\'-0"',
    '1" = 1\'-0"',
    '3/4" = 1\'-0"',
    '1/2" = 1\'-0"',
    '3/8" = 1\'-0"',
    '1/4" = 1\'-0"',
    '3/16" = 1\'-0"',
    '1/8" = 1\'-0"',
    '3/32" = 1\'-0"',
    '1/16" = 1\'-0"',
    '1/32" = 1\'-0"',
    '1:5',
    '1:10',
    '1:20',
    '1:50',
    '1:100',
    '1:200',
    '1:500',
  ]
  const [staffingMemberView, setStaffingMemberView] = useState('')
  const [teamOrganizationView, setTeamOrganizationView] = useState(() => {
    try {
      const saved = localStorage.getItem('team-org-view-v1')
      return saved && saved.trim() ? saved : 'Overall'
    } catch {
      return 'Overall'
    }
  })
  const [teamOrgOverallSearch, setTeamOrgOverallSearch] = useState('')
  const [teamHierarchyByProject, setTeamHierarchyByProject] = useState<Record<string, { principal: string[]; projectManager: string[]; drafter: string[] }>>(() => {
    try {
      const raw = localStorage.getItem('team-org-hierarchy-v1')
      const parsed = raw ? JSON.parse(raw) : {}
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  })
  const [teamOrgDropTarget, setTeamOrgDropTarget] = useState<'unplaced' | 'principal' | 'projectManager' | 'drafter' | null>(null)
  const [teamOrgOverallDropProject, setTeamOrgOverallDropProject] = useState<string | null>(null)
  const [teamOrgDraggingEmployee, setTeamOrgDraggingEmployee] = useState<string | null>(null)
  const [teamOrgJustDroppedProject, setTeamOrgJustDroppedProject] = useState<string | null>(null)
  const [teamOrgConnectorAnimating, setTeamOrgConnectorAnimating] = useState(false)
  const teamOrgWasOverUnplacedRef = useRef(false)
  const teamOrgDraggingEmployeeRef = useRef<string | null>(null)
  const teamOrgSuppressProjectOpenRef = useRef(false)
  const teamOrgJustDraggedRef = useRef(false)
  const [showTeamOrgAddStaffModal, setShowTeamOrgAddStaffModal] = useState(false)
  const [teamOrgAddStaffAttempted, setTeamOrgAddStaffAttempted] = useState(false)
  const [teamOrgNewStaffName, setTeamOrgNewStaffName] = useState('')
  const [teamOrgNewStaffPosition, setTeamOrgNewStaffPosition] = useState('')
  const [teamOrgNewStaffCurrentProject, setTeamOrgNewStaffCurrentProject] = useState('')
  const [teamOrgAddStaffAction, setTeamOrgAddStaffAction] = useState<'move' | 'add'>('move')
  const [teamOrganizationNotesByProject, setTeamOrganizationNotesByProject] = useState<Record<string, string>>(() => {
    try {
      const raw = localStorage.getItem('team-org-notes-v1')
      const parsed = raw ? JSON.parse(raw) : {}
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  })
  const [staffingSortKey, setStaffingSortKey] = useState<'status' | 'priority' | 'start' | 'due' | 'task' | 'notes' | 'assignee' | null>(null)
  const [staffingSortDir, setStaffingSortDir] = useState<'asc' | 'desc'>('asc')
  const [consultantItemsByTeam, setConsultantItemsByTeam] = useState<Record<string, ConsultantItemsBySection>>(() => {
    return {
      'All Teams': makeDefaultConsultantSections(),
    }
  })
  const [consultantDrafts, setConsultantDrafts] = useState<Record<ConsultantSectionKey, { text: string; assignee: string; dueDate: string }>>({
    toConsultant: { text: '', assignee: '-', dueDate: '' },
    fromConsultant: { text: '', assignee: '-', dueDate: '' },
    nextDeliverable: { text: '', assignee: '-', dueDate: '' },
  })
  const [showConsultantEdit, setShowConsultantEdit] = useState(false)
  const [editConsultantSection, setEditConsultantSection] = useState<ConsultantSectionKey>('toConsultant')
  const [editConsultantId, setEditConsultantId] = useState<string | null>(null)
  const [editConsultantText, setEditConsultantText] = useState('')
  const [editConsultantDueDate, setEditConsultantDueDate] = useState('')
  const consultantAttachmentInputRef = useRef<HTMLInputElement | null>(null)
  const [consultantAttachmentTarget, setConsultantAttachmentTarget] = useState<{ key: ConsultantSectionKey; itemId: string } | null>(null)
  const [consultantAttachmentBulkSection, setConsultantAttachmentBulkSection] = useState<'toConsultant' | 'fromConsultant' | null>(null)
  const [consultantAttachmentReplaceId, setConsultantAttachmentReplaceId] = useState<string | null>(null)
  const [consultantRowDragOverKey, setConsultantRowDragOverKey] = useState<string | null>(null)
  const [consultantImagePreviewList, setConsultantImagePreviewList] = useState<ConsultantAttachment[]>([])
  const [consultantImagePreviewIndex, setConsultantImagePreviewIndex] = useState(0)
  const [consultantImagePreviewSrc, setConsultantImagePreviewSrc] = useState<string | null>(null)
  const [consultantImagePreviewName, setConsultantImagePreviewName] = useState('')
  const [consultantImagePreviewOpen, setConsultantImagePreviewOpen] = useState(false)
  const [consultantImagePreviewContext, setConsultantImagePreviewContext] = useState<{ key: ConsultantSectionKey; itemId: string; attachmentId: string } | null>(null)
  const [consultantMarkupOpen, setConsultantMarkupOpen] = useState(false)
  const [consultantMarkupColor, setConsultantMarkupColor] = useState('#d73a49')
  const [consultantMarkupFillColor, setConsultantMarkupFillColor] = useState('#ffd54f')
  const [consultantMarkupFillOpacity, setConsultantMarkupFillOpacity] = useState(0.25)
  const [consultantMarkupLineWidth, setConsultantMarkupLineWidth] = useState(3)
  const [consultantMarkupDrawing, setConsultantMarkupDrawing] = useState(false)
  const [consultantMarkupTool, setConsultantMarkupTool] = useState<'select' | 'arrow' | 'line' | 'rect' | 'circle' | 'text' | 'pen' | 'highlight'>('pen')
  const [consultantMarkupHistory, setConsultantMarkupHistory] = useState<string[]>([])
  const [consultantMarkupHistoryIndex, setConsultantMarkupHistoryIndex] = useState(-1)
  const consultantMarkupCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const consultantLineColorInputRef = useRef<HTMLInputElement | null>(null)
  const consultantFillColorInputRef = useRef<HTMLInputElement | null>(null)
  const consultantMarkupStartRef = useRef<{ x: number; y: number } | null>(null)
  const consultantMarkupSnapshotRef = useRef<ImageData | null>(null)
  const [calendarFocusDate, setCalendarFocusDate] = useState(new Date())
  const [calendarUploadName, setCalendarUploadName] = useState('')
  const [calendarUploadFile, setCalendarUploadFile] = useState<File | null>(null)
  const [calendarUploads, setCalendarUploads] = useState<string[]>([])
  const [calendarDeadlines, setCalendarDeadlines] = useState<CalendarDeadline[]>(() => DEMO_CALENDAR_DEADLINES.map(normalizeCalendarDeadline))
  const [calendarHydrated, setCalendarHydrated] = useState(false)
  const [calendarDemoSeeded, setCalendarDemoSeeded] = useState(false)
  const [calendarSyncing, setCalendarSyncing] = useState(false)
  const [calendarSyncError, setCalendarSyncError] = useState('')
  const [, setCalendarLastSyncedAt] = useState<string>('')
  const [calendarLastUpdatedAt, setCalendarLastUpdatedAt] = useState<string>(() => now())
  const [consultantLastUpdatedAt, setConsultantLastUpdatedAt] = useState<string>(() => now())
  const [showCalendarYearLegend, setShowCalendarYearLegend] = useState(false)
  const [showCalendarListGuide, setShowCalendarListGuide] = useState(false)
  const [showConsultantLegend, setShowConsultantLegend] = useState(false)

  const CONSULTANT_ATTACHMENT_DB = 'mission-control-attachments-v1'
  const CONSULTANT_ATTACHMENT_STORE = 'kv'
  const CONSULTANT_ATTACHMENT_KEY = 'consultantItemsByTeam'

  const openAttachmentDb = () => new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('indexeddb-unavailable'))
      return
    }
    const req = window.indexedDB.open(CONSULTANT_ATTACHMENT_DB, 1)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(CONSULTANT_ATTACHMENT_STORE)) db.createObjectStore(CONSULTANT_ATTACHMENT_STORE)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error || new Error('indexeddb-open-failed'))
  })

  const loadAttachmentSnapshot = async (): Promise<Record<string, ConsultantItemsBySection> | null> => {
    try {
      const db = await openAttachmentDb()
      const value = await new Promise<Record<string, ConsultantItemsBySection> | null>((resolve, reject) => {
        const tx = db.transaction(CONSULTANT_ATTACHMENT_STORE, 'readonly')
        const store = tx.objectStore(CONSULTANT_ATTACHMENT_STORE)
        const req = store.get(CONSULTANT_ATTACHMENT_KEY)
        req.onsuccess = () => resolve((req.result || null) as Record<string, ConsultantItemsBySection> | null)
        req.onerror = () => reject(req.error || new Error('indexeddb-get-failed'))
      })
      db.close()
      return value
    } catch {
      return null
    }
  }

  const saveAttachmentSnapshot = async (items: Record<string, ConsultantItemsBySection>) => {
    try {
      const db = await openAttachmentDb()
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(CONSULTANT_ATTACHMENT_STORE, 'readwrite')
        const store = tx.objectStore(CONSULTANT_ATTACHMENT_STORE)
        store.put(items, CONSULTANT_ATTACHMENT_KEY)
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error || new Error('indexeddb-put-failed'))
      })
      db.close()
    } catch {
      // ignore indexedDB failures; API/localStorage remain primary paths
    }
  }

  const mergeConsultantDefaults = (incoming: Record<string, ConsultantItemsBySection>) => {
    const out: Record<string, ConsultantItemsBySection> = { ...incoming }
    const keys = Array.from(new Set(['All Teams', ...Object.keys(incoming)]))
    for (const key of keys) {
      const base = key === 'All Teams' ? makeDefaultConsultantSections() : makeEmptyConsultantSections()
      const cur = incoming[key] || makeEmptyConsultantSections()
      const mergeList = (curList: ConsultantItem[], baseList: ConsultantItem[]) => {
        const seen = new Set(curList.map((x) => `${x.title}|${x.dueDate}`.toLowerCase()))
        const add = baseList.filter((x) => !seen.has(`${x.title}|${x.dueDate}`.toLowerCase()))
        return [...curList, ...add]
      }
      out[key] = {
        toConsultant: mergeList(cur.toConsultant || [], base.toConsultant || []),
        fromConsultant: mergeList(cur.fromConsultant || [], base.fromConsultant || []),
        nextDeliverable: mergeList(cur.nextDeliverable || [], base.nextDeliverable || []),
      }
    }
    return out
  }

  const preserveLocalConsultantAttachments = (
    incoming: Record<string, ConsultantItemsBySection>,
    local: Record<string, ConsultantItemsBySection>,
  ) => {
    const out: Record<string, ConsultantItemsBySection> = { ...incoming }
    const sections: ConsultantSectionKey[] = ['toConsultant', 'fromConsultant', 'nextDeliverable']
    for (const team of Object.keys(out)) {
      const incomingTeam = out[team] || makeEmptyConsultantSections()
      const localTeam = local[team] || makeEmptyConsultantSections()
      const nextTeam = { ...incomingTeam }
      for (const section of sections) {
        const localById = new Map((localTeam[section] || []).map((item) => [item.id, item]))
        nextTeam[section] = (incomingTeam[section] || []).map((item) => {
          const localItem = localById.get(item.id)
          const incomingAttachments = Array.isArray(item.attachments) ? item.attachments : []
          const localAttachments = Array.isArray(localItem?.attachments) ? localItem!.attachments! : []
          const hasIncoming = incomingAttachments.length > 0
          const hasLocal = localAttachments.length > 0
          if (!hasIncoming && hasLocal) return { ...item, attachments: localAttachments }
          if (hasIncoming && hasLocal) {
            // Prefer local if it has more attachments than the incoming payload (poll can be briefly stale).
            if (localAttachments.length > incomingAttachments.length) {
              return { ...item, attachments: localAttachments }
            }
            // If counts are equal but local has at least one attachment id unknown to incoming, keep local.
            const incomingIds = new Set(incomingAttachments.map((a) => a.id))
            const localHasNew = localAttachments.some((a) => !incomingIds.has(a.id))
            if (localHasNew) return { ...item, attachments: localAttachments }
          }
          return item
        })
      }
      out[team] = nextTeam
    }
    return out
  }
  const [calendarMonthZoom, setCalendarMonthZoom] = useState<{
    start: { left: number; top: number; width: number; height: number }
    end: { left: number; top: number; width: number; height: number }
    expanded: boolean
    html: string
  } | null>(null)
  const [calendarDayMorph, setCalendarDayMorph] = useState<{
    active: boolean
    items: Array<{ dateKey: string; label: string; startX: number; startY: number; endX: number; endY: number }>
  } | null>(null)
  const [calendarMonthEntering, setCalendarMonthEntering] = useState(false)
  const [calendarMonthPhase, setCalendarMonthPhase] = useState(4)
  const [calendarWeekEntering, setCalendarWeekEntering] = useState(false)
  const [calendarWeekPhase, setCalendarWeekPhase] = useState(4)
  const calendarYearLegendCloseTimerRef = useRef<number | null>(null)
  const [tasksHydrated, setTasksHydrated] = useState(false)
  const [appStateHydrated, setAppStateHydrated] = useState(false)
  const [appStateLoadedFromApi, setAppStateLoadedFromApi] = useState(false)
  const [showAddDeadline, setShowAddDeadline] = useState(false)
  const [addCalendarItemAttempted, setAddCalendarItemAttempted] = useState(false)
  const [editingDeadline, setEditingDeadline] = useState<CalendarDeadline | null>(null)
  const [newDeadlineTitle, setNewDeadlineTitle] = useState('')
  const [newDeadlineDescription, setNewDeadlineDescription] = useState('')
  const [newDeadlineAssignTo, setNewDeadlineAssignTo] = useState('')
  const [newDeadlineNotes, setNewDeadlineNotes] = useState('')
  const [newDeadlineDate, setNewDeadlineDate] = useState('')
  const [newDeadlineStatus, setNewDeadlineStatus] = useState<'not-selected' | 'overdue' | 'on-track' | 'completed'>('not-selected')
  const [calendarDateInputType, setCalendarDateInputType] = useState<'text' | 'date'>('text')
  const [showAddContact, setShowAddContact] = useState(false)
  const [addContactAttempted, setAddContactAttempted] = useState(false)
  const [showEditContact, setShowEditContact] = useState(false)
  const [editContactIndex, setEditContactIndex] = useState<number | null>(null)
  const [newContactName, setNewContactName] = useState('')
  const [newContactRole, setNewContactRole] = useState('')
  const [newContactTeam, setNewContactTeam] = useState('')
  const [newContactCompany, setNewContactCompany] = useState('')
  const [newContactCompanyAbbreviation, setNewContactCompanyAbbreviation] = useState('')
  const [newContactPhone, setNewContactPhone] = useState('')
  const [, setNewContactPhoneType] = useState<'Cell' | 'Office'>('Cell')
  const [newContactAltPhone, setNewContactAltPhone] = useState('')
  const [, setNewContactAltPhoneType] = useState<'Cell' | 'Office'>('Office')
  const [newContactOfficeExtension, setNewContactOfficeExtension] = useState('')
  const [newContactNotes, setNewContactNotes] = useState('')
  const [newContactEmail, setNewContactEmail] = useState('')
  const [inlineDirectoryEdit, setInlineDirectoryEdit] = useState<{ idx: number; field: 'email' | 'office' | 'cell' } | null>(null)
  const [inlineDirectoryValue, setInlineDirectoryValue] = useState('')
  const contactUploadRef = useRef<HTMLInputElement | null>(null)
  const directorySearchInputRef = useRef<HTMLInputElement | null>(null)
  const directoryTeamFilter = 'All Teams'
  const [copyToast, setCopyToast] = useState('')
  const [actionToast, setActionToast] = useState<{ message: string; undo?: () => void } | null>(null)
  const [centerToastMessage, setCenterToastMessage] = useState('')
  const [showContactInfo, setShowContactInfo] = useState(false)
  const [contactInfoTitle, setContactInfoTitle] = useState('')
  const [contactInfoValue, setContactInfoValue] = useState('')
  const [contactInfoActionLabel, setContactInfoActionLabel] = useState('')
  const [contactInfoActionHref, setContactInfoActionHref] = useState('')
  const [directoryHydrated, setDirectoryHydrated] = useState(false)
  const [directoryContacts, setDirectoryContacts] = useState<DirectoryContact[]>(() => {
    try {
      const raw = localStorage.getItem('directory-contacts-v1')
      const parsed = raw ? JSON.parse(raw) : []
      return Array.isArray(parsed) ? parsed as DirectoryContact[] : []
    } catch {
      return []
    }
  })
  const [meetingAttendees, setMeetingAttendees] = useState<MeetingAttendee[]>(makeDefaultMeetingAttendees())
  const [showMeetingAttendeeEdit, setShowMeetingAttendeeEdit] = useState(false)
  const [editMeetingAttendeeId, setEditMeetingAttendeeId] = useState<string | null>(null)
  const [editMeetingAttendeeName, setEditMeetingAttendeeName] = useState('')
  const [editMeetingAttendeeCompany, setEditMeetingAttendeeCompany] = useState('')
  const [selectedDirectoryContactIndex, setSelectedDirectoryContactIndex] = useState('')
  const [directoryBulkSelection, setDirectoryBulkSelection] = useState<Set<number>>(() => new Set())
  const [bulkAssignTeamValue, setBulkAssignTeamValue] = useState('')
  const [bulkAssignDisciplineValue, setBulkAssignDisciplineValue] = useState('')
  const [directoryExportFormat, setDirectoryExportFormat] = useState<'excel' | 'pdf' | ''>('')
  const [directorySavedViews, setDirectorySavedViews] = useState<DirectorySavedView[]>(() => {
    try {
      const raw = localStorage.getItem('directory-saved-views-v1')
      const parsed = raw ? JSON.parse(raw) : []
      return Array.isArray(parsed) ? parsed as DirectorySavedView[] : []
    } catch {
      return []
    }
  })
  const [activeDirectoryViewId, setActiveDirectoryViewId] = useState<string | null>(null)
  const [directoryViewNameDraft, setDirectoryViewNameDraft] = useState('')
  const [showDirectorySaveGroupPopover, setShowDirectorySaveGroupPopover] = useState(false)
  const [staffingRowsData, setStaffingRowsData] = useState<StaffingRow[]>(makeDefaultStaffingRows())
  const [staffingLastUpdatedAt, setStaffingLastUpdatedAt] = useState(() => now())
  const [showStaffingEdit, setShowStaffingEdit] = useState(false)
  const [newStaffingStatus, setNewStaffingStatus] = useState('')
  const [newStaffingPriority, setNewStaffingPriority] = useState('')
  const [newStaffingStart, setNewStaffingStart] = useState('')
  const [newStaffingDue, setNewStaffingDue] = useState('')
  const [newStaffingTask, setNewStaffingTask] = useState('')
  const [newStaffingAssignee, setNewStaffingAssignee] = useState('')
  const [newStaffingNotes, setNewStaffingNotes] = useState('')
  const startDateRef = useRef<HTMLInputElement | null>(null)
  const dueDateRef = useRef<HTMLInputElement | null>(null)
  const syncedCalendarTaskKeysRef = useRef<Set<string>>(new Set())
  const syncedMeetingTaskKeysRef = useRef<Set<string>>(new Set())
  const syncedConsultantSendTaskKeysRef = useRef<Set<string>>(new Set())
  const [editStaffingId, setEditStaffingId] = useState<string | null>(null)
  const [editStaffingStatus, setEditStaffingStatus] = useState('Not Started')
  const [editStaffingPriority, setEditStaffingPriority] = useState('Medium')
  const [editStaffingStart, setEditStaffingStart] = useState('')
  const [editStaffingDue, setEditStaffingDue] = useState('')
  const [editStaffingTask, setEditStaffingTask] = useState('')
  const [editStaffingAssignee, setEditStaffingAssignee] = useState('Team')
  const [editStaffingNotes, setEditStaffingNotes] = useState('')
  const [meetingAgendaItems, setMeetingAgendaItems] = useState<string[]>(defaultMeetingAgendaItems)
  const [meetingActionItems, setMeetingActionItems] = useState<MeetingActionItem[]>(defaultMeetingActionItems)
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().slice(0, 10))
  const [meetingType, setMeetingType] = useState('OAC Coordination')
  const [meetingPreparedBy, setMeetingPreparedBy] = useState('Project Team')
  const [meetingLocation, setMeetingLocation] = useState('TBD')
  const [activeMeetingTab, setActiveMeetingTab] = useState<MeetingTabKey>(() => {
    try {
      const saved = localStorage.getItem('active-meeting-tab-v1')
      if (saved === 'meeting1' || saved === 'meeting2' || saved === 'meeting3') return saved
    } catch {
      // ignore storage failures
    }
    return 'meeting1'
  })
  const [meetingTabOrder, setMeetingTabOrder] = useState<MeetingTabKey[]>(['meeting1', 'meeting2', 'meeting3'])
  const [meetingDrafts, setMeetingDrafts] = useState<Record<MeetingTabKey, ReturnType<typeof makeBlankMeetingDraft>>>({
    meeting1: {
      date: new Date().toISOString().slice(0, 10),
      type: 'OAC Coordination',
      preparedBy: 'Project Team',
      location: 'TBD',
      attendees: makeDefaultMeetingAttendees(),
      agendaItems: [...defaultMeetingAgendaItems],
      actionItems: defaultMeetingActionItems,
    },
    meeting2: makeBlankMeetingDraft(),
    meeting3: makeBlankMeetingDraft(),
  })
  const [collapsedMeetingTopics, setCollapsedMeetingTopics] = useState<Record<string, boolean>>({})
  const [showDeleteMeetingConfirm, setShowDeleteMeetingConfirm] = useState(false)

  useEffect(() => {
    let active = true
    ;(async () => {
      let hasLocalCache = false

      // Load local cache first so refresh never appears to undo recent edits.
      try {
        const raw = localStorage.getItem('directory-contacts-v1')
        if (raw) {
          const parsed = JSON.parse(raw) as DirectoryContact[]
          if (active && Array.isArray(parsed) && parsed.length) {
            setDirectoryContacts(parsed)
            hasLocalCache = true
          }
        }
      } catch {
        // ignore bad local storage data
      }

      // Only apply API payload when local cache is empty.
      try {
        const res = await apiFetch(`${apiBase}/api/directory-contacts`)
        const json = await res.json()
        if (!hasLocalCache && active && json?.ok && Array.isArray(json.contacts) && json.contacts.length) {
          setDirectoryContacts(json.contacts as DirectoryContact[])
        }
      } catch {
        // server fetch is best-effort; local cache remains source of truth on load
      } finally {
        if (active) setDirectoryHydrated(true)
      }
    })()

    return () => {
      active = false
    }
  }, [apiBase])

  useEffect(() => {
    if (!directoryHydrated) return

    try {
      localStorage.setItem('directory-contacts-v1', JSON.stringify(directoryContacts))
    } catch {
      // ignore storage failures
    }

    ;(async () => {
      try {
        await apiFetch(`${apiBase}/api/directory-contacts`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contacts: directoryContacts }),
        })
      } catch {
        // server sync best-effort; local cache remains fallback
      }
    })()
  }, [directoryContacts, directoryHydrated, apiBase])

  useEffect(() => {
    try {
      localStorage.setItem('directory-saved-views-v1', JSON.stringify(directorySavedViews))
    } catch {
      // ignore storage failures
    }
  }, [directorySavedViews])

  useEffect(() => {
    try {
      localStorage.removeItem('directory-active-view-v1')
    } catch {
      // ignore storage failures
    }
  }, [])

  useEffect(() => {
    if (!activeDirectoryViewId) return
    const activeView = directorySavedViews.find((view) => view.id === activeDirectoryViewId)
    if (!activeView) {
      setActiveDirectoryViewId(null)
      return
    }
    if (directorySearch !== activeView.search) setDirectorySearch(activeView.search)
    if (directoryContactFilter !== activeView.contactFilter) setDirectoryContactFilter(activeView.contactFilter)
    if (directorySortBy !== activeView.sortBy) setDirectorySortBy(activeView.sortBy)
  }, [activeDirectoryViewId, directorySavedViews, directorySearch, directoryContactFilter, directorySortBy])

  useEffect(() => {
    if (!activeDirectoryViewId) return
    const activeView = directorySavedViews.find((view) => view.id === activeDirectoryViewId)
    if (!activeView) return
    const keys = new Set(activeView.contactKeys || [])
    const next = new Set<number>()
    if (keys.size) {
      directoryContacts.forEach((contact, idx) => {
        if (keys.has(directoryContactKey(contact))) next.add(idx)
      })
    }
    setDirectoryBulkSelection((prev) => (areNumberSetsEqual(prev, next) ? prev : next))
  }, [activeDirectoryViewId, directorySavedViews, directoryContacts])

  useEffect(() => {
    try {
      localStorage.setItem('active-tab-v1', activeTab)
    } catch {
      // ignore storage failures
    }

    try {
      const url = new URL(window.location.href)
      url.searchParams.set('tab', activeTab)
      window.history.replaceState(window.history.state, '', url.toString())
    } catch {
      // ignore URL state failures
    }
  }, [activeTab])


  useEffect(() => {
    try {
      if (profileSettingsTab) localStorage.setItem('profile-settings-tab-v1', profileSettingsTab)
      else localStorage.removeItem('profile-settings-tab-v1')
    } catch {
      // ignore storage failures
    }
  }, [profileSettingsTab])

  useEffect(() => {
    try {
      localStorage.setItem('projects-subtab-v1', projectsSubTab)
    } catch {
      // ignore storage failures
    }
  }, [projectsSubTab])

  useEffect(() => {
    try {
      localStorage.setItem('active-meeting-tab-v1', activeMeetingTab)
    } catch {
      // ignore storage failures
    }
  }, [activeMeetingTab])


  useEffect(() => {
    try {
      localStorage.setItem('project-overview-by-project-v1', JSON.stringify(projectOverviewByProject))
    } catch {
      // ignore storage failures
    }
  }, [projectOverviewByProject])

  useEffect(() => {
    try {
      localStorage.setItem('profile-dashboard-visibility-v1', JSON.stringify(profileDashboardVisibility))
    } catch {
      // ignore storage failures
    }
  }, [profileDashboardVisibility])

  useEffect(() => {
    try {
      localStorage.setItem('site-settings-default-v1', JSON.stringify(savedDefaultSiteSettings))
      setHasSavedDefaultSiteSettings(true)
    } catch {
      // ignore storage failures
    }
  }, [savedDefaultSiteSettings])


  useLayoutEffect(() => {
    if (!siteSettingsHydrated) return
    const root = document.documentElement
    const primaryAccent = siteSettings.theme.primaryAccent || siteSettings.theme.quicksand
    root.style.setProperty('--sapphire', siteSettings.theme.sapphire || siteSettings.theme.bodyText)
    root.style.setProperty('--royal-blue', siteSettings.theme.royalBlue)
    root.style.setProperty('--quicksand', siteSettings.theme.quicksand || primaryAccent)
    root.style.setProperty('--primary-accent', primaryAccent)
    root.style.setProperty('--secondary-accent', siteSettings.theme.secondaryAccent)
    root.style.setProperty('--status-success', siteSettings.theme.statusSuccess)
    root.style.setProperty('--status-warning', siteSettings.theme.statusWarning)
    root.style.setProperty('--status-danger', siteSettings.theme.statusDanger)
    root.style.setProperty('--status-info', siteSettings.theme.statusInfo)
    root.style.setProperty('--status-success-fg', siteSettings.theme.statusSuccessFg || siteSettings.theme.statusSuccess)
    root.style.setProperty('--status-success-bg', siteSettings.theme.statusSuccessBg || '#E8F1EB')
    root.style.setProperty('--status-warning-fg', siteSettings.theme.statusWarningFg || siteSettings.theme.statusWarning)
    root.style.setProperty('--status-warning-bg', siteSettings.theme.statusWarningBg || '#F7EEDF')
    root.style.setProperty('--status-danger-fg', siteSettings.theme.statusDangerFg || siteSettings.theme.statusDanger)
    root.style.setProperty('--status-danger-bg', siteSettings.theme.statusDangerBg || '#F6E8E5')
    root.style.setProperty('--status-info-fg', siteSettings.theme.statusInfoFg || siteSettings.theme.statusInfo)
    root.style.setProperty('--status-info-bg', siteSettings.theme.statusInfoBg || '#EAF0F4')
    root.style.setProperty('--swan-wing', siteSettings.theme.swanWing)
    root.style.setProperty('--shellstone', siteSettings.theme.shellstone || siteSettings.theme.bodyText)
    root.style.setProperty('--accent', primaryAccent)
    root.style.setProperty('--surface', siteSettings.theme.royalBlue)
    root.style.setProperty('--line', siteSettings.theme.line)
    root.style.setProperty('--text-soft', siteSettings.theme.textSoft)
    root.style.setProperty('--text', siteSettings.theme.bodyText)

    root.style.setProperty('--font-ui', siteSettings.typography.uiFont)
    root.style.setProperty('--font-tech', siteSettings.typography.technicalFont)
    root.style.setProperty('--fs-page-title', `${siteSettings.typography.pageTitle}px`)
    root.style.setProperty('--fs-section-title', `${siteSettings.typography.sectionTitle}px`)
    root.style.setProperty('--fs-card-title', `${siteSettings.typography.cardTitle}px`)
    root.style.setProperty('--fs-body', `${siteSettings.typography.bodyText}px`)
    root.style.setProperty('--fs-table', `${siteSettings.typography.tableText}px`)
    root.style.setProperty('--fs-label', `${siteSettings.typography.labels}px`)
  }, [siteSettingsHydrated, siteSettings.theme, siteSettings.typography])

  useEffect(() => {
    const href = siteSettings.companyIcon || '/vite.svg'
    let link = document.querySelector("link[rel='icon']") as HTMLLinkElement | null
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = href
  }, [siteSettings.companyIcon])

  useEffect(() => {
    let active = true
    Promise.all([
      tintSymbolImage('/symbols/aor.png', siteSettings.theme.quicksand, 0.84),
      tintSymbolImage('/symbols/consultants.png', siteSettings.theme.quicksand, 0.84),
      tintSymbolImage('/symbols/client.png', siteSettings.theme.quicksand, 0.84),
      tintSymbolImage('/office-employees-icon.jpg', siteSettings.theme.quicksand, 0.84),
      tintSymbolImage('/office-projects-icon.jpg', siteSettings.theme.quicksand, 0.84),
      tintSymbolImage('/office-locations-icon.jpg', siteSettings.theme.quicksand, 0.84),
    ]).then(([aor, consultants, client, employees, projects, locations]) => {
      if (!active) return
      setTintedSymbols({ aor, consultants, client })
      const nextOfficeSymbols = { employees, projects, locations }
      setTintedOfficeKpiSymbols(nextOfficeSymbols)
      try {
        localStorage.setItem('office-kpi-symbols-tinted-v1', JSON.stringify({ color: siteSettings.theme.quicksand, ...nextOfficeSymbols }))
      } catch {
        // ignore storage write errors
      }
    })
    return () => { active = false }
  }, [siteSettings.theme.quicksand])

  useEffect(() => {
    const loadTaskState = async () => {
      try {
        const res = await apiFetch(`${apiBase}/api/tasks-state?ts=${Date.now()}`)
        const data = res.ok ? await res.json() : null
        if (Array.isArray(data?.tasks) && Array.isArray(data?.history) && Array.isArray(data?.documents) && data.tasks.length) {
          useTaskStore.setState((prev) => ({ ...prev, tasks: data.tasks, history: data.history, documents: data.documents }))
        }
      } catch {
        // keep local persisted state when API is unavailable
      } finally {
        setTasksHydrated(true)
      }
    }

    loadTaskState()
  }, [apiBase])

  useEffect(() => {
    if (!tasksHydrated) return

    ;(async () => {
      try {
        await apiFetch(`${apiBase}/api/tasks-state`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tasks: store.tasks, history: store.history, documents: store.documents }),
        })
      } catch {
        // fallback remains local persisted store
      }
    })()
  }, [tasksHydrated, apiBase, store.tasks, store.history, store.documents])

  const mergeAndApplySiteSettings = (incoming: Partial<SiteSettings>) => {
    const merged: SiteSettings = {
      ...defaultSiteSettings,
      ...incoming,
      schemaVersion: 3,
      theme: { ...defaultTheme, ...(incoming.theme || {}) },
      typography: { ...defaultSiteSettings.typography, ...(incoming.typography || {}) },
    }
    setSiteSettings(merged)
    setSavedDefaultSiteSettings((prev) => (prev === defaultSiteSettings ? merged : prev))
  }

  useEffect(() => {
    let active = true
    appStateHydratedRef.current = false
    setSiteSettingsHydrated(false)
    setAppStateLoadedFromApi(false)

    const loadSiteSettings = async () => {
      try {
        const res = await apiFetch(`${apiBase}/api/site-settings?ts=${Date.now()}`)
        const data = res.ok ? await res.json() : null
        const settings = data?.settings
        if (active && settings && typeof settings === 'object') {
          mergeAndApplySiteSettings(settings as Partial<SiteSettings>)
        }
      } catch {
        // ignore; app-state load below is fallback
      } finally {
        if (active) setSiteSettingsHydrated(true)
      }
    }

    const loadAppState = async () => {
      let localState: {
        consultantItemsByTeam?: Record<string, ConsultantItemsBySection>
        staffingRowsData?: StaffingRow[]
        meetingAttendees?: MeetingAttendee[]
        meetingAgendaItems?: string[]
        meetingActionItems?: MeetingActionItem[]
        officeContactOverrides?: Record<string, OfficeContactCardDraft>
        consultantCompanyReviews?: Record<string, ConsultantReview[]>
        siteSettings?: Partial<SiteSettings>
        calendarView?: 'day' | 'week' | 'month' | 'year' | 'list'
        calendarFeedFilters?: {
          deliverables: boolean
          teamTasks: boolean
          myTasks: boolean
          consultantTasks: boolean
        }
        consultantView?: string
        deletedConsultantTrackers?: string[]
        customConsultantCompanies?: Array<{ type: string; company: string }>
        activeTab?: TabKey
        projectsSubTab?: 'current' | 'past'
        activeMeetingTab?: MeetingTabKey
      } | null = null

      try {
        const raw = localStorage.getItem('app-state-v1')
        if (raw) {
          localState = JSON.parse(raw)
          if (active && localState) {
            // consultant items are global; do not hydrate from local cache
            if (Array.isArray(localState.staffingRowsData)) setStaffingRowsData(localState.staffingRowsData)
            if (Array.isArray(localState.meetingAttendees)) setMeetingAttendees(localState.meetingAttendees)
            if (Array.isArray(localState.meetingAgendaItems)) setMeetingAgendaItems(localState.meetingAgendaItems)
            if (Array.isArray(localState.meetingActionItems)) setMeetingActionItems(normalizeMeetingActionItems(localState.meetingActionItems))
            if (localState.officeContactOverrides && typeof localState.officeContactOverrides === 'object') setOfficeContactOverrides(localState.officeContactOverrides)
            if (localState.consultantCompanyReviews && typeof localState.consultantCompanyReviews === 'object') {
              const incoming = localState.consultantCompanyReviews
              if (Object.keys(incoming).length > 0) setConsultantCompanyReviews((prev) => ({ ...incoming, ...prev }))
            }
            // siteSettings is global account-level data; do not hydrate from local cache
            if (isCalendarViewValue(localState.calendarView)) setCalendarView(localState.calendarView)
            if (localState.calendarFeedFilters) setCalendarFeedFilters(localState.calendarFeedFilters)
            if (typeof localState.consultantView === 'string' && localState.consultantView.trim()) setConsultantView(localState.consultantView)
            if (Array.isArray(localState.deletedConsultantTrackers)) setDeletedConsultantTrackers(localState.deletedConsultantTrackers.map((x) => String(x)))
            if (Array.isArray(localState.customConsultantCompanies)) {
              setCustomConsultantCompanies(localState.customConsultantCompanies.filter((x) => x && typeof x.type === 'string' && typeof x.company === 'string'))
            }
            const tabFromUrl = (() => {
              try {
                const tab = new URLSearchParams(window.location.search).get('tab')
                return isTabKey(tab) ? tab : null
              } catch {
                return null
              }
            })()
            if (!tabFromUrl && isTabKey(localState.activeTab)) setActiveTab(localState.activeTab)
            if (localState.projectsSubTab === 'current' || localState.projectsSubTab === 'past') setProjectsSubTab(localState.projectsSubTab)
            if (typeof localState.activeMeetingTab === 'string' && localState.activeMeetingTab.trim()) setActiveMeetingTab(localState.activeMeetingTab)
          }
        }
      } catch {
        // ignore bad local storage data
      }

      // Legacy migration: pull consultant UI bits from old local-only keys,
      // merge into localState snapshot, then remove old keys.
      try {
        const legacyConsultantView = localStorage.getItem('consultant-view-v1') || ''
        const legacyDeletedTrackersRaw = localStorage.getItem('deleted-consultant-trackers-v1')
        const legacyCustomCompaniesRaw = localStorage.getItem('custom-consultant-companies-v1')

        const legacyDeletedTrackers = legacyDeletedTrackersRaw ? JSON.parse(legacyDeletedTrackersRaw) : []
        const legacyCustomCompanies = legacyCustomCompaniesRaw ? JSON.parse(legacyCustomCompaniesRaw) : []

        if (!localState) localState = {}

        if (typeof legacyConsultantView === 'string' && legacyConsultantView.trim() && !localState.consultantView) {
          localState.consultantView = legacyConsultantView
          setConsultantView(legacyConsultantView)
        }

        if (Array.isArray(legacyDeletedTrackers) && legacyDeletedTrackers.length) {
          const existing = Array.isArray(localState.deletedConsultantTrackers)
            ? localState.deletedConsultantTrackers.map((x) => String(x))
            : []
          const merged = Array.from(new Set([...existing, ...legacyDeletedTrackers.map((x) => String(x))]))
          localState.deletedConsultantTrackers = merged
          setDeletedConsultantTrackers(merged)
        }

        if (Array.isArray(legacyCustomCompanies) && legacyCustomCompanies.length) {
          const existing = Array.isArray(localState.customConsultantCompanies)
            ? localState.customConsultantCompanies.filter((x) => x && typeof x.type === 'string' && typeof x.company === 'string')
            : []
          const incoming = legacyCustomCompanies.filter((x) => x && typeof x.type === 'string' && typeof x.company === 'string')
          const mergedMap = new Map<string, { type: string; company: string }>()
          for (const row of [...existing, ...incoming]) {
            const key = `${String(row.type).trim().toLowerCase()}|${String(row.company).trim().toLowerCase()}`
            if (!mergedMap.has(key)) mergedMap.set(key, { type: String(row.type), company: String(row.company) })
          }
          const merged = Array.from(mergedMap.values())
          localState.customConsultantCompanies = merged
          setCustomConsultantCompanies(merged)
        }

        localStorage.removeItem('consultant-view-v1')
        localStorage.removeItem('deleted-consultant-trackers-v1')
        localStorage.removeItem('custom-consultant-companies-v1')
      } catch {
        // ignore legacy migration read/remove failures
      }

      try {
        const res = await apiFetch(`${apiBase}/api/app-state?ts=${Date.now()}`)
        const data = res.ok ? await res.json() : null
        const state = data?.state
        if (!active || !state || typeof state !== 'object') return
        if (active) setAppStateLoadedFromApi(true)

        const apiConsultantItems = state.consultantItemsByTeam as Record<string, ConsultantItemsBySection> | undefined
        if (apiConsultantItems && typeof apiConsultantItems === 'object') {
          setConsultantItemsByTeam((prev) => preserveLocalConsultantAttachments(mergeConsultantDefaults(apiConsultantItems), prev))
        }

        const shouldApplyApiArray = <T,>(incoming: T[], localValue?: T[]) => {
          if (!Array.isArray(incoming)) return false
          // Prevent the common "flash then disappear" case:
          // local cache has data, but a stale/empty API payload arrives right after load.
          if (incoming.length === 0 && Array.isArray(localValue) && localValue.length > 0) return false
          return true
        }

        if (Array.isArray(state.staffingRowsData) && shouldApplyApiArray(state.staffingRowsData as StaffingRow[], localState?.staffingRowsData)) {
          setStaffingRowsData(state.staffingRowsData as StaffingRow[])
        }
        if (Array.isArray(state.meetingAttendees) && shouldApplyApiArray(state.meetingAttendees as MeetingAttendee[], localState?.meetingAttendees)) {
          setMeetingAttendees(state.meetingAttendees as MeetingAttendee[])
        }
        if (Array.isArray(state.meetingAgendaItems) && shouldApplyApiArray(state.meetingAgendaItems as string[], localState?.meetingAgendaItems)) {
          setMeetingAgendaItems(state.meetingAgendaItems as string[])
        }
        if (Array.isArray(state.meetingActionItems) && shouldApplyApiArray(state.meetingActionItems as MeetingActionItem[], localState?.meetingActionItems)) {
          setMeetingActionItems(normalizeMeetingActionItems(state.meetingActionItems as MeetingActionItem[]))
        }
        if (state.officeContactOverrides && typeof state.officeContactOverrides === 'object') {
          setOfficeContactOverrides(state.officeContactOverrides as Record<string, OfficeContactCardDraft>)
        }
        if (state.consultantCompanyReviews && typeof state.consultantCompanyReviews === 'object') {
          const incoming = state.consultantCompanyReviews as Record<string, ConsultantReview[]>
          if (Object.keys(incoming).length > 0) {
            setConsultantCompanyReviews((prev) => ({ ...incoming, ...prev }))
          }
        }
        // siteSettings must come from /api/site-settings (single global source)
        if (isCalendarViewValue((state as { calendarView?: unknown }).calendarView)) {
          setCalendarView((state as { calendarView: 'day' | 'week' | 'month' | 'year' | 'list' }).calendarView)
        }
        const incomingCalendarFeedFilters = (state as { calendarFeedFilters?: unknown }).calendarFeedFilters
        if (
          incomingCalendarFeedFilters &&
          typeof incomingCalendarFeedFilters === 'object' &&
          typeof (incomingCalendarFeedFilters as { deliverables?: unknown }).deliverables === 'boolean' &&
          typeof (incomingCalendarFeedFilters as { teamTasks?: unknown }).teamTasks === 'boolean' &&
          typeof (incomingCalendarFeedFilters as { myTasks?: unknown }).myTasks === 'boolean' &&
          typeof (incomingCalendarFeedFilters as { consultantTasks?: unknown }).consultantTasks === 'boolean'
        ) {
          setCalendarFeedFilters({
            deliverables: (incomingCalendarFeedFilters as { deliverables: boolean }).deliverables,
            teamTasks: (incomingCalendarFeedFilters as { teamTasks: boolean }).teamTasks,
            myTasks: (incomingCalendarFeedFilters as { myTasks: boolean }).myTasks,
            consultantTasks: (incomingCalendarFeedFilters as { consultantTasks: boolean }).consultantTasks,
          })
        }
        if (typeof (state as { consultantView?: unknown }).consultantView === 'string' && String((state as { consultantView?: unknown }).consultantView).trim()) {
          setConsultantView(String((state as { consultantView?: unknown }).consultantView))
        }
        if (Array.isArray((state as { deletedConsultantTrackers?: unknown }).deletedConsultantTrackers)) {
          setDeletedConsultantTrackers(((state as { deletedConsultantTrackers: unknown[] }).deletedConsultantTrackers || []).map((x) => String(x)))
        }
        if (Array.isArray((state as { customConsultantCompanies?: unknown }).customConsultantCompanies)) {
          const rows = (state as { customConsultantCompanies: Array<{ type?: unknown; company?: unknown }> }).customConsultantCompanies
          setCustomConsultantCompanies(rows.filter((x) => x && typeof x.type === 'string' && typeof x.company === 'string').map((x) => ({ type: String(x.type), company: String(x.company) })))
        }
        const tabFromUrl = (() => {
          try {
            const tab = new URLSearchParams(window.location.search).get('tab')
            return isTabKey(tab) ? tab : null
          } catch {
            return null
          }
        })()
        if (!tabFromUrl && isTabKey((state as { activeTab?: unknown }).activeTab as string)) {
          setActiveTab((state as { activeTab: TabKey }).activeTab)
        }
        const projectsSubTabState = (state as { projectsSubTab?: unknown }).projectsSubTab
        if (projectsSubTabState === 'current' || projectsSubTabState === 'past') {
          setProjectsSubTab(projectsSubTabState)
        }
        if (typeof (state as { activeMeetingTab?: unknown }).activeMeetingTab === 'string' && String((state as { activeMeetingTab?: unknown }).activeMeetingTab).trim()) {
          setActiveMeetingTab(String((state as { activeMeetingTab?: string }).activeMeetingTab))
        }
      } catch {
        try {
          const raw = localStorage.getItem('app-state-v1')
          if (raw && active) {
            const state = JSON.parse(raw) as {
              consultantItemsByTeam?: Record<string, ConsultantItemsBySection>
              staffingRowsData?: StaffingRow[]
              meetingAttendees?: MeetingAttendee[]
              meetingAgendaItems?: string[]
              meetingActionItems?: MeetingActionItem[]
              officeContactOverrides?: Record<string, OfficeContactCardDraft>
              consultantCompanyReviews?: Record<string, ConsultantReview[]>
              siteSettings?: Partial<SiteSettings>
              calendarView?: 'day' | 'week' | 'month' | 'year' | 'list'
              calendarFeedFilters?: {
                deliverables: boolean
                teamTasks: boolean
                myTasks: boolean
                consultantTasks: boolean
              }
              consultantView?: string
              deletedConsultantTrackers?: string[]
              customConsultantCompanies?: Array<{ type: string; company: string }>
              activeTab?: TabKey
              projectsSubTab?: 'current' | 'past'
              activeMeetingTab?: MeetingTabKey
            }
            if (state.consultantItemsByTeam) setConsultantItemsByTeam((prev) => preserveLocalConsultantAttachments(mergeConsultantDefaults(state.consultantItemsByTeam as Record<string, ConsultantItemsBySection>), prev))
            if (Array.isArray(state.staffingRowsData)) setStaffingRowsData(state.staffingRowsData)
            if (Array.isArray(state.meetingAttendees)) setMeetingAttendees(state.meetingAttendees)
            if (Array.isArray(state.meetingAgendaItems)) setMeetingAgendaItems(state.meetingAgendaItems)
            if (Array.isArray(state.meetingActionItems)) setMeetingActionItems(normalizeMeetingActionItems(state.meetingActionItems))
            if (state.officeContactOverrides && typeof state.officeContactOverrides === 'object') {
              setOfficeContactOverrides(state.officeContactOverrides)
            }
            if (state.consultantCompanyReviews && typeof state.consultantCompanyReviews === 'object') {
              const incoming = state.consultantCompanyReviews
              if (Object.keys(incoming).length > 0) {
                setConsultantCompanyReviews((prev) => ({ ...incoming, ...prev }))
              }
            }
            // siteSettings must come from /api/site-settings (single global source)
            if (isCalendarViewValue(state.calendarView)) {
              setCalendarView(state.calendarView)
            }
            if (state.calendarFeedFilters) {
              setCalendarFeedFilters(state.calendarFeedFilters)
            }
            if (typeof state.consultantView === 'string' && state.consultantView.trim()) {
              setConsultantView(state.consultantView)
            }
            if (Array.isArray(state.deletedConsultantTrackers)) {
              setDeletedConsultantTrackers(state.deletedConsultantTrackers.map((x) => String(x)))
            }
            if (Array.isArray(state.customConsultantCompanies)) {
              setCustomConsultantCompanies(state.customConsultantCompanies.filter((x) => x && typeof x.type === 'string' && typeof x.company === 'string'))
            }
            const tabFromUrl = (() => {
              try {
                const tab = new URLSearchParams(window.location.search).get('tab')
                return isTabKey(tab) ? tab : null
              } catch {
                return null
              }
            })()
            if (!tabFromUrl && isTabKey(state.activeTab)) {
              setActiveTab(state.activeTab)
            }
            if (state.projectsSubTab === 'current' || state.projectsSubTab === 'past') {
              setProjectsSubTab(state.projectsSubTab)
            }
            if (typeof state.activeMeetingTab === 'string' && state.activeMeetingTab.trim()) {
              setActiveMeetingTab(state.activeMeetingTab)
            }
          }
        } catch {
          // ignore bad local storage data
        }
      } finally {
        if (active) {
          appStateHydratedRef.current = true
          setAppStateHydrated(true)
        }
      }
    }
    loadSiteSettings()
    loadAppState()

    const pollSharedState = async () => {
      if (currentRole !== 'Admin') {
        try {
          const siteRes = await apiFetch(`${apiBase}/api/site-settings?ts=${Date.now()}`)
          const siteData = siteRes.ok ? await siteRes.json() : null
          const settings = siteData?.settings
          if (active && settings && typeof settings === 'object') {
            mergeAndApplySiteSettings(settings as Partial<SiteSettings>)
          }
        } catch {
          // ignore transient poll failures
        }
      }

      try {
        const res = await apiFetch(`${apiBase}/api/app-state?ts=${Date.now()}`)
        const data = res.ok ? await res.json() : null
        const state = data?.state
        if (!active || !state || typeof state !== 'object') return

        if (state.consultantItemsByTeam && typeof state.consultantItemsByTeam === 'object') {
          const incoming = state.consultantItemsByTeam as Record<string, ConsultantItemsBySection>
          const incomingCount = Object.values(incoming).reduce((acc, sections) => acc + ((sections?.toConsultant?.length || 0) + (sections?.fromConsultant?.length || 0)), 0)
          // Ignore transient empty payloads from poll to prevent "flash then disappear" regressions.
          if (incomingCount > 0) {
            setConsultantItemsByTeam((prev) => preserveLocalConsultantAttachments(mergeConsultantDefaults(incoming), prev))
          }
        }

        if (Array.isArray((state as { deletedConsultantTrackers?: unknown }).deletedConsultantTrackers)) {
          setDeletedConsultantTrackers(((state as { deletedConsultantTrackers: unknown[] }).deletedConsultantTrackers || []).map((x) => String(x)))
        }

        if (typeof (state as { consultantView?: unknown }).consultantView === 'string' && String((state as { consultantView?: unknown }).consultantView).trim()) {
          setConsultantView(String((state as { consultantView?: unknown }).consultantView))
        }

      } catch {
        // ignore transient poll failures
      }
    }

    const pollMs = activeTab === 'consultantCoordinationTracking' ? 3000 : 10000
    const poll = window.setInterval(() => { void pollSharedState() }, pollMs)
    const onFocus = () => { void pollSharedState() }
    window.addEventListener('focus', onFocus)

    return () => {
      active = false
      window.clearInterval(poll)
      window.removeEventListener('focus', onFocus)
    }
  }, [apiBase, hasSavedDefaultSiteSettings, activeTab, currentRole])

  useEffect(() => {
    try {
      localStorage.setItem('consultant-company-reviews-v1', JSON.stringify(consultantCompanyReviews))
    } catch {
      // ignore local storage failures
    }
  }, [consultantCompanyReviews])

  useEffect(() => {
    try {
      localStorage.setItem('directory-search-v1', directorySearch)
      localStorage.setItem('directory-contact-filter-v1', directoryContactFilter)
      localStorage.setItem('directory-sort-by-v1', directorySortBy)
    } catch {
      // ignore local storage failures
    }
  }, [directorySearch, directoryContactFilter, directorySortBy])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (activeTab !== 'directory') return
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        directorySearchInputRef.current?.focus()
        directorySearchInputRef.current?.select()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [activeTab])

  useEffect(() => {
    try {
      localStorage.setItem('calendar-view-v1', calendarView)
    } catch {
      // ignore local storage failures
    }
  }, [calendarView])

  useEffect(() => {
    try {
      localStorage.setItem('calendar-feed-filters-v1', JSON.stringify(calendarFeedFilters))
    } catch {
      // ignore local storage failures
    }
  }, [calendarFeedFilters])

  useEffect(() => {
    if (!appStateHydrated || !appStateLoadedFromApi) return
    ;(async () => {
      const snapshot = await loadAttachmentSnapshot()
      if (!snapshot) return
      setConsultantItemsByTeam((prev) => preserveLocalConsultantAttachments(snapshot, prev))
    })()
  }, [appStateHydrated, appStateLoadedFromApi])

  useEffect(() => {
    if (!appStateHydrated || !appStateLoadedFromApi) return
    void saveAttachmentSnapshot(consultantItemsByTeam)
  }, [appStateHydrated, appStateLoadedFromApi, consultantItemsByTeam])

  useEffect(() => {
    if (!appStateHydrated) return
    // Prevent refresh races from pushing partial/empty local state over server data.
    if (!appStateLoadedFromApi) return

    const countConsultantItems = (items?: Record<string, ConsultantItemsBySection>) => {
      if (!items || typeof items !== 'object') return 0
      return Object.values(items).reduce((acc, sections) => {
        return acc
          + (sections?.toConsultant?.length || 0)
          + (sections?.fromConsultant?.length || 0)
          + (sections?.nextDeliverable?.length || 0)
      }, 0)
    }

    const nextConsultantCount = countConsultantItems(consultantItemsByTeam)
    let hasLocalConsultantData = false
    try {
      const raw = localStorage.getItem('app-state-v1')
      if (raw) {
        const local = JSON.parse(raw) as { consultantItemsByTeam?: Record<string, ConsultantItemsBySection> }
        hasLocalConsultantData = countConsultantItems(local.consultantItemsByTeam) > 0
      }
    } catch {
      // ignore parse errors
    }

    // Guard against destructive overwrite during hydration races:
    // never write an empty consultant list over non-empty local cache.
    if (nextConsultantCount === 0 && hasLocalConsultantData) return

    const state = {
      consultantItemsByTeam,
      consultantView,
      deletedConsultantTrackers,
      activeTab,
      projectsSubTab,
      activeMeetingTab,
      staffingRowsData,
      meetingAttendees,
      meetingAgendaItems,
      meetingActionItems,
      officeContactOverrides,
      consultantCompanyReviews,
      calendarView,
      calendarFeedFilters,
    }

    try {
      localStorage.setItem('app-state-v1', JSON.stringify(state))
    } catch {
      // ignore local storage failures
    }

    ;(async () => {
      try {
        await apiFetch(`${apiBase}/api/app-state`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ state }),
        })
      } catch {
        // local fallback already written above
      }
    })()
  }, [appStateHydrated, appStateLoadedFromApi, apiBase, consultantItemsByTeam, consultantView, deletedConsultantTrackers, activeTab, projectsSubTab, activeMeetingTab, staffingRowsData, meetingAttendees, meetingAgendaItems, meetingActionItems, officeContactOverrides, consultantCompanyReviews, calendarView, calendarFeedFilters])

  useEffect(() => {
    // Global site settings are admin-managed only.
    if (currentRole !== 'Admin') return
    // Prevent writing code defaults to the API before the persisted settings hydrate.
    if (!siteSettingsHydrated) return

    ;(async () => {
      try {
        await apiFetch(`${apiBase}/api/site-settings`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ settings: siteSettings }),
        })
      } catch {
        showCenterToast('Theme sync failed — retrying automatically')
      }
    })()
  }, [apiBase, siteSettings, currentRole, siteSettingsHydrated])

  useEffect(() => {
    const hasOpenModal =
      showContactInfo || showAddContact || showEditContact || showAddDeadline || showConsultantEdit || showMeetingAttendeeEdit || showStaffingEdit
    if (hasOpenModal) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [showContactInfo, showAddContact, showEditContact, showAddDeadline, showConsultantEdit, showMeetingAttendeeEdit, showStaffingEdit])


  useEffect(() => () => clearCalendarYearLegendCloseTimer(), [])

  useEffect(() => {
    if (calendarView !== 'list') setShowCalendarListGuide(false)
  }, [calendarView])

  const getGuideCalloutStyle = (
    selector: string,
    opts?: {
      mode?: 'left-of' | 'right-of' | 'inside-right' | 'inside-left' | 'below-center' | 'center-of'
      dx?: number
      dy?: number
      anchor?: 'top' | 'middle'
      fallbackLeft?: number
      fallbackTop?: number
    },
  ) => {
    const mode = opts?.mode || 'left-of'
    const dx = opts?.dx ?? 12
    const dy = opts?.dy ?? 0
    const fallbackLeft = opts?.fallbackLeft ?? 200
    const fallbackTop = opts?.fallbackTop ?? 180
    const anchor = opts?.anchor ?? 'top'
    if (typeof document === 'undefined') return { left: fallbackLeft, top: fallbackTop }
    const el = document.querySelector(selector) as HTMLElement | null
    if (!el) return { left: fallbackLeft, top: fallbackTop }
    const r = el.getBoundingClientRect()
    const topBase = anchor === 'middle' ? (r.top + (r.height / 2)) : r.top
    if (mode === 'right-of') return { left: Math.round(r.right + dx), top: Math.round(topBase + dy) }
    if (mode === 'inside-right') return { left: Math.round(r.left + Math.max(120, r.width * 0.58) + dx), top: Math.round(topBase + dy) }
    if (mode === 'inside-left') return { left: Math.round(r.left + dx), top: Math.round(topBase + dy) }
    if (mode === 'below-center') return { left: Math.round(r.left + (r.width / 2) + dx), top: Math.round(r.bottom + dy) }
    if (mode === 'center-of') return { left: Math.round(r.left + (r.width / 2) + dx), top: Math.round(r.top + dy) }
    return { left: Math.round(r.left - dx), top: Math.round(topBase + dy) }
  }


  useEffect(() => {
    const loadCalendarState = async () => {
      try {
        const deadlinesRaw = localStorage.getItem('calendar-deadlines-v1')
        const uploadsRaw = localStorage.getItem('calendar-uploads-v1')
        if (deadlinesRaw) {
          const parsedDeadlines = JSON.parse(deadlinesRaw) as CalendarDeadline[]
          if (Array.isArray(parsedDeadlines)) setCalendarDeadlines(mergeDemoCalendarDeadlines(parsedDeadlines))
        }
        if (uploadsRaw) {
          const parsedUploads = JSON.parse(uploadsRaw) as string[]
          if (Array.isArray(parsedUploads)) setCalendarUploads(parsedUploads)
        }
      } catch {
        // ignore bad local storage data
      } finally {
        setCalendarHydrated(true)
      }

      setCalendarSyncing(true)
      setCalendarSyncError('')
      try {
        const res = await apiFetch(`${apiBase}/api/calendar-state?ts=${Date.now()}`)
        const data = res.ok ? await res.json() : null
        if (Array.isArray(data?.deadlines)) setCalendarDeadlines(mergeDemoCalendarDeadlines(data.deadlines as CalendarDeadline[]))
        if (Array.isArray(data?.uploads)) setCalendarUploads(data.uploads as string[])
        setCalendarLastSyncedAt(new Date().toISOString())
      } catch {
        setCalendarSyncError('Calendar sync unavailable')
      } finally {
        setCalendarSyncing(false)
      }
    }

    loadCalendarState()
  }, [apiBase])

  useEffect(() => {
    if (!calendarHydrated || calendarDemoSeeded) return
    const merged = mergeDemoCalendarDeadlines(calendarDeadlines)
    if (merged.length !== calendarDeadlines.length) {
      setCalendarDeadlines(merged)
      setCalendarLastUpdatedAt(now())
    }
    setCalendarDemoSeeded(true)
  }, [calendarHydrated, calendarDemoSeeded, calendarDeadlines])

  useEffect(() => {
    if (!calendarHydrated) return

    const saveCalendarState = async () => {
      try {
        await apiFetch(`${apiBase}/api/calendar-state`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ deadlines: calendarDeadlines, uploads: calendarUploads }),
        })
        setCalendarSyncError('')
        setCalendarLastSyncedAt(new Date().toISOString())
      } catch {
        setCalendarSyncError('Calendar sync unavailable')
        try {
          localStorage.setItem('calendar-deadlines-v1', JSON.stringify(calendarDeadlines))
          localStorage.setItem('calendar-uploads-v1', JSON.stringify(calendarUploads))
        } catch {
          // ignore storage failures
        }
      }
    }

    saveCalendarState()
  }, [calendarDeadlines, calendarUploads, calendarHydrated, apiBase])
  useEffect(() => {
    const clock = setInterval(() => setNowTick(Date.now()), 60000)
    return () => clearInterval(clock)
  }, [])

  useEffect(() => {
    const ensureProjectDoc = (title: 'Project Vault', baseUrl: string) => {
      const exists = store.documents.some((d) => d.title === title)
      if (!exists) {
        store.addDocument({
          title,
          kind: 'note',
          source: 'You',
          description: 'Project workspace initialized.',
          folderPath: `${baseUrl}/`,
          folders: [
            { name: 'inbox', path: `${baseUrl}/inbox/` },
            { name: 'outbound', path: `${baseUrl}/outbound/` },
          ],
        })
      }
    }

    ensureProjectDoc('Project Vault', 'http://187.124.92.179:8791')
  }, [store])

  const consultantTeamTabs = useMemo(() => {
    const deletedSet = new Set(deletedConsultantTrackers.map((x) => x.trim().toLowerCase()).filter(Boolean))
    const fromTrackers = Object.keys(consultantItemsByTeam || {}).filter((t) => t && t !== 'All Teams' && t !== ALL_CONSULTANT_TAB)
    const teams = fromTrackers.filter((t) => !deletedSet.has(t.trim().toLowerCase()))
    const sorted = teams.sort((a, b) => a.localeCompare(b))
    return [ALL_CONSULTANT_TAB, ...sorted]
  }, [consultantItemsByTeam, deletedConsultantTrackers])

  const staffingTeamMembers = useMemo(
    () =>
      directoryContacts
        .filter((c) => /\baor\s*team\b|\bar\s*team\b|\bcore\s*team\b/i.test((c.team || '').trim()))
        .map((c) => c.name)
        .filter(Boolean),
    [directoryContacts],
  )

  const aorCoreCompanies = useMemo(
    () =>
      Array.from(
        new Set(
          directoryContacts
            .filter((c) => /\baor\s*team\b|\bar\s*team\b|\bcore\s*team\b/i.test((c.team || '').trim()))
            .map((c) => (c.company || '').trim())
            .filter(Boolean),
        ),
      ),
    [directoryContacts],
  )

  useEffect(() => {
    if (consultantTeamTabs.length === 0) {
      if (consultantView !== ALL_CONSULTANT_TAB) setConsultantView(ALL_CONSULTANT_TAB)
      return
    }

    if (!consultantTeamTabs.includes(consultantView)) {
      // During initial hydration, do not immediately fall back to "All";
      // wait for persisted app-state teams to load first.
      if (!appStateHydrated) return
      setConsultantView(consultantTeamTabs[0])
    }
  }, [consultantTeamTabs, consultantView, appStateHydrated])

  useEffect(() => {
    if (staffingMemberView === '') {
      setStaffingMemberView('Team')
      return
    }
    if (staffingMemberView !== 'Team' && staffingTeamMembers.length > 0 && !staffingTeamMembers.includes(staffingMemberView)) {
      setStaffingMemberView('Team')
    }
  }, [staffingTeamMembers, staffingMemberView])

  const consultantTeamAbbrev = useMemo(() => {
    const mapTeam = (team: string) => {
      const t = team.toLowerCase()
      if (team === ALL_CONSULTANT_TAB) return 'ALL'
      if (/client/.test(t)) return 'CLNT'
      if (/mep/.test(t)) return 'MEP'
      if (/consultant/.test(t)) return 'CONS'
      if (/struct/.test(t)) return 'STR'
      if (/civil/.test(t)) return 'CIV'
      if (/landscape/.test(t)) return 'LAND'
      if (/interior/.test(t)) return 'INT'
      if (/mechanical/.test(t)) return 'MECH'
      if (/electrical/.test(t)) return 'ELEC'
      if (/plumb/.test(t)) return 'PLBG'
      if (/fire\s*protection|fp\b/.test(t)) return 'FP'
      if (/low\s*voltage|it\b/.test(t)) return 'IT'
      if (/audio\s*visual|\bav\b/.test(t)) return 'AV'
      if (/lighting/.test(t)) return 'LGT'
      if (/acoustic/.test(t)) return 'ACOU'
      if (/geotech/.test(t)) return 'GEO'
      if (/traffic/.test(t)) return 'TRAF'
      if (/code/.test(t)) return 'CODE'
      if (/signage|wayfinding/.test(t)) return 'SIGN'

      const words = team
        .replace(/team|group|consultants?/gi, '')
        .split(/\s+/)
        .map((w) => w.trim())
        .filter(Boolean)
      if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase()
      return (words[0] || team).slice(0, 4).toUpperCase()
    }

    return Object.fromEntries(consultantTeamTabs.map((team) => [team, mapTeam(team)]))
  }, [consultantTeamTabs])

  const consultantCompanies = useMemo(() => {
    const companies = directoryContacts
      .filter((c) => consultantView === ALL_CONSULTANT_TAB || (c.team || '').trim() === consultantView)
      .map((c) => (c.company || '').trim())
      .filter(Boolean)
    return uniqueSortedTrimmed(companies)
  }, [directoryContacts, consultantView])

  const activeDirectoryGroupKeySet = useMemo(() => {
    if (!activeDirectoryViewId) return null
    const found = directorySavedViews.find((view) => view.id === activeDirectoryViewId)
    const keys = found?.contactKeys || []
    return keys.length ? new Set(keys) : null
  }, [activeDirectoryViewId, directorySavedViews])

  const directoryVisibleIndices = useMemo(() => {
    const q = directorySearch.trim().toLowerCase()
    const out: number[] = []
    directoryContacts.forEach((c, idx) => {
      if (directoryTeamFilter !== 'All Teams' && c.team !== directoryTeamFilter) return
      if (activeDirectoryGroupKeySet && !activeDirectoryGroupKeySet.has(directoryContactKey(c))) return
      if (directoryContactFilter === 'archived') {
        if (!c.archived) return
      } else {
        if (c.archived) return
        if (directoryContactFilter === 'key' && !c.primaryContact) return
      }
      if (q) {
        const haystack = [c.name, c.team, c.role, c.company, c.email, c.phone, c.altPhone]
          .map((x) => (x || '').toLowerCase())
          .join(' ')
        if (!haystack.includes(q)) return
      }
      out.push(idx)
    })
    return out
  }, [directoryContacts, directorySearch, directoryContactFilter, directoryTeamFilter, activeDirectoryGroupKeySet])

  const directoryDuplicateGroups = useMemo(() => {
    const buckets = new Map<string, number[]>()
    directoryVisibleIndices.forEach((idx) => {
      const c = directoryContacts[idx]
      if (!c) return
      const email = (c.email || '').trim().toLowerCase()
      const phone = (c.phone || '').replace(/\D+/g, '')
      const name = (c.name || '').trim().toLowerCase()
      const company = (c.company || '').trim().toLowerCase()
      const key = email ? `email:${email}` : (phone ? `phone:${phone}` : `name:${name}|company:${company}`)
      const next = buckets.get(key) || []
      next.push(idx)
      buckets.set(key, next)
    })
    return Array.from(buckets.values()).filter((group) => group.length > 1)
  }, [directoryContacts, directoryVisibleIndices])

  useEffect(() => {
    setDirectoryBulkSelection((prev) => {
      if (!prev.size) return prev
      const valid = new Set<number>()
      prev.forEach((idx) => {
        if (idx >= 0 && idx < directoryContacts.length) valid.add(idx)
      })
      return valid
    })
  }, [directoryContacts])

  const consultantTrackerMeta = parseConsultantTrackerName(consultantView)

  const consultantDirectoryCompanies = useMemo(() => {
    const unique = new Map<string, string>()
    directoryContacts.forEach((contact) => {
      const company = (contact.company || '').trim()
      if (!company) return
      const key = company.toLowerCase()
      if (!unique.has(key)) unique.set(key, company)
    })
    return Array.from(unique.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
  }, [directoryContacts])

  const getDefaultDisciplineForCompany = (companyName: string) => {
    const target = (companyName || '').trim().toLowerCase()
    if (!target) return ''

    const counts = new Map<string, number>()
    directoryContacts.forEach((contact) => {
      const company = (contact.company || '').trim().toLowerCase()
      const discipline = (contact.team || '').trim()
      if (company !== target || !discipline) return
      counts.set(discipline, (counts.get(discipline) || 0) + 1)
    })

    if (!counts.size) return ''
    const ranked = Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }))
    return ranked[0]?.[0] || ''
  }

  const getDefaultAbbreviationForCompany = (companyName: string) => {
    const target = (companyName || '').trim().toLowerCase()
    if (!target) return ''

    const counts = new Map<string, number>()
    directoryContacts.forEach((contact) => {
      const company = (contact.company || '').trim().toLowerCase()
      const abbreviation = (contact.companyAbbreviation || '').trim()
      if (company !== target || !abbreviation) return
      counts.set(abbreviation, (counts.get(abbreviation) || 0) + 1)
    })

    if (!counts.size) return ''
    const ranked = Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }))
    return ranked[0]?.[0] || ''
  }

  const staffingRows = useMemo(() => {
    const baseRows = staffingMemberView && staffingMemberView !== 'Team'
      ? staffingRowsData.filter((r) => r.assignee === staffingMemberView)
      : [...staffingRowsData]

    if (staffingSortKey) {
      const priorityRank: Record<string, number> = { High: 1, Medium: 2, Low: 3 }
      const statusRank: Record<string, number> = { 'Not Started': 1, 'In Progress': 2, 'On Hold': 3, Complete: 4 }

      const sorted = baseRows.sort((a, b) => {
        let cmp = 0
        if (staffingSortKey === 'priority') {
          cmp = (priorityRank[a.priority] ?? 99) - (priorityRank[b.priority] ?? 99)
        } else if (staffingSortKey === 'status') {
          cmp = (statusRank[a.status] ?? 99) - (statusRank[b.status] ?? 99)
        } else {
          cmp = String(a[staffingSortKey] ?? '').localeCompare(String(b[staffingSortKey] ?? ''))
        }
        return staffingSortDir === 'asc' ? cmp : -cmp
      })
      return sorted
    }

    if (staffingMemberView === 'Team') {
      const pRank: Record<string, number> = { High: 1, Medium: 2, Low: 3 }
      return baseRows.sort((a, b) => {
        const aComplete = a.status === 'Complete'
        const bComplete = b.status === 'Complete'
        if (aComplete !== bComplete) return aComplete ? 1 : -1

        const pa = pRank[a.priority] ?? 99
        const pb = pRank[b.priority] ?? 99
        if (pa !== pb) return pa - pb
        return a.due.localeCompare(b.due)
      })
    }

    return baseRows
  }, [staffingRowsData, staffingMemberView, staffingSortDir, staffingSortKey])

  const sortStaffingBy = (key: 'status' | 'priority' | 'start' | 'due' | 'task' | 'notes' | 'assignee') => {
    if (staffingSortKey === key) {
      setStaffingSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      return
    }
    setStaffingSortKey(key)
    setStaffingSortDir('asc')
  }

  useEffect(() => {
    if (!calendarDeadlines.length) return

    const today = todayIso()
    const memberSet = new Set(staffingTeamMembers.map((x) => x.trim().toLowerCase()))
    const companySet = new Set(aorCoreCompanies.map((x) => x.trim().toLowerCase()))

    const toInsert: Array<{ id: string; status: string; priority: string; start: string; due: string; task: string; assignee: string; notes: string }> = []
    const noteUpdates: Array<{ due: string; task: string; assignee: string; notes: string }> = []

    for (const d of calendarDeadlines) {
      const key = `${d.source}|${d.dateKey}|${d.title}`
      const parsed = parseLegacyDeadline(d)
      const assignTo = parsed.assignTo
      if (!assignTo) continue

      const normalized = assignTo.toLowerCase()
      const isMember = memberSet.has(normalized)
      const isCompany = companySet.has(normalized)
      if (!isMember && !isCompany) continue

      const normalizedTaskTitle = parsed.mainTitle
      const normalizedAssignee = isCompany ? 'Team' : assignTo
      const normalizedNotes = parsed.description || `From calendar • ${d.source}`

      noteUpdates.push({ due: d.dateKey, task: normalizedTaskTitle, assignee: normalizedAssignee, notes: normalizedNotes })

      if (!syncedCalendarTaskKeysRef.current.has(key)) {
        toInsert.push({
          id: makeId(),
          status: 'Not Started',
          priority: 'Medium',
          start: today,
          due: d.dateKey,
          task: normalizedTaskTitle,
          assignee: normalizedAssignee,
          notes: normalizedNotes,
        })
        syncedCalendarTaskKeysRef.current.add(key)
      }
    }

    if (toInsert.length || noteUpdates.length) {
      setStaffingRowsData((prev) => {
        const withUpdates = prev.map((row) => {
          const match = noteUpdates.find((u) => u.due === row.due && u.task === row.task && u.assignee === row.assignee)
          if (!match) return row
          if (row.notes === match.notes) return row
          return { ...row, notes: match.notes }
        })
        return toInsert.length ? [...toInsert, ...withUpdates] : withUpdates
      })
    }
  }, [calendarDeadlines, staffingTeamMembers, aorCoreCompanies])

  useEffect(() => {
    if (!meetingActionItems.length) return

    const today = todayIso()
    const companySet = new Set(aorCoreCompanies.map((x) => x.trim().toLowerCase()))

    const toInsert: StaffingRow[] = []

    for (const item of meetingActionItems) {
      const owner = (item.owner || '').trim()
      if (!owner) continue

      const ownerLower = owner.toLowerCase()
      const ownerIsAor = ownerLower === 'aor'
      const ownerIsCompany = companySet.has(ownerLower)
      if (!ownerIsAor && !ownerIsCompany) continue

      const key = `meeting|${item.id}`
      if (syncedMeetingTaskKeysRef.current.has(key)) continue

      toInsert.push({
        id: makeId(),
        status: 'Not Started',
        priority: 'Medium',
        start: today,
        due: item.due || today,
        task: item.item || 'Meeting topic',
        assignee: 'Team',
        notes: `From meeting minutes • Owner: ${owner}`,
      })
      syncedMeetingTaskKeysRef.current.add(key)
    }

    if (toInsert.length) {
      setStaffingRowsData((prev) => [...toInsert, ...prev])
    }
  }, [meetingActionItems, aorCoreCompanies])

  useEffect(() => {
    const today = todayIso()
    const toInsert: StaffingRow[] = []

    for (const [team, sections] of Object.entries(consultantItemsByTeam)) {
      ;(sections.toConsultant || []).forEach((item) => {
        const title = (item.title || '').trim()
        if (!title) return

        const key = `consultant-send|${team}|${item.id}`
        if (syncedConsultantSendTaskKeysRef.current.has(key)) return

        toInsert.push({
          id: makeId(),
          status: 'Not Started',
          priority: 'Medium',
          start: today,
          due: isIsoDate(getNormalizedConsultantDueDate(item)) ? getNormalizedConsultantDueDate(item) : today,
          task: title,
          assignee: 'Team',
          notes: `From consultant send list • Team: ${team} • sourceId:${item.id}`,
        })
        syncedConsultantSendTaskKeysRef.current.add(key)
      })
    }

    if (toInsert.length) {
      setStaffingRowsData((prev) => {
        const existingSourceIds = new Set(
          prev
            .map((row) => {
              const m = (row.notes || '').match(/sourceId:([a-zA-Z0-9_-]+)/)
              return m ? m[1] : ''
            })
            .filter(Boolean),
        )

        const filtered = toInsert.filter((row) => {
          const m = (row.notes || '').match(/sourceId:([a-zA-Z0-9_-]+)/)
          return !(m && existingSourceIds.has(m[1]))
        })

        return filtered.length ? [...filtered, ...prev] : prev
      })
    }
  }, [consultantItemsByTeam])

  const isConsultantAllView = consultantView === ALL_CONSULTANT_TAB

  const currentConsultantItems = useMemo(() => {
    if (!isConsultantAllView) {
      return consultantItemsByTeam[consultantView] || { toConsultant: [], fromConsultant: [], nextDeliverable: [] }
    }

    const companyForTeam = (team: string) => {
      const cleanTeam = (team || '').trim()
      const fromTabName = (parseConsultantTrackerName(cleanTeam).company || '').trim()
      if (fromTabName) return fromTabName

      const normalize = (v: string) =>
        (v || '')
          .toLowerCase()
          .replace(/[—–-]/g, ' ')
          .replace(/\b(team|group|consultants?)\b/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()

      const teamNorm = normalize(cleanTeam)
      const fromDirectoryRaw = directoryContacts
        .filter((c) => {
          const ct = normalize((c.team || '').trim())
          if (!ct || !teamNorm) return false
          return ct === teamNorm || ct.includes(teamNorm) || teamNorm.includes(ct)
        })
        .map((c) => (c.company || '').trim())
        .filter(Boolean)

      if (fromDirectoryRaw.length) {
        const counts = new Map<string, number>()
        fromDirectoryRaw.forEach((name) => counts.set(name, (counts.get(name) || 0) + 1))
        const ranked = Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        if (ranked[0]?.[0]) return ranked[0][0]
      }

      // Fallback to the source tab label itself so company never renders blank in All view.
      return cleanTeam || 'Company TBD'
    }

    const toAllViewItem = (item: ConsultantItem, team: string, sourceCompany: string): ConsultantItem => ({
      ...item,
      id: `${team}::${item.id}`,
      assignee: team,
      sourceCompany,
    })

    const combined: ConsultantItemsBySection = { toConsultant: [], fromConsultant: [], nextDeliverable: [] }
    for (const [team, sections] of Object.entries(consultantItemsByTeam)) {
      if (team === 'All Teams') continue
      const sourceCompany = companyForTeam(team)
      ;(['toConsultant', 'fromConsultant', 'nextDeliverable'] as const).forEach((section) => {
        const rows = sections[section] || []
        rows.forEach((item) => {
          combined[section].push(toAllViewItem(item, team, sourceCompany))
        })
      })
    }
    return combined
  }, [consultantItemsByTeam, consultantView, isConsultantAllView, directoryContacts])

  const isIsoDate = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test((value || '').trim())
  const formatShortDate = (value?: string) => {
    const v = (value || '').trim()
    if (!isIsoDate(v)) return 'mm/dd/yyyy'
    const [y, m, d] = v.split('-')
    return `${m}/${d}/${y}`
  }

  const getNormalizedConsultantDueDate = (item: ConsultantItem) => (item.dueDate || '').trim()

  const getConsultantDueState = (item: ConsultantItem) => {
    const due = getNormalizedConsultantDueDate(item)
    if (!isIsoDate(due)) return 'on_track' as const
    const todayKey = todayIso()
    if (due < todayKey) return 'breached' as const

    const dueDate = new Date(`${due}T00:00:00`)
    const nowDate = new Date(`${todayKey}T00:00:00`)
    const days = Math.floor((dueDate.getTime() - nowDate.getTime()) / 86400000)
    if (days <= 2) return 'risk' as const
    return 'on_track' as const
  }

  const getConsultantSlaState = (item: ConsultantItem) => getConsultantDueState(item)
  const getConsultantSendState = (item: ConsultantItem) => getConsultantDueState(item)

  const displayConsultantItems = useMemo(() => {
    const sortByDueDate = (items: ConsultantItem[]) => {
      const next = [...items]
      next.sort((a, b) => {
        const aHasDue = isIsoDate(a.dueDate || '')
        const bHasDue = isIsoDate(b.dueDate || '')
        if (aHasDue !== bHasDue) return aHasDue ? 1 : -1
        const ad = aHasDue ? (a.dueDate || '').trim() : ''
        const bd = bHasDue ? (b.dueDate || '').trim() : ''
        return ad.localeCompare(bd) || (a.title || '').localeCompare(b.title || '')
      })
      return next
    }

    const applyColumnSort = (items: ConsultantItem[]) => {
      const next = [...items]
      const dir = consultantColumnSort.direction === 'asc' ? 1 : -1
      next.sort((a, b) => {
        if (consultantColumnSort.key === 'item') {
          const aa = String(a.title || '').toLowerCase()
          const bb = String(b.title || '').toLowerCase()
          return dir * aa.localeCompare(bb)
        }

        if (consultantColumnSort.key === 'assignee') {
          const aa = String(a.assignee || '').toLowerCase()
          const bb = String(b.assignee || '').toLowerCase()
          return dir * (aa.localeCompare(bb) || String(a.title || '').localeCompare(String(b.title || '')))
        }

        const aDue = String(a.dueDate || '').trim()
        const bDue = String(b.dueDate || '').trim()
        const aHasDue = isIsoDate(aDue)
        const bHasDue = isIsoDate(bDue)
        if (aHasDue !== bHasDue) return aHasDue ? dir : -dir
        if (!aHasDue && !bHasDue) return dir * String(a.title || '').localeCompare(String(b.title || ''))
        return dir * (aDue.localeCompare(bDue) || String(a.title || '').localeCompare(String(b.title || '')))
      })
      return next
    }

    if (!isConsultantAllView) {
      const receiveItems = [
        ...(currentConsultantItems.fromConsultant || []),
        ...(currentConsultantItems.nextDeliverable || []),
      ]
      return {
        toConsultant: applyColumnSort(currentConsultantItems.toConsultant || []),
        fromConsultant: applyColumnSort(receiveItems),
        nextDeliverable: applyColumnSort(currentConsultantItems.nextDeliverable || []),
      }
    }

    const today = new Date()
    const todayKey = today.toISOString().slice(0, 10)
    const weekEnd = new Date(today)
    weekEnd.setDate(today.getDate() + 7)
    const weekEndKey = weekEnd.toISOString().slice(0, 10)

    const applyFilterSort = (items: ConsultantItem[]) => {
      let next = [...items]

      if (allConsultantFilter === 'overdue') {
        next = next.filter((x) => isIsoDate(x.dueDate || '') && (x.dueDate || '').trim() < todayKey)
      } else if (allConsultantFilter === 'thisWeek') {
        next = next.filter((x) => isIsoDate(x.dueDate || '') && (x.dueDate || '').trim() >= todayKey && (x.dueDate || '').trim() <= weekEndKey)
      } else if (allConsultantFilter === 'noDue') {
        next = next.filter((x) => !isIsoDate(x.dueDate || ''))
      }

      if (allConsultantSort === 'company') {
        next.sort((a, b) => (a.assignee || '').localeCompare(b.assignee || '') || (a.title || '').localeCompare(b.title || ''))
      } else {
        next = sortByDueDate(next)
      }
      return next
    }

    const receiveItems = [
      ...(currentConsultantItems.fromConsultant || []),
      ...(currentConsultantItems.nextDeliverable || []),
    ]

    return {
      toConsultant: applyFilterSort(currentConsultantItems.toConsultant || []),
      fromConsultant: applyFilterSort(receiveItems),
      nextDeliverable: applyFilterSort(currentConsultantItems.nextDeliverable || []),
    }
  }, [isConsultantAllView, currentConsultantItems, allConsultantFilter, allConsultantSort, consultantColumnSort])

  const toggleConsultantColumnSort = (key: 'item' | 'assignee' | 'dueDate') => {
    setConsultantColumnSort((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }

  const consultantSortMarker = (key: 'item' | 'assignee' | 'dueDate') => {
    if (consultantColumnSort.key !== key) return '↕'
    return consultantColumnSort.direction === 'asc' ? '↑' : '↓'
  }

  useEffect(() => {
    setConsultantSendExpanded(false)
    setConsultantReceiveExpanded(false)
    setConsultantFeedExpanded(false)
    setSelectedConsultantDetailKey(null)
    setConsultantSelectedDetailKeys(new Set())
    setConsultantAttachmentPreviewKey(null)
    setConsultantAttachmentPinnedKey(null)
  }, [consultantView])

  useEffect(() => {
    try {
      const raw = localStorage.getItem('consultant-saved-views-v1')
      if (!raw) return
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) setSavedConsultantViews(parsed)
    } catch {}
  }, [])

  useEffect(() => {
    try { localStorage.setItem('consultant-saved-views-v1', JSON.stringify(savedConsultantViews)) } catch {}
  }, [savedConsultantViews])

  const startConsultantAttachmentPreviewHover = (key: string) => {
    if (consultantAttachmentHoverTimerRef.current) {
      window.clearTimeout(consultantAttachmentHoverTimerRef.current)
      consultantAttachmentHoverTimerRef.current = null
    }
    consultantAttachmentHoverTimerRef.current = window.setTimeout(() => {
      setConsultantAttachmentPreviewKey(key)
      consultantAttachmentHoverTimerRef.current = null
    }, 1000)
  }

  const stopConsultantAttachmentPreviewHover = () => {
    if (consultantAttachmentHoverTimerRef.current) {
      window.clearTimeout(consultantAttachmentHoverTimerRef.current)
      consultantAttachmentHoverTimerRef.current = null
    }
    if (!consultantAttachmentPinnedKey) setConsultantAttachmentPreviewKey(null)
  }

  const toggleConsultantAttachmentPreviewPinned = (key: string) => {
    setConsultantAttachmentPinnedKey((prev) => {
      const next = prev === key ? null : key
      setConsultantAttachmentPreviewKey(next || null)
      return next
    })
  }

  const resolveConsultantSectionIdentity = (section: ConsultantSectionKey | string): 'toConsultant' | 'fromConsultant' | '' => {
    if (section === 'toConsultant' || section === 'To Consultant') return 'toConsultant'
    if (section === 'fromConsultant' || section === 'nextDeliverable' || section === 'From Consultant' || section === 'Next Deliverable') return 'fromConsultant'
    return ''
  }

  const consultantNavSectionFromLane = (lane: string): 'toConsultant' | 'fromConsultant' | '' => resolveConsultantSectionIdentity(lane)

  const makeConsultantNavKey = (team: string, section: ConsultantSectionKey, id: string) => `${team}::${section}::${id}`

  const makeConsultantRowNavKey = (rawId: string, assignee: string | undefined, section: 'toConsultant' | 'fromConsultant') => {
    const isPrefixed = rawId.includes('::')
    const team = isConsultantAllView
      ? (String(assignee || '').trim() || (isPrefixed ? rawId.split('::')[0] : consultantView))
      : consultantView
    const baseId = isPrefixed ? rawId.split('::').slice(1).join('::') : rawId
    return makeConsultantNavKey(team, section, baseId)
  }

  const consultantFeedRowKey = (row: { id: string; team?: string }) => `${row.team || ''}::${row.id}`

  const toggleConsultantDetailSelection = (navKey: string) => {
    setSelectedConsultantDetailKey(navKey)
    setConsultantSelectedDetailKeys((prev) => {
      const next = new Set(prev)
      if (next.has(navKey)) next.delete(navKey)
      else next.add(navKey)
      return next
    })
  }

  const focusConsultantDetailRowByOffset = (currentNavKey: string, direction: 1 | -1) => {
    const rows = Array.from(document.querySelectorAll('[data-consultant-nav-key]')) as HTMLElement[]
    const idx = rows.findIndex((row) => row.getAttribute('data-consultant-nav-key') === currentNavKey)
    if (idx < 0) return
    const nextIdx = Math.max(0, Math.min(rows.length - 1, idx + direction))
    const next = rows[nextIdx]
    if (!next) return
    const nextKey = next.getAttribute('data-consultant-nav-key') || null
    if (nextKey) setSelectedConsultantDetailKey(nextKey)
    window.setTimeout(() => next.focus(), 0)
  }

  const bulkUpdateConsultantSelected = (patch: Partial<ConsultantItem>, sectionFilter?: 'toConsultant' | 'fromConsultant') => {
    if (isConsultantAllView) return
    const selected = consultantSelectedDetailKeys
    if (!selected.size) return
    setConsultantItemsByTeam((prev) => {
      const teamItems = prev[consultantView] || { toConsultant: [], fromConsultant: [], nextDeliverable: [] }
      const apply = (section: ConsultantSectionKey) => teamItems[section].map((x) => {
        const mappedSection = resolveConsultantSectionIdentity(section)
        if (!mappedSection) return x
        const key = makeConsultantNavKey(consultantView, mappedSection, x.id)
        const inScope = !sectionFilter || mappedSection === sectionFilter
        return inScope && selected.has(key) ? { ...x, ...patch } : x
      })
      return {
        ...prev,
        [consultantView]: {
          ...teamItems,
          toConsultant: apply('toConsultant'),
          fromConsultant: apply('fromConsultant'),
          nextDeliverable: apply('nextDeliverable'),
        },
      }
    })
  }

  const bulkDeleteConsultantSelected = (sectionFilter?: 'toConsultant' | 'fromConsultant') => {
    if (isConsultantAllView) return
    const selected = consultantSelectedDetailKeys
    if (!selected.size) return
    const selectedInScope = Array.from(selected).filter((key) => !sectionFilter || key.includes(`::${sectionFilter}::`))
    if (!selectedInScope.length) return
    setPendingDeleteConfirm({
      title: 'Delete Selected Items?',
      body: `Delete ${selectedInScope.length} selected item${selectedInScope.length === 1 ? '' : 's'}? This cannot be undone from this modal.`,
      onConfirm: () => {
        const selectedSet = new Set(selectedInScope)
        setConsultantItemsByTeam((prev) => {
          const teamItems = prev[consultantView] || { toConsultant: [], fromConsultant: [], nextDeliverable: [] }
          const keep = (section: ConsultantSectionKey) => teamItems[section].filter((x) => {
            const mappedSection = resolveConsultantSectionIdentity(section)
            if (!mappedSection) return true
            const key = makeConsultantNavKey(consultantView, mappedSection, x.id)
            return !selectedSet.has(key)
          })
          return {
            ...prev,
            [consultantView]: {
              ...teamItems,
              toConsultant: keep('toConsultant'),
              fromConsultant: keep('fromConsultant'),
              nextDeliverable: keep('nextDeliverable'),
            },
          }
        })
        setConsultantSelectedDetailKeys((prev) => {
          const next = new Set(prev)
          selectedInScope.forEach((k) => next.delete(k))
          return next
        })
        showCenterToast('Selected items deleted')
        setPendingDeleteConfirm(null)
      },
    })
  }

  const focusConsultantFeedRowByOffset = (currentRow: { id: string; team?: string }, direction: 1 | -1) => {
    const currentKey = consultantFeedRowKey(currentRow)
    const idx = consultantVisibleFeedRows.findIndex((r) => consultantFeedRowKey(r) === currentKey)
    if (idx < 0) return
    const nextIdx = Math.max(0, Math.min(consultantVisibleFeedRows.length - 1, idx + direction))
    const next = consultantVisibleFeedRows[nextIdx]
    if (!next) return
    setSelectedConsultantFeedId(next.id)
    const nextKey = consultantFeedRowKey(next)
    window.setTimeout(() => {
      const el = document.querySelector(`[data-consultant-feed-key="${nextKey}"]`) as HTMLElement | null
      el?.focus()
    }, 0)
  }

  const jumpToConsultantDetailRow = (row: { id: string; lane: string; team?: string }) => {
    const section = consultantNavSectionFromLane(row.lane)
    if (!section) return

    const team = (row.team || consultantView || '').trim()
    if (!team) return

    if (section === 'toConsultant') setConsultantSendExpanded(true)
    if (section === 'fromConsultant') setConsultantReceiveExpanded(true)

    const navKey = makeConsultantNavKey(team, section, row.id)
    const target = document.querySelector(`[data-consultant-nav-key="${navKey}"]`) as HTMLElement | null
    if (!target) {
      showCenterToast('Could not find target row')
      return
    }

    setSelectedConsultantDetailKey(navKey)
    setConsultantJumpSourceKey(navKey)
    window.setTimeout(() => setConsultantJumpSourceKey((prev) => (prev === navKey ? null : prev)), 2600)
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    target.classList.remove('consultant-row-jump-highlight')
    window.setTimeout(() => target.classList.add('consultant-row-jump-highlight'), 40)
    window.setTimeout(() => target.classList.remove('consultant-row-jump-highlight'), 3500)
    showCenterToast(`Jumped to item in ${section === 'toConsultant' ? 'To Client' : 'From Client'}`)
  }

  useEffect(() => {
    const onDocClick = () => {
      setConsultantAttachmentPinnedKey(null)
      setConsultantAttachmentPreviewKey(null)
    }
    document.addEventListener('click', onDocClick)
    return () => {
      document.removeEventListener('click', onDocClick)
      if (consultantAttachmentHoverTimerRef.current) {
        window.clearTimeout(consultantAttachmentHoverTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!consultantImagePreviewOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        setConsultantImagePreviewIndex((prev) => {
          const next = Math.min(consultantImagePreviewList.length - 1, prev + 1)
          const item = consultantImagePreviewList[next]
          if (item) {
            setConsultantImagePreviewSrc(item.dataUrl)
            setConsultantImagePreviewName(item.name)
          }
          return next
        })
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        setConsultantImagePreviewIndex((prev) => {
          const next = Math.max(0, prev - 1)
          const item = consultantImagePreviewList[next]
          if (item) {
            setConsultantImagePreviewSrc(item.dataUrl)
            setConsultantImagePreviewName(item.name)
          }
          return next
        })
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [consultantImagePreviewOpen, consultantImagePreviewList])

  useEffect(() => {
    if (!consultantMarkupOpen || !consultantImagePreviewSrc) return
    const canvas = consultantMarkupCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const img = new Image()
    img.onload = () => {
      canvas.width = Math.max(1, img.width)
      canvas.height = Math.max(1, img.height)
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      const first = canvas.toDataURL('image/png')
      setConsultantMarkupHistory((prev) => (prev.length ? prev : [first]))
      setConsultantMarkupHistoryIndex((prev) => (prev >= 0 ? prev : 0))
    }
    img.src = consultantImagePreviewSrc
  }, [consultantMarkupOpen, consultantImagePreviewSrc])

  useEffect(() => {
    if (!consultantMarkupOpen) return
    if (consultantMarkupHistoryIndex < 0) return
    const src = consultantMarkupHistory[consultantMarkupHistoryIndex]
    if (!src) return
    const canvas = consultantMarkupCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const img = new Image()
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    }
    img.src = src
  }, [consultantMarkupOpen, consultantMarkupHistory, consultantMarkupHistoryIndex])

  const drawOnMarkupCanvas = (clientX: number, clientY: number, begin = false) => {
    const canvas = consultantMarkupCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const rect = canvas.getBoundingClientRect()
    const x = ((clientX - rect.left) / rect.width) * canvas.width
    const y = ((clientY - rect.top) / rect.height) * canvas.height

    const start = consultantMarkupStartRef.current
    const color = consultantMarkupTool === 'highlight' ? 'rgba(255, 224, 102, 0.55)' : consultantMarkupColor
    const hex = consultantMarkupFillColor.replace('#', '')
    const rgb = hex.length === 3
      ? [hex[0] + hex[0], hex[1] + hex[1], hex[2] + hex[2]].map((h) => parseInt(h, 16))
      : [hex.slice(0, 2), hex.slice(2, 4), hex.slice(4, 6)].map((h) => parseInt(h || '00', 16))
    const fill = `rgba(${rgb[0] || 0}, ${rgb[1] || 0}, ${rgb[2] || 0}, ${Math.max(0, Math.min(1, consultantMarkupFillOpacity))})`
    ctx.strokeStyle = color
    ctx.fillStyle = fill
    ctx.lineWidth = consultantMarkupTool === 'highlight' ? Math.max(8, consultantMarkupLineWidth * 3) : consultantMarkupLineWidth
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    if (begin) {
      consultantMarkupStartRef.current = { x, y }
      consultantMarkupSnapshotRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height)
      if (consultantMarkupTool === 'pen' || consultantMarkupTool === 'highlight') {
        ctx.beginPath()
        ctx.moveTo(x, y)
      }
      return
    }

    if (!start) return

    if (consultantMarkupTool === 'pen' || consultantMarkupTool === 'highlight') {
      ctx.lineTo(x, y)
      ctx.stroke()
      return
    }

    const snap = consultantMarkupSnapshotRef.current
    if (snap) ctx.putImageData(snap, 0, 0)

    if (consultantMarkupTool === 'line' || consultantMarkupTool === 'arrow') {
      ctx.beginPath()
      ctx.moveTo(start.x, start.y)
      ctx.lineTo(x, y)
      ctx.stroke()
      if (consultantMarkupTool === 'arrow') {
        const angle = Math.atan2(y - start.y, x - start.x)
        const size = 10 + consultantMarkupLineWidth
        ctx.beginPath()
        ctx.moveTo(x, y)
        ctx.lineTo(x - size * Math.cos(angle - Math.PI / 6), y - size * Math.sin(angle - Math.PI / 6))
        ctx.lineTo(x - size * Math.cos(angle + Math.PI / 6), y - size * Math.sin(angle + Math.PI / 6))
        ctx.closePath()
        ctx.fillStyle = color
        ctx.fill()
      }
      return
    }

    if (consultantMarkupTool === 'rect') {
      const w = x - start.x
      const h = y - start.y
      ctx.fillRect(start.x, start.y, w, h)
      ctx.strokeRect(start.x, start.y, w, h)
      return
    }

    if (consultantMarkupTool === 'circle') {
      const cx = (start.x + x) / 2
      const cy = (start.y + y) / 2
      const rx = Math.abs(x - start.x) / 2
      const ry = Math.abs(y - start.y) / 2
      ctx.beginPath()
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()
    }
  }

  const commitMarkupStroke = () => {
    const canvas = consultantMarkupCanvasRef.current
    if (!canvas) return
    const next = canvas.toDataURL('image/png')
    setConsultantMarkupHistory((prev) => {
      const base = consultantMarkupHistoryIndex >= 0 ? prev.slice(0, consultantMarkupHistoryIndex + 1) : prev
      return [...base, next]
    })
    setConsultantMarkupHistoryIndex((prev) => Math.max(0, prev) + 1)
    consultantMarkupStartRef.current = null
    consultantMarkupSnapshotRef.current = null
  }

  const undoConsultantMarkup = () => {
    setConsultantMarkupHistoryIndex((prev) => Math.max(0, prev - 1))
  }

  const redoConsultantMarkup = () => {
    setConsultantMarkupHistoryIndex((prev) => Math.min(consultantMarkupHistory.length - 1, prev + 1))
  }

  const nudgeConsultantMarkup = (dx: number, dy: number) => {
    const canvas = consultantMarkupCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const snap = ctx.getImageData(0, 0, canvas.width, canvas.height)
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.putImageData(snap, dx, dy)
    commitMarkupStroke()
  }

  const addConsultantMarkupText = (clientX: number, clientY: number) => {
    const canvas = consultantMarkupCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const text = window.prompt('Enter markup text')
    if (!text || !text.trim()) return
    const rect = canvas.getBoundingClientRect()
    const x = ((clientX - rect.left) / rect.width) * canvas.width
    const y = ((clientY - rect.top) / rect.height) * canvas.height
    ctx.fillStyle = consultantMarkupColor
    ctx.font = `${Math.max(12, consultantMarkupLineWidth * 5)}px Inter, system-ui, sans-serif`
    ctx.textBaseline = 'top'
    ctx.fillText(text.trim(), x, y)
    commitMarkupStroke()
  }

  useEffect(() => {
    if (!consultantImagePreviewOpen || isConsultantAllView || !consultantImagePreviewContext) return
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return

      const k = e.key.toLowerCase()
      const accel = e.ctrlKey || e.metaKey

      if (accel && k === 'z' && !e.shiftKey) {
        e.preventDefault()
        undoConsultantMarkup()
        return
      }
      if ((accel && k === 'y') || (accel && e.shiftKey && k === 'z')) {
        e.preventDefault()
        redoConsultantMarkup()
        return
      }

      if (!accel && !e.altKey) {
        if (k === 'p') { e.preventDefault(); setConsultantMarkupTool('pen'); return }
        if (k === 'a') { e.preventDefault(); setConsultantMarkupTool('arrow'); return }
        if (k === 'e') { e.preventDefault(); setConsultantMarkupTool('circle'); return }
        if (k === 'r') { e.preventDefault(); setConsultantMarkupTool('rect'); return }
        if (k === 'l') { e.preventDefault(); setConsultantMarkupTool('line'); return }
        if (k === 't') { e.preventDefault(); setConsultantMarkupTool('text'); return }
      }

      if (consultantMarkupTool === 'select') {
        const step = e.shiftKey ? 10 : 1
        if (e.key === 'ArrowUp') { e.preventDefault(); nudgeConsultantMarkup(0, -step); return }
        if (e.key === 'ArrowDown') { e.preventDefault(); nudgeConsultantMarkup(0, step); return }
        if (e.key === 'ArrowLeft') { e.preventDefault(); nudgeConsultantMarkup(-step, 0); return }
        if (e.key === 'ArrowRight') { e.preventDefault(); nudgeConsultantMarkup(step, 0); return }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [consultantImagePreviewOpen, isConsultantAllView, consultantImagePreviewContext, consultantMarkupTool, consultantMarkupHistory.length])

  const consultantTaskDeadlines = useMemo<CalendarDeadline[]>(() => {
    const out: CalendarDeadline[] = []
    for (const [team, sections] of Object.entries(consultantItemsByTeam)) {
      const assignTo = team === 'All Teams' ? 'Team' : team
      ;(['fromConsultant', 'nextDeliverable'] as const).forEach((section) => {
        sections[section].forEach((item) => {
          const due = getNormalizedConsultantDueDate(item)
          if (!isIsoDate(due)) return
          out.push({
            title: item.title,
            description: section === 'fromConsultant' ? 'Items to Receive from Consultant' : 'Next Deliverable',
            assignTo,
            notes: 'Consultant task',
            dateKey: due,
            source: 'Consultant Task',
            status: 'not-selected',
          })
        })
      })
    }
    return out
  }, [consultantItemsByTeam])

  const teamTaskDeadlines = useMemo<CalendarDeadline[]>(() => {
    const toCalendarStatus = (taskStatus: string): 'not-selected' | 'overdue' | 'on-track' | 'completed' => {
      const normalized = (taskStatus || '').trim().toLowerCase()
      if (normalized === 'complete' || normalized === 'completed' || normalized === 'done') return 'completed'
      if (normalized.includes('overdue')) return 'overdue'
      if (normalized === 'not started') return 'not-selected'
      return 'on-track'
    }

    return staffingRowsData
      .filter((row) => isIsoDate(row.due) && row.status !== 'Complete')
      .map((row) => ({
        title: row.task,
        description: row.notes,
        assignTo: row.assignee,
        notes: `Priority: ${row.priority}`,
        dateKey: row.due.trim(),
        source: 'Team Task',
        status: toCalendarStatus(row.status),
      }))
  }, [staffingRowsData])

  const myTaskDeadlines = useMemo<CalendarDeadline[]>(() => {
    const me = currentUsername.trim().toLowerCase()
    if (!me) return []
    return teamTaskDeadlines
      .filter((d) => (d.assignTo || '').trim().toLowerCase() === me)
      .map((d) => ({ ...d, source: 'My Task' as const }))
  }, [teamTaskDeadlines, currentUsername])

  const filteredCalendarDeadlines = useMemo<CalendarDeadline[]>(() => {
    const buckets: CalendarDeadline[] = []
    if (calendarFeedFilters.deliverables) buckets.push(...calendarDeadlines)
    if (calendarFeedFilters.teamTasks) buckets.push(...teamTaskDeadlines)
    if (calendarFeedFilters.myTasks) buckets.push(...myTaskDeadlines)
    if (calendarFeedFilters.consultantTasks) buckets.push(...consultantTaskDeadlines)

    const seen = new Set<string>()
    return buckets.filter((d) => {
      const key = `${d.source}|${d.dateKey}|${d.title}|${d.assignTo || ''}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [calendarDeadlines, calendarFeedFilters, teamTaskDeadlines, myTaskDeadlines, consultantTaskDeadlines])

  useEffect(() => {
    if (!appStateHydrated) return
    setConsultantLastUpdatedAt(now())
  }, [consultantItemsByTeam, appStateHydrated])

  const addConsultantTrackerList = () => {
    const presetCompany = lastConsultantTrackerCompany || ''
    setNewConsultantTrackerCompany(presetCompany)
    setNewConsultantTrackerDiscipline(getDefaultDisciplineForCompany(presetCompany))
    setNewConsultantTrackerAbbreviation(getDefaultAbbreviationForCompany(presetCompany))
    setShowAddConsultantTrackerModal(true)
  }

  const saveConsultantTrackerList = () => {
    const company = newConsultantTrackerCompany.trim()
    const discipline = newConsultantTrackerDiscipline.trim()
    const nextName = [company, discipline].filter(Boolean).join(' — ')

    if (!nextName) return
    if (nextName.toLowerCase() === ALL_CONSULTANT_TAB.toLowerCase() || nextName.toLowerCase() === 'all teams') return

    const exists = Object.keys(consultantItemsByTeam || {}).some((k) => k.trim().toLowerCase() === nextName.toLowerCase())
    if (exists) {
      showCenterToast('Consultant tracker already exists')
      return
    }

    setConsultantItemsByTeam((prev) => {
      if (prev[nextName]) return prev
      return { ...prev, [nextName]: makeEmptyConsultantSections() }
    })
    setDeletedConsultantTrackers((prev) => prev.filter((x) => x.trim().toLowerCase() !== nextName.trim().toLowerCase()))
    setConsultantView(nextName)
    setLastConsultantTrackerCompany(company)
    setNewConsultantTrackerAbbreviation('')
    setShowAddConsultantTrackerModal(false)
  }

  const deleteCurrentConsultantTrackerList = () => {
    if (isConsultantAllView || !consultantView.trim()) return
    setShowDeleteConsultantTrackerConfirm(true)
  }

  const confirmDeleteCurrentConsultantTrackerList = () => {
    if (isConsultantAllView || !consultantView.trim()) return
    const deletedView = consultantView
    const deletedPayload = consultantItemsByTeam[deletedView]

    setConsultantItemsByTeam((prev) => {
      if (!prev[deletedView]) return prev
      const next = { ...prev }
      delete next[deletedView]
      return next
    })
    setDeletedConsultantTrackers((prev) => Array.from(new Set([...prev, deletedView])))
    setConsultantView(ALL_CONSULTANT_TAB)
    setShowDeleteConsultantTrackerConfirm(false)

    if (deletedPayload) {
      showActionToast('Consultant list deleted', () => {
        setConsultantItemsByTeam((prev) => ({ ...prev, [deletedView]: deletedPayload }))
        setDeletedConsultantTrackers((prev) => prev.filter((x) => x.trim().toLowerCase() !== deletedView.trim().toLowerCase()))
        setConsultantView(deletedView)
      })
    }
  }

  const addConsultantItem = (key: ConsultantSectionKey) => {
    const text = consultantDrafts[key].text.trim()
    const assignee = (consultantDrafts[key].assignee || '-').trim() || '-'
    const dueDate = consultantDrafts[key].dueDate.trim()
    if (!text) return
    const normalizedDue = dueDate || '-'

    setConsultantItemsByTeam((prev) => {
      const teamItems = prev[consultantView] || { toConsultant: [], fromConsultant: [], nextDeliverable: [] }
      return {
        ...prev,
        [consultantView]: {
          ...teamItems,
          [key]: [...teamItems[key], { id: makeId(), title: text, assignee, dueDate: normalizedDue }],
        },
      }
    })

    if (key === 'toConsultant') {
      const today = new Date().toISOString().slice(0, 10)
      setStaffingRowsData((prev) => [
        {
          id: makeId(),
          status: 'Not Started',
          priority: 'Medium',
          start: today,
          due: normalizedDue,
          task: text,
          assignee: 'Team',
          notes: `Send to ${consultantView}`,
        },
        ...prev,
      ])
    }

    setConsultantDrafts((prev) => ({ ...prev, [key]: { text: '', assignee: '-', dueDate: '' } }))
  }

  const deleteConsultantItemDirect = (key: ConsultantSectionKey, id: string) => {
    const deletedIndex = currentConsultantItems[key].findIndex((x) => x.id === id)
    const deleted = deletedIndex >= 0 ? currentConsultantItems[key][deletedIndex] : undefined
    if (!deleted) return

    setPendingDeleteConfirm({
      title: 'Delete Item?',
      body: deleted.title,
      onConfirm: () => {
        setConsultantItemsByTeam((prev) => {
          const teamItems = prev[consultantView] || { toConsultant: [], fromConsultant: [], nextDeliverable: [] }
          return {
            ...prev,
            [consultantView]: {
              ...teamItems,
              [key]: teamItems[key].filter((x) => x.id !== id),
            },
          }
        })

        showActionToast('Consultant item deleted', () => {
          setConsultantItemsByTeam((prev) => {
            const teamItems = prev[consultantView] || { toConsultant: [], fromConsultant: [], nextDeliverable: [] }
            const restored = [...teamItems[key]]
            const insertAt = Math.max(0, Math.min(deletedIndex, restored.length))
            restored.splice(insertAt, 0, deleted)

            return {
              ...prev,
              [consultantView]: {
                ...teamItems,
                [key]: restored,
              },
            }
          })
        })
        setPendingDeleteConfirm(null)
      },
    })
  }

  const editConsultantItem = (key: ConsultantSectionKey, id: string) => {
    const item = currentConsultantItems[key].find((x) => x.id === id)
    if (!item) return
    setEditConsultantSection(key)
    setEditConsultantId(id)
    setEditConsultantText(item.title)
    setEditConsultantDueDate(item.dueDate === '-' ? '' : item.dueDate)
    setShowConsultantEdit(true)
  }

  const updateConsultantItemInTeam = (
    team: string,
    key: ConsultantSectionKey,
    id: string,
    patch: Partial<ConsultantItem>,
  ) => {
    setConsultantItemsByTeam((prev) => {
      const teamItems = prev[team] || { toConsultant: [], fromConsultant: [], nextDeliverable: [] }
      return {
        ...prev,
        [team]: {
          ...teamItems,
          [key]: teamItems[key].map((x) => (x.id === id ? { ...x, ...patch } : x)),
        },
      }
    })
  }

  const saveConsultantEdit = () => {
    if (!editConsultantId) return
    const text = editConsultantText.trim()
    if (!text) return
    const dueDate = editConsultantDueDate.trim() || '-'
    setConsultantItemsByTeam((prev) => {
      const teamItems = prev[consultantView] || { toConsultant: [], fromConsultant: [], nextDeliverable: [] }
      return {
        ...prev,
        [consultantView]: {
          ...teamItems,
          [editConsultantSection]: teamItems[editConsultantSection].map((x) =>
            x.id === editConsultantId ? { ...x, title: text, dueDate } : x,
          ),
        },
      }
    })
    setShowConsultantEdit(false)
    setEditConsultantId(null)
  }

  const deleteConsultantEdit = () => {
    if (!editConsultantId) return

    const deletedIndex = currentConsultantItems[editConsultantSection].findIndex((x) => x.id === editConsultantId)
    const deleted = deletedIndex >= 0 ? currentConsultantItems[editConsultantSection][deletedIndex] : undefined
    if (!deleted) return

    setPendingDeleteConfirm({
      title: 'Delete Item?',
      body: deleted.title,
      onConfirm: () => {
        setConsultantItemsByTeam((prev) => {
          const teamItems = prev[consultantView] || { toConsultant: [], fromConsultant: [], nextDeliverable: [] }
          return {
            ...prev,
            [consultantView]: {
              ...teamItems,
              [editConsultantSection]: teamItems[editConsultantSection].filter((x) => x.id !== editConsultantId),
            },
          }
        })

        showActionToast('Consultant item deleted', () => {
          setConsultantItemsByTeam((prev) => {
            const teamItems = prev[consultantView] || { toConsultant: [], fromConsultant: [], nextDeliverable: [] }
            const restored = [...teamItems[editConsultantSection]]
            const insertAt = Math.max(0, Math.min(deletedIndex, restored.length))
            restored.splice(insertAt, 0, deleted)

            return {
              ...prev,
              [consultantView]: {
                ...teamItems,
                [editConsultantSection]: restored,
              },
            }
          })
        })

        setShowConsultantEdit(false)
        setEditConsultantId(null)
        setPendingDeleteConfirm(null)
      },
    })
  }

  const submitOnEnter = (key: ConsultantSectionKey, e: { key: string; preventDefault: () => void }) => {
    if (e.key !== 'Enter') return
    e.preventDefault()
    addConsultantItem(key)
  }

  const openConsultantAttachmentPicker = (key: ConsultantSectionKey, itemId: string, replaceId?: string) => {
    if (isConsultantAllView) return
    setConsultantAttachmentBulkSection(null)
    setConsultantAttachmentTarget({ key, itemId })
    setConsultantAttachmentReplaceId(replaceId || null)
    consultantAttachmentInputRef.current?.click()
  }

  const openConsultantBulkAttachmentPicker = (section: 'toConsultant' | 'fromConsultant') => {
    if (isConsultantAllView) return
    setConsultantAttachmentTarget(null)
    setConsultantAttachmentReplaceId(null)
    setConsultantAttachmentBulkSection(section)
    consultantAttachmentInputRef.current?.click()
  }

  const fileToDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
    const fr = new FileReader()
    fr.onload = () => resolve(String(fr.result || ''))
    fr.onerror = () => reject(new Error('read-failed'))
    fr.readAsDataURL(file)
  })

  const optimizeAttachmentFile = async (file: File) => {
    const maxBytes = 3 * 1024 * 1024
    if (file.size <= maxBytes) return { dataUrl: await fileToDataUrl(file), name: file.name }
    const raw = await fileToDataUrl(file)
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image()
      i.onload = () => resolve(i)
      i.onerror = () => reject(new Error('image-load-failed'))
      i.src = raw
    })
    const maxDim = 1600
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(img.width * scale))
    canvas.height = Math.max(1, Math.round(img.height * scale))
    const ctx = canvas.getContext('2d')
    if (!ctx) return { dataUrl: raw, name: file.name }
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    return { dataUrl: canvas.toDataURL('image/jpeg', 0.82), name: file.name.replace(/\.[a-zA-Z0-9]+$/, '.jpg') }
  }

  const attachConsultantImageFileInTeam = async (team: string, key: ConsultantSectionKey, itemId: string, file: File, replaceId?: string) => {
    if (!file.type.startsWith('image/')) {
      showCopyToast('Please choose an image file')
      return
    }

    try {
      const optimized = await optimizeAttachmentFile(file)
      let matched = false
      setConsultantItemsByTeam((prev) => {
        const teamItems = prev[team] || { toConsultant: [], fromConsultant: [], nextDeliverable: [] }
        return {
          ...prev,
          [team]: {
            ...teamItems,
            [key]: teamItems[key].map((x) => {
              if (x.id !== itemId) return x
              matched = true
              const existing = Array.isArray(x.attachments) ? x.attachments : []
              const incoming = { id: makeId(), name: optimized.name, dataUrl: optimized.dataUrl, createdAt: now(), addedBy: 'You' }
              const nextAttachments = replaceId
                ? existing.map((a) => (a.id === replaceId ? incoming : a))
                : [...existing, incoming]
              return { ...x, attachments: nextAttachments }
            }),
          },
        }
      })
      showCopyToast(matched ? (replaceId ? 'Reference image replaced' : 'Reference image attached') : 'Could not match target row')
    } catch {
      showCopyToast('Image attach failed')
    }
  }

  const attachConsultantImageFile = async (key: ConsultantSectionKey, itemId: string, file: File, replaceId?: string) => {
    await attachConsultantImageFileInTeam(consultantView, key, itemId, file, replaceId)
  }

  const attachConsultantImageFiles = async (key: ConsultantSectionKey, itemId: string, files: File[]) => {
    for (const file of files) await attachConsultantImageFile(key, itemId, file)
  }

  const onConsultantAttachmentFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = consultantAttachmentTarget
    const files = Array.from(event.target.files || [])
    const bulkSection = consultantAttachmentBulkSection
    event.target.value = ''
    if (!files.length) return
    if (bulkSection) {
      const selectedKeys = Array.from(consultantSelectedDetailKeys).filter((k) => k.includes(`::${bulkSection}::`))
      const ids = selectedKeys.map((k) => k.split('::').slice(-1)[0]).filter(Boolean)
      for (const id of ids) await attachConsultantImageFiles(bulkSection, id, files)
      showCopyToast(ids.length ? `Attached to ${ids.length} selected row${ids.length === 1 ? '' : 's'}` : 'No selected rows')
      setConsultantAttachmentBulkSection(null)
      return
    }
    if (!target) return
    if (consultantAttachmentReplaceId && files[0]) {
      await attachConsultantImageFile(target.key, target.itemId, files[0], consultantAttachmentReplaceId || undefined)
    } else {
      await attachConsultantImageFiles(target.key, target.itemId, files)
    }
    setConsultantAttachmentReplaceId(null)
  }

  const parseConsultantNavKey = (navKey: string): { team: string; key: ConsultantSectionKey; itemId: string } | null => {
    const parts = String(navKey || '').split('::')
    if (parts.length < 3) return null
    const team = parts[0]
    const key = parts[1]
    const itemId = parts.slice(2).join('::')
    if (!team || !itemId) return null
    if (key !== 'toConsultant' && key !== 'fromConsultant' && key !== 'nextDeliverable') return null
    return { team, key, itemId }
  }

  const onConsultantDrop = async (e: React.DragEvent, navKey: string) => {
    e.preventDefault()
    e.stopPropagation()
    setConsultantRowDragOverKey(null)
    const target = parseConsultantNavKey(navKey)
    if (!target) {
      showCopyToast('Could not resolve drop target')
      return
    }
    const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith('image/'))
    if (!files.length) return
    for (const file of files) await attachConsultantImageFileInTeam(target.team, target.key, target.itemId, file)
  }

  const copyAttachmentsFromPreviousItem = (key: ConsultantSectionKey, itemId: string) => {
    if (isConsultantAllView) return
    const list = currentConsultantItems[key]
    const idx = list.findIndex((x) => x.id === itemId)
    if (idx <= 0) {
      showCopyToast('No previous item to copy from')
      return
    }
    const prevWithAttachments = [...list.slice(0, idx)].reverse().find((x) => Array.isArray(x.attachments) && x.attachments.length)
    if (!prevWithAttachments?.attachments?.length) {
      showCopyToast('No previous attachments found')
      return
    }
    const clone = prevWithAttachments.attachments.map((a) => ({ ...a, id: makeId(), createdAt: now(), addedBy: 'You' }))
    setConsultantItemsByTeam((prev) => {
      const teamItems = prev[consultantView] || { toConsultant: [], fromConsultant: [], nextDeliverable: [] }
      return {
        ...prev,
        [consultantView]: {
          ...teamItems,
          [key]: teamItems[key].map((x) => (x.id === itemId ? { ...x, attachments: clone } : x)),
        },
      }
    })
    showCopyToast('Copied attachments from previous item')
  }

  const onConsultantRowPaste = async (e: React.ClipboardEvent, key: ConsultantSectionKey, itemId: string) => {
    const items = Array.from(e.clipboardData?.items || [])
    const imageItems = items.filter((it) => it.type.startsWith('image/'))
    if (!imageItems.length) return
    e.preventDefault()
    const files = imageItems.map((it) => it.getAsFile()).filter((f): f is File => !!f)
    await attachConsultantImageFiles(key, itemId, files)
  }

  const removeConsultantAttachment = (key: ConsultantSectionKey, itemId: string, attachmentId: string) => {
    if (isConsultantAllView) return
    const sourceItem = (consultantItemsByTeam[consultantView]?.[key] || []).find((x) => x.id === itemId)
    const removed = (sourceItem?.attachments || []).find((a) => a.id === attachmentId)
    setConsultantItemsByTeam((prev) => {
      const teamItems = prev[consultantView] || { toConsultant: [], fromConsultant: [], nextDeliverable: [] }
      return {
        ...prev,
        [consultantView]: {
          ...teamItems,
          [key]: teamItems[key].map((x) => {
            if (x.id !== itemId) return x
            const existing = Array.isArray(x.attachments) ? x.attachments : []
            return { ...x, attachments: existing.filter((a) => a.id !== attachmentId) }
          }),
        },
      }
    })
    if (removed) {
      showActionToast('Reference image removed', () => {
        setConsultantItemsByTeam((prev) => {
          const teamItems = prev[consultantView] || { toConsultant: [], fromConsultant: [], nextDeliverable: [] }
          return {
            ...prev,
            [consultantView]: {
              ...teamItems,
              [key]: teamItems[key].map((x) => {
                if (x.id !== itemId) return x
                const existing = Array.isArray(x.attachments) ? x.attachments : []
                return { ...x, attachments: [...existing, removed] }
              }),
            },
          }
        })

        if (consultantImagePreviewOpen && consultantImagePreviewContext && consultantImagePreviewContext.key === key && consultantImagePreviewContext.itemId === itemId) {
          setConsultantImagePreviewList((prev) => {
            if (prev.some((x) => x.id === removed.id)) return prev
            const next = [...prev, removed]
            return next
          })
          if (!consultantImagePreviewSrc) {
            setConsultantImagePreviewSrc(removed.dataUrl)
            setConsultantImagePreviewName(removed.name)
            setConsultantImagePreviewIndex(0)
          }
        }
      })
    }
  }

  const openConsultantImagePreview = (
    dataUrl: string,
    name: string,
    list?: ConsultantAttachment[],
    currentId?: string,
    context?: { key: ConsultantSectionKey; itemId: string; attachmentId: string },
  ) => {
    const previewList = Array.isArray(list) ? list : [{ id: makeId(), name, dataUrl, createdAt: now() }]
    const idx = currentId ? Math.max(0, previewList.findIndex((a) => a.id === currentId)) : 0
    const active = previewList[idx] || previewList[0]
    setConsultantMarkupOpen(true)
    setConsultantMarkupHistory([])
    setConsultantMarkupHistoryIndex(-1)
    setConsultantImagePreviewList(previewList)
    setConsultantImagePreviewIndex(idx)
    setConsultantImagePreviewSrc(active?.dataUrl || dataUrl)
    setConsultantImagePreviewName(active?.name || name)
    if (context) setConsultantImagePreviewContext(context)
    else setConsultantImagePreviewContext(null)
    setConsultantImagePreviewOpen(true)
  }

  const applyMarkupToActiveAttachment = (dataUrl: string) => {
    const ctx = consultantImagePreviewContext
    const active = consultantImagePreviewList[consultantImagePreviewIndex]
    if (!ctx || !active) return
    setConsultantItemsByTeam((prev) => {
      const teamItems = prev[consultantView] || { toConsultant: [], fromConsultant: [], nextDeliverable: [] }
      return {
        ...prev,
        [consultantView]: {
          ...teamItems,
          [ctx.key]: teamItems[ctx.key].map((x) => {
            if (x.id !== ctx.itemId) return x
            const existing = Array.isArray(x.attachments) ? x.attachments : []
            return {
              ...x,
              attachments: existing.map((a) => (a.id === active.id ? { ...a, dataUrl, createdAt: now(), addedBy: 'You' } : a)),
            }
          }),
        },
      }
    })
    setConsultantImagePreviewList((prev) => prev.map((a) => (a.id === active.id ? { ...a, dataUrl, createdAt: now(), addedBy: 'You' } : a)))
    setConsultantImagePreviewSrc(dataUrl)
  }

  const openMeetingAttendeeEdit = (id: string) => {
    const attendee = meetingAttendees.find((x) => x.id === id)
    if (!attendee) return
    setEditMeetingAttendeeId(id)
    setEditMeetingAttendeeName(attendee.name)
    setEditMeetingAttendeeCompany(attendee.company)
    setSelectedDirectoryContactIndex('')
    setShowMeetingAttendeeEdit(true)
  }

  const openMeetingAttendeeAdd = () => {
    setEditMeetingAttendeeId(null)
    setEditMeetingAttendeeName('')
    setEditMeetingAttendeeCompany('')
    setSelectedDirectoryContactIndex('')
    setShowMeetingAttendeeEdit(true)
  }

  const saveMeetingAttendeeEdit = () => {
    const name = editMeetingAttendeeName.trim()
    const company = editMeetingAttendeeCompany.trim()
    if (!name || !company) return

    const isDuplicate = meetingAttendees.some((x) => {
      if (editMeetingAttendeeId && x.id === editMeetingAttendeeId) return false
      return x.name.trim().toLowerCase() === name.toLowerCase()
    })
    if (isDuplicate) {
      showCenterToast('Already included')
      return
    }

    if (!editMeetingAttendeeId) {
      setMeetingAttendees((prev) => [...prev, { id: makeId(), name, company, present: true }])
      setShowMeetingAttendeeEdit(false)
      showCenterToast('Added')
      return
    }

    setMeetingAttendees((prev) => prev.map((x) => (x.id === editMeetingAttendeeId ? { ...x, name, company } : x)))
    setShowMeetingAttendeeEdit(false)
    setEditMeetingAttendeeId(null)
  }

  const deleteMeetingAttendee = () => {
    if (!editMeetingAttendeeId) return
    const deleted = meetingAttendees.find((x) => x.id === editMeetingAttendeeId)
    setMeetingAttendees((prev) => prev.filter((x) => x.id !== editMeetingAttendeeId))
    if (deleted) {
      showActionToast('Attendee deleted', () => setMeetingAttendees((prev) => [...prev, deleted]))
    }
    setShowMeetingAttendeeEdit(false)
    setEditMeetingAttendeeId(null)
  }

  const openStaffingEdit = (row: { id: string; status: string; priority: string; start: string; due: string; task: string; assignee: string; notes: string }) => {
    const normalizeDate = (value: string) => (/^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '')
    setEditStaffingId(row.id)
    setEditStaffingStatus(row.status)
    setEditStaffingPriority(row.priority)
    setEditStaffingStart(normalizeDate(row.start))
    setEditStaffingDue(normalizeDate(row.due))
    setEditStaffingTask(row.task)
    setEditStaffingAssignee(row.assignee)
    setEditStaffingNotes(row.notes)
    setShowStaffingEdit(true)
  }

  const saveStaffingEdit = () => {
    if (!editStaffingId) return
    if (!editStaffingTask.trim() || !editStaffingAssignee.trim() || !editStaffingDue.trim()) {
      showActionToast('Task, assignee, and due date are required')
      return
    }
    const stamp = now()
    setStaffingRowsData((prev) =>
      prev.map((r) =>
        r.id === editStaffingId
          ? {
              ...r,
              status: editStaffingStatus,
              priority: editStaffingPriority,
              start: editStaffingStart,
              due: editStaffingDue,
              task: editStaffingTask,
              assignee: editStaffingAssignee,
              notes: editStaffingNotes,
              updatedAt: stamp,
            }
          : r,
      ),
    )
    setStaffingLastUpdatedAt(stamp)
    showCenterToast('Saved')
    setShowStaffingEdit(false)
    setEditStaffingId(null)
  }

  const deleteStaffingRow = (id: string) => {
    const row = staffingRowsData.find((r) => r.id === id)
    if (!row) return
    const stamp = now()
    setStaffingRowsData((prev) => prev.filter((r) => r.id !== id))
    setStaffingLastUpdatedAt(stamp)
    showActionToast('Staffing row deleted', () => {
      const undoStamp = now()
      setStaffingRowsData((prev) => [{ ...row, updatedAt: undoStamp }, ...prev])
      setStaffingLastUpdatedAt(undoStamp)
    })
  }

  const openDatePicker = (ref: { current: HTMLInputElement | null }) => {
    const el = ref.current
    if (!el) return
    try {
      // Chromium + modern Safari support this
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(el as any).showPicker?.()
    } catch {
      // fallback: focus still opens picker on many mobile browsers
      el.focus()
    }
  }

  const submitNewStaffingRow = () => {
    const missing: string[] = []
    if (!newStaffingStatus.trim()) missing.push('Status')
    if (!newStaffingPriority.trim()) missing.push('Priority')
    if (!newStaffingTask.trim()) missing.push('Task')
    if (!newStaffingAssignee.trim()) missing.push('Assignee')
    if (missing.length) {
      showActionToast(`${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} required`)
      return
    }
    const stamp = now()
    setStaffingRowsData((prev) => [
      {
        id: makeId(),
        status: newStaffingStatus || 'Not Started',
        priority: newStaffingPriority || 'Medium',
        start: newStaffingStart || '',
        due: newStaffingDue || '',
        task: newStaffingTask.trim(),
        assignee: newStaffingAssignee || 'Team',
        notes: newStaffingNotes.trim() || '-',
        updatedAt: stamp,
      },
      ...prev,
    ])
    setStaffingLastUpdatedAt(stamp)
    setNewStaffingStatus('')
    setNewStaffingPriority('')
    setNewStaffingStart('')
    setNewStaffingDue('')
    setNewStaffingTask('')
    setNewStaffingAssignee('')
    setNewStaffingNotes('')
    showCenterToast('Added')
  }


  const normalizeDateKey = (d: Date) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  const parseLegacyDeadline = (d: CalendarDeadline) => {
    const assignMatch = d.title.match(/\(Assign to:\s*([^)]*)\)$/i)
    const assignFromTitle = (assignMatch?.[1] || '').trim()
    const baseTitle = d.title.replace(/\s*\(Assign to:[^)]*\)\s*$/i, '').trim()
    const [mainTitle, ...descParts] = baseTitle.split(' — ')
    return {
      title: d.title,
      mainTitle: (mainTitle || d.title || '').trim(),
      description: (d.description || descParts.join(' — ') || '').trim(),
      assignTo: (d.assignTo || assignFromTitle || '').trim(),
    }
  }

  const extractDeadlinesFromText = (text: string, source: string): CalendarDeadline[] => {
    const lines = text.split(/\r?\n/).slice(0, 300)
    const found: CalendarDeadline[] = []

    const addDate = (date: Date, title: string) => {
      if (Number.isNaN(date.getTime())) return
      found.push({ title: title.trim() || 'Deliverable', dateKey: normalizeDateKey(date), source })
    }

    const monthMap: Record<string, number> = {
      jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2, apr: 3, april: 3, may: 4,
      jun: 5, june: 5, jul: 6, july: 6, aug: 7, august: 7, sep: 8, sept: 8, september: 8,
      oct: 9, october: 9, nov: 10, november: 10, dec: 11, december: 11,
    }

    for (const line of lines) {
      const iso = line.match(/\b(20\d{2})-(\d{1,2})-(\d{1,2})\b/)
      if (iso) addDate(new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])), line)

      const us = line.match(/\b(\d{1,2})\/(\d{1,2})\/(20\d{2})\b/)
      if (us) addDate(new Date(Number(us[3]), Number(us[1]) - 1, Number(us[2])), line)

      const md = line.match(/\b([A-Za-z]{3,9})\s+(\d{1,2}),\s*(20\d{2})\b/)
      if (md) {
        const key = md[1].toLowerCase()
        const monthIdx = monthMap[key]
        if (monthIdx !== undefined) addDate(new Date(Number(md[3]), monthIdx, Number(md[2])), line)
      }
    }

    return found
  }

  const shiftCalendarFocus = (direction: -1 | 1) => {
    setCalendarFocusDate((d) => {
      const next = new Date(d)
      if (calendarView === 'day') {
        next.setDate(next.getDate() + direction)
      } else if (calendarView === 'week') {
        next.setDate(next.getDate() + 7 * direction)
      } else if (calendarView === 'month') {
        next.setMonth(next.getMonth() + direction)
      } else if (calendarView === 'year') {
        next.setFullYear(next.getFullYear() + direction)
      }
      return next
    })
  }

  const shiftDashboardMiniMonth = (direction: -1 | 1) => {
    setCalendarFocusDate((d) => {
      const next = new Date(d)
      next.setMonth(next.getMonth() + direction)
      return next
    })
  }

  const openEditDeadline = (item: CalendarDeadline) => {
    setEditingDeadline(item)
    const parsed = parseLegacyDeadline(item)
    setNewDeadlineTitle(parsed.mainTitle)
    setNewDeadlineDescription(parsed.description)
    setNewDeadlineAssignTo(parsed.assignTo)
    setNewDeadlineNotes(item.notes || '')
    setNewDeadlineDate(item.dateKey)
    setNewDeadlineStatus(item.status || 'not-selected')
    setCalendarDateInputType('date')
  }

  const saveEditedDeadline = () => {
    if (!editingDeadline) return
    const title = newDeadlineTitle.trim()
    const dateKey = newDeadlineDate.trim()
    if (!title || !dateKey) {
      showActionToast('Calendar title and date are required')
      return
    }
    const description = newDeadlineDescription.trim()
    const assignTo = newDeadlineAssignTo.trim()
    const notes = newDeadlineNotes.trim()
    const stamp = now()
    setCalendarDeadlines((prev) => prev.map((d) => (d === editingDeadline ? { ...d, title, description, assignTo, notes, dateKey, status: newDeadlineStatus } : d)))
    setCalendarLastUpdatedAt(stamp)
    showCenterToast('Saved')
    setEditingDeadline(null)
    setNewDeadlineTitle('')
    setNewDeadlineDescription('')
    setNewDeadlineAssignTo('')
    setNewDeadlineNotes('')
    setNewDeadlineDate('')
    setNewDeadlineStatus('not-selected')
    setCalendarDateInputType('text')
  }

  const deleteEditedDeadline = () => {
    if (!editingDeadline) return
    const deleted = editingDeadline
    const stamp = now()
    setCalendarDeadlines((prev) => prev.filter((d) => d !== editingDeadline))
    setCalendarLastUpdatedAt(stamp)
    showActionToast('Calendar Item Deleted', () => {
      const undoStamp = now()
      setCalendarDeadlines((prev) => [deleted, ...prev])
      setCalendarLastUpdatedAt(undoStamp)
    })
    setEditingDeadline(null)
    setNewDeadlineTitle('')
    setNewDeadlineDescription('')
    setNewDeadlineAssignTo('')
    setNewDeadlineNotes('')
    setNewDeadlineDate('')
    setNewDeadlineStatus('not-selected')
    setCalendarDateInputType('text')
  }

  const deleteCalendarDeadlineDirect = (item: CalendarDeadline) => {
    const stamp = now()
    setCalendarDeadlines((prev) => prev.filter((d) => d !== item))
    setCalendarLastUpdatedAt(stamp)
    showActionToast('Calendar Item Deleted', () => {
      const undoStamp = now()
      setCalendarDeadlines((prev) => [item, ...prev])
      setCalendarLastUpdatedAt(undoStamp)
    })
  }

  const safeDateInputValue = (value?: string) => {
    if (!value) return ''
    return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : ''
  }

  const formatMonthDay = (value?: string) => {
    const iso = safeDateInputValue(value)
    if (!iso) return '—'
    const date = new Date(`${iso}T00:00:00`)
    return Number.isNaN(date.getTime())
      ? '—'
      : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const formatMonthDayTime = (value?: string) => {
    if (!value) return '—'
    const date = new Date(value)
    return Number.isNaN(date.getTime())
      ? '—'
      : `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
  }

  const clearCalendarYearLegendCloseTimer = () => {
    if (calendarYearLegendCloseTimerRef.current !== null) {
      window.clearTimeout(calendarYearLegendCloseTimerRef.current)
      calendarYearLegendCloseTimerRef.current = null
    }
  }

  const scheduleCalendarYearLegendAutoClose = () => {
    clearCalendarYearLegendCloseTimer()
    calendarYearLegendCloseTimerRef.current = window.setTimeout(() => {
      setShowCalendarYearLegend(false)
      calendarYearLegendCloseTimerRef.current = null
    }, 3000)
  }

  const animateYearMonthToMonthView = (
    monthDate: Date,
    _sourceEl?: HTMLElement | null,
    sourceDayPoints?: Array<{ dateKey: string; label: string; x: number; y: number }>,
  ) => {
    setCalendarMonthZoom(null)
    setCalendarFocusDate(monthDate)
    setCalendarView('month')
    setCalendarMonthEntering(true)
    setCalendarMonthPhase(0)
    window.setTimeout(() => setCalendarMonthPhase(1), 50)
    window.setTimeout(() => setCalendarMonthPhase(2), 220)
    window.setTimeout(() => setCalendarMonthPhase(3), 430)
    window.setTimeout(() => setCalendarMonthPhase(4), 640)
    window.setTimeout(() => setCalendarMonthEntering(false), 760)

    if (sourceDayPoints?.length) {
      setCalendarDayMorph({
        active: false,
        items: sourceDayPoints.map((p) => ({ dateKey: p.dateKey, label: p.label, startX: p.x, startY: p.y, endX: p.x, endY: p.y })),
      })

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          setCalendarDayMorph((prev) => {
            if (!prev || !prev.items.length) return null
            const items = prev.items.map((it) => {
              const cell = document.querySelector(`.calendar-grid-month .calendar-cell[data-date='${it.dateKey}'] .day-num`) as HTMLElement | null
              const rect = cell?.getBoundingClientRect()
              return rect
                ? { ...it, endX: rect.left + rect.width / 2, endY: rect.top + rect.height / 2 }
                : it
            })
            return { ...prev, items }
          })
          window.requestAnimationFrame(() => setCalendarDayMorph((prev) => (prev ? { ...prev, active: true } : prev)))
          window.setTimeout(() => setCalendarDayMorph(null), 520)
        })
      })
    } else {
      setCalendarDayMorph(null)
    }
  }

  const animateMonthDayToWeekView = (
    dayDate: Date,
    sourceDayPoints?: Array<{ dateKey: string; label: string; x: number; y: number }>,
  ) => {
    setCalendarFocusDate(dayDate)
    setCalendarView('week')
    setCalendarWeekEntering(true)
    setCalendarWeekPhase(0)
    window.setTimeout(() => setCalendarWeekPhase(1), 50)
    window.setTimeout(() => setCalendarWeekPhase(2), 220)
    window.setTimeout(() => setCalendarWeekPhase(3), 430)
    window.setTimeout(() => setCalendarWeekPhase(4), 640)
    window.setTimeout(() => setCalendarWeekEntering(false), 760)

    if (sourceDayPoints?.length) {
      setCalendarDayMorph({
        active: false,
        items: sourceDayPoints.map((p) => ({ dateKey: p.dateKey, label: p.label, startX: p.x, startY: p.y, endX: p.x, endY: p.y })),
      })
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          setCalendarDayMorph((prev) => {
            if (!prev || !prev.items.length) return null
            const items = prev.items.map((it) => {
              const cell = document.querySelector(`.calendar-grid-week .calendar-cell[data-date='${it.dateKey}'] .day-num`) as HTMLElement | null
              const rect = cell?.getBoundingClientRect()
              return rect
                ? { ...it, endX: rect.left + rect.width / 2, endY: rect.top + rect.height / 2 }
                : it
            })
            return { ...prev, items }
          })
          window.requestAnimationFrame(() => setCalendarDayMorph((prev) => (prev ? { ...prev, active: true } : prev)))
          window.setTimeout(() => setCalendarDayMorph(null), 520)
        })
      })
    }
  }

  const copyText = async (text: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
        return true
      }
    } catch {
      // fallback below
    }

    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      return ok
    } catch {
      return false
    }
  }

  const showCopyToast = (message: string) => {
    setCopyToast(message)
    window.setTimeout(() => setCopyToast(''), 2000)
  }

  const showActionToast = (message: string, undo?: () => void) => {
    setActionToast({ message, undo })
    window.setTimeout(() => setActionToast(null), 5000)
  }

  const showCenterToast = (message: string) => {
    setCenterToastMessage(message)
    window.setTimeout(() => setCenterToastMessage(''), 2000)
  }

  const cropImageDataUrl = (source: string, options: { x: number; y: number; w: number; aspect: number }) => new Promise<string>((resolve) => {
    const img = new Image()
    img.onload = () => {
      const outW = options.aspect === 1 ? 300 : 500
      const outH = Math.round(outW / options.aspect)
      const canvas = document.createElement('canvas')
      canvas.width = outW
      canvas.height = outH
      const ctx = canvas.getContext('2d')
      if (!ctx) return resolve(source)

      const cropW = Math.max(1, (options.w / 100) * img.naturalWidth)
      const cropH = cropW / options.aspect
      const x = Math.max(0, Math.min(img.naturalWidth - cropW, (options.x / 100) * img.naturalWidth))
      const y = Math.max(0, Math.min(img.naturalHeight - cropH, (options.y / 100) * img.naturalHeight))

      ctx.drawImage(img, x, y, cropW, cropH, 0, 0, outW, outH)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = () => resolve(source)
    img.src = source
  })

  const openImageCropModal = (target: 'companyLogo' | 'companyIcon', source: string) => {
    if (!source) return
    setShowProfileSettings(false)
    setShowSiteSettings(false)
    setImageCropTarget(target)
    setImageCropSource(source)
    setImageCropRect({ x: 10, y: 10, w: 80 })
    setShowImageCropModal(true)
  }

  const clampImageCropRect = (rect: { x: number; y: number; w: number }, aspect: number) => {
    const minW = 18
    let w = Math.max(minW, Math.min(rect.w, 100))
    let h = w / aspect
    let x = Math.max(0, Math.min(rect.x, 100 - w))
    const y = Math.max(0, Math.min(rect.y, 100 - h))
    if (y + h > 100) {
      h = 100 - y
      w = h * aspect
      if (x + w > 100) x = 100 - w
    }
    return { x, y, w }
  }

  const startImageCropDrag = (mode: 'move' | 'nw' | 'ne' | 'sw' | 'se', e: any) => {
    if (!imageCropTarget) return
    e.preventDefault()
    e.stopPropagation()
    imageCropDragRef.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      startRect: imageCropRect,
    }
  }

  useEffect(() => {
    if (!showImageCropModal) return
    const aspect = imageCropTarget === 'companyIcon' ? 1 : 2
    const onMove = (e: MouseEvent) => {
      if (!imageCropDragRef.current) return
      const d = imageCropDragRef.current
      const dx = (e.clientX - d.startX) / 4
      const dy = (e.clientY - d.startY) / 4
      let next = d.startRect
      if (d.mode === 'move') {
        next = { ...d.startRect, x: d.startRect.x + dx, y: d.startRect.y + dy }
      } else {
        const delta = Math.abs(dx) > Math.abs(dy) ? dx : dy
        const signed = d.mode === 'nw' || d.mode === 'sw' ? -delta : delta
        const w = d.startRect.w + signed
        const h = w / aspect
        if (d.mode === 'nw') next = { x: d.startRect.x + (d.startRect.w - w), y: d.startRect.y + (d.startRect.w - w) / aspect, w }
        if (d.mode === 'ne') next = { x: d.startRect.x, y: d.startRect.y + (d.startRect.w - w) / aspect, w }
        if (d.mode === 'sw') next = { x: d.startRect.x + (d.startRect.w - w), y: d.startRect.y, w }
        if (d.mode === 'se') next = { x: d.startRect.x, y: d.startRect.y, w }
        void h
      }
      setImageCropRect(clampImageCropRect(next, aspect))
    }
    const onUp = () => { imageCropDragRef.current = null }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [showImageCropModal, imageCropTarget, imageCropRect])

  const applyImageCrop = async () => {
    if (!imageCropTarget || !imageCropSource) return
    const aspect = imageCropTarget === 'companyIcon' ? 1 : 2
    const cropped = await cropImageDataUrl(imageCropSource, { x: imageCropRect.x, y: imageCropRect.y, w: imageCropRect.w, aspect })
    if (imageCropTarget === 'companyLogo') {
      setSiteSettings((prev) => ({ ...prev, companyLogoCropped: cropped }))
      setSavedDefaultSiteSettings((prev) => ({ ...prev, companyLogoCropped: cropped }))
    } else {
      setSiteSettings((prev) => ({ ...prev, companyIconCropped: cropped }))
      setSavedDefaultSiteSettings((prev) => ({ ...prev, companyIconCropped: cropped }))
    }
    setShowImageCropModal(false)
    showCenterToast('Saved')
  }

  const addAppNotification = (topic: NotificationTopic, text: string) => {
    const level = notificationPrefs[topic]
    if (level === 'none') return
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    setAppNotifications((prev) => [{ id, topic, text, unread: true, ts: Date.now() }, ...prev].slice(0, 100))
  }

  const handleCompanyHolidayUpload = async (file: File) => {
    const source = 'Company Holiday Upload'
    const text = await file.text()
    const extracted = extractDeadlinesFromText(text, source)
      .map((d) => ({ ...d, title: `Holiday — ${d.title.replace(/\s+/g, ' ').trim()}` }))

    if (!extracted.length) {
      showActionToast('No valid holiday dates found in file')
      return
    }

    setCalendarDeadlines((prev) => {
      const seen = new Set(prev.map((d) => `${d.source}|${d.dateKey}|${d.title}`))
      const add = extracted.filter((d) => {
        const key = `${d.source}|${d.dateKey}|${d.title}`
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      return [...add, ...prev]
    })

    addAppNotification('projectChanges', `Imported ${extracted.length} holiday deadlines`)
    showCenterToast('Holidays imported')
  }

  const saveProjectOverview = () => {
    try {
      const next = { ...projectOverviewByProject, [projectOverviewKey]: projectOverview }
      localStorage.setItem('project-overview-by-project-v1', JSON.stringify(next))
    } catch {
      // ignore storage write errors
    }
    showCenterToast('Saved')
    addAppNotification('projectChanges', `${projectName}: Project Overview saved`)
  }

  const isStrongPassword = (value: string) => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(value)
  const extractPrimaryFamily = (fontStack: string) => fontStack.split(',')[0].trim().replace(/^['"]|['"]$/g, '')
  const usernameFromEmail = (email: string) => (email.split('@')[0] || '').trim().toLowerCase()

  const settingsIcon = (key: string) => {
    switch (key) {
      case 'myProfile':
      case 'userManagement':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.7" /><path d="M5.5 19.5c0-3.2 2.9-5.4 6.5-5.4s6.5 2.2 6.5 5.4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
      case 'accountSettings':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19.4 13.5a7.8 7.8 0 0 0 .1-1.5 7.8 7.8 0 0 0-.1-1.5l2-1.6-1.9-3.2-2.4 1a7.7 7.7 0 0 0-2.6-1.5L14 2h-4l-.5 3.2a7.7 7.7 0 0 0-2.6 1.5l-2.4-1-1.9 3.2 2 1.6A7.8 7.8 0 0 0 4.5 12c0 .5 0 1 .1 1.5l-2 1.6 1.9 3.2 2.4-1a7.7 7.7 0 0 0 2.6 1.5L10 22h4l.5-3.2a7.7 7.7 0 0 0 2.6-1.5l2.4 1 1.9-3.2-2-1.6ZM12 15.2A3.2 3.2 0 1 1 12 8.8a3.2 3.2 0 0 1 0 6.4Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg>
      case 'notifications':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4a5 5 0 0 0-5 5v3.5L5 15v1h14v-1l-2-2.5V9a5 5 0 0 0-5-5Z" fill="none" stroke="currentColor" strokeWidth="1.7" /><path d="M10 18a2 2 0 0 0 4 0" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
      case 'preferences':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l2.6 5.3L20 9l-4 3.9.9 5.6L12 15.9 7.1 18.5 8 12.9 4 9l5.4-.7L12 3Z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>
      case 'teamOrg':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="8" cy="8" r="2.2" fill="none" stroke="currentColor" strokeWidth="1.6" /><circle cx="16" cy="8" r="2.2" fill="none" stroke="currentColor" strokeWidth="1.6" /><path d="M4.5 18c0-2.6 2-4.4 3.5-4.4S11.5 15.4 11.5 18M12.5 18c0-2.6 2-4.4 3.5-4.4s3.5 1.8 3.5 4.4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
      case 'permissions':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="10" width="14" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.7" /><path d="M8 10V7.8A4 4 0 0 1 12 4a4 4 0 0 1 4 3.8V10" fill="none" stroke="currentColor" strokeWidth="1.7" /></svg>
      case 'companySettings':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h16M6 20V9h5v11M13 20V5h5v15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
      case 'integrations':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 8a3 3 0 0 1 3-3h3M15 16a3 3 0 0 1-3 3H9" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><path d="M8 12h8" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
      case 'billing':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="6" width="18" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.7" /><path d="M3 10h18" fill="none" stroke="currentColor" strokeWidth="1.7" /></svg>
      case 'help':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.7" /><path d="M9.8 9a2.4 2.4 0 1 1 4.2 1.6c-.6.7-1.4 1.1-1.8 1.9" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><circle cx="12" cy="16.8" r=".9" fill="currentColor" /></svg>
      case 'logout':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 7l5 5-5 5M19 12H9" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /><path d="M10 5H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
      case 'projectDashboards':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12.5 12 5l9 7.5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/><path d="M6 10.5V20h12v-9.5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
      case 'directory':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="5" width="16" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="1.7"/><path d="M9 9h6M9 13h6M9 17h4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
      case 'calendar':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="5.5" width="17" height="15" rx="2" fill="none" stroke="currentColor" strokeWidth="1.7"/><path d="M7 3.8v3.2M17 3.8v3.2M3.5 9h17" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
      case 'consultantCoordinationTracking':
      case 'consultantList':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="8" cy="9" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.7"/><circle cx="16" cy="9" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.7"/><path d="M4.5 18c.4-2.5 2.4-4 3.5-4s3.1 1.5 3.5 4M12.5 18c.4-2.5 2.4-4 3.5-4s3.1 1.5 3.5 4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
      case 'teamOrganization':
      case 'officeOverview':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="3" fill="none" stroke="currentColor" strokeWidth="1.7"/><path d="M5.5 19.5c0-3.2 2.9-5.4 6.5-5.4s6.5 2.2 6.5 5.4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
      case 'staffingAllocation':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 7h12M6 12h12M6 17h7" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/><path d="m14.5 17 1.8 1.8 3.2-3.2" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
      case 'meetingActionTracking':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="1.7"/><path d="M8 9h8M8 13h8M8 17h5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
      case 'projectStandards':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 18h16M7 18l7-12 3 5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
      case 'projects':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3.5 7.5h7l1.5 2h8.5v9a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/></svg>
      case 'activity':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12h4l2-5 4 10 2-5h4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
      case 'settings':
        return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19.4 13.5a7.8 7.8 0 0 0 .1-1.5 7.8 7.8 0 0 0-.1-1.5l2-1.6-1.9-3.2-2.4 1a7.7 7.7 0 0 0-2.6-1.5L14 2h-4l-.5 3.2a7.7 7.7 0 0 0-2.6 1.5l-2.4-1-1.9 3.2 2 1.6A7.8 7.8 0 0 0 4.5 12c0 .5 0 1 .1 1.5l-2 1.6 1.9 3.2 2.4-1a7.7 7.7 0 0 0 2.6 1.5L10 22h4l.5-3.2a7.7 7.7 0 0 0 2.6-1.5l2.4 1 1.9-3.2-2-1.6ZM12 15.2A3.2 3.2 0 1 1 12 8.8a3.2 3.2 0 0 1 0 6.4Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg>
      default:
        return null
    }
  }

  const handlePopoverSubmitKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return
    const target = e.target as HTMLElement
    if (target.tagName === 'TEXTAREA') return

    const popover = e.currentTarget
    if (popover.classList.contains('site-settings-popover')) return

    const primary =
      popover.querySelector<HTMLButtonElement>('[data-primary-action="true"]') ||
      Array.from(popover.querySelectorAll<HTMLButtonElement>('button')).find((btn) => {
        const label = (btn.textContent || '').trim().toLowerCase()
        if (!label) return false
        if (btn.classList.contains('danger')) return false
        if (label === 'cancel') return false
        return ['save', 'add', 'update', 'done', '+ new task'].includes(label)
      })

    if (!primary) return
    e.preventDefault()
    primary.click()
  }

  const formatPhone = (raw?: string) => {
    const digits = (raw || '').replace(/\D/g, '')
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    }
    return raw || ''
  }

  const formatPhoneInput = (raw?: string) => {
    const digits = (raw || '').replace(/\D/g, '').slice(0, 10)
    if (!digits) return ''
    if (digits.length < 4) return `(${digits}`
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }

  const openEditContactByIndex = (idx: number) => {
    const c = directoryContacts[idx]
    if (!c) return
    const cell = c.phoneType === 'Cell' ? (c.phone || '') : c.altPhoneType === 'Cell' ? (c.altPhone || '') : ''
    const office = c.phoneType === 'Office' ? (c.phone || '') : c.altPhoneType === 'Office' ? (c.altPhone || '') : ''
    setEditContactIndex(idx)
    setNewContactName(c.name || '')
    setNewContactRole(c.role || '')
    setNewContactTeam(c.team || '')
    setNewContactCompany(c.company || '')
    setNewContactCompanyAbbreviation(c.companyAbbreviation || '')
    setNewContactPhone(cell)
    setNewContactPhoneType('Cell')
    setNewContactAltPhone(office)
    setNewContactAltPhoneType('Office')
    setNewContactOfficeExtension(c.officeExtension || '')
    setNewContactNotes(c.notes || '')
    setNewContactEmail(c.email || '')
    setShowEditContact(true)
  }

  const saveInlineDirectoryEdit = () => {
    if (!inlineDirectoryEdit) return
    const { idx, field } = inlineDirectoryEdit
    const value = inlineDirectoryValue.trim()

    setDirectoryContacts((prev) => prev.map((c, i) => {
      if (i !== idx) return c
      if (field === 'email') return { ...c, email: value }

      if (field === 'office') {
        if (c.phoneType === 'Office') return { ...c, phone: value }
        if (c.altPhoneType === 'Office') return { ...c, altPhone: value }
        return { ...c, altPhoneType: 'Office', altPhone: value }
      }

      if (c.phoneType === 'Cell') return { ...c, phone: value }
      if (c.altPhoneType === 'Cell') return { ...c, altPhone: value }
      return { ...c, phoneType: 'Cell', phone: value }
    }))

    setInlineDirectoryEdit(null)
    setInlineDirectoryValue('')
  }

  const togglePrimaryContact = (idx: number) => {
    setDirectoryContacts((prev) => prev.map((c, i) => (i === idx ? { ...c, primaryContact: !c.primaryContact } : c)))
  }

  const toggleDirectoryBulkIndex = (idx: number, checked: boolean) => {
    setDirectoryBulkSelection((prev) => {
      const next = new Set(prev)
      if (checked) next.add(idx)
      else next.delete(idx)
      return next
    })
  }

  const applyBulkAssignTeam = () => {
    const value = bulkAssignTeamValue.trim()
    if (!value || !directoryBulkSelection.size) return
    setDirectoryContacts((prev) => prev.map((c, i) => (directoryBulkSelection.has(i) ? { ...c, team: value } : c)))
    setBulkAssignTeamValue('')
    showActionToast(`Assigned team to ${directoryBulkSelection.size} contact${directoryBulkSelection.size === 1 ? '' : 's'}`)
  }

  const applyBulkAssignDiscipline = () => {
    const value = bulkAssignDisciplineValue.trim()
    if (!value || !directoryBulkSelection.size) return
    setDirectoryContacts((prev) => prev.map((c, i) => (directoryBulkSelection.has(i) ? { ...c, role: value } : c)))
    setBulkAssignDisciplineValue('')
    showActionToast(`Tagged discipline for ${directoryBulkSelection.size} contact${directoryBulkSelection.size === 1 ? '' : 's'}`)
  }

  const buildDirectoryExportRows = () => {
    const selected = [...directoryBulkSelection]
      .map((idx) => directoryContacts[idx])
      .filter(Boolean)
      .sort((a, b) => (a.company || '').localeCompare(b.company || '', undefined, { sensitivity: 'base' }) || (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))

    const rows: string[][] = []
    let lastCompany = ''
    selected.forEach((c) => {
      const company = (c.company || '').trim()
      const office = c.phoneType === 'Office' ? c.phone : c.altPhoneType === 'Office' ? c.altPhone : ''
      const cell = c.phoneType === 'Cell' ? c.phone : c.altPhoneType === 'Cell' ? c.altPhone : ''
      if (rows.length && company !== lastCompany) rows.push(['', '', '', '', '', '', '', '', ''])
      rows.push([
        c.team || '',
        company,
        c.name || '',
        c.role || '',
        c.email || '',
        office || '',
        c.officeExtension || '',
        cell || '',
        c.notes || '',
      ])
      lastCompany = company
    })

    return rows
  }

  const exportSelectedDirectoryContactsExcel = async () => {
    const rows = buildDirectoryExportRows()
    if (!rows.length) return
    const header = ['Consultant Type', 'Company', 'Name', 'Role', 'Email', 'Office', 'Ext', 'Cell', 'Notes']
    const escapeCsv = (v: string) => {
      const s = String(v ?? '')
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
      return s
    }
    const csv = [header, ...rows].map((r) => r.map((c) => escapeCsv(String(c || ''))).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `directory-selected-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const exportSelectedDirectoryContactsPdf = async () => {
    const rows = buildDirectoryExportRows()
    if (!rows.length) return
    const { jsPDF } = await import('jspdf')
    const autoTable = (await import('jspdf-autotable')).default
    const header = ['Consultant Type', 'Company', 'Name', 'Role', 'Email', 'Office', 'Ext', 'Cell', 'Notes']
    const doc = new jsPDF({ unit: 'pt', format: 'letter' })
    doc.setFontSize(12)
    doc.text('Directory Export', 40, 32)
    autoTable(doc, {
      head: [header],
      body: rows,
      startY: 42,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [45, 45, 45] },
    })
    doc.save(`directory-selected-${new Date().toISOString().slice(0, 10)}.pdf`)
  }

  const handleDirectoryExportFormatChange = async (value: 'excel' | 'pdf' | '') => {
    setDirectoryExportFormat(value)
    if (value === 'excel') await exportSelectedDirectoryContactsExcel()
    if (value === 'pdf') await exportSelectedDirectoryContactsPdf()
    setDirectoryExportFormat('')
  }

  const buildDirectorySavedViewPayload = (name: string): DirectorySavedView => ({
    id: makeId(),
    name: name.trim(),
    search: directorySearch,
    contactFilter: directoryContactFilter,
    sortBy: directorySortBy,
    contactKeys: [...directoryBulkSelection]
      .map((idx) => directoryContacts[idx])
      .filter(Boolean)
      .map((c) => directoryContactKey(c)),
    createdAt: now(),
    updatedAt: now(),
  })

  const saveCurrentDirectoryView = () => {
    const name = (directoryViewNameDraft || '').trim()
    if (!name) {
      showCopyToast('Enter a contact group name')
      return
    }
    const payload = buildDirectorySavedViewPayload(name)
    setDirectorySavedViews((prev) => [...prev, payload])
    setActiveDirectoryViewId(payload.id)
    setDirectoryViewNameDraft('')
    setShowDirectorySaveGroupPopover(false)
    showCopyToast(`Saved group: ${payload.name}`)
  }

  const updateActiveDirectoryView = () => {
    if (!activeDirectoryViewId) return
    setDirectorySavedViews((prev) => prev.map((view) => (
      view.id === activeDirectoryViewId
        ? {
          ...view,
          search: directorySearch,
          contactFilter: directoryContactFilter,
          sortBy: directorySortBy,
          contactKeys: [...directoryBulkSelection]
            .map((idx) => directoryContacts[idx])
            .filter(Boolean)
            .map((c) => directoryContactKey(c)),
          updatedAt: now(),
        }
        : view
    )))
    showCopyToast('Updated saved view')
  }

  const applyDirectorySavedView = (viewId: string) => {
    if (!viewId) {
      setActiveDirectoryViewId(null)
      setDirectoryBulkSelection(new Set())
      return
    }
    const view = directorySavedViews.find((item) => item.id === viewId)
    if (!view) return
    setDirectorySearch(view.search)
    setDirectoryContactFilter(view.contactFilter)
    setDirectorySortBy(view.sortBy)
    setDirectoryBulkSelection(() => {
      const keys = new Set(view.contactKeys || [])
      if (!keys.size) return new Set()
      const next = new Set<number>()
      directoryContacts.forEach((contact, idx) => {
        if (keys.has(directoryContactKey(contact))) next.add(idx)
      })
      return next
    })
    setActiveDirectoryViewId(view.id)
  }

  const deleteActiveDirectoryGroup = () => {
    if (!activeDirectoryViewId) return
    const target = directorySavedViews.find((view) => view.id === activeDirectoryViewId)
    setDirectorySavedViews((prev) => prev.filter((view) => view.id !== activeDirectoryViewId))
    setActiveDirectoryViewId(null)
    showCopyToast(target?.name ? `Deleted group: ${target.name}` : 'Deleted group')
  }

  const copyEmailsFromSelectedDirectoryContacts = async () => {
    const emails = [...directoryBulkSelection]
      .map((idx) => (directoryContacts[idx]?.email || '').trim())
      .filter(Boolean)
    const unique = Array.from(new Set(emails))
    if (!unique.length) {
      showCopyToast('No emails in selection')
      return
    }
    const ok = await copyText(unique.join('; '))
    showCopyToast(ok ? `Copied ${unique.length} email${unique.length === 1 ? '' : 's'}` : 'Copy failed')
  }

  const archiveSelectedDirectoryContacts = () => {
    if (!directoryBulkSelection.size) return
    const selected = new Set(directoryBulkSelection)
    const count = [...selected].filter((idx) => directoryContacts[idx] && !directoryContacts[idx].archived).length
    if (!count) return

    setDirectoryContacts((prev) => prev.map((c, i) => (selected.has(i) ? { ...c, archived: true } : c)))
    setDirectoryBulkSelection(new Set())
    showActionToast(`Archived ${count} contact${count === 1 ? '' : 's'}`, () => {
      setDirectoryContacts((prev) => prev.map((c, i) => (selected.has(i) ? { ...c, archived: false } : c)))
    })
  }

  const restoreSelectedDirectoryContacts = () => {
    if (!directoryBulkSelection.size) return
    const count = [...directoryBulkSelection].filter((idx) => directoryContacts[idx]?.archived).length
    if (!count) return
    setDirectoryContacts((prev) => prev.map((c, i) => (directoryBulkSelection.has(i) ? { ...c, archived: false } : c)))
    setDirectoryBulkSelection(new Set())
    showActionToast(`Restored ${count} contact${count === 1 ? '' : 's'}`)
  }

  const mergeFirstDirectoryDuplicateGroup = () => {
    const group = directoryDuplicateGroups[0]
    if (!group || group.length < 2) return
    const sorted = [...group].sort((a, b) => a - b)
    const [keepIdx, ...dropIdxs] = sorted
    setDirectoryContacts((prev) => {
      const keep = prev[keepIdx]
      if (!keep) return prev
      const merged = { ...keep }
      dropIdxs.forEach((idx) => {
        const d = prev[idx]
        if (!d) return
        if (!merged.email && d.email) merged.email = d.email
        if (!merged.phone && d.phone) merged.phone = d.phone
        if (!merged.altPhone && d.altPhone) merged.altPhone = d.altPhone
        if (!merged.role && d.role) merged.role = d.role
        if (!merged.company && d.company) merged.company = d.company
        if (!merged.team && d.team) merged.team = d.team
        merged.primaryContact = !!(merged.primaryContact || d.primaryContact)
      })
      const next = prev.filter((_, idx) => !dropIdxs.includes(idx)).map((row, idx) => (idx === keepIdx ? merged : row))
      return next
    })
    setDirectoryBulkSelection(new Set())
    showCenterToast(`Merged ${group.length} duplicate contacts`)
  }

  const resetContactForm = () => {
    setNewContactName('')
    setNewContactRole('')
    setNewContactTeam('')
    setNewContactCompany('')
    setNewContactCompanyAbbreviation('')
    setNewContactPhone('')
    setNewContactPhoneType('Cell')
    setNewContactAltPhone('')
    setNewContactAltPhoneType('Office')
    setNewContactOfficeExtension('')
    setNewContactNotes('')
    setNewContactEmail('')
  }

  const mapContactRow = (header: string[], row: Array<string | number | Date | null | undefined>): DirectoryContact | null => {
    const get = (...keys: string[]) => {
      const idx = header.findIndex((h) => keys.some((k) => h.includes(k)))
      return idx >= 0 ? String(row[idx] ?? '').trim() : ''
    }
    const name = get('name', 'contact')
    if (!name) return null
    const role = get('role', 'title') || 'Role'
    const team = get('team', 'group') || 'Team'
    const company = get('company', 'firm', 'organization')
    const email = get('email', 'mail')
    const phone = get('phone', 'cell', 'mobile')
    const altPhone = get('office', 'alt', 'secondary')
    const officeExtension = get('extension', 'ext')
    return {
      name,
      role,
      team,
      company,
      email,
      phone,
      phoneType: 'Cell',
      altPhone,
      altPhoneType: 'Office',
      officeExtension,
    }
  }

  const importContactsFromFile = async (file: File) => {
    const lower = file.name.toLowerCase()
    const out: DirectoryContact[] = []

    if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
      throw new Error('Excel import is temporarily disabled for security hardening. Use CSV/TXT/PDF.')
    }

    if (lower.endsWith('.csv')) {
      const text = await file.text()
      const lines = text.split(/\r?\n/).filter(Boolean)
      if (lines.length) {
        const delim = lines[0].includes('\t') ? '\t' : lines[0].includes('|') ? '|' : ','
        const header = lines[0].split(delim).map((x) => x.trim().toLowerCase())
        for (let i = 1; i < lines.length; i += 1) {
          const cols = lines[i].split(delim).map((x) => x.trim())
          const c = mapContactRow(header, cols)
          if (c) out.push(c)
        }
      }
    } else {
      // txt/pdf fallback: parse line-delimited CSV-ish text when possible
      const text = await file.text()
      const lines = text.split(/\r?\n/).filter(Boolean)
      if (lines.length) {
        const delim = lines[0].includes('\t') ? '\t' : lines[0].includes('|') ? '|' : ','
        const header = lines[0].split(delim).map((x) => x.trim().toLowerCase())
        for (let i = 1; i < lines.length; i += 1) {
          const cols = lines[i].split(delim).map((x) => x.trim())
          const c = mapContactRow(header, cols)
          if (c) out.push(c)
        }
      }
    }

    if (!out.length) return 0

    setDirectoryContacts((prev) => {
      const existingKeys = new Set(prev.map((c) => `${c.name.toLowerCase()}|${(c.email || '').toLowerCase()}`))
      const add = out.filter((c) => !existingKeys.has(`${c.name.toLowerCase()}|${(c.email || '').toLowerCase()}`))
      return [...add, ...prev]
    })

    return out.length
  }

  const importSheetIndexFromFile = async (file: File) => {
    const lower = file.name.toLowerCase()
    const normalize = (value: unknown) => String(value ?? '').trim()
    const defaultCols = ['Assign To', 'Sheet Number', 'Sheet Name', 'Scale']

    const normalizeKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

    const mapMatrixToSheetRows = (header: string[], body: string[][]) => {
      const idxByKey = new Map<string, number>()
      header.forEach((h, i) => {
        const key = normalizeKey(h)
        if (key && !idxByKey.has(key)) idxByKey.set(key, i)
      })

      const pickIdx = (...aliases: string[]) => {
        for (const alias of aliases) {
          const idx = idxByKey.get(normalizeKey(alias))
          if (idx !== undefined) return idx
        }
        return -1
      }

      const respIdx = pickIdx('assign to', 'responsibility', 'discipline', 'owner', 'trade')
      const numIdx = pickIdx('sheet number', 'sheet no', 'sheet #', 'number')
      const nameIdx = pickIdx('sheet name', 'sheet title', 'title', 'name')
      const scaleIdx = pickIdx('scale')

      const extraColumns = header.filter((h) => {
        const n = normalizeKey(h)
        return n && !defaultCols.some((d) => normalizeKey(d) === n)
      })

      const mappedRows = body
        .map((r) => {
          const row: Record<string, string> = {
            'Assign To': respIdx >= 0 ? normalize(r[respIdx]) : '',
            'Sheet Number': numIdx >= 0 ? normalize(r[numIdx]) : '',
            'Sheet Name': nameIdx >= 0 ? normalize(r[nameIdx]) : '',
            Scale: scaleIdx >= 0 ? normalize(r[scaleIdx]) : '',
          }

          extraColumns.forEach((col) => {
            const idx = header.findIndex((h) => normalizeKey(h) === normalizeKey(col))
            row[col] = idx >= 0 ? normalize(r[idx]) : ''
          })

          return row
        })
        .filter((r) => Object.values(r).some((v) => v.length > 0))

      return { rows: mappedRows, extraColumns }
    }

    if (lower.endsWith('.pdf')) {
      const pdfjs = await import('pdfjs-dist')
      const workerSrc = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default
      const pdfLib = pdfjs as unknown as {
        GlobalWorkerOptions: { workerSrc: string }
        getDocument: (params: { data: ArrayBuffer }) => { promise: Promise<{ numPages: number; getPage: (page: number) => Promise<{ getTextContent: () => Promise<{ items: Array<{ str?: string }> }> }> }> }
      }
      pdfLib.GlobalWorkerOptions.workerSrc = workerSrc

      const buffer = await file.arrayBuffer()
      const doc = await pdfLib.getDocument({ data: buffer }).promise
      const lines: string[] = []

      for (let pageNum = 1; pageNum <= doc.numPages; pageNum += 1) {
        const page = await doc.getPage(pageNum)
        const textContent = await page.getTextContent()
        const items = (textContent.items || []) as Array<{ str?: string }>
        const pageText = items.map((it) => String(it?.str || '').trim()).filter(Boolean).join(' ')
        const pageLines = pageText
          .split(/(?=[A-Z]{1,3}[-_.]?\d{1,4}(?:\.\d{1,2})?\b)/g)
          .map((x) => x.trim())
          .filter(Boolean)
        lines.push(...pageLines)
      }

      const parsedRows: Array<Record<string, string>> = []

      for (const line of lines) {
        const numMatch = line.match(/\b([A-Z]{1,3}[-_.]?\d{1,4}(?:\.\d{1,2})?)\b/)
        if (!numMatch) continue

        const sheetNumber = numMatch[1]
        const before = line.slice(0, numMatch.index || 0).trim()
        const after = line.slice((numMatch.index || 0) + sheetNumber.length).trim()

        const scaleMatch = line.match(/\b((?:\d+\/\d+|\d+)\s*=\s*1'?-?0"?|NTS|NOT TO SCALE|AS NOTED)\b/i)
        const scale = scaleMatch ? scaleMatch[1].toUpperCase() : ''

        const responsibilityMatch = line.match(/\b(ARCH|ARCHITECT|INTERIORS?|MEP|MECHANICAL|ELECTRICAL|PLUMBING|STRUCTURAL|CIVIL)\b/i)
        const responsibility = responsibilityMatch ? responsibilityMatch[1].toUpperCase() : before

        const sheetName = after.replace(scaleMatch?.[0] || '', '').replace(/\s{2,}/g, ' ').trim()

        parsedRows.push({
          'Assign To': responsibility,
          'Sheet Number': sheetNumber,
          'Sheet Name': sheetName,
          Scale: scale,
        })
      }

      const deduped = parsedRows.filter((row, idx, arr) =>
        arr.findIndex((x) => x['Sheet Number'] === row['Sheet Number'] && x['Sheet Name'] === row['Sheet Name']) === idx,
      )

      setSheetIndexColumns(defaultCols)
      setSheetIndexMarkerColumns([])
      setSheetIndexRows(deduped)
      setSheetIndexDraftRow({})
      setSheetIndexFileName(file.name)
      return deduped.length
    }

    let header: string[] = []
    let rows: string[][] = []

    if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
      throw new Error('Excel import is temporarily disabled for security hardening. Use CSV/TXT/PDF.')
    }

    if (lower.endsWith('.csv')) {
      const text = await file.text()
      const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0)
      if (!lines.length) return 0
      const delim = lines[0].includes('\t') ? '\t' : lines[0].includes('|') ? '|' : ','
      const parsed = lines.map((line) => line.split(delim).map((x) => x.trim()))
      header = parsed[0] || []
      rows = parsed.slice(1)
    } else {
      const text = await file.text()
      const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0)
      if (!lines.length) return 0
      const delim = lines[0].includes('\t') ? '\t' : lines[0].includes('|') ? '|' : ','
      const parsed = lines.map((line) => line.split(delim).map((x) => x.trim()))
      header = parsed[0] || []
      rows = parsed.slice(1)
    }

    if (!header.length) return 0

    const mapped = mapMatrixToSheetRows(header, rows)
    const mergedCols = [...defaultCols]
    for (const col of mapped.extraColumns) {
      if (!mergedCols.some((x) => normalizeKey(x) === normalizeKey(col))) mergedCols.push(col)
    }

    setSheetIndexColumns(mergedCols)
    setSheetIndexMarkerColumns([])
    setSheetIndexRows(mapped.rows)
    setSheetIndexDraftRow({})
    setSheetIndexFileName(file.name)
    return mapped.rows.length
  }

  const goTab = (tab: TabKey) => {
    if (!canViewTab(tab)) return
    setActiveTab(tab)
    setMobileNavOpen(false)
  }

  const addMeetingTab = () => {
    const nextIndex = meetingTabOrder.length + 1
    const key = `meeting${nextIndex}`

    setMeetingDrafts((prev) => ({
      ...prev,
      [activeMeetingTab]: {
        date: meetingDate,
        type: meetingType,
        preparedBy: meetingPreparedBy,
        location: meetingLocation,
        attendees: meetingAttendees,
        agendaItems: meetingAgendaItems,
        actionItems: meetingActionItems,
      },
      [key]: makeBlankMeetingDraft(),
    }))
    setMeetingTabOrder((prev) => [...prev, key])
    setMeetingDate('')
    setMeetingType('')
    setMeetingPreparedBy('')
    setMeetingLocation('')
    setMeetingAttendees([])
    setMeetingAgendaItems([])
    setMeetingActionItems([])
    setCollapsedMeetingTopics({})
    setActiveMeetingTab(key)
  }

  const exportMeetingToExcel = async () => {
    const rows: string[] = []
    rows.push('Field,Value')
    rows.push(`Project,${projectName}`)
    rows.push(`Date,${meetingDate}`)
    rows.push(`Meeting Type,${meetingType}`)
    rows.push(`Prepared By,${meetingPreparedBy}`)
    rows.push(`Meeting Location,${meetingLocation}`)
    rows.push('')
    rows.push('Attendees')
    rows.push('Name,Company,Present')
    for (const a of meetingAttendees) rows.push(`${a.name || ''},${a.company || ''},${a.present ? 'Yes' : 'No'}`)
    rows.push('')
    rows.push('Meeting Topics')
    rows.push('Number,Date,Item,Owner,Due,Status,Level')
    const topicsRows = meetingActionItems
      .filter((x) => !x.parentId)
      .flatMap((t, i) => {
        const num = t.number || `${i + 1}`
        const children = meetingActionItems.filter((x) => x.parentId === t.id)
        return [
          { Number: num, Date: t.date, Item: t.item, Owner: '', Due: '', Status: t.status, Level: 'Topic' },
          ...children.map((c, idx) => ({
            Number: `${num}.${idx + 1}`,
            Date: c.date,
            Item: c.item,
            Owner: c.owner,
            Due: c.due,
            Status: c.status,
            Level: 'Sub-item',
          })),
        ]
      })
    for (const r of topicsRows) rows.push(`${r.Number || ''},${r.Date || ''},${(r.Item || '').replace(/,/g, ';')},${r.Owner || ''},${r.Due || ''},${r.Status || ''},${r.Level || ''}`)
    rows.push('')
    rows.push('Additional Topics')
    rows.push('Topic')
    for (const t of meetingAgendaItems) rows.push((t || '').replace(/,/g, ';'))

    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `meeting-minutes-${activeMeetingTab}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const exportMeetingToPdf = async () => {
    const target = document.querySelector('.calendar-main') as HTMLElement | null
    if (!target) {
      showActionToast('Meeting area not ready for PDF export')
      return
    }

    const pageWidthPt = 612 // letter
    const pageHeightPt = 792 // letter
    const marginPt = 36
    const printableWidthPt = pageWidthPt - marginPt * 2
    const printableHeightPt = pageHeightPt - marginPt * 2
    const baseWidthPx = 1200
    const pageContentHeightPx = Math.floor(baseWidthPx * (printableHeightPt / printableWidthPt))

    const liveSelects = Array.from(target.querySelectorAll('select')) as HTMLSelectElement[]
    liveSelects.forEach((s) => s.setAttribute('data-export-value', s.value))

    const clone = target.cloneNode(true) as HTMLElement
    clone.style.width = `${baseWidthPx}px`
    clone.style.maxWidth = `${baseWidthPx}px`

    liveSelects.forEach((s) => s.removeAttribute('data-export-value'))

    clone
      .querySelectorAll('.meeting-export-actions, .meeting-row-action-btn, .meeting-topic-toggle-outside, .meeting-subitem-add-btn, .meeting-attendees-add-btn')
      .forEach((el) => el.remove())

    clone.querySelectorAll('button').forEach((btn) => {
      const label = (btn.textContent || '').trim()
      if (!label || /^add\s+/i.test(label)) {
        btn.remove()
        return
      }
      const span = document.createElement('span')
      span.textContent = label
      btn.replaceWith(span)
    })

    clone.querySelectorAll('input').forEach((input) => {
      const span = document.createElement('span')
      span.textContent = input.value || ''
      span.className = 'meeting-print-field'
      input.replaceWith(span)
    })

    clone.querySelectorAll('select').forEach((select) => {
      const span = document.createElement('span')
      const exportValue = select.getAttribute('data-export-value') || (select as HTMLSelectElement).value
      const matched = Array.from(select.options).find((o) => o.value === exportValue)
      span.textContent = matched?.textContent || exportValue || ''
      span.className = 'meeting-print-field'
      select.replaceWith(span)
    })

    clone.querySelectorAll('[contenteditable=""], [contenteditable="true"]').forEach((el) => {
      el.removeAttribute('contenteditable')
    })

    clone.classList.add('meeting-print-page')
    clone.style.width = `${baseWidthPx}px`
    clone.style.minHeight = `${pageContentHeightPx}px`
    clone.style.boxSizing = 'border-box'

    clone.querySelectorAll('*').forEach((el) => {
      const h = el as HTMLElement
      h.style.color = '#000'
      h.style.background = 'transparent'
      h.style.boxShadow = 'none'
      h.style.textShadow = 'none'
      h.style.fontFamily = 'Lato, Inter, system-ui, Arial, sans-serif'
    })

    clone.querySelectorAll('.meeting-action-sub-closed, .meeting-action-sub-closed *').forEach((el) => {
      ;(el as HTMLElement).style.opacity = '0.25'
    })
    clone.querySelectorAll('.meeting-attendees-row.absent, .meeting-attendees-row.absent *').forEach((el) => {
      ;(el as HTMLElement).style.opacity = '0.25'
    })
    clone.querySelectorAll('.meeting-action-topic-row').forEach((el) => {
      ;(el as HTMLElement).style.background = '#efefef'
    })
    clone.querySelectorAll('.meeting-attendees-header, .meeting-attendees-row, .meeting-action-header, .meeting-action-row').forEach((el) => {
      const h = el as HTMLElement
      h.style.borderTop = '1px solid #b8b8b8'
      h.style.borderLeft = '0'
      h.style.borderRight = '0'
      h.style.borderBottom = '0'
    })

    const mount = document.createElement('div')
    mount.style.position = 'fixed'
    mount.style.left = '-99999px'
    mount.style.top = '0'
    mount.style.width = `${baseWidthPx}px`
    mount.style.opacity = '0'
    mount.style.pointerEvents = 'none'
    mount.style.zIndex = '-1'

    const measureHost = document.createElement('div')
    measureHost.style.width = `${baseWidthPx}px`
    mount.appendChild(measureHost)
    document.body.appendChild(mount)

    const measureHeight = (el: HTMLElement) => {
      const probe = el.cloneNode(true) as HTMLElement
      measureHost.appendChild(probe)
      const h = Math.ceil(probe.getBoundingClientRect().height)
      probe.remove()
      return h
    }

    const makePage = () => {
      const page = document.createElement('div')
      page.className = 'meeting-print-page'
      page.style.width = `${baseWidthPx}px`
      page.style.minHeight = `${pageContentHeightPx}px`
      return page
    }

    const pages: HTMLElement[] = []
    let currentPage = makePage()
    pages.push(currentPage)
    let currentHeight = 0

    const appendUnit = (unit: HTMLElement, forceNewPage = false) => {
      const h = measureHeight(unit)
      if ((forceNewPage || currentHeight + h > pageContentHeightPx) && currentPage.children.length > 0) {
        currentPage = makePage()
        pages.push(currentPage)
        currentHeight = 0
      }
      currentPage.appendChild(unit)
      currentHeight += h
    }

    const headerRow = clone.querySelector('.calendar-header-row') as HTMLElement | null
    const topRow = clone.querySelector('.meeting-top-row') as HTMLElement | null
    const blocks = Array.from(clone.querySelectorAll(':scope > .meeting-block')) as HTMLElement[]
    const actionBlock = blocks[0] || null
    const additionalBlock = blocks[1] || null

    if (headerRow) appendUnit(headerRow.cloneNode(true) as HTMLElement)
    if (topRow) appendUnit(topRow.cloneNode(true) as HTMLElement)

    if (actionBlock) {
      const actionHeading = actionBlock.querySelector('h3')
      const actionHeader = actionBlock.querySelector('.meeting-action-header')
      const headingUnit = document.createElement('div')
      if (actionHeading) headingUnit.appendChild(actionHeading.cloneNode(true))
      if (actionHeader) headingUnit.appendChild(actionHeader.cloneNode(true))
      appendUnit(headingUnit)

      const topicBlocks = Array.from(actionBlock.querySelectorAll(':scope > .meeting-topic-block')) as HTMLElement[]
      topicBlocks.forEach((topic, idx) => {
        const needsBreak = currentHeight + measureHeight(topic) > pageContentHeightPx && idx > 0
        if (needsBreak) {
          appendUnit((headingUnit.cloneNode(true) as HTMLElement), true)
        }
        appendUnit(topic.cloneNode(true) as HTMLElement)
      })
    }

    if (additionalBlock) appendUnit(additionalBlock.cloneNode(true) as HTMLElement)

    const renderHost = document.createElement('div')
    renderHost.style.width = `${baseWidthPx}px`
    pages.forEach((p) => renderHost.appendChild(p))
    mount.appendChild(renderHost)

    try {
      const { default: html2canvas } = await import('html2canvas')
      const { jsPDF } = await import('jspdf')
      const doc = new jsPDF({ unit: 'pt', format: 'letter' })

      for (let i = 0; i < pages.length; i += 1) {
        const pageEl = pages[i]
        const canvas = await html2canvas(pageEl, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          width: pageEl.scrollWidth,
          height: pageEl.scrollHeight,
        })

        const img = canvas.toDataURL('image/png')
        if (i > 0) doc.addPage('letter')
        const renderHeightPt = printableWidthPt * (canvas.height / canvas.width)
        doc.addImage(img, 'PNG', marginPt, marginPt, printableWidthPt, Math.min(printableHeightPt, renderHeightPt))
      }

      doc.save(`meeting-minutes-${activeMeetingTab}.pdf`)
    } finally {
      mount.remove()
    }
  }

  const hydrateMeetingDraft = (draft: ReturnType<typeof makeBlankMeetingDraft>) => {
    setMeetingDate(draft.date)
    setMeetingType(draft.type)
    setMeetingPreparedBy(draft.preparedBy)
    setMeetingLocation(draft.location)
    setMeetingAttendees(draft.attendees)
    setMeetingAgendaItems(draft.agendaItems)
    setMeetingActionItems(draft.actionItems)
    setCollapsedMeetingTopics({})
  }

  useEffect(() => {
    setMeetingDrafts((prev) => ({
      ...prev,
      [activeMeetingTab]: {
        date: meetingDate,
        type: meetingType,
        preparedBy: meetingPreparedBy,
        location: meetingLocation,
        attendees: meetingAttendees,
        agendaItems: meetingAgendaItems,
        actionItems: meetingActionItems,
      },
    }))
  }, [
    activeMeetingTab,
    meetingDate,
    meetingType,
    meetingPreparedBy,
    meetingLocation,
    meetingAttendees,
    meetingAgendaItems,
    meetingActionItems,
  ])

  const deleteActiveMeetingTab = () => {
    if (meetingTabOrder.length <= 1) {
      showActionToast('Cannot delete the last remaining meeting sheet')
      return
    }

    const currentDraft = {
      date: meetingDate,
      type: meetingType,
      preparedBy: meetingPreparedBy,
      location: meetingLocation,
      attendees: meetingAttendees,
      agendaItems: meetingAgendaItems,
      actionItems: meetingActionItems,
    }

    const removedKey = activeMeetingTab
    const removedIndex = meetingTabOrder.indexOf(removedKey)
    const nextOrder = meetingTabOrder.filter((k) => k !== removedKey)
    const nextKey = nextOrder[Math.max(0, removedIndex - 1)] || nextOrder[0]

    setMeetingTabOrder(nextOrder)
    setMeetingDrafts((prev) => {
      const out = { ...prev, [removedKey]: currentDraft }
      delete out[removedKey]
      return out
    })

    const nextDraft = meetingDrafts[nextKey] || makeBlankMeetingDraft()
    setActiveMeetingTab(nextKey)
    hydrateMeetingDraft(nextDraft)
    setShowDeleteMeetingConfirm(false)

    showActionToast('Meeting sheet deleted', () => {
      setMeetingTabOrder((prev) => {
        const copy = [...prev]
        copy.splice(Math.max(0, Math.min(removedIndex, copy.length)), 0, removedKey)
        return copy
      })
      setMeetingDrafts((prev) => ({ ...prev, [removedKey]: currentDraft }))
      setActiveMeetingTab(removedKey)
      hydrateMeetingDraft(currentDraft)
    })
  }

  const switchMeetingTab = (next: MeetingTabKey) => {
    if (next === activeMeetingTab) return
    setMeetingDrafts((prev) => ({
      ...prev,
      [activeMeetingTab]: {
        date: meetingDate,
        type: meetingType,
        preparedBy: meetingPreparedBy,
        location: meetingLocation,
        attendees: meetingAttendees,
        agendaItems: meetingAgendaItems,
        actionItems: meetingActionItems,
      },
    }))

    const target = meetingDrafts[next] || makeBlankMeetingDraft()
    hydrateMeetingDraft(target)
    setActiveMeetingTab(next)
  }

  const abbreviateMeetingType = (value: string) => {
    const cleaned = (value || '').trim()
    if (!cleaned) return ''
    const words = cleaned.split(/\s+/).filter(Boolean)
    if (words.length === 1) return words[0].slice(0, 4).toUpperCase()
    return words.slice(0, 4).map((w) => w[0].toUpperCase()).join('')
  }

  const projectName = siteSettings.projectName || 'Project Name'
  const companyName = siteSettings.companyName || 'Arc Ops'
  const companyLogoOriginal = siteSettings.companyLogo || '/archops-logo-transparent.png'
  const companyLogo = siteSettings.companyLogoCropped || companyLogoOriginal
  const companyIconOriginal = siteSettings.companyIcon || '/vite.svg'
  const companyIcon = siteSettings.companyIconCropped || companyIconOriginal
  const unreadNotificationCount = appNotifications.filter((n) => n.unread).length
  const isReadOnlyActiveTab = currentRole === 'Admin' ? false : !canEditTab(activeTab)
  const isDrafterProjectDashboard = activeTab === 'projectDashboards' && effectiveRole === 'Drafter'
  const isAdminRole = effectiveRole === 'Admin'
  const executiveSyncMap = EXECUTIVE_SYNC_MAP
  const contentRef = useRef<HTMLElement | null>(null)
  const isWriteActionLabel = (label: string) => /\b(add|save|delete|remove|upload|create|edit)\b/i.test(label)
  useEffect(() => {
    const root = contentRef.current
    if (!root) return

    const buttons = Array.from(root.querySelectorAll('button')) as HTMLButtonElement[]
    buttons.forEach((btn) => {
      const label = (btn.textContent || '').trim()
      if (isReadOnlyActiveTab && isWriteActionLabel(label)) {
        btn.classList.add('role-hidden-action')
      } else {
        btn.classList.remove('role-hidden-action')
      }
    })
  }, [isReadOnlyActiveTab, activeTab])

  const profileOfficePlaceholder = 'Office Location'

  const openMeetingActionFromDashboard = (actionId: string, parentId?: string) => {
    if (parentId) {
      setCollapsedMeetingTopics((prev) => ({ ...prev, [parentId]: false }))
    }
    setActiveTab('meetingActionTracking')
    window.setTimeout(() => {
      const row = document.querySelector(`[data-meeting-action-id="${actionId}"]`) as HTMLElement | null
      if (!row) return
      row.scrollIntoView({ behavior: 'smooth', block: 'center' })
      row.classList.add('meeting-row-flash')
      window.setTimeout(() => row.classList.remove('meeting-row-flash'), 1400)
    }, 120)
  }

  const openStaffingFromDashboard = (assignee: string, taskId?: string) => {
    setActiveTab('staffingAllocation')
    setStaffingMemberView(assignee || 'Team')
    if (!taskId) return
    window.setTimeout(() => {
      const row = document.querySelector(`[data-staffing-row-id="${taskId}"]`) as HTMLElement | null
      if (!row) return
      row.scrollIntoView({ behavior: 'smooth', block: 'center' })
      row.classList.add('meeting-row-flash')
      window.setTimeout(() => row.classList.remove('meeting-row-flash'), 1400)
    }, 120)
  }

  const dashboardDeadlineRows = useMemo(() => {
    return calendarDeadlines
      .map((d) => ({ ...d, delta: daysUntil(d.dateKey) }))
      .sort((a, b) => (a.delta ?? 9999) - (b.delta ?? 9999))
  }, [calendarDeadlines])

  const dashboardMiniWeeks = useMemo(() => {
    const year = calendarFocusDate.getFullYear()
    const month = calendarFocusDate.getMonth()
    const first = new Date(year, month, 1)
    const start = new Date(first)
    start.setDate(start.getDate() - start.getDay())
    const last = new Date(year, month + 1, 0)
    const end = new Date(last)
    end.setDate(end.getDate() + (6 - end.getDay()))

    const rows: Date[][] = []
    const cur = new Date(start)
    while (cur <= end) {
      rows.push(
        Array.from({ length: 7 }).map((_, i) => {
          const d = new Date(cur)
          d.setDate(cur.getDate() + i)
          return d
        }),
      )
      cur.setDate(cur.getDate() + 7)
    }
    return rows
  }, [calendarFocusDate])

  const dashboardDateKey = (d: Date) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  const overdueDeadlines = dashboardDeadlineRows.filter((d) => d.delta !== null && d.delta < 0)
  const thisWeekDeadlines = dashboardDeadlineRows.filter((d) => d.delta !== null && d.delta >= 0 && d.delta <= 7)
  const nextUpcomingDeadline = dashboardDeadlineRows.find((d) => d.delta !== null && d.delta >= 0) || null

  const meetingTopics = meetingActionItems.filter((x) => !x.parentId)
  const meetingSubItems = meetingActionItems.filter((x) => !!x.parentId)
  const openMeetingSubItems = meetingSubItems.filter((x) => x.status !== 'closed')

  const meetingCompanies = uniqueSortedTrimmed(meetingAttendees.map((p) => p.company || ''))
  const dashboardMeetingActionItems = openMeetingSubItems.filter((x) => {
    const owner = (x.owner || '').trim().toLowerCase()
    if (!owner) return false
    if (owner === 'aor') return true
    return meetingCompanies.some((company) => owner === company.toLowerCase())
  })

  const consultantRows = Object.entries(consultantItemsByTeam).flatMap(([team, sections]) =>
    ([
      ...sections.toConsultant.map((item) => ({ team, lane: 'To Consultant', ...item })),
      ...sections.fromConsultant.map((item) => ({ team, lane: 'From Consultant', ...item })),
      ...sections.nextDeliverable.map((item) => ({ team, lane: 'Next Deliverable', ...item })),
    ]),
  )

  const consultantCoordRows = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const scoped = consultantRows.filter((row) => {
      if (isConsultantAllView) return row.team !== 'All Teams'
      return row.team === consultantView
    })
    return scoped.map((row) => {
      const dueRaw = String(row.dueDate || '').trim()
      const hasDue = !!dueRaw && dueRaw !== '-'
      const due = hasDue ? new Date(`${dueRaw}T00:00:00`) : null
      const delta = due ? Math.floor((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)) : null
      const status = !hasDue
        ? 'noDue'
        : delta !== null && delta < 0
          ? 'overdue'
          : row.waitingOn === 'consultant'
            ? 'waiting'
            : delta !== null && delta <= 2
              ? 'atRisk'
              : 'onTrack'
      return { ...row, status, sourceCompany: parseConsultantTrackerName(row.team).company || row.sourceCompany }
    })
  }, [consultantRows, isConsultantAllView, consultantView])

  const consultantRiskCounts = useMemo(() => ({
    atRisk: consultantCoordRows.filter((row) => row.status === 'atRisk').length,
    waiting: consultantCoordRows.filter((row) => row.status === 'waiting').length,
    overdue: consultantCoordRows.filter((row) => row.status === 'overdue').length,
    noDue: consultantCoordRows.filter((row) => row.status === 'noDue').length,
  }), [consultantCoordRows])

  const resolveConsultantCompanyName = (teamLabel: string, itemTitle?: string, itemId?: string, sourceCompany?: string) => {
    const explicitSourceCompany = (sourceCompany || '').trim()
    if (explicitSourceCompany) return explicitSourceCompany

    let raw = (teamLabel || '').trim()

    // In All view rows, ids are prefixed as "{team}::{id}"; recover team if assignee label is empty.
    if (!raw && (itemId || '').includes('::')) {
      raw = (itemId || '').split('::')[0].trim()
    }

    const companyMap = new Map<string, string>()
    directoryContacts.forEach((c) => {
      const company = (c.company || '').trim()
      if (!company) return
      companyMap.set(company.toLowerCase(), company)
    })

    const fromTitle = (itemTitle || '').split('—').map((p) => p.trim()).find((part) => companyMap.has(part.toLowerCase()))
    if (fromTitle) return companyMap.get(fromTitle.toLowerCase()) || fromTitle

    if (raw && raw.toLowerCase() !== 'all teams' && raw.toLowerCase() !== 'all') {
      const parsed = parseConsultantTrackerName(raw)
      if ((parsed.company || '').trim()) return parsed.company.trim()

      const parts = raw.split('—').map((p) => p.trim()).filter(Boolean)
      if (parts.length >= 2) {
        const first = parts[0].toLowerCase()
        const last = parts[parts.length - 1].toLowerCase()
        if (companyMap.has(first)) return companyMap.get(first) || parts[0]
        if (companyMap.has(last)) return companyMap.get(last) || parts[parts.length - 1]
      }

      const fromTeam = directoryContacts.find((c) => (c.team || '').trim().toLowerCase() === raw.toLowerCase() && (c.company || '').trim())?.company || ''
      if (fromTeam) return fromTeam
      return raw
    }

    return '—'
  }

  const consultantFilteredFeedRows = useMemo(() => {
    if (consultantRiskFilter === 'all') return consultantCoordRows
    return consultantCoordRows.filter((row) => row.status === consultantRiskFilter)
  }, [consultantCoordRows, consultantRiskFilter])

  const consultantVisibleFeedRows = useMemo(() => (
    consultantFilteredFeedRows.slice(0, consultantFeedExpanded ? consultantFilteredFeedRows.length : 5)
  ), [consultantFilteredFeedRows, consultantFeedExpanded])

  const selectedConsultantFeedRow = useMemo(() => (
    consultantFilteredFeedRows.find((row) => row.id === selectedConsultantFeedId) || consultantFilteredFeedRows[0] || null
  ), [consultantFilteredFeedRows, selectedConsultantFeedId])

  const linkedMeetingDecisions = useMemo(() => {
    if (!selectedConsultantFeedRow) return [] as MeetingActionItem[]
    const q = selectedConsultantFeedRow.title.toLowerCase()
    return meetingActionItems.filter((item) => (item.item || '').toLowerCase().includes(q)).slice(0, 4)
  }, [meetingActionItems, selectedConsultantFeedRow])

  const linkedContactsForConsultant = useMemo(() => {
    const selectedCompanyFromTab = (consultantTrackerMeta.company || '').trim().toLowerCase()
    const selectedTeam = (selectedConsultantFeedRow?.team || consultantView || '').trim()
    const parsed = parseConsultantTrackerName(selectedTeam)
    const rowCompany = (parsed.company || '').trim().toLowerCase()
    const companyFromTeam = (directoryContacts.find((c) => (c.team || '').trim().toLowerCase() === selectedTeam.toLowerCase() && (c.company || '').trim())?.company || '').trim().toLowerCase()

    const isAorTab = /\baor\b|\bcore\b/i.test(consultantView)

    // For AOR/Core tab without an explicit company, show AOR/Core key contacts directly from team mapping.
    // This ensures contacts are visible as soon as the tab is created, even before any feed items exist.
    if (isAorTab && !selectedCompanyFromTab && !rowCompany && !companyFromTeam) {
      const aorPrimary = directoryContacts.filter((c) => /\baor\b|\bcore\b/i.test((c.team || '').trim()) && !!c.primaryContact && !c.archived)
      if (aorPrimary.length) return aorPrimary
      return directoryContacts.filter((c) => /\baor\b|\bcore\b/i.test((c.team || '').trim()) && !c.archived)
    }

    const aorFallbackCompany = (directoryContacts.find((c) => /\baor\b|\bcore\b/i.test((c.team || '').trim()) && (c.company || '').trim())?.company || '').trim().toLowerCase()

    // Requirement: linked key contacts should follow the company selected in Consultant Coordination.
    const targetCompany = selectedCompanyFromTab || rowCompany || companyFromTeam || (isAorTab ? aorFallbackCompany : '')
    if (!targetCompany) return [] as DirectoryContact[]

    const primary = directoryContacts.filter((c) => ((c.company || '').trim().toLowerCase() === targetCompany) && !!c.primaryContact && !c.archived)
    if (primary.length) return primary

    return directoryContacts
      .filter((c) => ((c.company || '').trim().toLowerCase() === targetCompany) && !c.archived)
  }, [directoryContacts, selectedConsultantFeedRow?.team, consultantTrackerMeta.company, consultantView])

  useEffect(() => {
    if (!consultantFilteredFeedRows.length) {
      if (selectedConsultantFeedId) setSelectedConsultantFeedId(null)
      return
    }
    if (!selectedConsultantFeedId || !consultantFilteredFeedRows.some((row) => row.id === selectedConsultantFeedId)) {
      setSelectedConsultantFeedId(consultantFilteredFeedRows[0].id)
    }
  }, [consultantFilteredFeedRows, selectedConsultantFeedId])

  const officeEmployeesAll = useMemo(() => {
    const keyFor = (name: string) => name.trim().toLowerCase()
    const merged = [...defaultOfficeEmployees, ...officeCustomEmployees]
    const deduped = new Map<string, OfficeEmployeeRow>()
    for (const row of merged) {
      const key = keyFor(row.name)
      if (!key || officeRemovedEmployees[key]) continue
      const saved = officeContactOverrides[key]
      deduped.set(key, saved
        ? {
            name: String(saved.name || row.name || ''),
            position: String(saved.position || row.position || ''),
            project: typeof saved.project === 'string' ? saved.project : String(row.project || ''),
            location: String(saved.location || row.location || ''),
          }
        : {
            name: String(row.name || ''),
            position: String(row.position || ''),
            project: String(row.project || ''),
            location: String(row.location || ''),
          })
    }
    return Array.from(deduped.values())
  }, [officeContactOverrides, officeCustomEmployees, officeRemovedEmployees])

  const getEmployeeProjects = (name: string, fallbackProject?: string) => {
    const key = normalizeNameKey(name)

    let override = officeContactOverrides[key]
    if (!override) {
      // Fallback for legacy/stale keys: match by normalized map key OR draft name.
      for (const [rawKey, draft] of Object.entries(officeContactOverrides || {})) {
        const rawNorm = normalizeNameKey(String(rawKey || ''))
        const draftNorm = normalizeNameKey(String(draft?.name || ''))
        if (rawNorm === key || draftNorm === key) {
          override = draft
          break
        }
      }
    }

    const projects = uniqueTrimmedCaseInsensitive([
      ...(override ? (override.projects?.filter(Boolean) || []) : []),
      override ? (override.project || '') : '',
      override ? '' : (fallbackProject || ''),
    ])

    return projects
  }

  const getRowProjectsLikePopup = (row: OfficeEmployeeRow) => {
    const rowKey = normalizeNameKey(row.name)

    const exactKey = officeContactKey(row.name)
    let saved = officeContactOverrides[exactKey]
    if (!saved) {
      for (const [rawKey, entry] of Object.entries(officeContactOverrides || {})) {
        const rawNorm = normalizeNameKey(String(rawKey || ''))
        const draftNorm = normalizeNameKey(String(entry?.name || ''))
        if (rawNorm === rowKey || draftNorm === rowKey) {
          saved = entry
          break
        }
      }
    }

    const primaryProject = saved?.project || row.project
    const accessProjects = Array.isArray(saved?.projects)
      ? saved.projects.filter(Boolean)
      : [primaryProject].filter(Boolean)

    return uniqueTrimmedCaseInsensitive(accessProjects)
  }

  const officeEmployees = useMemo(() => {
    const query = officeEmployeeSearch.trim().toLowerCase()
    const filtered = query
      ? officeEmployeesAll.filter((r) =>
        [r.name, r.position, ...getEmployeeProjects(r.name, r.project)]
          .map((v) => (v || '').toLowerCase())
          .some((v) => v.includes(query)),
      )
      : [...officeEmployeesAll]

    filtered.sort((a, b) => {
      const left = String(a[officeEmployeeSortKey] || '').toLowerCase()
      const right = String(b[officeEmployeeSortKey] || '').toLowerCase()
      const cmp = left.localeCompare(right)
      return officeEmployeeSortDir === 'asc' ? cmp : -cmp
    })
    return filtered
  }, [officeContactOverrides, officeEmployeeSortDir, officeEmployeeSortKey, officeEmployeesAll, officeEmployeeSearch])

  const normalizeProjectKey = (projectValue: string) => projectValue.trim().toLowerCase()
  const isProjectMatch = (projectValue: string, normalizedProjectKey: string) => normalizeProjectKey(projectValue) === normalizedProjectKey

  const dashboardTeamMembers = useMemo(() => {
    const projectKey = projectName.trim().toLowerCase()
    return officeEmployeesAll
      .filter((row) => getRowProjectsLikePopup(row).some((projectValue) => isProjectMatch(projectValue, projectKey)))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [officeEmployeesAll, projectName, officeContactOverrides])

  const effectiveProjectAccess = useMemo(() => {
    const dedupe = (list: string[]) => list
      .map((x) => (x || '').trim())
      .filter(Boolean)
      .filter((x, idx, arr) => arr.findIndex((v) => v.toLowerCase() === x.toLowerCase()) === idx)

    if (!currentProjectAccess.includes('*')) return dedupe(currentProjectAccess)

    if (!(effectiveRole === 'Principal' || effectiveRole === 'Project Manager')) return ['*']

    const username = currentUsername.trim().toLowerCase()
    const account = profileAccounts.find((a) => a.username.trim().toLowerCase() === username || a.email.trim().toLowerCase() === username)
    const accountProjects = account?.project ? [account.project] : []

    const byNameProjects = account?.name
      ? officeEmployeesAll
          .filter((row) => row.name.trim().toLowerCase() === account.name.trim().toLowerCase())
          .flatMap((row) => getRowProjectsLikePopup(row))
      : []

    const scoped = dedupe([...accountProjects, ...byNameProjects])
    return scoped.length ? scoped : [projectName]
  }, [currentProjectAccess, currentUsername, effectiveRole, officeEmployeesAll, profileAccounts, projectName, officeContactOverrides])

  const teamOrganizationProjects = useMemo(() => {
    const employeeProjects = officeEmployeesAll.flatMap((row) => getRowProjectsLikePopup(row).map((p) => p.trim())).filter(Boolean)
    const customCardProjects = customProjectsOverviewCards.map((card) => (card.name || '').trim()).filter(Boolean)
    const overviewProjects = Object.keys(projectOverviewByProject || {}).map((p) => (p || '').trim()).filter(Boolean)

    const merged = [projectName, ...employeeProjects, ...customCardProjects, ...overviewProjects].map((p) => (p || '').trim()).filter(Boolean)
    const byNormalized = new Map<string, string>()
    for (const project of merged) {
      const norm = project.toLowerCase()
      if (!norm || byNormalized.has(norm)) continue
      byNormalized.set(norm, project)
    }

    const all = Array.from(byNormalized.values()).sort((a, b) => a.localeCompare(b))
    const isExecutiveLevel = /executive/i.test(String(effectiveRole || ''))
    if (effectiveProjectAccess.includes('*') || isExecutiveLevel) return all
    const allowed = new Set(effectiveProjectAccess.map((x) => x.trim().toLowerCase()))
    return all.filter((p) => allowed.has(p.trim().toLowerCase()))
  }, [officeEmployeesAll, customProjectsOverviewCards, projectOverviewByProject, projectName, effectiveProjectAccess, effectiveRole, officeContactOverrides])

  const toTitleCase = (value: string) => value.replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())

  const companyProjectsOverviewCards = useMemo(() => {
    const projects = Array.from(new Set([projectName, ...officeEmployeesAll.flatMap((row) => getRowProjectsLikePopup(row))]))
      .map((p) => p.trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))

    const generated = projects.map((name) => {
      const normalizedProject = name.trim().toLowerCase()
      const projectRows = officeEmployeesAll.filter((row) =>
        getRowProjectsLikePopup(row).some((p) => normalizeProjectKey(p) === normalizedProject),
      )
      const projectOverviewForCard = projectOverviewByProject[normalizedProject] || blankProjectOverview()
      return {
        name,
        location: projectOverviewForCard.location || profileOfficePlaceholder,
        employeeCount: projectRows.length,
        buildingPhoto: projectOverviewForCard.buildingPhoto,
        phaseDeadlines: projectOverviewForCard.phaseDeadlines,
        progress: calculateTimelineProgress(projectOverviewForCard),
      }
    })

    const merged = [...generated]
    for (const customCard of customProjectsOverviewCards) {
      if (!merged.some((card) => card.name.trim().toLowerCase() === customCard.name.trim().toLowerCase())) merged.push(customCard)
    }
    return merged.sort((a, b) => a.name.localeCompare(b.name))
  }, [customProjectsOverviewCards, officeContactOverrides, officeEmployeesAll, profileOfficePlaceholder, projectOverviewByProject, projectProgress])

  useEffect(() => {
    try {
      localStorage.setItem('past-project-keys-v1', JSON.stringify(pastProjectKeys))
    } catch {
      // ignore
    }
  }, [pastProjectKeys])

  useEffect(() => {
    try {
      localStorage.setItem('deleted-project-keys-v1', JSON.stringify(deletedProjectKeys))
    } catch {
      // ignore
    }
  }, [deletedProjectKeys])

  const filteredCompanyProjectsOverviewCards = useMemo(() => {
    const q = projectsSearch.trim().toLowerCase()
    const scoped = companyProjectsOverviewCards.filter((card) => {
      const key = card.name.trim().toLowerCase()
      return !pastProjectKeys[key] && !deletedProjectKeys[key]
    })
    if (!q) return scoped
    return scoped.filter((card) => [card.name, card.location].join(' ').toLowerCase().includes(q))
  }, [companyProjectsOverviewCards, projectsSearch, pastProjectKeys])

  const pastProjectsOverviewCards = useMemo(
    () => companyProjectsOverviewCards.filter((card) => {
      const key = card.name.trim().toLowerCase()
      return pastProjectKeys[key] && !deletedProjectKeys[key]
    }),
    [companyProjectsOverviewCards, pastProjectKeys, deletedProjectKeys],
  )

  const projectAccessOptions = useMemo(
    () => companyProjectsOverviewCards.map((card) => card.name).filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [companyProjectsOverviewCards],
  )

  const headerProjectButtons = useMemo(() => {
    if (effectiveProjectAccess.includes('*')) return projectAccessOptions
    const allowed = new Set(effectiveProjectAccess.map((x) => x.trim().toLowerCase()))
    return projectAccessOptions.filter((name) => allowed.has(name.trim().toLowerCase()))
  }, [effectiveProjectAccess, projectAccessOptions])
  const filteredHeaderProjectButtons = useMemo(() => {
    const q = headerProjectSearch.trim().toLowerCase()
    const scoped = headerProjectButtons.filter((name) => {
      const key = name.trim().toLowerCase()
      if (deletedProjectKeys[key]) return false
      if (headerProjectScope === 'past') return Boolean(pastProjectKeys[key])
      if (headerProjectScope === 'current') return !pastProjectKeys[key]
      return true
    })
    if (!q) return scoped
    return scoped.filter((name) => name.toLowerCase().includes(q))
  }, [headerProjectButtons, headerProjectSearch, headerProjectScope, pastProjectKeys, deletedProjectKeys])

  const addProjectsCard = () => {
    setAddProjectAttempted(true)
    const name = newProjectName.trim()
    const location = newProjectLocation.trim()
    const missing: string[] = []
    if (!name) missing.push('Project Name')
    if (!location) missing.push('Project Location')
    if (missing.length > 0) {
      showActionToast(`${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} required`)
      return
    }
    setCustomProjectsOverviewCards((prev) => [
      ...prev,
      {
        name,
        location,
        employeeCount: 0,
        buildingPhoto: '',
        phaseDeadlines: { Concept: '', SD: '', DD: '', CC: '' },
        progress: 0,
      },
    ])
    const key = name.trim().toLowerCase()
    setDeletedProjectKeys((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
    setPastProjectKeys((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
    setNewProjectName('')
    setNewProjectLocation('')
    setAddProjectAttempted(false)
    setShowAddProjectModal(false)
    showCenterToast('Added')
  }

  const openProjectCardModal = (projectNameValue: string) => {
    const key = projectNameValue.trim().toLowerCase()
    const overview = projectOverviewByProject[key] || blankProjectOverview()
    const isCustom = customProjectsOverviewCards.some((card) => card.name.trim().toLowerCase() === key)
    setProjectCardDraft({
      name: projectNameValue,
      client: overview.client || '',
      projectType: overview.projectType || '',
      storyCount: overview.storyCount || '',
      totalSquareFootage: overview.totalSquareFootage || '',
      topOff: overview.topOff || '',
      tco: overview.tco || '',
      co: overview.co || '',
      isCustom,
    })
  }

  const saveProjectCardModal = () => {
    if (!projectCardDraft) return
    const key = projectCardDraft.name.trim().toLowerCase()
    const base = projectOverviewByProject[key] || blankProjectOverview()
    setProjectOverviewByProject((prev) => ({
      ...prev,
      [key]: {
        ...base,
        client: projectCardDraft.client,
        projectType: projectCardDraft.projectType,
        storyCount: projectCardDraft.storyCount,
        totalSquareFootage: projectCardDraft.totalSquareFootage,
        topOff: projectCardDraft.topOff,
        tco: projectCardDraft.tco,
        co: projectCardDraft.co,
      },
    }))
    setProjectCardDraft(null)
    showCenterToast('Saved')
  }

  const deleteProjectCardModal = () => {
    if (!projectCardDraft) return
    const key = projectCardDraft.name.trim().toLowerCase()
    const wasPast = !!pastProjectKeys[key]
    const deletedCustom = customProjectsOverviewCards.find((card) => card.name.trim().toLowerCase() === key) || null

    setCustomProjectsOverviewCards((prev) => prev.filter((card) => card.name.trim().toLowerCase() !== key))
    setPastProjectKeys((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
    setDeletedProjectKeys((prev) => ({ ...prev, [key]: true }))
    setProjectCardDraft(null)

    showActionToast('Project deleted', () => {
      setDeletedProjectKeys((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
      if (wasPast) setPastProjectKeys((prev) => ({ ...prev, [key]: true }))
      if (deletedCustom) setCustomProjectsOverviewCards((prev) => [deletedCustom, ...prev.filter((card) => card.name.trim().toLowerCase() !== key)])
    })
  }

  const toggleProjectPastCurrent = () => {
    if (!projectCardDraft) return
    const key = projectCardDraft.name.trim().toLowerCase()
    const isPast = !!pastProjectKeys[key]
    if (isPast) {
      setPastProjectKeys((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
      setProjectCardDraft(null)
      showCenterToast('Moved to Current Projects')
      return
    }
    setPastProjectKeys((prev) => ({ ...prev, [key]: true }))
    setProjectCardDraft(null)
    showCenterToast('Moved to Past Projects')
  }

  useEffect(() => {
    if (teamOrganizationProjects.length <= 1 && teamOrganizationView === 'Overall') {
      setTeamOrganizationView(teamOrganizationProjects[0] || 'Overall')
      return
    }
    if (teamOrganizationView === 'Overall') return
    if (!teamOrganizationProjects.includes(teamOrganizationView)) {
      setTeamOrganizationView(teamOrganizationProjects.length <= 1 ? (teamOrganizationProjects[0] || 'Overall') : 'Overall')
    }
  }, [teamOrganizationProjects, teamOrganizationView])

  const teamOrganizationRows = useMemo(() => {
    const isExecutiveLevel = /executive/i.test(String(effectiveRole || ''))
    if (teamOrganizationView === 'Overall') {
      const allowed = new Set(teamOrganizationProjects.map((p) => p.trim().toLowerCase()))
      return officeEmployeesAll.filter((row) =>
        getRowProjectsLikePopup(row).some((p) => allowed.has(p.trim().toLowerCase())),
      )
    }

    try {
      const projectKey = String(teamOrganizationView || '').trim().toLowerCase()
      const baseRows = officeEmployeesAll.filter((row) =>
        getRowProjectsLikePopup(row).some((p) => normalizeProjectKey(p) === projectKey),
      )
      if (isExecutiveLevel) return baseRows

      const principalName = officeEmployeesAll.find((emp) =>
        getEmployeeProjects(String(emp.name || ''), emp.project).some((p) => normalizeProjectKey(String(p || '')) === projectKey)
        && /\bprincipal\b/i.test(String(emp.position || '')),
      )?.name?.trim().toLowerCase() || ''

      if (!principalName) return baseRows

      const filtered = baseRows.filter((row) => {
        const reportsTo = String(officeContactOverrides?.[officeContactKey(String(row.name || ''))]?.reportsTo || '').trim().toLowerCase()
        return reportsTo === principalName
      })

      return filtered
    } catch {
      return officeEmployeesAll.filter((row) =>
        getRowProjectsLikePopup(row).some((p) => p.trim().toLowerCase() === String(teamOrganizationView || '').trim().toLowerCase()),
      )
    }
  }, [officeEmployeesAll, teamOrganizationView, teamOrganizationProjects, effectiveRole, officeContactOverrides])

  useEffect(() => {
    try {
      localStorage.setItem('team-org-notes-v1', JSON.stringify(teamOrganizationNotesByProject))
    } catch {
      // ignore
    }
  }, [teamOrganizationNotesByProject])

  useEffect(() => {
    try {
      localStorage.setItem('team-org-hierarchy-v1', JSON.stringify(teamHierarchyByProject))
    } catch {
      // ignore
    }
  }, [teamHierarchyByProject])

  useEffect(() => {
    try {
      localStorage.setItem('team-org-view-v1', teamOrganizationView)
    } catch {
      // ignore
    }
  }, [teamOrganizationView])

  const overallProjectGroups = useMemo(() => {
    const grouped = new Map<string, OfficeEmployeeRow[]>()
    const allowedProjects = new Set(teamOrganizationProjects.map((p) => p.trim().toLowerCase()))

    for (const project of teamOrganizationProjects) {
      const key = (project || '').trim()
      if (!key) continue
      if (!grouped.has(key)) grouped.set(key, [])
    }

    const sourceRows = officeEmployeesAll
    for (const row of sourceRows) {
      const rowProjects = getRowProjectsLikePopup(row)
      for (const project of rowProjects) {
        const projectKey = project.trim().toLowerCase()
        if (!allowedProjects.has(projectKey)) continue
        const existingKey = Array.from(grouped.keys()).find((k) => normalizeProjectKey(k) === projectKey) || project
        const list = grouped.get(existingKey) || []
        list.push({ ...row, project })
        grouped.set(existingKey, list)
      }
    }

    return Array.from(grouped.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([project, rows]) => {
        const hierarchy = teamHierarchyByProject[project] || { principal: [], projectManager: [], drafter: [] }
        const orderedNames = [
          ...hierarchy.principal,
          ...hierarchy.projectManager,
          ...hierarchy.drafter,
        ].map((name) => name.trim().toLowerCase())
        const orderIndex = new Map<string, number>()
        orderedNames.forEach((name, idx) => {
          if (!orderIndex.has(name)) orderIndex.set(name, idx)
        })

        const sortedRows = [...rows].sort((a, b) => {
          const aKey = a.name.trim().toLowerCase()
          const bKey = b.name.trim().toLowerCase()
          const aIdx = orderIndex.get(aKey)
          const bIdx = orderIndex.get(bKey)
          const aRank = aIdx === undefined ? Number.POSITIVE_INFINITY : aIdx
          const bRank = bIdx === undefined ? Number.POSITIVE_INFINITY : bIdx
          if (aRank !== bRank) return aRank - bRank
          return a.name.localeCompare(b.name)
        })

        const assignedNameKeys = new Set(sortedRows.map((row) => row.name.trim().toLowerCase()))

        const projectManagerNames = Array.from(new Set((hierarchy.projectManager || [])
          .map((name) => name.trim())
          .filter((name) => assignedNameKeys.has(name.toLowerCase()))))

        if (projectManagerNames.length === 0) {
          const fallbackPms = sortedRows
            .filter((row) => /project\s*manager|manager/i.test(row.position || ''))
            .map((row) => row.name.trim())
            .filter(Boolean)
          projectManagerNames.push(...Array.from(new Set(fallbackPms)))
        }

        const principalNames = new Set(
          (hierarchy.principal || [])
            .map((name) => name.trim().toLowerCase())
            .filter((name) => assignedNameKeys.has(name)),
        )

        return {
          project,
          rows: sortedRows,
          projectManagerNames,
          projectManagerLabel: projectManagerNames.join(', '),
          principalNames,
        }
      })
  }, [officeEmployeesAll, teamHierarchyByProject, teamOrganizationProjects, officeContactOverrides])

  const filteredOverallProjectGroups = useMemo(() => {
    const q = teamOrgOverallSearch.trim().toLowerCase()
    if (!q) return overallProjectGroups
    return overallProjectGroups.filter((group) => {
      const projectMatch = group.project.toLowerCase().includes(q)
      const pmMatch = (group.projectManagerLabel || '').toLowerCase().includes(q)
      const memberMatch = group.rows.some((row) => row.name.toLowerCase().includes(q) || (row.position || '').toLowerCase().includes(q))
      return projectMatch || pmMatch || memberMatch
    })
  }, [overallProjectGroups, teamOrgOverallSearch])

  const globalHierarchyAssignedNames = useMemo(() => {
    const assigned = new Set<string>()
    Object.values(teamHierarchyByProject || {}).forEach((group) => {
      ;(group?.principal || []).forEach((name) => assigned.add(String(name || '').trim().toLowerCase()))
      ;(group?.projectManager || []).forEach((name) => assigned.add(String(name || '').trim().toLowerCase()))
      ;(group?.drafter || []).forEach((name) => assigned.add(String(name || '').trim().toLowerCase()))
    })
    return assigned
  }, [teamHierarchyByProject])

  const overallStaffNotPlaced = useMemo(() => {
    const byName = new Map<string, OfficeEmployeeRow>()

    officeEmployeesAll.forEach((row) => {
      const key = normalizeNameKey(row.name)
      if (!key) return

      // Strict rule: same project resolution model as the contact-card popup.
      const currentProjects = getRowProjectsLikePopup(row)
      if (currentProjects.length > 0) return

      if (globalHierarchyAssignedNames.has(key)) return
      if (!byName.has(key)) byName.set(key, row)
    })

    return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [officeEmployeesAll, globalHierarchyAssignedNames, officeContactOverrides])

  const teamOrganizationKey = teamOrganizationView === 'Overall' ? 'Overall' : teamOrganizationView

  useEffect(() => {
    if (!teamOrganizationRows.length) return
    setTeamHierarchyByProject((prev) => {
      if (prev[teamOrganizationKey]) return prev

      const principal = teamOrganizationRows
        .filter((row) => /principal|executive|president|ceo/i.test(row.position || ''))
        .map((row) => row.name)
        .slice(0, 1)

      const projectManagers = teamOrganizationRows
        .filter((row) => /project\s*manager|manager/i.test(row.position || ''))
        .map((row) => row.name)

      const drafters = teamOrganizationRows
        .filter((row) => /drafter|draft/i.test(row.position || ''))
        .map((row) => row.name)

      return {
        ...prev,
        [teamOrganizationKey]: {
          principal,
          projectManager: Array.from(new Set(projectManagers)),
          drafter: Array.from(new Set(drafters)),
        },
      }
    })
  }, [teamOrganizationKey, teamOrganizationRows])

  const teamOrganizationHierarchy = useMemo(
    () => teamHierarchyByProject[teamOrganizationKey] || { principal: [], projectManager: [], drafter: [] },
    [teamHierarchyByProject, teamOrganizationKey],
  )

  const teamOrganizationStaffNotPlaced = useMemo(
    () => overallStaffNotPlaced,
    [overallStaffNotPlaced],
  )

  const findTeamOrganizationRow = (name: string) => teamOrganizationRows.find((row) => row.name === name)
  const canDragAcrossProjects = true
  const teamOrgTargetProject = teamOrganizationView === 'Overall' ? (teamOrganizationProjects[0] || 'Project') : teamOrganizationView
  const teamOrgTransferCandidates = useMemo(() => {
    const targetProjectKey = teamOrgTargetProject.trim().toLowerCase()
    const existingInTarget = new Set(teamOrganizationRows.map((row) => row.name.trim().toLowerCase()))
    const byName = new Map<string, OfficeEmployeeRow>()
    officeEmployeesAll.forEach((row) => {
      const name = row.name.trim()
      if (!name) return
      const key = name.toLowerCase()
      if (existingInTarget.has(key)) return
      const rowProjects = getRowProjectsLikePopup(row).map((p) => p.trim().toLowerCase())
      if (targetProjectKey && rowProjects.includes(targetProjectKey)) return
      if (!byName.has(key)) byName.set(key, row)
    })
    return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [officeEmployeesAll, teamOrgTargetProject, teamOrganizationRows])

  const teamOrgSelectedMemberProjects = useMemo(() => {
    const selectedName = teamOrgNewStaffName.trim()
    if (!selectedName) return [] as string[]
    return getEmployeeProjects(selectedName, teamOrgNewStaffCurrentProject)
  }, [teamOrgNewStaffName, teamOrgNewStaffCurrentProject, officeContactOverrides, officeEmployeesAll])

  const setDragAvatarPreview = (event: React.DragEvent, employeeName: string) => {
    if (teamOrganizationView !== 'Overall') return
    if (!event.dataTransfer) return

    const initials = employeeName
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || 'NA'

    const ghost = document.createElement('div')
    ghost.style.width = '44px'
    ghost.style.height = '44px'
    ghost.style.borderRadius = '999px'
    ghost.style.background = 'var(--secondary-accent)'
    ghost.style.color = 'var(--text)'
    ghost.style.display = 'inline-flex'
    ghost.style.alignItems = 'center'
    ghost.style.justifyContent = 'center'
    ghost.style.fontWeight = '700'
    ghost.style.fontSize = '12px'
    ghost.style.boxShadow = '0 8px 18px rgba(0,0,0,0.22)'
    ghost.style.border = '1px solid color-mix(in srgb, var(--line) 45%, white)'
    ghost.textContent = initials

    document.body.appendChild(ghost)
    event.dataTransfer.setDragImage(ghost, 22, 22)
    requestAnimationFrame(() => {
      ghost.remove()
    })
  }

  const moveEmployeeToProject = (employeeName: string, targetProject: string) => {
    const wanted = employeeName.trim().toLowerCase()
    const matches = officeEmployeesAll.filter((row) => row.name.trim().toLowerCase() === wanted)
    const source = matches.find((row) => getRowProjectsLikePopup(row).length === 0) || matches[0]
    if (!source) {
      showCenterToast(`Could not place ${employeeName}`)
      return
    }
    const nextProject = targetProject.trim()
    if (!nextProject) return
    const key = officeContactKey(employeeName)
    const currentProjects = getEmployeeProjects(employeeName, source.project)
    const removedProjects = currentProjects.filter((p) => p.trim().toLowerCase() !== nextProject.toLowerCase())
    setOfficeContactOverrides((prev) => {
      const existing = prev[key]
      if (currentProjects.length === 1 && currentProjects[0].trim().toLowerCase() === nextProject.toLowerCase()) return prev
      return {
        ...prev,
        [key]: {
          ...(existing || {}),
          name: existing?.name || source.name,
          position: existing?.position || source.position,
          location: existing?.location || source.location,
          project: nextProject,
          projects: [nextProject],
          reportsTo: getProjectPrincipal(nextProject) || existing?.reportsTo || '',
        },
      }
    })
    setTeamOrgJustDroppedProject(nextProject)
    window.setTimeout(() => {
      setTeamOrgJustDroppedProject((prev) => (prev && prev.toLowerCase() === nextProject.toLowerCase() ? null : prev))
    }, 220)
    const removedText = removedProjects.length
      ? ` (removed from ${removedProjects.map((p) => toTitleCase(p)).join(', ')})`
      : ''
    showCenterToast(`Moved ${employeeName} to ${toTitleCase(nextProject)}${removedText}`)
  }

  const addEmployeeToProject = (employeeName: string, targetProject: string) => {
    const wanted = employeeName.trim().toLowerCase()
    const matches = officeEmployeesAll.filter((row) => row.name.trim().toLowerCase() === wanted)
    const source = matches[0]
    if (!source) {
      showCenterToast(`Could not add ${employeeName}`)
      return
    }
    const nextProject = targetProject.trim()
    if (!nextProject) return
    const key = officeContactKey(employeeName)
    setOfficeContactOverrides((prev) => {
      const existing = prev[key]
      const currentProjects = getEmployeeProjects(employeeName, source.project)
      if (currentProjects.some((p) => p.trim().toLowerCase() === nextProject.toLowerCase())) return prev
      const mergedProjects = [...currentProjects, nextProject]
      const primaryProject = existing?.project || source.project || nextProject
      return {
        ...prev,
        [key]: {
          ...(existing || {}),
          name: existing?.name || source.name,
          position: existing?.position || source.position,
          location: existing?.location || source.location,
          project: primaryProject,
          projects: mergedProjects,
          reportsTo: existing?.reportsTo || getProjectPrincipal(primaryProject) || '',
        },
      }
    })
    setTeamOrgJustDroppedProject(nextProject)
    window.setTimeout(() => {
      setTeamOrgJustDroppedProject((prev) => (prev && prev.toLowerCase() === nextProject.toLowerCase() ? null : prev))
    }, 220)
    showCenterToast(`Added ${employeeName} to ${toTitleCase(nextProject)}`)
  }

  const unassignEmployeeFromAllProjects = (employeeName: string) => {
    const wanted = employeeName.trim().toLowerCase()
    const matches = officeEmployeesAll.filter((row) => row.name.trim().toLowerCase() === wanted)
    const source = matches[0]
    if (!source) return
    const currentProjects = getEmployeeProjects(employeeName, source.project)
    if (currentProjects.length === 0) return
    const key = officeContactKey(employeeName)
    setOfficeContactOverrides((prev) => {
      const existing = prev[key]
      return {
        ...prev,
        [key]: {
          ...(existing || {}),
          name: existing?.name || source.name,
          position: existing?.position || source.position,
          location: existing?.location || source.location,
          project: '',
          projects: [],
          reportsTo: '',
        },
      }
    })
    showCenterToast(`Moved ${employeeName} to Staff Not Placed`)
  }

  const handleDropToOverallUnplaced = (e: any) => {
    if (!canDragAcrossProjects) return
    e.preventDefault()
    e.stopPropagation()
    const employeeName = getValidTeamOrgDropEmployee(e) || teamOrgDraggingEmployeeRef.current || teamOrgDraggingEmployee
    if (employeeName) unassignEmployeeFromAllProjects(employeeName)
    clearTeamOrgDragState()
  }

  const handleDropToProjectUnplaced = (e: any) => {
    e.preventDefault()
    e.stopPropagation()
    const employeeName = getValidTeamOrgDropEmployee(e) || teamOrgDraggingEmployeeRef.current || teamOrgDraggingEmployee
    if (employeeName) {
      removeEmployeeFromCurrentHierarchy(employeeName)
      if (canDragAcrossProjects) unassignEmployeeFromAllProjects(employeeName)
    }
    clearTeamOrgDragState()
  }

  const finalizeTeamOrgDrag = () => {
    const activeDragName = teamOrgDraggingEmployeeRef.current || teamOrgDraggingEmployee
    if ((teamOrgOverallDropProject === '__unplaced__' || teamOrgWasOverUnplacedRef.current) && activeDragName) {
      unassignEmployeeFromAllProjects(activeDragName)
    }
    clearTeamOrgDragState()
  }

  const updateTeamOrganizationHierarchy = (next: { principal: string[]; projectManager: string[]; drafter: string[] }) => {
    setTeamHierarchyByProject((prev) => ({ ...prev, [teamOrganizationKey]: next }))
  }

  const triggerTeamOrgConnectorAnimation = () => {
    setTeamOrgConnectorAnimating(false)
    setTimeout(() => setTeamOrgConnectorAnimating(true), 0)
    setTimeout(() => setTeamOrgConnectorAnimating(false), 210)
  }

  const startTeamOrgDrag = (e: any, employeeName: string) => {
    teamOrgJustDraggedRef.current = true
    teamOrgDraggingEmployeeRef.current = employeeName
    setTeamOrgDraggingEmployee(employeeName)

    const dt = e?.dataTransfer
    if (dt) {
      dt.effectAllowed = 'move'
      try {
        dt.setData('text/plain', employeeName)
      } catch {
        // Some browsers are stricter about drag payload APIs; keep drag usable.
      }
    }

    try {
      setDragAvatarPreview(e, employeeName)
    } catch {
      // Custom drag preview is cosmetic; never block dragging if it fails.
    }
  }

  const getValidTeamOrgDropEmployee = (e: any) => {
    // Prefer ref (sync) first, then state.
    if (teamOrgDraggingEmployeeRef.current) return teamOrgDraggingEmployeeRef.current
    if (teamOrgDraggingEmployee) return teamOrgDraggingEmployee

    const employeeName = (e.dataTransfer.getData('text/plain') || '').trim()
    return employeeName || null
  }

  const clearTeamOrgDragState = () => {
    teamOrgWasOverUnplacedRef.current = false
    teamOrgDraggingEmployeeRef.current = null
    setTeamOrgOverallDropProject(null)
    setTeamOrgDropTarget(null)
    setTeamOrgDraggingEmployee(null)
    window.setTimeout(() => { teamOrgJustDraggedRef.current = false }, 180)
  }

  const openTeamOrgCard = (row: OfficeEmployeeRow) => {
    if (teamOrgJustDraggedRef.current) {
      teamOrgJustDraggedRef.current = false
      return
    }
    openOfficeContactCard(row)
  }

  const removeEmployeeFromCurrentHierarchy = (employeeName: string) => {
    const current = teamHierarchyByProject[teamOrganizationKey] || { principal: [], projectManager: [], drafter: [] }
    const cleaned = {
      principal: current.principal.filter((name) => name !== employeeName),
      projectManager: current.projectManager.filter((name) => name !== employeeName),
      drafter: current.drafter.filter((name) => name !== employeeName),
    }
    updateTeamOrganizationHierarchy(cleaned)
    triggerTeamOrgConnectorAnimation()
  }

  const moveEmployeeToHierarchyLevel = (employeeName: string, level: 'principal' | 'projectManager' | 'drafter') => {
    // In project-specific view, assigning to a role also assigns the employee to that project.
    if (teamOrganizationView !== 'Overall') {
      moveEmployeeToProject(employeeName, teamOrganizationView)
    }

    const current = teamHierarchyByProject[teamOrganizationKey] || { principal: [], projectManager: [], drafter: [] }
    const cleaned = {
      principal: current.principal.filter((name) => name !== employeeName),
      projectManager: current.projectManager.filter((name) => name !== employeeName),
      drafter: current.drafter.filter((name) => name !== employeeName),
    }
    if (!cleaned[level].includes(employeeName)) cleaned[level].push(employeeName)
    updateTeamOrganizationHierarchy(cleaned)
    triggerTeamOrgConnectorAnimation()
  }

  const resetTeamOrgAddStaffModal = () => {
    setShowTeamOrgAddStaffModal(false)
    setTeamOrgAddStaffAttempted(false)
    setTeamOrgNewStaffName('')
    setTeamOrgNewStaffPosition('')
    setTeamOrgNewStaffCurrentProject('')
    setTeamOrgAddStaffAction('move')
  }

  const moveTeamOrgStaffMember = () => {
    setTeamOrgAddStaffAttempted(true)
    const name = teamOrgNewStaffName.trim()
    if (!name) {
      showActionToast('Name is required')
      return
    }
    moveEmployeeToProject(name, teamOrgTargetProject)
    resetTeamOrgAddStaffModal()
  }

  const addTeamOrgStaffToProject = () => {
    setTeamOrgAddStaffAttempted(true)
    const name = teamOrgNewStaffName.trim()
    if (!name) {
      showActionToast('Name is required')
      return
    }
    addEmployeeToProject(name, teamOrgTargetProject)
    resetTeamOrgAddStaffModal()
  }

  const applyTeamOrgAddStaffAction = () => {
    if (teamOrgAddStaffAction === 'move') {
      moveTeamOrgStaffMember()
      return
    }
    addTeamOrgStaffToProject()
  }

  const toggleOfficeEmployeeSort = (key: keyof OfficeEmployeeRow) => {
    if (officeEmployeeSortKey === key) {
      setOfficeEmployeeSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      return
    }
    setOfficeEmployeeSortKey(key)
    setOfficeEmployeeSortDir('asc')
  }

  const normalizeSheetIndexKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
  const sheetIndexMarkerColumnKeys = useMemo(
    () => new Set(sheetIndexMarkerColumns.map((c) => normalizeSheetIndexKey(c))),
    [sheetIndexMarkerColumns],
  )

  const getProjectPrincipal = (project: string) => {
    const normalizedProject = project.trim().toLowerCase()
    if (!normalizedProject) return ''
    const principal = officeEmployeesAll.find((emp) =>
      getEmployeeProjects(emp.name, emp.project).some((p) => normalizeProjectKey(p) === normalizedProject)
      && /\bprincipal\b/i.test(emp.position || ''),
    )
    return principal?.name || ''
  }

  const openOfficeContactCard = (row: OfficeEmployeeRow) => {
    const match = directoryContacts.find((c) => c.name.trim().toLowerCase() === row.name.trim().toLowerCase())
    const saved = officeContactOverrides[officeContactKey(row.name)]
    const accountMatch = profileAccounts.find((a) => a.name.trim().toLowerCase() === row.name.trim().toLowerCase())
    setOfficeCardIsNew(false)
    const primaryProject = saved?.project || row.project
    const accessProjects = Array.isArray((saved as OfficeContactCardDraft | undefined)?.projects)
      ? (saved as OfficeContactCardDraft).projects.filter(Boolean)
      : [primaryProject].filter(Boolean)
    setOfficeContactCardDraft({
      name: saved?.name || row.name,
      position: saved?.position || row.position,
      accessTier: (saved as OfficeContactCardDraft | undefined)?.accessTier || accountMatch?.tier || '',
      project: primaryProject,
      projects: accessProjects.length ? accessProjects : [primaryProject].filter(Boolean),
      location: saved?.location || row.location,
      reportsTo: saved?.reportsTo || getProjectPrincipal(primaryProject),
      pastProjects: (saved as OfficeContactCardDraft | undefined)?.pastProjects || '',
      email: saved?.email || match?.email || '',
      phone: saved?.phone || formatPhone(match?.phone) || '',
      extension: saved?.extension || '',
      cell: saved?.cell || formatPhone(match?.altPhone) || '',
    })
    setOfficeContactSaveAttempted(false)
    setShowOfficeContactCard(true)
  }

  const openAddOfficeContactCard = () => {
    setOfficeCardIsNew(true)
    setOfficeContactCardDraft({
      name: '',
      position: '',
      accessTier: '',
      project: '',
      projects: [''],
      location: '',
      reportsTo: '',
      pastProjects: '',
      email: '',
      phone: '',
      extension: '',
      cell: '',
    })
    setOfficeContactSaveAttempted(false)
    setShowOfficeContactCard(true)
  }

  const saveOfficeContactCard = () => {
    if (!officeContactCardDraft) return
    setOfficeContactSaveAttempted(true)
    const missing: string[] = []
    const key = officeContactKey(officeContactCardDraft.name)
    if (!key) missing.push('Employee Name')
    if (!officeContactCardDraft.accessTier.trim()) missing.push('Web Access')
    if (missing.length > 0) {
      showActionToast(`${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} required`)
      return
    }
    setOfficeContactOverrides((prev) => ({ ...prev, [key]: officeContactCardDraft }))
    setOfficeRemovedEmployees((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
    if (officeCardIsNew) {
      setOfficeCustomEmployees((prev) => {
        if (prev.some((x) => officeContactKey(x.name) === key)) return prev
        return [
          {
            name: officeContactCardDraft.name,
            position: officeContactCardDraft.position,
            project: officeContactCardDraft.project,
            location: officeContactCardDraft.location,
          },
          ...prev,
        ]
      })
    }
    setOfficeContactSaveAttempted(false)
    setShowOfficeContactCard(false)
    showCenterToast('Saved')
  }

  const deleteOfficeContactCard = () => {
    if (!officeContactCardDraft) return
    const key = officeContactKey(officeContactCardDraft.name)
    if (!key) return

    const deletedDraft = officeContactCardDraft
    const deletedCustom = officeCustomEmployees.find((x) => officeContactKey(x.name) === key) || null
    const deletedOverride = officeContactOverrides[key] || null

    setShowOfficeContactCard(false)
    setOfficeRemovedEmployees((prev) => ({ ...prev, [key]: true }))
    setOfficeCustomEmployees((prev) => prev.filter((x) => officeContactKey(x.name) !== key))

    setActionToast({
      message: '',
      undo: () => {
        setOfficeRemovedEmployees((prev) => {
          const next = { ...prev }
          delete next[key]
          return next
        })
        if (deletedCustom) setOfficeCustomEmployees((prev) => [deletedCustom, ...prev.filter((x) => officeContactKey(x.name) !== key)])
        if (deletedOverride) setOfficeContactOverrides((prev) => ({ ...prev, [key]: deletedOverride }))
        else setOfficeContactOverrides((prev) => {
          const next = { ...prev }
          delete next[key]
          return next
        })
        setOfficeContactCardDraft(deletedDraft)
      },
    })
    window.setTimeout(() => setActionToast(null), 3000)
  }

  useEffect(() => {
    try {
      localStorage.setItem('office-contact-overrides-v1', JSON.stringify(officeContactOverrides))
    } catch {
      // ignore storage failures
    }
  }, [officeContactOverrides])

  useEffect(() => {
    try {
      localStorage.setItem('office-custom-employees-v1', JSON.stringify(officeCustomEmployees))
    } catch {
      // ignore storage failures
    }
  }, [officeCustomEmployees])

  useEffect(() => {
    try {
      localStorage.setItem('office-removed-employees-v1', JSON.stringify(officeRemovedEmployees))
    } catch {
      // ignore storage failures
    }
  }, [officeRemovedEmployees])

  useEffect(() => {
    try {
      localStorage.setItem('odp-profile-accounts-v1', JSON.stringify(profileAccounts))
    } catch {
      // ignore storage failures
    }
  }, [profileAccounts])

  useEffect(() => {
    const norm = (v?: string) => (v || '').trim().toLowerCase()
    const isAorTeam = (team?: string) => /\baor\s*team\b|\baor\b/i.test((team || '').trim())

    const sourceRows = officeEmployees.filter((row) => norm(row.project) === norm(projectName))
    const sourceByName = new Map(sourceRows.map((row) => [norm(row.name), row]))

    setDirectoryContacts((prev) => {
      let changed = false

      const next = prev
        .filter((contact) => {
          if (!contact.autoFromOffice) return true
          const keep = sourceByName.has(norm(contact.name))
          if (!keep) changed = true
          return keep
        })
        .map((contact) => {
          if (!contact.autoFromOffice) return contact
          const src = sourceByName.get(norm(contact.name))
          if (!src) return contact

          const saved = officeContactOverrides[officeContactKey(src.name)]
          const updated: DirectoryContact = {
            ...contact,
            name: src.name,
            role: src.position || contact.role,
            team: 'AOR Team',
            company: companyName,
            email: saved?.email || contact.email,
            phone: saved?.phone || contact.phone,
            phoneType: 'Office',
            altPhone: saved?.cell || contact.altPhone,
            altPhoneType: 'Cell',
            autoFromOffice: true,
          }
          if (JSON.stringify(updated) !== JSON.stringify(contact)) changed = true
          return updated
        })

      for (const src of sourceRows) {
        const exists = next.some((c) => norm(c.name) === norm(src.name) && isAorTeam(c.team))
        if (exists) continue
        const saved = officeContactOverrides[officeContactKey(src.name)]
        next.unshift({
          name: src.name,
          role: src.position || 'Role',
          team: 'AOR Team',
          company: companyName,
          email: saved?.email || '',
          phone: saved?.phone || '',
          phoneType: 'Office',
          altPhone: saved?.cell || '',
          altPhoneType: 'Cell',
          autoFromOffice: true,
        })
        changed = true
      }

      return changed ? next : prev
    })
  }, [officeEmployees, officeContactOverrides, projectName, companyName])

  const isOfficeOrganizationTab = activeTab === 'officeOverview'

  const baseConsultantTypeSections: Array<{ type: string, companies: string[] }> = [
    { type: 'MEP', companies: ['MEP Partners', 'Systems Design Group'] },
    { type: 'Structural', companies: ['Structural Group', 'Frame Engineering'] },
    { type: 'Civil', companies: ['Civil Works Studio'] },
    { type: 'Landscape', companies: ['Landscape Studio'] },
    { type: 'Lighting', companies: ['Lumen Consultants'] },
    { type: 'Cost Consultant', companies: ['Cost Advisory Group'] },
  ]

  const consultantProjectsByCompany: Record<string, string[]> = {
    'MEP::MEP Partners': ['Tower 36', 'Cloud One'],
    'MEP::Systems Design Group': ['Cloud One'],
    'Structural::Structural Group': ['Tower 36'],
    'Structural::Frame Engineering': ['Project North'],
    'Civil::Civil Works Studio': ['Tower 36'],
    'Landscape::Landscape Studio': ['Cloud One'],
    'Lighting::Lumen Consultants': ['Tower 36', 'Project North'],
    'Cost Consultant::Cost Advisory Group': ['Cloud One', 'Project North'],
  }

  const [customConsultantCompanies, setCustomConsultantCompanies] = useState<Array<{ type: string, company: string }>>([])
  const consultantTypeSections = useMemo<Array<{ type: string, companies: string[] }>>(() => {
    const grouped = new Map<string, Set<string>>()
    for (const section of baseConsultantTypeSections) {
      if (!grouped.has(section.type)) grouped.set(section.type, new Set())
      for (const company of section.companies) grouped.get(section.type)?.add(company)
    }
    for (const row of customConsultantCompanies) {
      const type = row.type.trim()
      const company = row.company.trim()
      if (!type || !company) continue
      if (!grouped.has(type)) grouped.set(type, new Set())
      grouped.get(type)?.add(company)
    }
    return Array.from(grouped.entries()).map(([type, companies]) => ({ type, companies: Array.from(companies) }))
  }, [customConsultantCompanies])
  const [selectedConsultantCompany, setSelectedConsultantCompany] = useState<{ type: string, company: string } | null>(null)
  const [showAddConsultantCompany, setShowAddConsultantCompany] = useState(false)
  const [newConsultantType, setNewConsultantType] = useState('')
  const [newConsultantCompany, setNewConsultantCompany] = useState('')
  const [consultantReviewDraftRating, setConsultantReviewDraftRating] = useState(0)
  const [consultantReviewDraftNotes, setConsultantReviewDraftNotes] = useState('')
  const [editingConsultantReviewId, setEditingConsultantReviewId] = useState<string | null>(null)
  const [consultantCompanyPhotos, setConsultantCompanyPhotos] = useState<Record<string, string>>({})
  const [consultantCompanyDetails, setConsultantCompanyDetails] = useState<Record<string, {
    location: string
    yearsWorkingTogether: string
    primaryContactName: string
    primaryContactEmail: string
    primaryContactPhone: string
  }>>({})
  const [hiddenConsultantCompanies, setHiddenConsultantCompanies] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setEditingConsultantReviewId(null)
    setConsultantReviewDraftNotes('')
    setConsultantReviewDraftRating(0)
  }, [selectedConsultantCompany?.type, selectedConsultantCompany?.company])


  const completeTwoFactorLogin = async () => {
    if (!twoFactorChallengeId) return
    setLoginPending(true)
    setTwoFactorError('')
    try {
      const res = await apiFetch(`${apiBase}/api/auth/2fa/challenge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challengeId: twoFactorChallengeId, code: twoFactorCodeInput, recoveryCode: twoFactorRecoveryInput }),
      })
      const data = await res.json()
      if (!res.ok || !data?.ok || !data?.token || !data?.expiresAt) {
        setTwoFactorError(data?.error || 'Invalid authentication code')
        return
      }
      const username = loginUsername.trim().toLowerCase()
      localStorage.setItem('odp-auth-token-v1', data.token)
      localStorage.setItem('odp-auth-exp-v1', String(Number(data.expiresAt)))
      localStorage.setItem('odp-auth-user-v1', username)
      const normalizedRole = normalizeRole(String(data?.profile?.accessTier || 'Executive'))
      const projects = Array.isArray(data?.profile?.projects) ? data.profile.projects.map((x: unknown) => String(x)) : ['*']
      localStorage.setItem('odp-auth-role-v1', normalizedRole)
      localStorage.setItem('odp-auth-projects-v1', JSON.stringify(projects))
      localStorage.setItem('odp-tenant-id-v1', String(data.tenantId || tenantId || 'default'))
      setTenantId(String(data.tenantId || tenantId || 'default'))
      setCurrentUsername(username)
      setCurrentRole(normalizedRole)
      setCurrentProjectAccess(projects)
      setShowTwoFactorModal(false)
      setTwoFactorChallengeId('')
      setIsAuthenticated(true)
      navigateTo('app')
    } catch {
      setTwoFactorError('Two-factor service unavailable')
    } finally {
      setLoginPending(false)
    }
  }

  const startTwoFactorEnrollment = async () => {
    setTwoFactorError('')
    try {
      const res = await apiFetch(`${apiBase}/api/auth/2fa/enroll/start`, { method: 'POST' })
      const data = await res.json()
      if (!res.ok || !data?.ok) {
        setTwoFactorError(data?.error || 'Unable to start 2FA enrollment')
        return
      }
      setTwoFactorEnrollmentSecret(String(data.secret || ''))
      setTwoFactorEnrollmentQr(String(data.qrDataUrl || ''))
    } catch {
      setTwoFactorError('Unable to reach 2FA service')
    }
  }

  const verifyTwoFactorEnrollment = async () => {
    setTwoFactorError('')
    try {
      const res = await apiFetch(`${apiBase}/api/auth/2fa/enroll/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: twoFactorEnrollmentCode }),
      })
      const data = await res.json()
      if (!res.ok || !data?.ok) {
        setTwoFactorError(data?.error || 'Invalid setup code')
        return
      }
      setTwoFactorRecoveryCodes(Array.isArray(data?.recoveryCodes) ? data.recoveryCodes : [])
      showCenterToast('Two-factor enabled')
    } catch {
      setTwoFactorError('Unable to verify 2FA setup')
    }
  }

  const submitLogin = async () => {
    const username = loginUsername.trim()
    const normalizedTenantId = tenantId.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '') || 'default'
    if (!username || !loginPassword) {
      setLoginError('Enter username and password')
      return
    }
    setLoginPending(true)
    try {
      const res = await apiFetch(`${apiBase}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password: loginPassword, remember: loginRemember, tenantId: normalizedTenantId }),
      })
      const raw = await res.text()
      let data: any = null
      try { data = raw ? JSON.parse(raw) : null } catch { data = null }
      if (data?.ok && data?.requiresTwoFactor && data?.challengeId) {
        setTwoFactorChallengeId(String(data.challengeId))
        setTwoFactorCodeInput('')
        setTwoFactorRecoveryInput('')
        setTwoFactorError('')
        setShowTwoFactorModal(true)
        setLoginError('')
        return
      }
      if (!res.ok || !data?.ok || !data?.token || !data?.expiresAt) {
        if (!data) {
          setLoginError(`Login failed (${res.status})`)
        } else {
          setLoginError(data?.error || 'Invalid username or password')
        }
        return
      }
      localStorage.setItem('odp-auth-token-v1', data.token)
      localStorage.setItem('odp-auth-exp-v1', String(Number(data.expiresAt)))
      localStorage.setItem('odp-auth-user-v1', username)
      const normalizedRole = normalizeRole(String(data?.profile?.accessTier || 'Executive'))
      const projects = Array.isArray(data?.profile?.projects) ? data.profile.projects.map((x: unknown) => String(x)) : ['*']
      localStorage.setItem('odp-auth-role-v1', normalizedRole)
      localStorage.setItem('odp-auth-projects-v1', JSON.stringify(projects))
      localStorage.setItem('odp-tenant-id-v1', String(data.tenantId || normalizedTenantId || 'default'))
      setTenantId(String(data.tenantId || normalizedTenantId || 'default'))
      setCurrentUsername(username)
      setCurrentRole(normalizedRole)
      setCurrentProjectAccess(projects)
      const requirePasswordChange = Boolean(data?.mustChangePassword)
      setMustChangePassword(requirePasswordChange)
      if (requirePasswordChange) setShowChangePasswordModal(true)
      setLoginError('')
      setIsAuthenticated(true)
      setLoginUsername('')
      setLoginPassword('')
      navigateTo('app')
    } catch (err) {
      setLoginError(`Login service unavailable (${err instanceof Error ? err.message : 'network error'})`)
    } finally {
      setLoginPending(false)
    }
  }

  if (routeView === 'app' && isAuthenticated && !siteSettingsHydrated) {
    return (
      <div className="public-wrap">
        <main className="landing-main" style={{ minHeight: '50vh', display: 'grid', placeItems: 'center' }}>
          <p className="muted">Loading your saved website settings…</p>
        </main>
      </div>
    )
  }

  if (routeView !== 'app') {
    return (
      <div className="public-wrap">
        {routeView === 'landing' ? (
          <>
            <header className="landing-header">
              <img className="landing-logo" src={companyLogo} alt={`${companyName} logo`} />
              <button className="landing-login-btn" onClick={() => navigateTo('login')}>{isAuthenticated ? 'Open Dashboard' : 'Login'}</button>
            </header>
            <main className="landing-main">
              <section className="landing-hero">
                <p className="landing-kicker">Construction Intelligence Platform</p>
                <h1>{companyName} Project Command Center</h1>
                <p>
                  We centralize design coordination, staffing, deadlines, meeting actions, consultant communication,
                  and redline workflows into one operating dashboard for fast-moving project teams.
                </p>
                <div className="landing-cta-row">
                  <button onClick={() => navigateTo('login')}>Access Workspace</button>
                  <span>Built for architecture, engineering, and owner-side execution.</span>
                </div>
              </section>
              <section className="landing-grid">
                <article>
                  <h3>Project Dashboards</h3>
                  <p>Live milestone, deliverable, and risk visibility across teams.</p>
                </article>
                <article>
                  <h3>Coordination + Minutes</h3>
                  <p>Track consultant action items, meeting topics, and accountability in one place.</p>
                </article>
                <article>
                  <h3>Standards + Redlines</h3>
                  <p>Manage drawing standards, incoming files, and review cycles with audit trail.</p>
                </article>
              </section>
            </main>
          </>
        ) : (
          <main className="login-split-wrap">
            <section className="login-brand-pane">
              <img className="login-brand-logo" src={companyLogo} alt={`${companyName} logo`} />
              <h1>{companyName}</h1>
              <p>Project operations, staffing, and consultant coordination — all in one command center.</p>
            </section>
            <section className="login-form-pane">
              <div className="login-card" onKeyDown={(e) => { if (e.key === 'Enter') void submitLogin() }}>
                <h2>Sign In</h2>
                <p>Use your credentials to access the dashboard.</p>
                <label>
                  Username
                  <input value={loginUsername} onChange={(e) => setLoginUsername(e.target.value)} placeholder="Cailin" />
                </label>
                <label>
                  Password
                  <input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="••••••••" />
                </label>
                <label className="login-remember-row">
                  <input type="checkbox" checked={loginRemember} onChange={(e) => setLoginRemember(e.target.checked)} />
                  <span>Remember me (30 days)</span>
                </label>
                {loginError ? <div className="login-error">{loginError}</div> : null}
                <button onClick={() => void submitLogin()} disabled={loginPending}>{loginPending ? 'Signing in…' : 'Login'}</button>
                <button className="login-back-link" onClick={() => navigateTo('landing')}>Back to site</button>
              </div>
            </section>
          </main>
        )}
      </div>
    )
  }

  return (
    <div className={`wrap ${(activeTab === 'directory' || activeTab === 'consultantList') ? 'directory-page' : ''}`}>
      <header className="top-header">
        <div className="top-header-logo-wrap">
          <div className="brand-link header-brand-stack settings-logo-btn" title={`${companyName} logo`}>
            <img className="header-logo" src={companyLogo} alt={`${companyName} logo`} />
          </div>
        </div>
        <nav className="top-header-vertical-nav" aria-label="Header navigation">
          {filteredHeaderProjectButtons.length ? filteredHeaderProjectButtons.map((project) => (
            <button
              key={`header-project-${project}`}
              type="button"
              className={`top-header-nav-tab ${projectName.trim().toLowerCase() === project.trim().toLowerCase() ? 'active' : ''}`}
              onClick={() => {
                setSiteSettings((prev) => ({ ...prev, projectName: project }))
                setActiveTab('projectDashboards')
              }}
            >
              {project}
            </button>
          )) : (
            <button type="button" className="top-header-nav-tab" onClick={() => setActiveTab('projectDashboards')}>
              {projectName}
            </button>
          )}
        </nav>
        <div className="top-header-right-actions">
          <input
            className="top-header-search"
            placeholder="Search projects"
            value={headerProjectSearch}
            onChange={(e) => setHeaderProjectSearch(e.target.value)}
          />
          <select className="top-header-filter" value={headerProjectScope} onChange={(e) => setHeaderProjectScope(e.target.value as 'all' | 'current' | 'past')}>
            <option value="current">Current</option>
            <option value="past">Past</option>
            <option value="all">All</option>
          </select>
          <div className="header-profile-meta">
            <strong>{currentUsername}</strong>
            <small>{effectiveRole}{currentRole === 'Admin' && rolePreview ? ' (preview)' : ''}</small>
          </div>
          <button ref={notificationsBtnRef} type="button" className="top-header-nav-tab profile-bell-btn" onClick={() => setShowNotifications((v) => !v)} title="Notifications" aria-label="Notifications">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M12 4a5 5 0 0 0-5 5v3.5L5 15v1h14v-1l-2-2.5V9a5 5 0 0 0-5-5Z" fill="none" stroke="currentColor" strokeWidth="1.7" />
              <path d="M10 18a2 2 0 0 0 4 0" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
            {unreadNotificationCount > 0 ? <span className="profile-bell-badge">{unreadNotificationCount}</span> : null}
          </button>
          <button ref={settingsGearBtnRef} type="button" className="top-header-nav-tab profile-gear-btn" onClick={() => setShowProfileSettings((v) => { const next = !v; if (next) setProfileSettingsTab(null); return next })} title="Profile Settings" aria-label="Profile Settings">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M19.4 13.5a7.8 7.8 0 0 0 .1-1.5 7.8 7.8 0 0 0-.1-1.5l2-1.6-1.9-3.2-2.4 1a7.7 7.7 0 0 0-2.6-1.5L14 2h-4l-.5 3.2a7.7 7.7 0 0 0-2.6 1.5l-2.4-1-1.9 3.2 2 1.6A7.8 7.8 0 0 0 4.5 12c0 .5 0 1 .1 1.5l-2 1.6 1.9 3.2 2.4-1a7.7 7.7 0 0 0 2.6 1.5L10 22h4l.5-3.2a7.7 7.7 0 0 0 2.6-1.5l2.4 1 1.9-3.2-2-1.6ZM12 15.2A3.2 3.2 0 1 1 12 8.8a3.2 3.2 0 0 1 0 6.4Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            </svg>
          </button>
          {showNotifications ? (
            <div ref={notificationsDropdownRef} className="profile-notifications-dropdown profile-settings-dropdown" onClick={(e) => e.stopPropagation()}>
              <h3 className="profile-settings-detail-title"><span className="profile-settings-menu-icon">{settingsIcon('notifications')}</span><span>Notifications</span></h3>
              <div className="profile-notification-list">
                {appNotifications.length ? appNotifications.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    className={`profile-notification-item ${n.unread ? 'unread' : ''}`}
                    onClick={() => setAppNotifications((prev) => prev.map((x) => x.id === n.id ? { ...x, unread: false } : x))}
                  >
                    {n.text}
                  </button>
                )) : <p className="muted">No notifications yet.</p>}
              </div>
            </div>
          ) : null}
          {showProfileSettings ? (
            <>
              <div ref={settingsDropdownRef} className="profile-settings-dropdown" onClick={(e) => e.stopPropagation()}>
                <div className="profile-settings-account-header">
                  <strong>{currentUsername}</strong>
                </div>
                <div className="profile-settings-divider" />
                <div className="profile-settings-menu">
                  {[
                    ['myProfile', 'Profile'],
                    ['notifications', 'Notifications'],
                    ['preferences', 'Preferences'],
                    ['permissions', 'Permissions'],
                    ['divider', ''],
                    ['help', 'Help & Support'],
                    ['logout', 'Log out'],
                  ].map(([key, label], idx) => (
                    key === 'divider' ? <div key={`profile-menu-divider-${idx}`} className="profile-settings-divider" /> : (
                      <button
                        key={key}
                        type="button"
                        className={`profile-settings-menu-item ${profileSettingsTab === key ? 'active' : ''}`}
                        onClick={() => {
                          if (key === 'logout') {
                            setShowLogoutConfirm(true)
                            return
                          }
                          setProfileSettingsTab(key)
                        }}
                      >
                        <span className="profile-settings-menu-icon">{settingsIcon(key)}</span>
                        <span>{label}</span>
                      </button>
                    )
                  ))}
                </div>
              </div>
              {profileSettingsTab ? (
                <div ref={settingsDetailRef} className="profile-settings-detail" onClick={(e) => e.stopPropagation()}>
                  <div className="profile-settings-content">
                    {profileSettingsTab === 'preferences' ? (
                      <>
                        <h3 className="profile-settings-detail-title">
                          <span className="profile-settings-menu-icon">{settingsIcon('preferences')}</span>
                          <span>Preferences</span>
                        </h3>
                        <div className="profile-access-card">
                          <strong>{currentUsername}</strong>
                          <small>{effectiveRole}{currentRole === 'Admin' && rolePreview ? ' (preview)' : ''}</small>
                        </div>
                        <p className="muted">Toggle dashboard containers on/off.</p>
                        <div className="preferences-dashboard-list">
                          <label><span>Deadlines & Milestones</span><input type="checkbox" checked={profileDashboardVisibility.deadlinesMilestones} onChange={(e) => setProfileDashboardVisibility((prev) => ({ ...prev, deadlinesMilestones: e.target.checked }))} /></label>
                          <label><span>Calendar</span><input type="checkbox" checked={profileDashboardVisibility.calendar} onChange={(e) => setProfileDashboardVisibility((prev) => ({ ...prev, calendar: e.target.checked }))} /></label>
                          <label><span>Meeting Topics & Actions</span><input type="checkbox" checked={profileDashboardVisibility.meetingTopicsActions} onChange={(e) => setProfileDashboardVisibility((prev) => ({ ...prev, meetingTopicsActions: e.target.checked }))} /></label>
                          <label><span>Task Health</span><input type="checkbox" checked={profileDashboardVisibility.taskHealth} onChange={(e) => setProfileDashboardVisibility((prev) => ({ ...prev, taskHealth: e.target.checked }))} /></label>
                          <label><span>Consultant Coordination</span><input type="checkbox" checked={profileDashboardVisibility.consultantCoordination} onChange={(e) => setProfileDashboardVisibility((prev) => ({ ...prev, consultantCoordination: e.target.checked }))} /></label>
                        </div>
                      </>
                    ) : (
                      (() => {
                        const title = ({
                          myProfile: 'My Profile',
                          accountSettings: 'Account Settings',
                          notifications: 'Notifications',
                          teamOrg: 'Team & Organization',
                          permissions: 'Permissions',
                          companySettings: 'Company Settings',
                          userManagement: 'User Management',
                          integrations: 'Integrations',
                          billing: 'Billing',
                          help: 'Help & Support',
                        } as Record<string, string>)[profileSettingsTab] || 'Settings'

                        const SectionHeader = () => (
                          <h3 className="profile-settings-detail-title">
                            <span className="profile-settings-menu-icon">{settingsIcon(profileSettingsTab)}</span>
                            <span>{title}</span>
                          </h3>
                        )

                        if (profileSettingsTab === 'myProfile') {
                          return (
                            <>
                              <SectionHeader />
                              <div className="site-settings-typography-grid my-profile-grid">
                                <label><span>Name</span><input className="my-profile-borderless" defaultValue={currentUsername} readOnly /></label>
                                <label><span>Title / Role</span><input className="my-profile-borderless" defaultValue={effectiveRole} readOnly /></label>
                                <label><span>Email</span><input defaultValue={`${currentUsername.toLowerCase()}@company.com`} readOnly /></label>
                                <label><span>Phone</span><input defaultValue="(555) 000-0000" readOnly /></label>
                                <label><span>Office Location</span><input defaultValue={projectOverview.location || profileOfficePlaceholder} readOnly /></label>
                                <label><span>Current Projects</span><textarea className="profile-current-projects-textarea" rows={Math.max(4, headerProjectButtons.length + 1)} value={headerProjectButtons.join('\n')} readOnly /></label>
                                <label><span>Time Zone</span><select defaultValue="UTC"><option>UTC</option><option>America/New_York</option><option>America/Chicago</option><option>America/Denver</option><option>America/Los_Angeles</option></select></label>
                              </div>
                            </>
                          )
                        }

                        if (profileSettingsTab === 'accountSettings') {
                          return (
                            <>
                              <SectionHeader />
                              <div className="site-settings-typography-grid">
                                <label>
                                  <span>Change Password</span>
                                  <button type="button" className="settings-action-btn account-settings-btn" onClick={() => { setPasswordUpdateError(''); setCurrentPasswordInput(''); setNewPasswordInput(''); setConfirmPasswordInput(''); setShowProfileSettings(false); setProfileSettingsTab(null); setShowChangePasswordModal(true) }}>Open Password Update</button>
                                </label>
                                <label>
                                  <span>Two-Factor Authentication</span>
                                  <button type="button" className="settings-action-btn account-settings-btn" onClick={() => { setShowProfileSettings(false); setProfileSettingsTab(null); setShowTwoFactorModal(true); void startTwoFactorEnrollment() }}>Setup 2FA</button>
                                </label>
                              </div>
                              <div style={{ marginTop: 14 }}>
                                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                  <strong>Startup Health</strong>
                                  <button type="button" className="settings-action-btn account-settings-btn" onClick={() => void runStartupHealthChecks()} disabled={startupHealthLoading}>
                                    {startupHealthLoading ? 'Running…' : 'Run checks'}
                                  </button>
                                </div>
                                <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
                                  {startupHealthChecks.map((check) => (
                                    <div key={check.label} className="row" style={{ justifyContent: 'space-between', border: '1px solid var(--line)', borderRadius: 8, padding: '6px 10px' }}>
                                      <span>{check.label}</span>
                                      <span style={{ color: check.ok ? 'var(--status-success)' : 'var(--status-danger)' }}>{check.ok ? 'OK' : `Fail (${check.status})`}</span>
                                    </div>
                                  ))}
                                  {!startupHealthChecks.length ? <p className="muted">Run checks to verify startup-critical API endpoints.</p> : null}
                                </div>
                              </div>
                            </>
                          )
                        }

                        if (profileSettingsTab === 'notifications') {
                          return (
                            <>
                              <SectionHeader />
                              <div className="site-settings-typography-grid">
                                <label><span>Task Updates</span><select value={notificationPrefs.taskUpdates} onChange={(e) => setNotificationPrefs((p) => ({ ...p, taskUpdates: e.target.value as NotificationLevel }))}><option value="none">None</option><option value="all">All</option><option value="mine">Only specific to me</option></select></label>
                                <label><span>Assignment Changes</span><select value={notificationPrefs.assignmentChanges} onChange={(e) => setNotificationPrefs((p) => ({ ...p, assignmentChanges: e.target.value as NotificationLevel }))}><option value="none">None</option><option value="all">All</option><option value="mine">Only specific to me</option></select></label>
                                <label><span>Meeting Reminders</span><select value={notificationPrefs.meetingReminders} onChange={(e) => setNotificationPrefs((p) => ({ ...p, meetingReminders: e.target.value as NotificationLevel }))}><option value="none">None</option><option value="all">All</option><option value="mine">Only specific to me</option></select></label>
                                <label><span>Consultant Updates</span><select value={notificationPrefs.consultantUpdates} onChange={(e) => setNotificationPrefs((p) => ({ ...p, consultantUpdates: e.target.value as NotificationLevel }))}><option value="none">None</option><option value="all">All</option><option value="mine">Only specific to me</option></select></label>
                                <label><span>Project Changes</span><select value={notificationPrefs.projectChanges} onChange={(e) => setNotificationPrefs((p) => ({ ...p, projectChanges: e.target.value as NotificationLevel }))}><option value="none">None</option><option value="all">All</option><option value="mine">Only specific to me</option></select></label>
                                <label><span>Delivery</span><input value="In-app notifications only" readOnly /></label>
                              </div>
                            </>
                          )
                        }

                        if (profileSettingsTab === 'permissions') {
                          const tabLabelMap: Record<TabKey, string> = {
                            projectDashboards: 'Project Dashboards',
                            projects: 'Projects',
                            officeOverview: 'Employee List',
                            consultantCoordinationTracking: 'Consultant Coordination',
                            drawingVersionHistory: 'Drawing Version History',
                            decisionLogs: 'Decision Logs',
                            caWorkflows: 'CA Workflows',
                            staffingAllocation: 'Task List',
                            teamTasks: 'Teams',
                            teamOrganization: 'Team Organization',
                            meetingActionTracking: 'Meeting Minutes',
                            calendar: 'Calendar',
                            projectStandards: 'Revit Standards',
                            directory: 'Directory',
                            consultantList: 'Consultants',
                            activity: 'Activity',
                            settings: 'Help & Support',
                          }

                          return (
                            <>
                              <SectionHeader />
                              <div className="profile-access-card">
                                <strong>Role: {effectiveRole}</strong>
                                <small>Tab-level access for this login</small>
                              </div>
                              {currentRole === 'Admin' ? (
                                <label className="profile-role-preview-row">
                                  <span>Switch role (admin only)</span>
                                  <select value={rolePreview || ''} onChange={(e) => setRolePreview(e.target.value || null)}>
                                    <option value="">Admin (full access)</option>
                                    {COMPANY_TIERS.map((tier) => <option key={`preview-tier-${tier}`} value={tier}>{tier}</option>)}
                                  </select>
                                </label>
                              ) : null}
                              <div className="permissions-tab-list">
                                {allowedTabs.map((tab) => (
                                  <div key={`perm-${tab}`} className="permissions-tab-row">
                                    <span className="permissions-tab-name">{tabLabelMap[tab] || tab}:</span>
                                    <strong className="permissions-tab-capability">{editableTabs.includes(tab) ? 'View/Edit' : 'View Only'}</strong>
                                  </div>
                                ))}
                              </div>
                            </>
                          )
                        }

                        if (profileSettingsTab === 'teamOrg') {
                          return (
                            <>
                              <SectionHeader />
                              <div className="site-settings-typography-grid">
                                <label><span>Department</span><input defaultValue="Architecture" /></label>
                                <label><span>Reporting Manager</span><input defaultValue="TBD" /></label>
                                <label><span>Teams Overseen</span><input defaultValue={effectiveRole === 'Executive' || effectiveRole === 'Principal' ? 'All active teams' : 'N/A'} /></label>
                                <label><span>Direct Reports</span><input defaultValue={effectiveRole === 'Executive' || effectiveRole === 'Principal' ? 'Configured by org chart' : 'N/A'} /></label>
                              </div>
                            </>
                          )
                        }

                        if (profileSettingsTab === 'companySettings') {
                          return (
                            <>
                              <SectionHeader />
                              {!isAdminRole ? <p className="muted">Company Settings are configured at the company account level and are read-only for non-admin roles.</p> : null}
                              <div className="site-settings-typography-grid">
                                <label><span>Company Name</span><input defaultValue={companyName} readOnly={!isAdminRole} /></label>
                                <label><span>Office Locations</span><input defaultValue={profileOfficePlaceholder} readOnly={!isAdminRole} /></label>
                                <label><span>Tiers</span><textarea className="profile-current-projects-textarea" defaultValue={COMPANY_TIERS.join('\n')} readOnly={!isAdminRole} /></label>
                                <div className="company-settings-actions">
                                  <label className="company-settings-action-row">
                                    <span>Web Settings</span>
                                    {isAdminRole || effectiveRole === 'Executive' ? (
                                      <button type="button" className="settings-action-btn web-settings-open-btn" onClick={() => { setShowProfileSettings(false); setProfileSettingsTab(null); setShowSiteSettings(true) }}>Open Web Settings</button>
                                    ) : (
                                      <input value="Admin or Executive only" readOnly />
                                    )}
                                  </label>
                                  <label className="company-settings-action-row">
                                    <span>Holidays Upload</span>
                                    {isAdminRole || effectiveRole === 'Executive' ? (
                                      <>
                                        <button type="button" className="settings-action-btn holiday-upload-btn" onClick={() => companyHolidayUploadRef.current?.click()}>Upload Holiday Calendar</button>
                                        <input
                                          ref={companyHolidayUploadRef}
                                          type="file"
                                          accept=".csv,.txt"
                                          style={{ display: 'none' }}
                                          onChange={async (e) => {
                                            const file = e.target.files?.[0]
                                            if (!file) return
                                            await handleCompanyHolidayUpload(file)
                                            e.currentTarget.value = ''
                                          }}
                                        />
                                      </>
                                    ) : (
                                      <input value="Admin or Executive uploads company holidays" readOnly />
                                    )}
                                  </label>
                                </div>
                                <div className="company-brand-assets-row">
                                  <label className="company-brand-asset-item">
                                    <span>Company Logo</span>
                                    <div className="company-logo-upload-wrap">
                                      {companyLogo ? (
                                        <button type="button" className="company-image-preview-btn" onClick={() => openImageCropModal('companyLogo', companyLogoOriginal)}>
                                          <img src={companyLogoOriginal} alt="Company logo" className="company-logo-upload-preview" />
                                        </button>
                                      ) : <div className="muted">No logo uploaded</div>}
                                      {isAdminRole || effectiveRole === 'Executive' ? (
                                        <>
                                          <button type="button" onClick={() => companyLogoUploadRef.current?.click()}>Upload Logo</button>
                                          <input
                                            ref={companyLogoUploadRef}
                                            type="file"
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            onChange={(e) => {
                                              const file = e.target.files?.[0]
                                              if (!file) return
                                              const reader = new FileReader()
                                              reader.onload = () => {
                                                const result = typeof reader.result === 'string' ? reader.result : ''
                                                if (!result) return
                                                setSiteSettings((prev) => ({ ...prev, companyLogo: result, companyLogoCropped: '' }));
                                                setSavedDefaultSiteSettings((prev) => ({ ...prev, companyLogo: result, companyLogoCropped: '' }))
                                                showCenterToast('Saved')
                                              }
                                              reader.readAsDataURL(file)
                                              e.currentTarget.value = ''
                                            }}
                                          />
                                        </>
                                      ) : null}
                                    </div>
                                  </label>
                                  <label className="company-brand-asset-item">
                                    <span>Company Icon</span>
                                    <div className="company-logo-upload-wrap">
                                      {companyIcon ? (
                                        <button type="button" className="company-image-preview-btn" onClick={() => openImageCropModal('companyIcon', companyIconOriginal)}>
                                          <img src={companyIconOriginal} alt="Company icon" className="company-logo-upload-preview" />
                                        </button>
                                      ) : <div className="muted">No icon uploaded</div>}
                                      {isAdminRole || effectiveRole === 'Executive' ? (
                                        <>
                                          <button type="button" onClick={() => companyIconUploadRef.current?.click()}>Upload Icon</button>
                                          <input
                                            ref={companyIconUploadRef}
                                            type="file"
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            onChange={(e) => {
                                              const file = e.target.files?.[0]
                                              if (!file) return
                                              const reader = new FileReader()
                                              reader.onload = () => {
                                                const result = typeof reader.result === 'string' ? reader.result : ''
                                                if (!result) return
                                                setSiteSettings((prev) => ({ ...prev, companyIcon: result, companyIconCropped: '' }));
                                                setSavedDefaultSiteSettings((prev) => ({ ...prev, companyIcon: result, companyIconCropped: '' }))
                                                showCenterToast('Saved')
                                              }
                                              reader.readAsDataURL(file)
                                              e.currentTarget.value = ''
                                            }}
                                          />
                                        </>
                                      ) : null}
                                    </div>
                                  </label>
                                </div>
                              </div>
                            </>
                          )
                        }

                        if (profileSettingsTab === 'userManagement') {
                          return (
                            <>
                              <SectionHeader />
                              {!isAdminRole ? <p className="muted">Admin only.</p> : null}
                              <div className="site-settings-typography-grid">
                                <label><span>Invite User</span><button type="button" className="settings-action-btn user-management-btn" disabled={!isAdminRole} onClick={() => { setInviteUserPassword(''); setInviteUserPasswordConfirm(''); setShowProfileSettings(false); setProfileSettingsTab(null); setShowInviteUserModal(true) }}>Create New Employee Contact Card</button></label>
                                <label><span>Delete User</span><button type="button" className="settings-action-btn user-management-btn" disabled={!isAdminRole} onClick={() => { setShowProfileSettings(false); setProfileSettingsTab(null); setShowDeactivateUserModal(true) }}>Open Company Employee List</button></label>
                              </div>
                            </>
                          )
                        }

                        if (profileSettingsTab === 'integrations') {
                          return (
                            <>
                              <SectionHeader />
                              {!isAdminRole ? <p className="muted">Admin only.</p> : null}
                              <div className="site-settings-typography-grid">
                                <label><span>Email</span><select><option>Not connected</option><option>Outlook</option><option>Gmail</option></select></label>
                                <label><span>Calendar Sync</span><select><option>Not connected</option><option>Connected</option></select></label>
                                <label><span>File Storage</span><select><option>Not connected</option><option>Google Drive</option><option>Dropbox</option></select></label>
                                <label><span>BIM / Revit</span><select><option>Not connected</option><option>Connected</option></select></label>
                              </div>
                            </>
                          )
                        }

                        if (profileSettingsTab === 'billing') {
                          return (
                            <>
                              <SectionHeader />
                              {!(isAdminRole || effectiveRole === 'Executive') ? <p className="muted">Admin/Executive only.</p> : null}
                              <p className="muted">View only.</p>
                              <div className="site-settings-typography-grid">
                                <label><span>Plan</span><input defaultValue="Pro" readOnly /></label>
                                <label><span>Usage</span><input defaultValue="Current usage available" readOnly /></label>
                                <label><span>Payment Method</span><input defaultValue="Card on file" readOnly /></label>
                                <label><span>Invoices</span><input defaultValue="Available" readOnly /></label>
                              </div>
                            </>
                          )
                        }

                        if (profileSettingsTab === 'help') {
                          return (
                            <>
                              <SectionHeader />
                              <div className="site-settings-typography-grid">
                                <label><span>Contact Support</span><input placeholder="Describe issue" /></label>
                                <label><span>Report Bug</span><input placeholder="Bug summary" /></label>
                                <label><span>Feature Request</span><input placeholder="Feature idea" /></label>
                                <label><span>Documentation</span><input defaultValue="docs.openclaw.ai" /></label>
                              </div>
                            </>
                          )
                        }

                        return (
                          <>
                            <SectionHeader />
                            <p className="muted">This section is ready.</p>
                          </>
                        )
                      })()
                    )}
                    <div className="row profile-settings-save-row" style={{ marginTop: 8 }}>
                      <button className="profile-settings-save-btn" onClick={() => setShowProfileSettings(false)}>Save</button>
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </header>

      <div className={`shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <div className="left-column">
          <div className="mission-title left-nav-brand">
            <span>{projectName}</span>
          </div>
          <button className="mobile-nav-toggle" onClick={() => setMobileNavOpen((v) => !v)} aria-label="Toggle navigation">☰</button>

          <aside className={`sidebar ${mobileNavOpen ? 'mobile-open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}>
            <button className="sidebar-collapse-btn" type="button" onClick={() => setSidebarCollapsed((v) => !v)} aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              <span className="nav-main-icon" aria-hidden="true">{sidebarCollapsed ? '»' : '«'}</span>
            </button>
            {canViewTab('projectDashboards') ? <button title="Dashboard" className={`nav-item ${activeTab === 'projectDashboards' ? 'active' : ''}`} onClick={() => goTab('projectDashboards')}><span className="nav-main-icon" aria-hidden="true">{settingsIcon('projectDashboards')}</span><span className="nav-main-label">Dashboard</span></button> : null}
            {(canViewTab('directory') || canViewTab('teamOrganization')) ? <button title="People" className={`nav-item ${(activeTab === 'directory' || activeTab === 'teamOrganization') ? 'active' : ''}`} onClick={(e) => { if (canViewTab('directory')) goTab('directory'); else if (canViewTab('teamOrganization')) goTab('teamOrganization'); if (sidebarCollapsed) { const host = (e.currentTarget.closest('.left-column') as HTMLElement | null); const top = host ? e.currentTarget.getBoundingClientRect().top - host.getBoundingClientRect().top : e.currentTarget.offsetTop; setNavFlyoutTop(top); setNavFlyoutGroup('corePeople') } }}><span className="nav-main-icon" aria-hidden="true">{settingsIcon('officeOverview')}</span><span className="nav-main-label">People</span></button> : null}
            {canViewTab('directory') && !sidebarCollapsed && (activeTab === 'directory' || activeTab === 'teamOrganization') ? <button className={`nav-sub-item ${activeTab === 'directory' ? 'active' : ''}`} onClick={() => goTab('directory')}>Directory</button> : null}
            {canViewTab('teamOrganization') && !sidebarCollapsed && (activeTab === 'directory' || activeTab === 'teamOrganization') ? <button className={`nav-sub-item ${activeTab === 'teamOrganization' ? 'active' : ''}`} onClick={() => goTab('teamOrganization')}>Team Organization</button> : null}
            {(canViewTab('calendar') || canViewTab('staffingAllocation') || canViewTab('meetingActionTracking') || canViewTab('projectStandards')) ? <button title="Projects" className={`nav-item ${(activeTab === 'calendar' || activeTab === 'staffingAllocation' || activeTab === 'meetingActionTracking' || activeTab === 'projectStandards') ? 'active' : ''}`} onClick={(e) => { if (canViewTab('calendar')) goTab('calendar'); else if (canViewTab('staffingAllocation')) goTab('staffingAllocation'); else if (canViewTab('meetingActionTracking')) goTab('meetingActionTracking'); else if (canViewTab('projectStandards')) goTab('projectStandards'); if (sidebarCollapsed) { const host = (e.currentTarget.closest('.left-column') as HTMLElement | null); const top = host ? e.currentTarget.getBoundingClientRect().top - host.getBoundingClientRect().top : e.currentTarget.offsetTop; setNavFlyoutTop(top); setNavFlyoutGroup('coreProjects') } }}><span className="nav-main-icon" aria-hidden="true">{settingsIcon('projects')}</span><span className="nav-main-label">Projects</span></button> : null}
            {canViewTab('calendar') && !sidebarCollapsed && (activeTab === 'calendar' || activeTab === 'staffingAllocation' || activeTab === 'meetingActionTracking' || activeTab === 'projectStandards') ? <button className={`nav-sub-item ${activeTab === 'calendar' ? 'active' : ''}`} onClick={() => goTab('calendar')}>Calendar / Deliverables</button> : null}
            {canViewTab('staffingAllocation') && !sidebarCollapsed && (activeTab === 'calendar' || activeTab === 'staffingAllocation' || activeTab === 'meetingActionTracking' || activeTab === 'projectStandards') ? <button className={`nav-sub-item ${activeTab === 'staffingAllocation' ? 'active' : ''}`} onClick={() => goTab('staffingAllocation')}>Task List</button> : null}
            {canViewTab('meetingActionTracking') && !sidebarCollapsed && (activeTab === 'calendar' || activeTab === 'staffingAllocation' || activeTab === 'meetingActionTracking' || activeTab === 'projectStandards') ? <button className={`nav-sub-item ${activeTab === 'meetingActionTracking' ? 'active' : ''}`} onClick={() => goTab('meetingActionTracking')}>Meeting Minutes</button> : null}
            {canViewTab('projectStandards') && !sidebarCollapsed && (activeTab === 'calendar' || activeTab === 'staffingAllocation' || activeTab === 'meetingActionTracking' || activeTab === 'projectStandards') ? <button className={`nav-sub-item ${activeTab === 'projectStandards' ? 'active' : ''}`} onClick={() => goTab('projectStandards')}>Revit Standards</button> : null}
            {canViewTab('consultantCoordinationTracking') ? <button title="Consultants" className={`nav-item ${activeTab === 'consultantCoordinationTracking' ? 'active' : ''}`} onClick={(e) => { goTab('consultantCoordinationTracking'); if (sidebarCollapsed) { const host = (e.currentTarget.closest('.left-column') as HTMLElement | null); const top = host ? e.currentTarget.getBoundingClientRect().top - host.getBoundingClientRect().top : e.currentTarget.offsetTop; setNavFlyoutTop(top); setNavFlyoutGroup('coreConsultants') } }}><span className="nav-main-icon" aria-hidden="true">{settingsIcon('consultantList')}</span><span className="nav-main-label">Consultants</span></button> : null}
            {canViewTab('consultantCoordinationTracking') && !sidebarCollapsed && activeTab === 'consultantCoordinationTracking' ? <button className={`nav-sub-item ${activeTab === 'consultantCoordinationTracking' ? 'active' : ''}`} onClick={() => goTab('consultantCoordinationTracking')}>Consultant Coordination</button> : null}
            {(canViewTab('officeOverview') || canViewTab('teamTasks') || canViewTab('consultantList') || canViewTab('projects') || canViewTab('activity') || canViewTab('settings')) ? <div className="nav-section-label">Executive</div> : null}
            {canViewTab('officeOverview') ? <button title="Employees" className={`nav-item ${activeTab === 'officeOverview' || activeTab === 'teamTasks' ? 'active' : ''}`} onClick={(e) => { goTab('officeOverview'); if (sidebarCollapsed) { const host = (e.currentTarget.closest('.left-column') as HTMLElement | null); const top = host ? e.currentTarget.getBoundingClientRect().top - host.getBoundingClientRect().top : e.currentTarget.offsetTop; setNavFlyoutTop(top); setNavFlyoutGroup('people') } }}><span className="nav-main-icon" aria-hidden="true">{settingsIcon('officeOverview')}</span><span className="nav-main-label">Employees</span></button> : null}
            {canViewTab('officeOverview') && (activeTab === 'officeOverview' || activeTab === 'teamTasks') ? <button className={`nav-sub-item ${activeTab === 'officeOverview' ? 'active' : ''}`} onClick={() => goTab('officeOverview')}>Employee List</button> : null}
            {canViewTab('teamTasks') && (activeTab === 'officeOverview' || activeTab === 'teamTasks') ? <button className={`nav-sub-item ${activeTab === 'teamTasks' ? 'active' : ''}`} onClick={() => goTab('teamTasks')}>Teams</button> : null}
            {canViewTab('consultantList') ? <button title="Consultants" className={`nav-item directory-menu-tab ${activeTab === 'consultantList' ? 'active' : ''}`} onClick={() => goTab('consultantList')}><span className="nav-main-icon" aria-hidden="true">{settingsIcon('consultantList')}</span><span className="nav-main-label">Consultants</span></button> : null}
            {canViewTab('projects') ? <button title="Projects" className={`nav-item ${activeTab === 'projects' ? 'active' : ''}`} onClick={(e) => { setProjectsSubTab('current'); goTab('projects'); if (sidebarCollapsed) { const host = (e.currentTarget.closest('.left-column') as HTMLElement | null); const top = host ? e.currentTarget.getBoundingClientRect().top - host.getBoundingClientRect().top : e.currentTarget.offsetTop; setNavFlyoutTop(top); setNavFlyoutGroup('projects') } }}><span className="nav-main-icon" aria-hidden="true">{settingsIcon('projects')}</span><span className="nav-main-label">Projects</span></button> : null}
            {canViewTab('projects') && activeTab === 'projects' ? <button className={`nav-sub-item ${activeTab === 'projects' && projectsSubTab === 'current' ? 'active' : ''}`} onClick={() => { setProjectsSubTab('current'); goTab('projects') }}>Current</button> : null}
            {canViewTab('projects') && activeTab === 'projects' ? <button className={`nav-sub-item ${activeTab === 'projects' && projectsSubTab === 'past' ? 'active' : ''}`} onClick={() => { setProjectsSubTab('past'); goTab('projects') }}>Past</button> : null}
            {canViewTab('activity') ? <button title="Activity" className={`nav-item ${activeTab === 'activity' ? 'active' : ''}`} onClick={() => goTab('activity')}><span className="nav-main-icon" aria-hidden="true">{settingsIcon('activity')}</span><span className="nav-main-label">Activity</span></button> : null}
            {canViewTab('settings') ? <div className="profile-settings-divider nav-settings-divider" /> : null}
            {canViewTab('settings') ? <button title="Settings" className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`} onClick={(e) => { goTab('settings'); if (sidebarCollapsed) { const host = (e.currentTarget.closest('.left-column') as HTMLElement | null); const top = host ? e.currentTarget.getBoundingClientRect().top - host.getBoundingClientRect().top : e.currentTarget.offsetTop; setNavFlyoutTop(top); setNavFlyoutGroup('settings') } }}><span className="nav-main-icon" aria-hidden="true">{settingsIcon('settings')}</span><span className="nav-main-label">Settings</span></button> : null}
            {canViewTab('settings') && activeTab === 'settings' ? <button className={`nav-sub-item ${activeTab === 'settings' && profileSettingsTab === 'accountSettings' ? 'active' : ''}`} onClick={() => { goTab('settings'); setProfileSettingsTab('accountSettings') }}>Account Settings</button> : null}
            {canViewTab('settings') && activeTab === 'settings' ? <button className={`nav-sub-item ${activeTab === 'settings' && profileSettingsTab === 'companySettings' ? 'active' : ''}`} onClick={() => { goTab('settings'); setProfileSettingsTab('companySettings') }}>Company Settings</button> : null}
            {canViewTab('settings') && activeTab === 'settings' ? <button className={`nav-sub-item ${activeTab === 'settings' && profileSettingsTab === 'userManagement' ? 'active' : ''}`} onClick={() => { goTab('settings'); setProfileSettingsTab('userManagement') }}>User Management</button> : null}
            {canViewTab('settings') && activeTab === 'settings' && isAdminRole ? <button className={`nav-sub-item ${activeTab === 'settings' && profileSettingsTab === 'integrations' ? 'active' : ''}`} onClick={() => { goTab('settings'); setProfileSettingsTab('integrations') }}>Integrations</button> : null}
            {canViewTab('settings') && activeTab === 'settings' && (isAdminRole || effectiveRole === 'Executive') ? <button className={`nav-sub-item ${activeTab === 'settings' && profileSettingsTab === 'billing' ? 'active' : ''}`} onClick={() => { goTab('settings'); setProfileSettingsTab('billing') }}>Billing</button> : null}
          </aside>

          {sidebarCollapsed && navFlyoutGroup ? (
            <div className="sidebar-flyout" style={{ top: navFlyoutTop }} onClick={(e) => e.stopPropagation()}>
              {navFlyoutGroup === 'corePeople' ? (
                <>
                  {canViewTab('directory') ? <button className={`nav-sub-item ${activeTab === 'directory' ? 'active' : ''}`} onClick={() => { goTab('directory'); setNavFlyoutGroup(null) }}>Directory</button> : null}
                  {canViewTab('teamOrganization') ? <button className={`nav-sub-item ${activeTab === 'teamOrganization' ? 'active' : ''}`} onClick={() => { goTab('teamOrganization'); setNavFlyoutGroup(null) }}>Team Organization</button> : null}
                </>
              ) : null}
              {navFlyoutGroup === 'coreProjects' ? (
                <>
                  {canViewTab('calendar') ? <button className={`nav-sub-item ${activeTab === 'calendar' ? 'active' : ''}`} onClick={() => { goTab('calendar'); setNavFlyoutGroup(null) }}>Calendar / Deliverables</button> : null}
                  {canViewTab('staffingAllocation') ? <button className={`nav-sub-item ${activeTab === 'staffingAllocation' ? 'active' : ''}`} onClick={() => { goTab('staffingAllocation'); setNavFlyoutGroup(null) }}>Task List</button> : null}
                  {canViewTab('meetingActionTracking') ? <button className={`nav-sub-item ${activeTab === 'meetingActionTracking' ? 'active' : ''}`} onClick={() => { goTab('meetingActionTracking'); setNavFlyoutGroup(null) }}>Meeting Minutes</button> : null}
                  {canViewTab('projectStandards') ? <button className={`nav-sub-item ${activeTab === 'projectStandards' ? 'active' : ''}`} onClick={() => { goTab('projectStandards'); setNavFlyoutGroup(null) }}>Revit Standards</button> : null}
                </>
              ) : null}
              {navFlyoutGroup === 'coreConsultants' ? (
                <>
                  {canViewTab('consultantCoordinationTracking') ? <button className={`nav-sub-item ${activeTab === 'consultantCoordinationTracking' ? 'active' : ''}`} onClick={() => { goTab('consultantCoordinationTracking'); setNavFlyoutGroup(null) }}>Consultant Coordination</button> : null}
                </>
              ) : null}
              {navFlyoutGroup === 'people' ? (
                <>
                  {canViewTab('officeOverview') ? <button className={`nav-sub-item ${activeTab === 'officeOverview' ? 'active' : ''}`} onClick={() => { goTab('officeOverview'); setNavFlyoutGroup(null) }}>Employee List</button> : null}
                  {canViewTab('teamTasks') ? <button className={`nav-sub-item ${activeTab === 'teamTasks' ? 'active' : ''}`} onClick={() => { goTab('teamTasks'); setNavFlyoutGroup(null) }}>Teams</button> : null}
                </>
              ) : null}
              {navFlyoutGroup === 'projects' ? (
                <>
                  {canViewTab('projects') ? <button className={`nav-sub-item ${activeTab === 'projects' && projectsSubTab === 'current' ? 'active' : ''}`} onClick={() => { setProjectsSubTab('current'); goTab('projects'); setNavFlyoutGroup(null) }}>Current</button> : null}
                  {canViewTab('projects') ? <button className={`nav-sub-item ${activeTab === 'projects' && projectsSubTab === 'past' ? 'active' : ''}`} onClick={() => { setProjectsSubTab('past'); goTab('projects'); setNavFlyoutGroup(null) }}>Past</button> : null}
                </>
              ) : null}
              {navFlyoutGroup === 'settings' ? (
                <>
                  {canViewTab('settings') ? <button className={`nav-sub-item ${activeTab === 'settings' && profileSettingsTab === 'accountSettings' ? 'active' : ''}`} onClick={() => { goTab('settings'); setProfileSettingsTab('accountSettings'); setNavFlyoutGroup(null) }}>Account Settings</button> : null}
                  {canViewTab('settings') ? <button className={`nav-sub-item ${activeTab === 'settings' && profileSettingsTab === 'companySettings' ? 'active' : ''}`} onClick={() => { goTab('settings'); setProfileSettingsTab('companySettings'); setNavFlyoutGroup(null) }}>Company Settings</button> : null}
                  {canViewTab('settings') ? <button className={`nav-sub-item ${activeTab === 'settings' && profileSettingsTab === 'userManagement' ? 'active' : ''}`} onClick={() => { goTab('settings'); setProfileSettingsTab('userManagement'); setNavFlyoutGroup(null) }}>User Management</button> : null}
                  {canViewTab('settings') && isAdminRole ? <button className={`nav-sub-item ${activeTab === 'settings' && profileSettingsTab === 'integrations' ? 'active' : ''}`} onClick={() => { goTab('settings'); setProfileSettingsTab('integrations'); setNavFlyoutGroup(null) }}>Integrations</button> : null}
                  {canViewTab('settings') && (isAdminRole || effectiveRole === 'Executive') ? <button className={`nav-sub-item ${activeTab === 'settings' && profileSettingsTab === 'billing' ? 'active' : ''}`} onClick={() => { goTab('settings'); setProfileSettingsTab('billing'); setNavFlyoutGroup(null) }}>Billing</button> : null}
                </>
              ) : null}
            </div>
          ) : null}
        </div>

        <section
          ref={contentRef}
          className={`content ${(activeTab === 'directory' || activeTab === 'consultantList') ? 'directory-content' : ''} ${activeTab === 'calendar' ? 'calendar-content' : ''} ${isReadOnlyActiveTab ? 'read-only-tab' : ''}`}
          onClickCapture={() => {}}
          onChangeCapture={() => {}}
        >
          {isReadOnlyActiveTab ? <div className="role-readonly-banner">View only: {effectiveRole}{currentRole === 'Admin' && rolePreview ? ' (preview)' : ''} role cannot edit this tab.</div> : null}

          {activeTab === 'activity' && canViewTab('activity') ? (
            <section className="panel">
              <div className="panel-head" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <h1>Activity</h1>
                <button className="btn" type="button" onClick={() => void loadActivityFeed()} disabled={activityLoading}>{activityLoading ? 'Refreshing…' : 'Refresh'}</button>
              </div>
              <p className="muted">Audit timeline for key operational changes. This view is intentionally separate to avoid dashboard clutter.</p>
              <div className="row" style={{ gap: 10, marginBottom: 12 }}>
                <select value={activityModuleFilter} onChange={(e) => setActivityModuleFilter(e.target.value)}>
                  <option value="">All modules</option>
                  <option value="tasks">Tasks</option>
                  <option value="calendar">Calendar</option>
                  <option value="consultant">Consultant</option>
                  <option value="directory">Directory</option>
                  <option value="meeting">Meeting</option>
                  <option value="settings">Settings</option>
                  <option value="staffing">Staffing</option>
                </select>
                <select value={activitySeverityFilter} onChange={(e) => setActivitySeverityFilter(e.target.value)}>
                  <option value="">All severities</option>
                  <option value="info">Info</option>
                  <option value="important">Important</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              {activityError ? <p className="muted" style={{ color: 'var(--status-danger)' }}>{activityError}</p> : null}
              {(!activityLoading && !activityRows.length) ? <p className="muted">No activity yet.</p> : null}
              <div style={{ display: 'grid', gap: 8 }}>
                {activityRows.map((row) => (
                  <article key={row.id} className="card" style={{ padding: 10 }}>
                    <div className="row" style={{ justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                      <strong>{row.summary || 'Updated record'}</strong>
                      <span className={`badge ${row.severity === 'critical' ? 'danger' : row.severity === 'important' ? 'warn' : ''}`}>{row.severity || 'info'}</span>
                    </div>
                    <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
                      {new Date(row.timestamp || Date.now()).toLocaleString()} • {row.module} • {row.actor || 'system'} • {row.entity_type}:{row.entity_id}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          {activeTab === 'settings' && canViewTab('settings') ? (
            <section className="panel">
              <div className="panel-head">
                <h1>Settings</h1>
              </div>
              <p className="muted">Use the left-nav Settings subsections for admin/executive configuration.</p>
              <div className="profile-settings-menu" style={{ marginTop: 10 }}>
                <button type="button" className={`profile-settings-menu-item ${profileSettingsTab === 'accountSettings' ? 'active' : ''}`} onClick={() => { setShowProfileSettings(true); setProfileSettingsTab('accountSettings') }}><span className="profile-settings-menu-icon">{settingsIcon('accountSettings')}</span><span>Account Settings</span></button>
                <button type="button" className={`profile-settings-menu-item ${profileSettingsTab === 'companySettings' ? 'active' : ''}`} onClick={() => { setShowProfileSettings(true); setProfileSettingsTab('companySettings') }}><span className="profile-settings-menu-icon">{settingsIcon('companySettings')}</span><span>Company Settings</span></button>
                <button type="button" className={`profile-settings-menu-item ${profileSettingsTab === 'userManagement' ? 'active' : ''}`} onClick={() => { setShowProfileSettings(true); setProfileSettingsTab('userManagement') }}><span className="profile-settings-menu-icon">{settingsIcon('userManagement')}</span><span>User Management</span></button>
                {isAdminRole ? <button type="button" className={`profile-settings-menu-item ${profileSettingsTab === 'integrations' ? 'active' : ''}`} onClick={() => { setShowProfileSettings(true); setProfileSettingsTab('integrations') }}><span className="profile-settings-menu-icon">{settingsIcon('integrations')}</span><span>Integrations</span></button> : null}
                {(isAdminRole || effectiveRole === 'Executive') ? <button type="button" className={`profile-settings-menu-item ${profileSettingsTab === 'billing' ? 'active' : ''}`} onClick={() => { setShowProfileSettings(true); setProfileSettingsTab('billing') }}><span className="profile-settings-menu-icon">{settingsIcon('billing')}</span><span>Billing</span></button> : null}
              </div>
            </section>
          ) : null}

          {(activeTab === 'projectDashboards' || activeTab === 'officeOverview') && (
            <section className="panel dashboard-panel">
              <div className="dashboard-head">
                <h1>{isOfficeOrganizationTab ? 'Employee List' : projectName}</h1>
                {!isOfficeOrganizationTab && (projectOverview.location || '').trim() ? <p className="muted">{projectOverview.location}</p> : null}
                {isOfficeOrganizationTab ? (
                  <div className="office-employees-search-row">
                    <input
                      className="search-input-icon"
                      value={officeEmployeeSearch}
                      onChange={(e) => setOfficeEmployeeSearch(e.target.value)}
                      placeholder="Search by name, position, or project"
                      aria-label="Search company employees"
                    />
                  </div>
                ) : null}
              </div>

              <div className="dashboard-kpis">
                <article className={`dashboard-kpi-card ${isOfficeOrganizationTab ? 'office-kpi-card' : ''}`}>
                  <span>
                    {isOfficeOrganizationTab ? <img className="office-kpi-symbol-img" src={tintedOfficeKpiSymbols.employees} alt="" aria-hidden /> : null}
                    {isOfficeOrganizationTab ? 'Number of Employees' : 'This Week'}
                  </span>
                  <strong>{isOfficeOrganizationTab ? officeEmployeesAll.length : thisWeekDeadlines.length}</strong>
                  <small>{isOfficeOrganizationTab ? 'employees' : 'deadlines'}</small>
                </article>
                <article className={`dashboard-kpi-card ${isOfficeOrganizationTab ? 'office-kpi-card' : ''}`}>
                  <span>
                    {isOfficeOrganizationTab ? <img className="office-kpi-symbol-img" src={tintedOfficeKpiSymbols.projects} alt="" aria-hidden /> : null}
                    {isOfficeOrganizationTab ? 'Number of Projects' : 'Overdue'}
                  </span>
                  <strong>{isOfficeOrganizationTab ? new Set(officeEmployeesAll.map((e) => (e.project || '').trim().toLowerCase()).filter(Boolean)).size : overdueDeadlines.length}</strong>
                  <small>{isOfficeOrganizationTab ? 'projects' : 'deliverables'}</small>
                </article>
                <article className={`dashboard-kpi-card ${isOfficeOrganizationTab ? 'office-kpi-card' : ''}`}>
                  <span>
                    {isOfficeOrganizationTab ? <img className="office-kpi-symbol-img" src={tintedOfficeKpiSymbols.locations} alt="" aria-hidden /> : null}
                    {isOfficeOrganizationTab ? 'Number of Locations' : 'Open Actions'}
                  </span>
                  <strong>{isOfficeOrganizationTab ? new Set(officeEmployeesAll.map((e) => (e.location || '').trim().toLowerCase()).filter(Boolean)).size : dashboardMeetingActionItems.length}</strong>
                  <small>{isOfficeOrganizationTab ? 'locations' : 'meeting sub-items'}</small>
                </article>
              </div>

              <div className={`dashboard-layout ${isOfficeOrganizationTab ? 'office-org-layout' : ''}`}>
                <div className="dashboard-main-col">
                  {!isOfficeOrganizationTab ? (
                    <article className="dashboard-card dashboard-overview-hero">
                      <div className="dashboard-overview-head-row">
                        <h3>Project Overview</h3>
                      </div>
                      <input
                        className="dashboard-project-location-input"
                        placeholder="Location"
                        value={projectOverview.location || ''}
                        onChange={(e) => updateProjectOverview((prev) => ({ ...prev, location: e.target.value }))}
                      />
                      <div className="dashboard-overview-hero-body">
                        <div className="dashboard-overview-photo-wrap">
                          {projectOverview.buildingPhoto ? (
                            <img src={projectOverview.buildingPhoto} alt="Project building" className="dashboard-overview-photo" />
                          ) : (
                            <div className="dashboard-overview-photo-placeholder">No building photo uploaded</div>
                          )}
                          {!isDrafterProjectDashboard ? (
                            <label className="dashboard-upload-photo-btn">
                              Upload Image
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                  const file = e.target.files?.[0]
                                  if (!file) return
                                  const reader = new FileReader()
                                  reader.onload = () => {
                                    const result = typeof reader.result === 'string' ? reader.result : ''
                                    if (!result) return
                                    updateProjectOverview((prev) => ({ ...prev, buildingPhoto: result }))
                                  }
                                  reader.readAsDataURL(file)
                                  e.currentTarget.value = ''
                                }}
                              />
                            </label>
                          ) : null}
                        </div>
                        <div className="dashboard-phase-column">
                          <div className="dashboard-phase-progress" aria-label="Project progress scale">
                            {!isDrafterProjectDashboard ? (
                              <div className="dashboard-phase-start-row">
                                <div className="dashboard-phase-edge-group">
                                  <span className="muted small">Start</span>
                                  <button
                                    type="button"
                                    className={`dashboard-phase-date-btn ${projectOverview.startDate ? 'has-date' : ''}`}
                                    aria-label="Set project start date"
                                    title={projectOverview.startDate || 'Set project start date'}
                                    onClick={() => {
                                      const el = phaseDateInputRefs.current.__start__
                                      if (!el) return
                                      try {
                                        if (typeof el.showPicker === 'function') el.showPicker()
                                        else el.click()
                                      } catch {
                                        el.click()
                                      }
                                    }}
                                  >
                                    {projectOverview.startDate || (
                                      <svg className="dashboard-phase-date-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                        <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" ry="2.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
                                        <path d="M3.5 9.5h17" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                                        <path d="M8 3.8v3.4M16 3.8v3.4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                                      </svg>
                                    )}
                                  </button>
                                  <input
                                    ref={(el) => { phaseDateInputRefs.current.__start__ = el }}
                                    type="date"
                                    className="dashboard-phase-date-hidden-input"
                                    value={projectOverview.startDate || ''}
                                    onChange={(e) => updateProjectOverview((prev) => ({ ...prev, startDate: e.target.value }))}
                                  />
                                </div>

                                <div className="dashboard-phase-edge-group end">
                                  <span className="muted small">End</span>
                                  <button
                                    type="button"
                                    className={`dashboard-phase-date-btn ${projectOverview.endDate ? 'has-date' : ''}`}
                                    aria-label="Set project end date"
                                    title={projectOverview.endDate || 'Set project end date'}
                                    onClick={() => {
                                      const el = phaseDateInputRefs.current.__end__
                                      if (!el) return
                                      try {
                                        if (typeof el.showPicker === 'function') el.showPicker()
                                        else el.click()
                                      } catch {
                                        el.click()
                                      }
                                    }}
                                  >
                                    {projectOverview.endDate || (
                                      <svg className="dashboard-phase-date-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                        <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" ry="2.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
                                        <path d="M3.5 9.5h17" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                                        <path d="M8 3.8v3.4M16 3.8v3.4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                                      </svg>
                                    )}
                                  </button>
                                  <input
                                    ref={(el) => { phaseDateInputRefs.current.__end__ = el }}
                                    type="date"
                                    className="dashboard-phase-date-hidden-input"
                                    value={projectOverview.endDate || ''}
                                    onChange={(e) => updateProjectOverview((prev) => ({ ...prev, endDate: e.target.value }))}
                                  />
                                </div>
                              </div>
                            ) : null}
                            <div className="dashboard-phase-progress-track" style={{ ['--progress' as string]: `${timelineProjectProgress}%` } as React.CSSProperties}>
                              <div className="dashboard-phase-progress-fill" />
                              {([12.5, 37.5, 62.5, 87.5] as const).map((stop, i) => (
                                <span
                                  key={`phase-dot-${stop}`}
                                  className={`dashboard-phase-progress-dot ${timelineProjectProgress >= stop ? 'active' : ''}`}
                                  style={{ ['--stop' as string]: `${stop}%` } as React.CSSProperties}
                                  title={`Phase ${i + 1}`}
                                />
                              ))}
                            </div>
                            <input
                              type="range"
                              min={0}
                              max={100}
                              value={timelineProjectProgress}
                              readOnly
                              className="dashboard-phase-mobile-slider"
                              aria-label="Project progress timeline slider"
                            />
                          </div>

                          <div className="dashboard-phase-strip">
                            {(['Concept', 'SD', 'DD', 'CC'] as const).map((phase) => {
                              const phaseLabel = phase === 'CC' ? 'CD' : phase
                              return (
                              <div key={phase} className="dashboard-phase-pill">
                                <strong>{phaseLabel}</strong>
                                <>
                                  <button
                                    type="button"
                                    className={`dashboard-phase-date-btn ${projectOverview.phaseDeadlines[phase] ? 'has-date' : ''}`}
                                    aria-label={`Set ${phaseLabel} due date`}
                                    title={projectOverview.phaseDeadlines[phase] || `Set ${phaseLabel} due date`}
                                    onClick={() => {
                                      if (isDrafterProjectDashboard) return
                                      const el = phaseDateInputRefs.current[phase]
                                      if (!el) return
                                      try {
                                        if (typeof el.showPicker === 'function') el.showPicker()
                                        else el.click()
                                      } catch {
                                        el.click()
                                      }
                                    }}
                                  >
                                    {projectOverview.phaseDeadlines[phase]
                                      ? projectOverview.phaseDeadlines[phase]
                                      : (
                                        <svg className="dashboard-phase-date-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                          <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" ry="2.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
                                          <path d="M3.5 9.5h17" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                                          <path d="M8 3.8v3.4M16 3.8v3.4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                                        </svg>
                                      )}
                                  </button>
                                  {!isDrafterProjectDashboard ? (
                                    <input
                                      ref={(el) => { phaseDateInputRefs.current[phase] = el }}
                                      type="date"
                                      className="dashboard-phase-date-hidden-input"
                                      value={projectOverview.phaseDeadlines[phase] || ''}
                                      onChange={(e) => updateProjectOverview((prev) => ({
                                        ...prev,
                                        phaseDeadlines: { ...prev.phaseDeadlines, [phase]: e.target.value },
                                      }))}
                                    />
                                  ) : null}
                                </>
                              </div>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="dashboard-overview-save-row">
                        <button type="button" className="dashboard-overview-save-btn" onClick={saveProjectOverview}>Save</button>
                      </div>
                    </article>
                  ) : null}

                  <div className="dashboard-grid">
                    {(!isOfficeOrganizationTab && profileDashboardVisibility.deadlinesMilestones) ? (
                      <article
                        className="dashboard-card dashboard-card-clickable"
                        onClick={() => {
                          setCalendarView('list')
                          setActiveTab('calendar')
                        }}
                        title={`Open Deliverables List • Source: ${executiveSyncMap.deadlinesMilestones.join(', ')}`}
                      >
                        <h3>Deadlines & Milestones</h3>
                        <p>This week’s deadlines: <strong>{thisWeekDeadlines.length}</strong></p>
                        <ul>
                          {thisWeekDeadlines.slice(0, 5).map((d) => (
                            <li key={`${d.dateKey}-${d.title}`}>{d.dateKey} — {d.title}</li>
                          ))}
                          {thisWeekDeadlines.length === 0 ? <li>No deadlines this week.</li> : null}
                        </ul>
                        <p><strong>Next upcoming deadline</strong></p>
                        <ul>
                          {nextUpcomingDeadline ? <li>{nextUpcomingDeadline.dateKey} — {nextUpcomingDeadline.title}</li> : <li>No upcoming deadlines.</li>}
                        </ul>
                      </article>
                    ) : null}

                    {(!isOfficeOrganizationTab && profileDashboardVisibility.calendar) ? (
                    <article className="dashboard-card dashboard-mini-calendar-card">
                      <h3>Calendar</h3>
                      <div className="dashboard-mini-nav">
                        <button type="button" className="calendar-inline-nav" aria-label="Previous month" onClick={() => shiftDashboardMiniMonth(-1)}>◀</button>
                        <p>{calendarFocusDate.toLocaleString(undefined, { month: 'long' })} {calendarFocusDate.getFullYear()}</p>
                        <button type="button" className="calendar-inline-nav" aria-label="Next month" onClick={() => shiftDashboardMiniMonth(1)}>▶</button>
                      </div>
                      <div className="dashboard-mini-weekdays" aria-label="Weekday headers">
                        <span className="muted small dashboard-mini-weekday">Sun</span><span className="muted small dashboard-mini-weekday">Mon</span><span className="muted small dashboard-mini-weekday">Tue</span><span className="muted small dashboard-mini-weekday">Wed</span><span className="muted small dashboard-mini-weekday">Thu</span><span className="muted small dashboard-mini-weekday">Fri</span><span className="muted small dashboard-mini-weekday">Sat</span>
                      </div>
                      <div className="dashboard-mini-days-grid">
                        {dashboardMiniWeeks.flatMap((row, rowIndex) => row.map((d, colIndex) => {
                          const inMonth = d.getMonth() === calendarFocusDate.getMonth()
                          const isToday = d.toDateString() === new Date().toDateString()
                          const hasDeadline = calendarDeadlines.some((x) => x.dateKey === dashboardDateKey(d))
                          return (
                            <button
                              key={`dash-mini-${rowIndex}-${colIndex}`}
                              className={`dashboard-mini-day ${isToday ? 'today-year' : ''}`}
                              style={{ opacity: inMonth ? 1 : 0.225 }}
                              onClick={() => { setCalendarFocusDate(d); setCalendarView('day'); setActiveTab('calendar') }}
                              title={d.toDateString()}
                            >
                              <span className={`year-day-num ${hasDeadline ? 'deadline-ring' : ''}`}>{String(d.getDate()).padStart(2, '0')}</span>
                            </button>
                          )
                        }))}
                      </div>
                    </article>
                    ) : null}

                    {(activeTab === 'projectDashboards' && profileDashboardVisibility.meetingTopicsActions) ? (
                    <article className="dashboard-card">
                      <h3>Meeting Topics / Actions</h3>
                      <p>Open topics: <strong>{meetingTopics.filter((x) => x.status !== 'closed').length}</strong></p>
                      <ul>
                        {dashboardMeetingActionItems.slice(0, 5).map((a) => (
                          <li key={a.id}><button type="button" className="dashboard-link-btn" onClick={() => openMeetingActionFromDashboard(a.id, a.parentId)} title="Open in Meeting Minutes">{a.date || 'No date'} — {a.item} — {a.owner} • due {a.due || 'TBD'}</button></li>
                        ))}
                        {dashboardMeetingActionItems.length === 0 ? <li>No matching AOR/company action items.</li> : null}
                      </ul>
                    </article>
                    ) : null}

                    {(activeTab === 'projectDashboards' && profileDashboardVisibility.taskHealth) ? (
                    <article className="dashboard-card dashboard-card-clickable" onClick={() => openStaffingFromDashboard('Team')} title="Open Team task list">
                      <h3>Task List Health</h3>
                      <p>High priority tasks: <strong>{staffingRowsData.filter((r) => r.priority === 'High').length}</strong></p>
                      <ul>
                        {staffingRowsData.filter((r) => r.priority === 'High').slice(0, 8).map((r) => (
                          <li key={r.id}><button type="button" className="dashboard-link-btn" onClick={(e) => { e.stopPropagation(); openStaffingFromDashboard(r.assignee || 'Team', r.id) }} title="Open responsible team member task list">{r.task} — Responsible: {r.assignee}</button></li>
                        ))}
                        {staffingRowsData.filter((r) => r.priority === 'High').length === 0 ? <li>No high priority tasks.</li> : null}
                      </ul>
                    </article>
                    ) : null}

                    {(activeTab === 'projectDashboards' && profileDashboardVisibility.consultantCoordination) ? (
                    <article className="dashboard-card">
                      <h3>Consultant Coordination</h3>
                      <p>To consultant pending: <strong>{consultantRows.filter((x) => x.lane === 'To Consultant').length}</strong> • From consultant pending: <strong>{consultantRows.filter((x) => x.lane === 'From Consultant').length}</strong></p>
                      <ul>{consultantRows.slice(0, 5).map((x) => <li key={x.id}>{x.team} • {x.lane} — {x.title} ({x.dueDate})</li>)}</ul>
                    </article>
                    ) : null}
                  </div>

                  {isOfficeOrganizationTab ? (
                    <article className="dashboard-card" style={{ marginTop: 16 }}>
                      <div className="office-employees-head-row">
                        <h3>Company Employees</h3>
                        <div className="row" style={{ gap: 8 }}>
                          <button type="button" onClick={openAddOfficeContactCard}>＋ Add Employee</button>
                          <button type="button" onClick={() => officeEmployeesUploadRef.current?.click()}>⤴ Upload</button>
                          <input
                            ref={officeEmployeesUploadRef}
                            type="file"
                            accept=".txt,.csv,.pdf"
                            style={{ display: 'none' }}
                            onChange={async (e) => {
                              const f = e.target.files?.[0]
                              if (!f) return
                              const lower = f.name.toLowerCase()
                              if (lower.endsWith('.txt') || lower.endsWith('.csv')) {
                                const text = await f.text()
                                const parsed = text
                                  .split(/\r?\n/)
                                  .map((line) => line.trim())
                                  .filter(Boolean)
                                  .map((line) => line.split(/[\t,]/).map((x) => x.trim()))
                                  .filter((parts) => parts.length >= 4)
                                  .map((parts) => ({ name: parts[0], position: parts[1], project: parts[2], location: parts[3] }))
                                if (parsed.length) {
                                  setOfficeCustomEmployees((prev) => [...parsed, ...prev])
                                  showCenterToast('Uploaded')
                                } else {
                                  showActionToast('No rows found (expected name, position, project, location)')
                                }
                              } else {
                                showCenterToast('Uploaded')
                              }
                              e.currentTarget.value = ''
                            }}
                          />
                        </div>
                      </div>
                      <div className="staffing-table-wrap office-employees-table">
                        <div className="staffing-table-header">
                          <button type="button" onClick={() => toggleOfficeEmployeeSort('name')}>Name {officeEmployeeSortKey === 'name' ? (officeEmployeeSortDir === 'asc' ? '↑' : '↓') : ''}</button>
                          <button type="button" onClick={() => toggleOfficeEmployeeSort('position')}>Position {officeEmployeeSortKey === 'position' ? (officeEmployeeSortDir === 'asc' ? '↑' : '↓') : ''}</button>
                          <button type="button" onClick={() => toggleOfficeEmployeeSort('project')}>Project {officeEmployeeSortKey === 'project' ? (officeEmployeeSortDir === 'asc' ? '↑' : '↓') : ''}</button>
                          <button type="button" onClick={() => toggleOfficeEmployeeSort('location')}>Location {officeEmployeeSortKey === 'location' ? (officeEmployeeSortDir === 'asc' ? '↑' : '↓') : ''}</button>
                        </div>
                        {officeEmployees.map((row, idx) => (
                          <div
                            key={`${row.name}-${row.project}-${idx}`}
                            className="staffing-table-row"
                            onClick={() => openOfficeContactCard(row)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                openOfficeContactCard(row)
                              }
                            }}
                            title={`Open contact card for ${row.name}`}
                          >
                            <span>{row.name}</span>
                            <span>{row.position}</span>
                            <span>
                              <span style={{ display: 'inline-grid', justifyItems: 'center', textAlign: 'center', gap: 2 }}>
                                {getRowProjectsLikePopup(row).map((project, projectIdx) => (
                                  <span key={`${row.name}-project-${projectIdx}`}>{project}</span>
                                ))}
                              </span>
                            </span>
                            <span>{row.location}</span>
                          </div>
                        ))}
                      </div>
                    </article>
                  ) : null}

                  {isOfficeOrganizationTab && showOfficeContactCard && officeContactCardDraft ? (
                    <div className="calendar-modal-backdrop" onClick={() => setShowOfficeContactCard(false)}>
                      <div className="calendar-popover directory-contact-popover office-contact-card side-detail-panel" onClick={(e) => e.stopPropagation()}>
                        <div className="office-contact-card-head">
                          <button className="office-contact-card-x" onClick={() => setShowOfficeContactCard(false)} aria-label="Close">×</button>
                        </div>

                        <div className="office-contact-card-main">
                          <div className="office-contact-avatar" aria-label={`${officeContactCardDraft.name} profile image`} title={officeContactCardDraft.name}>
                            {officeContactCardDraft.name
                              .split(' ')
                              .filter(Boolean)
                              .slice(0, 2)
                              .map((part) => part[0]?.toUpperCase())
                              .join('') || 'NA'}
                          </div>
                          <div className="office-contact-identity">
                            <input className={`office-contact-name ${officeContactSaveAttempted && !officeContactCardDraft.name.trim() ? 'required-missing' : ''}`} placeholder="Employee name *" value={officeContactCardDraft.name} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, name: e.target.value } : prev))} />
                            <input className="office-contact-position" placeholder="Position" value={officeContactCardDraft.position} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, position: e.target.value } : prev))} />
                          </div>
                        </div>

                        <div className="office-contact-meta">
                          <div className="office-contact-meta-row office-contact-project-row">
                            <strong>Current Projects:</strong>
                            <div className="office-contact-project-pills">
                              {(officeContactCardDraft.projects?.length ? officeContactCardDraft.projects : [officeContactCardDraft.project || '']).map((proj, idx) => (
                                <div key={'access-' + idx + '-' + (proj || 'new')} className="office-contact-project-pill">
                                  <select value={proj || ''} onChange={(e) => setOfficeContactCardDraft((prev) => {
                                    if (!prev) return prev
                                    const nextValue = e.target.value
                                    const projects = [...(prev.projects?.length ? prev.projects : [prev.project || ''])]
                                    projects[idx] = nextValue
                                    const deduped = projects.filter((x, i) => x || i === projects.length - 1).filter((x, i, arr) => !x || arr.indexOf(x) === i)
                                    const withTrailingEmpty = deduped.includes('') ? deduped : [...deduped, '']
                                    const primary = withTrailingEmpty[0] || ''
                                    return { ...prev, project: primary, projects: withTrailingEmpty, reportsTo: idx === 0 ? (getProjectPrincipal(primary) || prev.reportsTo) : prev.reportsTo }
                                  })}>
                                    <option value="">Select project</option>
                                    {projectAccessOptions.map((name) => <option key={name} value={name}>{toTitleCase(name)}</option>)}
                                  </select>
                                  {idx > 0 ? (
                                    <button type="button" className="project-access-remove-btn" onClick={() => setOfficeContactCardDraft((prev) => {
                                      if (!prev) return prev
                                      const projects = [...(prev.projects?.length ? prev.projects : [prev.project || ''])]
                                      projects.splice(idx, 1)
                                      const primary = projects[0] || ''
                                      return { ...prev, project: primary, projects }
                                    })}>×</button>
                                  ) : null}
                                </div>
                              ))}
                              <button type="button" className="project-access-add-btn" onClick={() => setOfficeContactCardDraft((prev) => {
                                if (!prev) return prev
                                const projects = [...(prev.projects?.length ? prev.projects : [prev.project || ''])]
                                if (projects.includes('')) return prev
                                return { ...prev, projects: [...projects, ''] }
                              })}>＋</button>
                            </div>
                          </div>
                          <div className="office-contact-meta-hint">Current projects control this user's app access scope.</div>
                          <div className="office-contact-meta-split">
                            <div className="office-contact-meta-left">
                              <div className="office-contact-meta-row office-contact-meta-row-half"><strong className={officeContactSaveAttempted && !officeContactCardDraft.accessTier.trim() ? 'required-missing' : ''}>Web Access *:</strong> <select className={`office-contact-access-tier ${officeContactCardDraft.accessTier ? 'has-value' : ''} ${officeContactSaveAttempted && !officeContactCardDraft.accessTier.trim() ? 'required-missing' : ''}`} value={officeContactCardDraft.accessTier} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, accessTier: e.target.value } : prev))}>
                                <option value="">Web access</option>
                                {COMPANY_TIERS.map((tier) => <option key={`office-tier-${tier}`} value={tier}>{tier}</option>)}
                              </select></div>
                              <div className="office-contact-meta-hint">Web access controls user's app access scope.</div>
                              <div className="office-contact-meta-row office-contact-meta-row-half"><strong>Location:</strong> <input placeholder="TBD" value={officeContactCardDraft.location} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, location: e.target.value } : prev))} /></div>
                              <div className="office-contact-meta-row office-contact-meta-row-half"><strong>Reports To:</strong> <input placeholder="Principal/Project Manager" value={officeContactCardDraft.reportsTo} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, reportsTo: e.target.value } : prev))} /></div>
                            </div>
                            <div className="office-contact-meta-right">
                              <label>Past Projects</label>
                              <textarea value={officeContactCardDraft.pastProjects} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, pastProjects: e.target.value } : prev))} placeholder="List past projects" />
                            </div>
                          </div>
                        </div>

                        <div className="office-contact-grid">
                          <div className="office-contact-col">
                            <div className="office-contact-field"><label><span className="office-contact-icon">✉︎</span> Email:</label><input value={officeContactCardDraft.email} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, email: e.target.value } : prev))} /></div>
                            <div className="office-contact-field"><label><span className="office-contact-icon">☎︎</span> Cell:</label><input value={officeContactCardDraft.cell} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, cell: e.target.value } : prev))} /></div>
                          </div>

                          <div className="office-contact-col office-contact-col-right">
                            <div className="office-contact-field"><label><span className="office-contact-icon">☎︎</span> Phone:</label><input value={officeContactCardDraft.phone} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, phone: e.target.value } : prev))} /></div>
                            <div className="office-contact-field"><label>Extension:</label><input value={officeContactCardDraft.extension} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, extension: e.target.value } : prev))} /></div>
                          </div>
                        </div>
                        <div className="row office-contact-actions" style={{ justifyContent: 'flex-end', gap: 8 }}>
                          <button onClick={() => setShowOfficeContactCard(false)}>Close</button>
                          {activeTab === 'officeOverview' ? <button className="danger" onClick={deleteOfficeContactCard}>Delete</button> : null}
                          <button className="modal-save-btn" onClick={saveOfficeContactCard}>Save</button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>

                {!isOfficeOrganizationTab ? (
                  <aside className="dashboard-side-col">
                    <article className="dashboard-card">
                      <h3>Team Members</h3>
                      <ul>
                        {dashboardTeamMembers.slice(0, 12).map((member, idx) => (
                          <li key={`${member.name}-${member.project}-${idx}`}>{member.name} — {member.position || 'Role'}</li>
                        ))}
                        {dashboardTeamMembers.length === 0 ? <li>No team members assigned to this project.</li> : null}
                      </ul>
                    </article>
                  </aside>
                ) : null}
              </div>
            </section>
          )}

          {activeTab === 'consultantCoordinationTracking' && (
            <section className="panel calendar-panel">
              <div className="calendar-shell">
                <aside className="calendar-left-rail consultant-left-rail">
                  <div className="consultant-desktop-nav">
                    {(consultantTeamTabs.length ? consultantTeamTabs : [ALL_CONSULTANT_TAB]).map((team) => (
                      <button
                        key={team}
                        className={`rail-dot ${consultantView === team ? 'active' : ''}`}
                        onClick={() => setConsultantView(team)}
                        title={team}
                      >
                        {consultantTeamAbbrev[team] || team}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="rail-dot consultant-add-tracker"
                    onClick={addConsultantTrackerList}
                    title="Add consultant tracker list"
                    aria-label="Add consultant tracker list"
                  >
                    +
                  </button>

                  <select
                    id="consultant-team-select"
                    className="consultant-team-select consultant-mobile-nav"
                    value={consultantView}
                    onChange={(e) => setConsultantView(e.target.value)}
                  >
                    {(consultantTeamTabs.length ? consultantTeamTabs : [ALL_CONSULTANT_TAB]).map((team) => (
                      <option key={team} value={team}>{team}</option>
                    ))}
                  </select>
                </aside>

                <div className="calendar-main">
                  <div className="calendar-header-row">
                    <h1 className="calendar-title">{isConsultantAllView ? consultantView : consultantTrackerMeta.discipline}</h1>
                    <div className="calendar-year-legend-wrap" style={{ marginLeft: 'auto' }}>
                      <button
                        type="button"
                        className="calendar-year-legend-btn"
                        aria-label="Show consultant status legend"
                        onClick={() => setShowConsultantLegend((v) => !v)}
                      >
                        <svg className="calendar-year-legend-btn-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                          <circle cx="12" cy="12" r="9" fill="none" />
                          <line x1="12" y1="10" x2="12" y2="16" />
                          <circle cx="12" cy="7" r="0.9" fill="currentColor" stroke="none" />
                        </svg>
                      </button>
                      {showConsultantLegend ? (
                        <div className="calendar-year-legend" aria-label="Consultant status legend">
                          <span className="calendar-year-legend-item">On Track = <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--status-success)' }} /></span>
                          <span className="calendar-year-legend-item">Due Soon (due within 2 days) = <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--status-warning)' }} /></span>
                          <span className="calendar-year-legend-item">Overdue (due date passed) = <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--status-danger)' }} /></span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="consultant-company-title">
                    {isConsultantAllView
                      ? <span className="consultant-all-summary">The Combined Tasks Between All Consultants</span>
                      : consultantTrackerMeta.company
                        ? <span className="consultant-company-name">{consultantTrackerMeta.company}</span>
                        : consultantCompanies.length
                          ? consultantCompanies.map((company) => (
                            <span key={company} className="consultant-company-name">
                              {company}
                            </span>
                          ))
                          : 'No company listed'}
                  </div>

                  <div className="consultant-health-strip">
                    <button type="button" className={`consultant-health-chip ${consultantRiskFilter === 'all' ? 'active' : ''}`} onClick={() => setConsultantRiskFilter('all')}>All ({consultantCoordRows.length})</button>
                    <button type="button" className={`consultant-health-chip ${consultantRiskFilter === 'atRisk' ? 'active' : ''}`} onClick={() => setConsultantRiskFilter('atRisk')}>At Risk ({consultantRiskCounts.atRisk})</button>
                    <button type="button" className={`consultant-health-chip ${consultantRiskFilter === 'waiting' ? 'active' : ''}`} onClick={() => setConsultantRiskFilter('waiting')}>Waiting ({consultantRiskCounts.waiting})</button>
                    <button type="button" className={`consultant-health-chip ${consultantRiskFilter === 'overdue' ? 'active' : ''}`} onClick={() => setConsultantRiskFilter('overdue')}>Overdue ({consultantRiskCounts.overdue})</button>
                    <button type="button" className={`consultant-health-chip ${consultantRiskFilter === 'noDue' ? 'active' : ''}`} onClick={() => setConsultantRiskFilter('noDue')}>No Due Date ({consultantRiskCounts.noDue})</button>
                    {isConsultantAllView ? (
                      <>
                        <select className="consultant-health-filter-select push" value={allConsultantFilter} onChange={(e) => setAllConsultantFilter(e.target.value as 'all' | 'overdue' | 'thisWeek' | 'noDue')}>
                          <option value="all">All</option>
                          <option value="overdue">Overdue</option>
                          <option value="thisWeek">This Week</option>
                          <option value="noDue">No Due Date</option>
                        </select>
                        <select className="consultant-health-filter-select" value={allConsultantSort} onChange={(e) => setAllConsultantSort(e.target.value as 'due' | 'company')}>
                          <option value="due">Sort: Due Date</option>
                          <option value="company">Sort: Company</option>
                        </select>
                      </>
                    ) : null}
                    <select
                      className="consultant-health-filter-select"
                      value=""
                      onChange={(e) => {
                        const idx = Number(e.target.value)
                        if (Number.isNaN(idx) || idx < 0 || !savedConsultantViews[idx]) return
                        const v = savedConsultantViews[idx]
                        setConsultantRiskFilter(v.risk)
                        setAllConsultantFilter(v.allFilter)
                        setAllConsultantSort(v.allSort)
                        showCenterToast(`Loaded view: ${v.name}`)
                      }}
                    >
                      <option value="">Saved view…</option>
                      {savedConsultantViews.map((v, i) => <option key={`${v.name}-${i}`} value={i}>{v.name}</option>)}
                    </select>
                    <button
                      type="button"
                      className="consultant-health-chip"
                      onClick={() => {
                        const name = typeof window !== 'undefined' ? window.prompt('Save current view as:') : ''
                        const trimmed = (name || '').trim()
                        if (!trimmed) return
                        setSavedConsultantViews((prev) => ([...prev.filter((x) => x.name !== trimmed), { name: trimmed, risk: consultantRiskFilter, allFilter: allConsultantFilter, allSort: allConsultantSort }]))
                        showCenterToast(`Saved view: ${trimmed}`)
                      }}
                    >Save View</button>
                  </div>

                  <div className="consultant-command-grid">
                    <div className="consultant-command-feed">
                      {consultantFilteredFeedRows.length === 0 ? <div className="muted">No items for this filter.</div> : consultantVisibleFeedRows.map((row) => (
                        <div
                          key={`feed-${row.team}-${row.id}`}
                          className={`consultant-command-feed-row ${selectedConsultantFeedRow?.id === row.id ? 'active' : ''}`}
                          role="button"
                          tabIndex={0}
                          data-consultant-feed-key={consultantFeedRowKey(row)}
                          onClick={() => {
                            setSelectedConsultantFeedId(row.id)
                            const rowKey = consultantFeedRowKey(row)
                            window.setTimeout(() => {
                              const el = document.querySelector(`[data-consultant-feed-key="${rowKey}"]`) as HTMLElement | null
                              el?.focus()
                            }, 0)
                            jumpToConsultantDetailRow(row)
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              setSelectedConsultantFeedId(row.id)
                              jumpToConsultantDetailRow(row)
                              return
                            }
                            if (e.key === 'ArrowDown') {
                              e.preventDefault()
                              focusConsultantFeedRowByOffset(row, 1)
                              return
                            }
                            if (e.key === 'ArrowUp') {
                              e.preventDefault()
                              focusConsultantFeedRowByOffset(row, -1)
                              return
                            }
                            if (e.key === 'Home') {
                              e.preventDefault()
                              const first = consultantVisibleFeedRows[0]
                              if (!first) return
                              setSelectedConsultantFeedId(first.id)
                              const el = document.querySelector(`[data-consultant-feed-key="${consultantFeedRowKey(first)}"]`) as HTMLElement | null
                              el?.focus()
                              return
                            }
                            if (e.key === 'End') {
                              e.preventDefault()
                              const last = consultantVisibleFeedRows[consultantVisibleFeedRows.length - 1]
                              if (!last) return
                              setSelectedConsultantFeedId(last.id)
                              const el = document.querySelector(`[data-consultant-feed-key="${consultantFeedRowKey(last)}"]`) as HTMLElement | null
                              el?.focus()
                            }
                          }}
                        >
                          <div>
                            <strong>{row.title}</strong>
                            <div className="muted">
                              {row.lane === 'To Consultant' ? 'To Client' : row.lane === 'From Consultant' ? 'From Client' : row.lane} • {isConsultantAllView ? `Company Name: ${resolveConsultantCompanyName(row.team, row.title, row.id, row.sourceCompany)}` : `Assignee: ${row.assignee || '-'}`} • {formatShortDate(row.dueDate)}
                              {Array.isArray(row.attachments) && row.attachments.length ? (
                                <>
                                  {' '}•{' '}
                                  <span
                                    className="consultant-inline-attachment-count consultant-inline-attachment-trigger"
                                    onMouseEnter={() => startConsultantAttachmentPreviewHover(`${row.team}::${row.id}`)}
                                    onMouseLeave={stopConsultantAttachmentPreviewHover}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      toggleConsultantAttachmentPreviewPinned(`${row.team}::${row.id}`)
                                    }}
                                    aria-label={`${row.attachments.length} attachment${row.attachments.length === 1 ? '' : 's'}`}
                                  >
                                    <svg className="consultant-action-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M21.44 11.05l-8.49 8.49a6 6 0 01-8.49-8.49l9.19-9.2a4 4 0 115.66 5.66l-9.2 9.2a2 2 0 01-2.83-2.83l8.49-8.49" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                    <span>{row.attachments.length}</span>
                                    {consultantAttachmentPreviewKey === `${row.team}::${row.id}` || consultantAttachmentPinnedKey === `${row.team}::${row.id}` ? (
                                      <div className="consultant-attachment-hover-popover" onMouseEnter={() => startConsultantAttachmentPreviewHover(`${row.team}::${row.id}`)} onMouseLeave={stopConsultantAttachmentPreviewHover}>
                                        <div className="consultant-attachment-hover-title">Attachments</div>
                                        {row.attachments.map((att) => (
                                          <button
                                            key={att.id}
                                            type="button"
                                            title={att.name}
                                            className="consultant-attachment-hover-thumb"
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              openConsultantImagePreview(att.dataUrl, att.name, row.attachments || [], att.id)
                                            }}
                                          >
                                            <img src={att.dataUrl} alt={att.name} />
                                          </button>
                                        ))}
                                      </div>
                                    ) : null}
                                  </span>
                                </>
                              ) : null}
                            </div>
                          </div>
                          <div className="consultant-command-feed-actions">
                            <button
                              type="button"
                              className={`consultant-feed-status consultant-feed-status-btn ${row.status}`}
                              onClick={(e) => {
                                e.stopPropagation()
                                if (row.status === 'overdue' || row.status === 'noDue') return
                                const key = row.lane === 'To Consultant' ? 'toConsultant' : row.lane === 'From Consultant' ? 'fromConsultant' : 'nextDeliverable'
                                const waitingOn = row.waitingOn === 'consultant' ? 'internal' : 'consultant'
                                updateConsultantItemInTeam(row.team, key, row.id, { waitingOn })
                              }}
                            >
                              {row.status === 'waiting' ? 'Waiting' : row.status === 'atRisk' ? 'At Risk' : 'On Track'}
                            </button>
                          </div>
                        </div>
                      ))}
                      {consultantFilteredFeedRows.length > 5 ? (
                        <button
                          type="button"
                          className="consultant-overflow-toggle"
                          onClick={() => setConsultantFeedExpanded((v) => !v)}
                        >
                          {consultantFeedExpanded
                            ? '▲ Show less'
                            : `▼ ${consultantFilteredFeedRows.length - 5} more — click to view`}
                        </button>
                      ) : null}
                    </div>

                    <aside className="consultant-linked-panel">
                      <h4>Linked Records</h4>
                      <div className="muted">Decisions</div>
                      {linkedMeetingDecisions.length ? linkedMeetingDecisions.map((item) => <div key={`decision-${item.id}`} className="consultant-linked-item">• {item.item}</div>) : <div className="consultant-linked-item muted">No linked decisions yet.</div>}
                      <h5 style={{ marginTop: 10, marginBottom: 6 }}>Primary Contacts</h5>
                      {linkedContactsForConsultant.length ? linkedContactsForConsultant.map((contact, idx) => {
                        const ext = (contact.officeExtension || '').trim()
                        const officeRaw = (contact.phoneType === 'Office' ? (contact.phone || '') : contact.altPhoneType === 'Office' ? (contact.altPhone || '') : '').trim()
                        const cellRaw = (contact.phoneType === 'Cell' ? (contact.phone || '') : contact.altPhoneType === 'Cell' ? (contact.altPhone || '') : '').trim()
                        const officeDisplay = officeRaw ? `${officeRaw}${ext ? ` ext. ${ext}` : ''}` : ''
                        const cellDisplay = cellRaw
                        const email = (contact.email || '').trim()
                        const officeDial = officeRaw ? officeRaw.replace(/[^\d+]/g, '') : ''
                        const cellDial = cellRaw ? cellRaw.replace(/[^\d+]/g, '') : ''
                        return (
                          <div key={`linked-contact-${contact.name}-${idx}`} className="consultant-linked-item" style={{ display: 'grid', gap: 2, marginBottom: 8 }}>
                            <button
                              type="button"
                              className="contact-action-btn"
                              style={{
                                cursor: 'pointer',
                                color: 'var(--quicksand)',
                                textDecoration: 'underline',
                                fontWeight: 600,
                                background: 'transparent',
                                border: 'none',
                                padding: 0,
                                textAlign: 'left',
                                justifyContent: 'flex-start',
                              }}
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                const key = directoryContactKey(contact)
                                const foundIndex = directoryContacts.findIndex((row) => directoryContactKey(row) === key)
                                if (foundIndex >= 0) {
                                  setEditContactIndex(foundIndex)
                                  setShowEditContact(true)
                                  return
                                }
                                showCenterToast('Contact not found in Directory')
                              }}
                            >
                              {contact.name}
                            </button>
                            <div style={{ paddingLeft: 20 }}>
                              {email ? (
                                <a
                                  className="muted"
                                  href={`mailto:${email}`}
                                  style={{ fontSize: 12 }}
                                  title="Email (also copies on click)"
                                  onClick={async () => {
                                    const ok = await copyText(email)
                                    showCopyToast(ok ? 'Copied' : 'Copy failed')
                                  }}
                                >
                                  {email}
                                </a>
                              ) : <span className="muted" style={{ fontSize: 12 }}>—</span>}
                            </div>
                            <div style={{ paddingLeft: 20 }}>
                              {officeDial ? (
                                <a
                                  className="muted"
                                  href={`tel:${officeDial}`}
                                  style={{ fontSize: 12 }}
                                  title="Call Work (also copies on click)"
                                  onClick={async () => {
                                    const ok = await copyText(officeDisplay)
                                    showCopyToast(ok ? 'Copied' : 'Copy failed')
                                  }}
                                >
                                  Work: {officeDisplay}
                                </a>
                              ) : <span className="muted" style={{ fontSize: 12 }}>Work: —</span>}
                            </div>
                            <div style={{ paddingLeft: 20 }}>
                              {cellDial ? (
                                <a
                                  className="muted"
                                  href={`tel:${cellDial}`}
                                  style={{ fontSize: 12 }}
                                  title="Call Cell (also copies on click)"
                                  onClick={async () => {
                                    const ok = await copyText(cellDisplay)
                                    showCopyToast(ok ? 'Copied' : 'Copy failed')
                                  }}
                                >
                                  Cell: {cellDisplay}
                                </a>
                              ) : <span className="muted" style={{ fontSize: 12 }}>Cell: —</span>}
                            </div>
                          </div>
                        )
                      }) : <div className="consultant-linked-item muted">No linked primary contacts.</div>}
                    </aside>
                  </div>

                  <div className="consultant-sections">
                    <section className="consultant-section-card">
                      <h3 style={{ margin: 0 }}>Items to Send to Consultant</h3>
                      {!isConsultantAllView && Array.from(consultantSelectedDetailKeys).filter((k) => k.includes('::toConsultant::')).length > 0 ? (
                        <div className="consultant-bulk-actions" style={{ marginTop: 8, marginBottom: 8 }}>
                          <span className="muted small" style={{ marginRight: 6 }}>{Array.from(consultantSelectedDetailKeys).filter((k) => k.includes('::toConsultant::')).length} selected</span>
                          <button type="button" className="consultant-overflow-toggle" onClick={() => openConsultantBulkAttachmentPicker('toConsultant')}>Attach to Selected</button>
                          <button type="button" className="consultant-overflow-toggle" onClick={() => bulkUpdateConsultantSelected({ waitingOn: 'consultant' }, 'toConsultant')}>Set Waiting</button>
                          <button type="button" className="consultant-overflow-toggle" onClick={() => bulkUpdateConsultantSelected({ waitingOn: 'internal' }, 'toConsultant')}>Set On Track</button>
                          <button type="button" className="consultant-overflow-toggle danger" onClick={() => bulkDeleteConsultantSelected('toConsultant')}>Delete</button>
                          <button
                            type="button"
                            className="consultant-overflow-toggle"
                            onClick={() => setConsultantSelectedDetailKeys((prev) => new Set(Array.from(prev).filter((k) => !k.includes('::toConsultant::'))))}
                          >
                            Clear
                          </button>
                        </div>
                      ) : null}
                      {!isConsultantAllView ? (
                        <>
                        <div className="consultant-entry-row">
                          <input
                            className="consultant-entry-line"
                            placeholder="Add new item…"
                            value={consultantDrafts.toConsultant.text}
                            onChange={(e) =>
                              setConsultantDrafts((prev) => ({ ...prev, toConsultant: { ...prev.toConsultant, text: e.target.value } }))
                            }
                            onKeyDown={(e) => submitOnEnter('toConsultant', e)}
                          />
                          <input
                            className="consultant-entry-assignee"
                            aria-label="Assignee"
                            placeholder="Assignee"
                            value={consultantDrafts.toConsultant.assignee === '-' ? '' : consultantDrafts.toConsultant.assignee}
                            onChange={(e) =>
                              setConsultantDrafts((prev) => ({ ...prev, toConsultant: { ...prev.toConsultant, assignee: e.target.value || '-' } }))
                            }
                          />
                          <input
                            className="consultant-entry-date"
                            type="date"
                            aria-label="Due date"
                            value={consultantDrafts.toConsultant.dueDate}
                            onChange={(e) =>
                              setConsultantDrafts((prev) => ({ ...prev, toConsultant: { ...prev.toConsultant, dueDate: e.target.value } }))
                            }
                            onKeyDown={(e) => submitOnEnter('toConsultant', e)}
                          />
                          <button type="button" className="consultant-entry-submit" onClick={() => addConsultantItem('toConsultant')} title="Add item" aria-label="Add item">+</button>
                        </div>

                        </>
                      ) : null}
                      <div className="consultant-section-body consultant-rows">
                        <div className="consultant-row-pair consultant-row-header">
                          <div className="consultant-item-cell">
                            <button type="button" className="consultant-inline-col-header consultant-inline-col-header-btn" onClick={() => toggleConsultantColumnSort('item')}>
                              Item <span className="consultant-col-sort-marker">{consultantSortMarker('item')}</span>
                            </button>
                          </div>
                          <div className="consultant-assignee-cell">
                            <button type="button" className="consultant-inline-col-header consultant-inline-col-header-btn" onClick={() => toggleConsultantColumnSort('assignee')}>
                              Assignee <span className="consultant-col-sort-marker">{consultantSortMarker('assignee')}</span>
                            </button>
                          </div>
                          <div className="consultant-due-cell">
                            <button type="button" className="consultant-inline-col-header consultant-inline-col-header-btn" onClick={() => toggleConsultantColumnSort('dueDate')}>
                              Due Date <span className="consultant-col-sort-marker">{consultantSortMarker('dueDate')}</span>
                            </button>
                          </div>
                        </div>
                        {displayConsultantItems.toConsultant.slice(0, consultantSendExpanded ? displayConsultantItems.toConsultant.length : 6).map((item) => {
                          const toRowNavKey = makeConsultantRowNavKey(String(item.id || ''), item.assignee, 'toConsultant')
                          return (
                          <div
                            key={item.id}
                            className={`consultant-row-pair ${isConsultantAllView ? 'consultant-row-readonly' : ''} ${selectedConsultantDetailKey === toRowNavKey ? 'consultant-row-active' : ''} ${(selectedConsultantDetailKey === toRowNavKey || consultantSelectedDetailKeys.has(toRowNavKey)) ? 'consultant-row-selected' : ''} ${consultantJumpSourceKey === toRowNavKey ? 'consultant-row-from-summary' : ''} ${consultantRowDragOverKey === toRowNavKey ? 'consultant-row-drag-over' : ''}`}
                            data-consultant-nav-key={toRowNavKey}
                            title={consultantJumpSourceKey === toRowNavKey ? `Opened from Summary Panel • Last updated ${formatMonthDayTime(consultantLastUpdatedAt)}` : `Last updated ${formatMonthDayTime(consultantLastUpdatedAt)}`}
                            tabIndex={0}
                            onKeyDown={(e) => {
                              const target = e.target as HTMLElement | null
                              const current = e.currentTarget as HTMLElement
                              const inControl = !!target && target !== current && !!target.closest('input, textarea, select, button, a, [contenteditable="true"]')
                              if (inControl) return
                              if (e.key === 'ArrowDown') {
                                e.preventDefault()
                                focusConsultantDetailRowByOffset(toRowNavKey, 1)
                                return
                              }
                              if (e.key === 'ArrowUp') {
                                e.preventDefault()
                                focusConsultantDetailRowByOffset(toRowNavKey, -1)
                                return
                              }
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                toggleConsultantDetailSelection(toRowNavKey)
                              }
                            }}
                            onFocus={() => setSelectedConsultantDetailKey(toRowNavKey)}
                            onClick={(e) => {
                              const target = e.target as HTMLElement | null
                              if (target?.closest('input, textarea, select, button, a, [contenteditable="true"]')) return
                              setSelectedConsultantDetailKey(toRowNavKey)
                              ;(e.currentTarget as HTMLElement).focus()
                            }}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'copy' }}
                            onDragEnter={() => setConsultantRowDragOverKey(toRowNavKey)}
                            onDragLeave={() => setConsultantRowDragOverKey(null)}
                            onPaste={(e) => { if (!isConsultantAllView) void onConsultantRowPaste(e, 'toConsultant', item.id) }}
                            onDrop={(e) => { if (!isConsultantAllView) void onConsultantDrop(e, toRowNavKey) }}
                          >
                            <div className="consultant-item-cell consultant-item-cell-editable" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <button
                                type="button"
                                className={`consultant-select-btn ${consultantSelectedDetailKeys.has(toRowNavKey) ? 'active' : ''}`}
                                aria-label={consultantSelectedDetailKeys.has(toRowNavKey) ? 'Deselect row' : 'Select row'}
                                aria-pressed={consultantSelectedDetailKeys.has(toRowNavKey)}
                                title={consultantSelectedDetailKeys.has(toRowNavKey) ? 'Deselect row' : 'Select row'}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  toggleConsultantDetailSelection(toRowNavKey)
                                  const row = (e.currentTarget as HTMLElement).closest('[data-consultant-nav-key]') as HTMLElement | null
                                  row?.focus()
                                }}
                              >
                                {consultantSelectedDetailKeys.has(toRowNavKey) ? '☑' : '☐'}
                              </button>
                              <span
                                title={getConsultantSendState(item) === 'breached' ? 'Send status: Overdue' : getConsultantSendState(item) === 'risk' ? 'Send status: Due soon' : 'Send status: On track'}
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  flexShrink: 0,
                                  background: getConsultantSendState(item) === 'breached'
                                    ? 'var(--status-danger)'
                                    : getConsultantSendState(item) === 'risk'
                                      ? 'var(--status-warning)'
                                      : 'var(--status-success)',
                                }}
                              />
                              <div style={{ display: 'grid', gap: 2, width: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <div className="consultant-inline-input" aria-label="Task text">{item.title}</div>
                                  {!isConsultantAllView ? (
                                    <button
                                      className={`consultant-feed-status consultant-feed-status-btn ${item.waitingOn === 'consultant' ? 'waiting' : 'onTrack'}`}
                                      onClick={() => updateConsultantItemInTeam(consultantView, 'toConsultant', item.id, { waitingOn: item.waitingOn === 'consultant' ? 'internal' : 'consultant' })}
                                      title="Toggle waiting state"
                                      aria-label="Toggle waiting state"
                                      type="button"
                                      style={{ whiteSpace: 'nowrap' }}
                                    >
                                      {item.waitingOn === 'consultant' ? 'Waiting' : 'On Track'}
                                    </button>
                                  ) : null}
                                </div>
                                {isConsultantAllView ? (
                                  <div className="muted small">
                                    To Client • {isConsultantAllView ? `Company Name: ${resolveConsultantCompanyName('', item.title, item.id, item.sourceCompany) || 'Company TBD'}` : `Assignee: ${item.assignee || '-'}`} • {formatShortDate(item.dueDate)}
                                  </div>
                                ) : null}
                                {Array.isArray(item.attachments) && item.attachments.length ? (
                                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                                    {item.attachments.map((att) => (
                                      <div key={att.id} className="consultant-attachment-thumb" style={{ position: 'relative' }}>
                                        <button
                                          type="button"
                                          title={att.name}
                                          onClick={() => openConsultantImagePreview(att.dataUrl, att.name, item.attachments || [], att.id, { key: 'toConsultant', itemId: item.id, attachmentId: att.id })}
                                          style={{ border: '1px solid var(--line)', borderRadius: 6, padding: 0, background: 'transparent', cursor: 'pointer' }}
                                        >
                                          <img src={att.dataUrl} alt={att.name} style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            <div className="consultant-assignee-cell">
                              <div className="consultant-inline-assignee consultant-inline-assignee-readonly">{item.assignee || '-'}</div>
                            </div>
                            <div className={`consultant-due-cell ${!item.dueDate ? 'is-empty' : ''}`}>
                              <div className="consultant-inline-date consultant-inline-date-readonly">{formatShortDate(item.dueDate)}</div>
                            </div>
                            {!isConsultantAllView ? (
                              <div className="consultant-row-actions" role="group" aria-label="Item actions">
                                <button className="consultant-edit-btn" onClick={() => openConsultantAttachmentPicker('toConsultant', item.id)} title="Attach reference image" aria-label="Attach reference image">
                                  <svg className="consultant-action-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M21.44 11.05l-8.49 8.49a6 6 0 01-8.49-8.49l9.19-9.2a4 4 0 115.66 5.66l-9.2 9.2a2 2 0 01-2.83-2.83l8.49-8.49" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                </button>
                                <button className="consultant-edit-btn" onClick={() => copyAttachmentsFromPreviousItem('toConsultant', item.id)} title="Copy attachments from previous item" aria-label="Copy attachments from previous item">⎘</button>
                                <button className="consultant-edit-btn" onClick={() => editConsultantItem('toConsultant', item.id)} title="Edit item" aria-label="Edit item">
                                  <svg className="consultant-action-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M16.5 3.5a2.12 2.12 0 113 3L8 18l-4 1 1-4 11.5-11.5z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                </button>
                                <button className="consultant-edit-btn consultant-edit-btn-delete" onClick={() => deleteConsultantItemDirect('toConsultant', item.id)} title="Delete item" aria-label="Delete item">
                                  <svg className="consultant-action-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M8 6V4h8v2m-1 0v14H9V6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                </button>
                              </div>
                            ) : null}
                          </div>
                        )})}
                        {displayConsultantItems.toConsultant.length > 6 ? (
                          <button
                            type="button"
                            className="consultant-overflow-toggle"
                            onClick={() => setConsultantSendExpanded((v) => !v)}
                          >
                            {consultantSendExpanded
                              ? '▲ Show less'
                              : `▼ ${displayConsultantItems.toConsultant.length - 6} more — click to view`}
                          </button>
                        ) : null}
                        {displayConsultantItems.toConsultant.length === 0 ? (
                          <div className="muted consultant-empty-state">No items to send yet — add your first item above.</div>
                        ) : null}
                      </div>
                    </section>

                    <section className="consultant-section-card">
                      <h3 style={{ margin: 0 }}>Items Expected from Consultant</h3>
                      {!isConsultantAllView && Array.from(consultantSelectedDetailKeys).filter((k) => k.includes('::fromConsultant::')).length > 0 ? (
                        <div className="consultant-bulk-actions" style={{ marginTop: 8, marginBottom: 8 }}>
                          <span className="muted small" style={{ marginRight: 6 }}>{Array.from(consultantSelectedDetailKeys).filter((k) => k.includes('::fromConsultant::')).length} selected</span>
                          <button type="button" className="consultant-overflow-toggle" onClick={() => openConsultantBulkAttachmentPicker('fromConsultant')}>Attach to Selected</button>
                          <button type="button" className="consultant-overflow-toggle" onClick={() => bulkUpdateConsultantSelected({ waitingOn: 'consultant' }, 'fromConsultant')}>Set Waiting</button>
                          <button type="button" className="consultant-overflow-toggle" onClick={() => bulkUpdateConsultantSelected({ waitingOn: 'internal' }, 'fromConsultant')}>Set On Track</button>
                          <button type="button" className="consultant-overflow-toggle danger" onClick={() => bulkDeleteConsultantSelected('fromConsultant')}>Delete</button>
                          <button
                            type="button"
                            className="consultant-overflow-toggle"
                            onClick={() => setConsultantSelectedDetailKeys((prev) => new Set(Array.from(prev).filter((k) => !k.includes('::fromConsultant::'))))}
                          >
                            Clear
                          </button>
                        </div>
                      ) : null}
                      {!isConsultantAllView ? (
                        <>
                        <div className="consultant-entry-row">
                          <input
                            className="consultant-entry-line"
                            placeholder="Add new item…"
                            value={consultantDrafts.fromConsultant.text}
                            onChange={(e) =>
                              setConsultantDrafts((prev) => ({ ...prev, fromConsultant: { ...prev.fromConsultant, text: e.target.value } }))
                            }
                            onKeyDown={(e) => submitOnEnter('fromConsultant', e)}
                          />
                          <input
                            className="consultant-entry-assignee"
                            aria-label="Assignee"
                            placeholder="Assignee"
                            value={consultantDrafts.fromConsultant.assignee === '-' ? '' : consultantDrafts.fromConsultant.assignee}
                            onChange={(e) =>
                              setConsultantDrafts((prev) => ({ ...prev, fromConsultant: { ...prev.fromConsultant, assignee: e.target.value || '-' } }))
                            }
                          />
                          <input
                            className="consultant-entry-date"
                            type="date"
                            aria-label="Due date"
                            value={consultantDrafts.fromConsultant.dueDate}
                            onChange={(e) =>
                              setConsultantDrafts((prev) => ({ ...prev, fromConsultant: { ...prev.fromConsultant, dueDate: e.target.value } }))
                            }
                            onKeyDown={(e) => submitOnEnter('fromConsultant', e)}
                          />
                          <button type="button" className="consultant-entry-submit" onClick={() => addConsultantItem('fromConsultant')} title="Add item" aria-label="Add item">+</button>
                        </div>

                        </>
                      ) : null}
                      <div className="consultant-section-body consultant-rows">
                        <div className="consultant-row-pair consultant-row-header">
                          <div className="consultant-item-cell">
                            <button type="button" className="consultant-inline-col-header consultant-inline-col-header-btn" onClick={() => toggleConsultantColumnSort('item')}>
                              Item <span className="consultant-col-sort-marker">{consultantSortMarker('item')}</span>
                            </button>
                          </div>
                          <div className="consultant-assignee-cell">
                            <button type="button" className="consultant-inline-col-header consultant-inline-col-header-btn" onClick={() => toggleConsultantColumnSort('assignee')}>
                              Assignee <span className="consultant-col-sort-marker">{consultantSortMarker('assignee')}</span>
                            </button>
                          </div>
                          <div className="consultant-due-cell">
                            <button type="button" className="consultant-inline-col-header consultant-inline-col-header-btn" onClick={() => toggleConsultantColumnSort('dueDate')}>
                              Due Date <span className="consultant-col-sort-marker">{consultantSortMarker('dueDate')}</span>
                            </button>
                          </div>
                        </div>
                        {displayConsultantItems.fromConsultant.slice(0, consultantReceiveExpanded ? displayConsultantItems.fromConsultant.length : 6).map((item) => {
                          const rawFromId = String(item.id || '')
                          const fromRowTeam = isConsultantAllView && rawFromId.includes('::') ? rawFromId.split('::')[0] : consultantView
                          const fromRowItemId = rawFromId.includes('::') ? rawFromId.split('::').slice(1).join('::') : rawFromId
                          const fromRowSection: ConsultantSectionKey = !isConsultantAllView && currentConsultantItems.nextDeliverable.some((x) => x.id === item.id) && !currentConsultantItems.fromConsultant.some((x) => x.id === item.id)
                            ? 'nextDeliverable'
                            : 'fromConsultant'
                          const fromRowNavKey = makeConsultantNavKey(fromRowTeam, fromRowSection, fromRowItemId)
                          return (
                          <div
                            key={item.id}
                            className={`consultant-row-pair ${isConsultantAllView ? 'consultant-row-readonly' : ''} ${selectedConsultantDetailKey === fromRowNavKey ? 'consultant-row-active' : ''} ${(selectedConsultantDetailKey === fromRowNavKey || consultantSelectedDetailKeys.has(fromRowNavKey)) ? 'consultant-row-selected' : ''} ${consultantJumpSourceKey === fromRowNavKey ? 'consultant-row-from-summary' : ''} ${consultantRowDragOverKey === fromRowNavKey ? 'consultant-row-drag-over' : ''}`}
                            data-consultant-nav-key={fromRowNavKey}
                            title={consultantJumpSourceKey === fromRowNavKey ? `Opened from Summary Panel • Last updated ${formatMonthDayTime(consultantLastUpdatedAt)}` : `Last updated ${formatMonthDayTime(consultantLastUpdatedAt)}`}
                            tabIndex={0}
                            onKeyDown={(e) => {
                              const target = e.target as HTMLElement | null
                              const current = e.currentTarget as HTMLElement
                              const inControl = !!target && target !== current && !!target.closest('input, textarea, select, button, a, [contenteditable="true"]')
                              if (inControl) return
                              if (e.key === 'ArrowDown') {
                                e.preventDefault()
                                focusConsultantDetailRowByOffset(fromRowNavKey, 1)
                                return
                              }
                              if (e.key === 'ArrowUp') {
                                e.preventDefault()
                                focusConsultantDetailRowByOffset(fromRowNavKey, -1)
                                return
                              }
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                toggleConsultantDetailSelection(fromRowNavKey)
                              }
                            }}
                            onFocus={() => setSelectedConsultantDetailKey(fromRowNavKey)}
                            onClick={(e) => {
                              const target = e.target as HTMLElement | null
                              if (target?.closest('input, textarea, select, button, a, [contenteditable="true"]')) return
                              setSelectedConsultantDetailKey(fromRowNavKey)
                              ;(e.currentTarget as HTMLElement).focus()
                            }}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'copy' }}
                            onDragEnter={() => setConsultantRowDragOverKey(fromRowNavKey)}
                            onDragLeave={() => setConsultantRowDragOverKey(null)}
                            onPaste={(e) => { if (!isConsultantAllView) void onConsultantRowPaste(e, fromRowSection, fromRowItemId) }}
                            onDrop={(e) => { if (!isConsultantAllView) void onConsultantDrop(e, fromRowNavKey) }}
                          >
                            <div className="consultant-item-cell consultant-item-cell-editable" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <button
                                type="button"
                                className={`consultant-select-btn ${consultantSelectedDetailKeys.has(fromRowNavKey) ? 'active' : ''}`}
                                aria-label={consultantSelectedDetailKeys.has(fromRowNavKey) ? 'Deselect row' : 'Select row'}
                                aria-pressed={consultantSelectedDetailKeys.has(fromRowNavKey)}
                                title={consultantSelectedDetailKeys.has(fromRowNavKey) ? 'Deselect row' : 'Select row'}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  toggleConsultantDetailSelection(fromRowNavKey)
                                  const row = (e.currentTarget as HTMLElement).closest('[data-consultant-nav-key]') as HTMLElement | null
                                  row?.focus()
                                }}
                              >
                                {consultantSelectedDetailKeys.has(fromRowNavKey) ? '☑' : '☐'}
                              </button>
                              <span
                                title={getConsultantSlaState(item) === 'breached' ? 'SLA: Breached' : getConsultantSlaState(item) === 'risk' ? 'SLA: At Risk' : 'SLA: On Track'}
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  flexShrink: 0,
                                  background: getConsultantSlaState(item) === 'breached'
                                    ? 'var(--status-danger)'
                                    : getConsultantSlaState(item) === 'risk'
                                      ? 'var(--status-warning)'
                                      : 'var(--status-success)',
                                }}
                              />
                              <div style={{ display: 'grid', gap: 2, width: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <div className="consultant-inline-input" aria-label="Task text">{item.title}</div>
                                  {!isConsultantAllView ? (
                                    <button
                                      className={`consultant-feed-status consultant-feed-status-btn ${item.waitingOn === 'consultant' ? 'waiting' : 'onTrack'}`}
                                      onClick={() => updateConsultantItemInTeam(consultantView, fromRowSection, fromRowItemId, { waitingOn: item.waitingOn === 'consultant' ? 'internal' : 'consultant' })}
                                      title="Toggle waiting state"
                                      aria-label="Toggle waiting state"
                                      type="button"
                                      style={{ whiteSpace: 'nowrap' }}
                                    >
                                      {item.waitingOn === 'consultant' ? 'Waiting' : 'On Track'}
                                    </button>
                                  ) : null}
                                </div>
                                {isConsultantAllView ? (
                                  <div className="muted small">
                                    From Client • {isConsultantAllView ? `Company Name: ${resolveConsultantCompanyName('', item.title, item.id, item.sourceCompany) || 'Company TBD'}` : `Assignee: ${item.assignee || '-'}`} • {formatShortDate(item.dueDate)}
                                  </div>
                                ) : null}
                                {Array.isArray(item.attachments) && item.attachments.length ? (
                                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                                    {item.attachments.map((att) => (
                                      <div key={att.id} className="consultant-attachment-thumb" style={{ position: 'relative' }}>
                                        <button
                                          type="button"
                                          title={att.name}
                                          onClick={() => openConsultantImagePreview(att.dataUrl, att.name, item.attachments || [], att.id, { key: fromRowSection, itemId: fromRowItemId, attachmentId: att.id })}
                                          style={{ border: '1px solid var(--line)', borderRadius: 6, padding: 0, background: 'transparent', cursor: 'pointer' }}
                                        >
                                          <img src={att.dataUrl} alt={att.name} style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            <div className="consultant-assignee-cell">
                              <div className="consultant-inline-assignee consultant-inline-assignee-readonly">{item.assignee || '-'}</div>
                            </div>
                            <div className={`consultant-due-cell ${!item.dueDate ? 'is-empty' : ''}`}>
                              <div className="consultant-inline-date consultant-inline-date-readonly">{formatShortDate(item.dueDate)}</div>
                            </div>
                            {!isConsultantAllView ? (
                              <div className="consultant-row-actions" role="group" aria-label="Item actions">
                                <button className="consultant-edit-btn" onClick={() => openConsultantAttachmentPicker(fromRowSection, fromRowItemId)} title="Attach reference image" aria-label="Attach reference image">
                                  <svg className="consultant-action-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M21.44 11.05l-8.49 8.49a6 6 0 01-8.49-8.49l9.19-9.2a4 4 0 115.66 5.66l-9.2 9.2a2 2 0 01-2.83-2.83l8.49-8.49" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                </button>
                                <button className="consultant-edit-btn" onClick={() => copyAttachmentsFromPreviousItem(fromRowSection, fromRowItemId)} title="Copy attachments from previous item" aria-label="Copy attachments from previous item">⎘</button>
                                <button className="consultant-edit-btn" onClick={() => editConsultantItem(fromRowSection, fromRowItemId)} title="Edit item" aria-label="Edit item">
                                  <svg className="consultant-action-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M16.5 3.5a2.12 2.12 0 113 3L8 18l-4 1 1-4 11.5-11.5z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                </button>
                                <button className="consultant-edit-btn consultant-edit-btn-delete" onClick={() => deleteConsultantItemDirect(fromRowSection, fromRowItemId)} title="Delete item" aria-label="Delete item">
                                  <svg className="consultant-action-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M8 6V4h8v2m-1 0v14H9V6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                </button>
                              </div>
                            ) : null}
                          </div>
                        )})}
                        {displayConsultantItems.fromConsultant.length > 6 ? (
                          <button
                            type="button"
                            className="consultant-overflow-toggle"
                            onClick={() => setConsultantReceiveExpanded((v) => !v)}
                          >
                            {consultantReceiveExpanded
                              ? '▲ Show less'
                              : `▼ ${displayConsultantItems.fromConsultant.length - 6} more — click to view`}
                          </button>
                        ) : null}
                        {displayConsultantItems.fromConsultant.length === 0 ? (
                          <div className="muted consultant-empty-state">No expected items yet — add your first item above.</div>
                        ) : null}
                      </div>
                    </section>

                  </div>

                  <div className="consultant-footer-actions">
                    <div className="staffing-last-updated muted">Last updated: {formatMonthDayTime(consultantLastUpdatedAt)} • Saved</div>
                    {!isConsultantAllView ? (
                      <button type="button" className="consultant-delete-tracker-btn" onClick={deleteCurrentConsultantTrackerList}>
                        Delete Consultant List
                      </button>
                    ) : null}
                  </div>

                  <input
                    ref={consultantAttachmentInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={onConsultantAttachmentFileChange}
                  />

                  {consultantImagePreviewOpen && consultantImagePreviewSrc ? (
                    <div className="calendar-modal-backdrop" onClick={() => setConsultantImagePreviewOpen(false)}>
                      <div className="calendar-popover consultant-preview-popover" style={{ width: 'min(980px, calc(100vw - 30px))' }} onClick={(e) => e.stopPropagation()}>
                        <div className="consultant-preview-header">
                          <div>
                            <h3 style={{ margin: 0, fontWeight: 700 }}>{consultantImagePreviewName || 'Reference image'}</h3>
                            {(() => {
                              const active = consultantImagePreviewList[consultantImagePreviewIndex]
                              if (!active) return null
                              return (
                                <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                                  {(active.addedBy || 'You')} • {formatMonthDayTime(active.createdAt)}
                                </div>
                              )
                            })()}
                          </div>
                        </div>
                        {!isConsultantAllView && consultantImagePreviewContext ? (
                          <div className="consultant-preview-controls-row">
                            <div className="consultant-preview-controls-left">
                              <div className="consultant-markup-control-group" aria-label="Line controls">
                                <span className="consultant-markup-control-label">Line</span>
                                <button type="button" className="consultant-markup-color-chip" style={{ background: consultantMarkupColor }} title="Outline color" aria-label="Outline color" onClick={() => consultantLineColorInputRef.current?.click()} />
                                <input ref={consultantLineColorInputRef} className="consultant-markup-color-input-hidden" type="color" value={consultantMarkupColor} onChange={(e) => setConsultantMarkupColor(e.target.value)} title="Outline color" aria-label="Outline color" />
                                <input className="consultant-markup-range-input" type="range" min={1} max={10} value={consultantMarkupLineWidth} onChange={(e) => setConsultantMarkupLineWidth(Number(e.target.value || 3))} title="Outline thickness" aria-label="Outline thickness" />
                              </div>
                              {consultantMarkupTool === 'rect' || consultantMarkupTool === 'circle' ? (
                                <div className="consultant-markup-control-group" aria-label="Fill controls">
                                  <span className="consultant-markup-control-label">Fill</span>
                                  <button type="button" className="consultant-markup-color-chip" style={{ background: consultantMarkupFillColor }} title="Fill color" aria-label="Fill color" onClick={() => consultantFillColorInputRef.current?.click()} />
                                  <input ref={consultantFillColorInputRef} className="consultant-markup-color-input-hidden" type="color" value={consultantMarkupFillColor} onChange={(e) => setConsultantMarkupFillColor(e.target.value)} title="Fill color" aria-label="Fill color" />
                                  <input className="consultant-markup-range-input" type="range" min={0} max={100} value={Math.round(consultantMarkupFillOpacity * 100)} onChange={(e) => setConsultantMarkupFillOpacity(Number(e.target.value || 0) / 100)} title="Fill opacity" aria-label="Fill opacity" />
                                  <div className="consultant-markup-percent-wrap">
                                    <input className="consultant-markup-percent-input" type="number" min={0} max={100} step={1} value={Math.round(consultantMarkupFillOpacity * 100)} onChange={(e) => {
                                      const n = Number(e.target.value)
                                      if (Number.isNaN(n)) return
                                      const clamped = Math.max(0, Math.min(100, n))
                                      setConsultantMarkupFillOpacity(clamped / 100)
                                    }} title="Fill opacity percent" aria-label="Fill opacity percent" />
                                    <span className="consultant-markup-percent-sign">%</span>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                            <div className="consultant-preview-controls-right">
                              <button type="button" className="consultant-overflow-toggle" onClick={undoConsultantMarkup} disabled={consultantMarkupHistoryIndex <= 0}>↶</button>
                              <button type="button" className="consultant-overflow-toggle" onClick={redoConsultantMarkup} disabled={consultantMarkupHistoryIndex >= consultantMarkupHistory.length - 1}>↷</button>
                              <button type="button" className="consultant-overflow-toggle" onClick={() => { setConsultantMarkupHistory((prev) => (prev.length ? [prev[0]] : prev)); setConsultantMarkupHistoryIndex(0) }}>Clear</button>
                              <button type="button" className="consultant-overflow-toggle" onClick={() => setConsultantImagePreviewOpen(false)}>Cancel</button>
                              <button type="button" className="consultant-overflow-toggle" onClick={() => {
                                const canvas = consultantMarkupCanvasRef.current
                                if (!canvas) return
                                applyMarkupToActiveAttachment(canvas.toDataURL('image/png'))
                              }}>Save</button>
                            </div>
                          </div>
                        ) : null}
                        <div className="consultant-preview-body" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button type="button" className="consultant-overflow-toggle" onClick={() => setConsultantImagePreviewIndex((prev) => {
                            const next = Math.max(0, prev - 1)
                            const item = consultantImagePreviewList[next]
                            if (item) { setConsultantImagePreviewSrc(item.dataUrl); setConsultantImagePreviewName(item.name) }
                            return next
                          })}>←</button>
                          <div style={{ display: 'flex', alignItems: 'stretch', gap: 10, flex: 1 }}>
                            {!isConsultantAllView && consultantImagePreviewContext ? (
                              <div className="consultant-markup-toolbar consultant-markup-toolbar-vertical">
                                {[
                                  ['select', '🖱', 'Select/Pan'],
                                  ['arrow', '↖', 'Arrow'],
                                  ['line', '／', 'Line'],
                                  ['rect', '▭', 'Rectangle'],
                                  ['circle', '◯', 'Circle'],
                                  ['text', 'T', 'Text'],
                                  ['pen', '✎', 'Pen'],
                                  ['highlight', '🖍', 'Highlighter'],
                                ].map(([key, icon, label]) => (
                                  <button
                                    key={String(key)}
                                    type="button"
                                    className={`consultant-markup-tool ${consultantMarkupTool === key ? 'active' : ''}`}
                                    title={String(label)}
                                    aria-label={String(label)}
                                    onClick={() => setConsultantMarkupTool(key as 'select' | 'arrow' | 'line' | 'rect' | 'circle' | 'text' | 'pen' | 'highlight')}
                                  >
                                    {icon}
                                  </button>
                                ))}
                              </div>
                            ) : null}
                            <div className="consultant-preview-image-wrap">
                              {!isConsultantAllView && consultantImagePreviewContext ? (
                                <canvas
                                  ref={consultantMarkupCanvasRef}
                                  className="consultant-markup-canvas"
                                  onMouseDown={(e) => {
                                    if (consultantMarkupTool === 'select') return
                                    if (consultantMarkupTool === 'text') { addConsultantMarkupText(e.clientX, e.clientY); return }
                                    setConsultantMarkupDrawing(true)
                                    drawOnMarkupCanvas(e.clientX, e.clientY, true)
                                  }}
                                  onMouseMove={(e) => { if (consultantMarkupDrawing) drawOnMarkupCanvas(e.clientX, e.clientY) }}
                                  onMouseUp={() => { if (consultantMarkupDrawing) commitMarkupStroke(); setConsultantMarkupDrawing(false) }}
                                  onMouseLeave={() => { if (consultantMarkupDrawing) commitMarkupStroke(); setConsultantMarkupDrawing(false) }}
                                />
                              ) : (
                                <img src={consultantImagePreviewSrc} alt={consultantImagePreviewName || 'Reference image'} style={{ width: 'auto', maxWidth: '100%', height: 'auto', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8, border: '1px solid var(--line)' }} />
                              )}
                              {!isConsultantAllView && consultantImagePreviewContext ? (
                              <button
                                type="button"
                                className="consultant-preview-delete"
                                aria-label="Delete image"
                                title="Delete image"
                                onClick={() => {
                                  const active = consultantImagePreviewList[consultantImagePreviewIndex]
                                  if (!active) return
                                  removeConsultantAttachment(consultantImagePreviewContext.key, consultantImagePreviewContext.itemId, active.id)
                                  const nextList = consultantImagePreviewList.filter((x) => x.id !== active.id)
                                  setConsultantImagePreviewList(nextList)
                                  const nextIdx = Math.max(0, Math.min(consultantImagePreviewIndex, nextList.length - 1))
                                  setConsultantImagePreviewIndex(nextIdx)
                                  const nextActive = nextList[nextIdx]
                                  if (nextActive) {
                                    setConsultantImagePreviewSrc(nextActive.dataUrl)
                                    setConsultantImagePreviewName(nextActive.name)
                                  } else {
                                    setConsultantImagePreviewOpen(false)
                                  }
                                }}
                              >
                                ×
                              </button>
                            ) : null}
                          </div>
                          </div>
                          <button type="button" className="consultant-overflow-toggle" onClick={() => setConsultantImagePreviewIndex((prev) => {
                            const next = Math.min(consultantImagePreviewList.length - 1, prev + 1)
                            const item = consultantImagePreviewList[next]
                            if (item) { setConsultantImagePreviewSrc(item.dataUrl); setConsultantImagePreviewName(item.name) }
                            return next
                          })}>→</button>
                        </div>
                        {consultantImagePreviewList.length > 1 ? (
                          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                            {consultantImagePreviewList.map((att, idx) => (
                              <button key={att.id} type="button" onClick={() => { setConsultantImagePreviewIndex(idx); setConsultantImagePreviewSrc(att.dataUrl); setConsultantImagePreviewName(att.name) }} style={{ border: idx === consultantImagePreviewIndex ? '2px solid var(--accent)' : '1px solid var(--line)', borderRadius: 6, padding: 0, background: 'transparent' }}>
                                <img src={att.dataUrl} alt={att.name} style={{ width: 46, height: 46, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  {showAddConsultantTrackerModal ? (
                    <div className="calendar-modal-backdrop" onClick={() => setShowAddConsultantTrackerModal(false)}>
                      <div className="calendar-popover consultant-edit-popover" style={{ width: 600 }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          e.preventDefault()
                          setShowAddConsultantTrackerModal(false)
                          return
                        }
                        handlePopoverSubmitKey(e)
                      }}>
                        <h3>Add Consultant Tracker List</h3>
                        <div className="row consultant-edit-row">
                          <select
                            className="consultant-edit-item"
                            autoFocus
                            value={newConsultantTrackerCompany}
                            onChange={(e) => {
                              const company = e.target.value
                              setNewConsultantTrackerCompany(company)
                              const autoDiscipline = getDefaultDisciplineForCompany(company)
                              const autoAbbreviation = getDefaultAbbreviationForCompany(company)
                              setNewConsultantTrackerDiscipline(autoDiscipline)
                              setNewConsultantTrackerAbbreviation(autoAbbreviation)
                            }}
                          >
                            <option value="">Select Company</option>
                            {consultantDirectoryCompanies.map((company) => (
                              <option key={`consultant-company-option-${company}`} value={company}>{company}</option>
                            ))}
                          </select>
                        </div>
                        <div className="row consultant-edit-row">
                          <input
                            className="consultant-edit-item"
                            value={newConsultantTrackerDiscipline}
                            readOnly
                            tabIndex={-1}
                            onMouseDown={(e) => e.preventDefault()}
                            placeholder="Will auto-fill after company selection"
                          />
                          <input
                            className="consultant-edit-item"
                            value={newConsultantTrackerAbbreviation}
                            readOnly
                            tabIndex={-1}
                            onMouseDown={(e) => e.preventDefault()}
                            placeholder="Will auto-fill after company selection"
                          />
                        </div>
                        <div className="muted small" style={{ marginTop: -2, color: 'var(--text-soft)' }}>
                          {newConsultantTrackerCompany.trim()
                            ? `Auto-filled from directory for: ${newConsultantTrackerCompany.trim()}`
                            : 'Select a company to auto-fill discipline and abbreviation from directory'}
                        </div>
                        <div className="row">
                          <button onClick={() => setShowAddConsultantTrackerModal(false)}>Cancel</button>
                          <button className="calendar-primary-btn" data-primary-action="true" onClick={saveConsultantTrackerList} disabled={!newConsultantTrackerCompany.trim()}>
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {showDeleteConsultantTrackerConfirm ? (
                    <div className="calendar-modal-backdrop" onClick={() => setShowDeleteConsultantTrackerConfirm(false)}>
                      <div className="calendar-popover consultant-edit-popover consultant-delete-confirm-popover" onClick={(e) => e.stopPropagation()}>
                        <h3>Delete Consultant List?</h3>
                        <p className="muted" style={{ marginTop: 0 }}>This will remove this consultant tracker from the nav rail.</p>
                        <div className="row">
                          <button onClick={() => setShowDeleteConsultantTrackerConfirm(false)}>Cancel</button>
                          <button className="danger" onClick={confirmDeleteCurrentConsultantTrackerList}>Delete</button>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {pendingDeleteConfirm ? (
                    <div className="calendar-modal-backdrop" onClick={() => setPendingDeleteConfirm(null)}>
                      <div className="calendar-popover consultant-edit-popover consultant-delete-confirm-popover" onClick={(e) => e.stopPropagation()}>
                        <h3>{pendingDeleteConfirm.title}</h3>
                        <p className="muted" style={{ marginTop: 0, whiteSpace: 'pre-wrap' }}>{pendingDeleteConfirm.body}</p>
                        <div className="row">
                          <button onClick={() => setPendingDeleteConfirm(null)}>Cancel</button>
                          <button className="danger" onClick={pendingDeleteConfirm.onConfirm}>Delete</button>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {showConsultantEdit && (
                    <div className="calendar-modal-backdrop" onClick={() => setShowConsultantEdit(false)}>
                      <div className="calendar-popover consultant-edit-popover" onClick={(e) => e.stopPropagation()} onKeyDown={handlePopoverSubmitKey}>
                        <h3>Edit Item</h3>
                        <div className="row consultant-edit-row">
                          <input className="consultant-edit-item" value={editConsultantText} onChange={(e) => setEditConsultantText(e.target.value)} placeholder="Item" />
                          <input type="date" value={editConsultantDueDate} onChange={(e) => setEditConsultantDueDate(e.target.value)} />
                        </div>
                        <div className="row">
                          <button onClick={() => setShowConsultantEdit(false)}>Cancel</button>
                          <button className="danger" onClick={deleteConsultantEdit}>Delete</button>
                          <button className="calendar-primary-btn" onClick={saveConsultantEdit}>Save</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {activeTab === 'drawingVersionHistory' && (
            <section className="vault-layout">
              <section className="panel">
                <div className="panel-head">
                  <h1>Search Vault Documents</h1>
                </div>
                <section className="filters" style={{ marginTop: 12 }}>
                  <input className="search-input-icon" value={docSearch} onChange={(e) => setDocSearch(e.target.value)} placeholder="Search vault documents" />
                </section>
              </section>

              <section className="panel">
                <div className="panel-head">
                  <h1>Project Vault</h1>
                </div>
                <p className="muted">Project-scoped document hub for files/links/notes with review request descriptions.</p>

                <section className="vault-form">
                  <input
                    type="file"
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null
                      setDocFile(f)
                      setDocFileName(f?.name || '')
                    }}
                  />
                  <input
                    value={docDescription}
                    onChange={(e) => setDocDescription(e.target.value)}
                    placeholder="What do you want reviewed/checked?"
                  />
                  <button
                    onClick={async () => {
                      const inferredTitle = (docFileName || '').trim()
                      if (!inferredTitle || !docFile) return
                      try {
                        setRedlineStatus('Uploading…')
                        const form = new FormData()
                        form.append('file', docFile)
                        form.append('project', docProject)
                        form.append('description', docDescription.trim())
                        const res = await apiFetch(`${apiBase}/api/redline`, {
                          method: 'POST',
                          body: form,
                        })
                        if (!res.ok) throw new Error(`HTTP ${res.status}`)
                        store.queueAgentTask({ title: inferredTitle })
                        setDocDescription('')
                        setDocFileName('')
                        setDocFile(null)
                        setDocSearch('')
                        setRedlineStatus('Queued for redline processing ✅')
                      } catch (e) {
                        setRedlineStatus(`Failed to queue: ${e instanceof Error ? e.message : 'unknown error'}`)
                      }
                    }}
                  >
                    RedLine
                  </button>
                </section>
                {redlineStatus ? <p className="muted small">{redlineStatus}</p> : null}
              </section>
            </section>
          )}


          {activeTab === 'decisionLogs' && (
            <section className="panel">
              <h1>Decision Logs</h1>
              <p className="muted">Style-lab placeholder: define structure for key decisions, owners, and timestamps.</p>
            </section>
          )}

          {activeTab === 'caWorkflows' && (
            <section className="panel">
              <h1>CA Workflows</h1>
              <p className="muted">Style-lab placeholder: construction administration queues and handoffs.</p>
            </section>
          )}

          {activeTab === 'staffingAllocation' && (
            <section className="panel calendar-panel">
              <div className="calendar-shell">
                <aside className="calendar-left-rail consultant-left-rail">
                  {['Team', ...(staffingTeamMembers.length ? staffingTeamMembers : ['No AOR/Core members'])].map((member) => (
                    <button
                      key={member}
                      className={`rail-dot ${staffingMemberView === member ? 'active' : ''}`}
                      onClick={() => member !== 'No AOR/Core members' && setStaffingMemberView(member)}
                      title={member}
                    >
                      {member}
                    </button>
                  ))}
                </aside>
                <div className="calendar-main">
                  <div className="calendar-header-row">
                    <h1 className="calendar-title">{staffingMemberView || 'Team'}</h1>
                  </div>
                  <div className="staffing-table-row muted staffing-action-row">
                    <select value={newStaffingStatus} onChange={(e) => setNewStaffingStatus(e.target.value)} className="staffing-status-select">
                      <option value="" disabled>Status *</option>
                      <option>In Progress</option>
                      <option>On Hold</option>
                      <option>Complete</option>
                      <option>Not Started</option>
                    </select>
                    <select value={newStaffingPriority} onChange={(e) => setNewStaffingPriority(e.target.value)} className="staffing-priority-select">
                      <option value="" disabled>Priority *</option>
                      <option>High</option>
                      <option>Medium</option>
                      <option>Low</option>
                    </select>
                    <input
                      ref={startDateRef}
                      className={`staffing-date-icon ${newStaffingStart ? 'has-value' : ''}`}
                      type="date"
                      aria-label="Start date"
                      value={newStaffingStart}
                      onClick={() => openDatePicker(startDateRef)}
                      onChange={(e) => setNewStaffingStart(e.target.value)}
                    />
                    <input
                      ref={dueDateRef}
                      className={`staffing-date-icon ${newStaffingDue ? 'has-value' : ''}`}
                      type="date"
                      aria-label="Due date"
                      value={newStaffingDue}
                      onClick={() => openDatePicker(dueDateRef)}
                      onChange={(e) => setNewStaffingDue(e.target.value)}
                    />
                    <input
                      className="staffing-text-input"
                      placeholder={staffingMemberView === 'Team' ? 'Team task overview *' : 'Task *'}
                      value={newStaffingTask}
                      onChange={(e) => setNewStaffingTask(e.target.value)}
                    />
                    <input className="staffing-text-input" placeholder="Add notes" value={newStaffingNotes} onChange={(e) => setNewStaffingNotes(e.target.value)} />
                    <select className="staffing-priority-select" value={newStaffingAssignee} onChange={(e) => setNewStaffingAssignee(e.target.value)}>
                      <option value="" disabled>Assignee *</option>
                      {['Team', ...(staffingTeamMembers.length ? staffingTeamMembers : ['No AOR/Core members'])].map((member) => (
                        <option key={member} value={member}>{member}</option>
                      ))}
                    </select>
                    <button className="staffing-enter-btn" onClick={submitNewStaffingRow}>+ New Task</button>
                  </div>
                  <div className="staffing-table-wrap">
                    <div className="staffing-table-header">
                      <button type="button" onClick={() => sortStaffingBy('status')}>Status</button>
                      <button type="button" onClick={() => sortStaffingBy('priority')}>Priority</button>
                      <button type="button" onClick={() => sortStaffingBy('start')}>Start Date</button>
                      <button type="button" onClick={() => sortStaffingBy('due')}>Due Date</button>
                      <button type="button" onClick={() => sortStaffingBy('task')}>Task</button>
                      <button type="button" onClick={() => sortStaffingBy('notes')}>Notes</button>
                      <button type="button" onClick={() => sortStaffingBy('assignee')}>Assignee</button>
                      <span>Actions</span>
                    </div>

                    {staffingRows.map((row) => (
                      <div key={row.id} data-staffing-row-id={row.id} className={`staffing-table-row ${row.status === 'Complete' ? 'staffing-row-complete' : ''}`}>
                        <span className={`staffing-status-pill status-${row.status.toLowerCase().replace(/\s+/g, '-')}`}>{row.status}</span>
                        <span>{row.priority}</span>
                        <span>{formatMonthDay(row.start)}</span>
                        <span>{formatMonthDay(row.due)}</span>
                        <span>{row.task}</span>
                        <span>{row.notes}</span>
                        <span>{row.assignee}</span>
                        <div className="staffing-row-actions">
                          <button className="staffing-row-edit-btn" onClick={() => openStaffingEdit(row)} title="Edit row">✎</button>
                          <button className="staffing-row-edit-btn" onClick={() => deleteStaffingRow(row.id)} title="Delete row">🗑</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="staffing-last-updated muted">Last updated: {formatMonthDayTime(staffingLastUpdatedAt)}</div>

                  {showStaffingEdit && (
                    <div className="calendar-modal-backdrop" onClick={() => setShowStaffingEdit(false)}>
                      <div className="calendar-popover staffing-edit-popover side-detail-panel" onClick={(e) => e.stopPropagation()} onKeyDown={handlePopoverSubmitKey}>
                        <h3>Edit Task Row</h3>
                        <div className="row">
                          <div className="staffing-field-group">
                            <label>Status</label>
                            <select value={editStaffingStatus} onChange={(e) => setEditStaffingStatus(e.target.value)}>
                              <option>In Progress</option>
                              <option>On Hold</option>
                              <option>Complete</option>
                              <option>Not Started</option>
                            </select>
                          </div>
                          <div className="staffing-field-group">
                            <label>Priority</label>
                            <select value={editStaffingPriority} onChange={(e) => setEditStaffingPriority(e.target.value)}>
                              <option>High</option>
                              <option>Medium</option>
                              <option>Low</option>
                            </select>
                          </div>
                          <div className="staffing-field-group">
                            <label>Start Date</label>
                            <input type="date" value={editStaffingStart} onChange={(e) => setEditStaffingStart(e.target.value)} />
                          </div>
                          <div className="staffing-field-group">
                            <label>Due Date</label>
                            <input type="date" value={editStaffingDue} onChange={(e) => setEditStaffingDue(e.target.value)} />
                          </div>
                          <div className="staffing-field-group">
                            <label>Assignee</label>
                            <select className="staffing-assignee-select" value={editStaffingAssignee} onChange={(e) => setEditStaffingAssignee(e.target.value)}>
                              {['Team', ...(staffingTeamMembers.length ? staffingTeamMembers : ['No AOR/Core members'])].map((member) => (
                                <option key={member} value={member}>{member}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="row">
                          <div className="staffing-field-group staffing-popup-wide">
                            <label>Task</label>
                            <input value={editStaffingTask} onChange={(e) => setEditStaffingTask(e.target.value)} placeholder="Task" />
                          </div>
                        </div>
                        <div className="row">
                          <div className="staffing-field-group staffing-popup-wide">
                            <label>Notes</label>
                            <input value={editStaffingNotes} onChange={(e) => setEditStaffingNotes(e.target.value)} placeholder="Notes" />
                          </div>
                        </div>
                        <div className="row">
                          <button className="danger" onClick={() => { if (editStaffingId) { deleteStaffingRow(editStaffingId); setShowStaffingEdit(false); setEditStaffingId(null) } }}>Delete</button>
                          <button onClick={() => setShowStaffingEdit(false)}>Cancel</button>
                          <button className="calendar-primary-btn" onClick={saveStaffingEdit}>Save</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {(activeTab === 'teamTasks' || activeTab === 'teamOrganization') && (
            <section className="panel calendar-panel">
              <div className="calendar-shell">
                <aside className="calendar-left-rail consultant-left-rail">
                  {teamOrganizationProjects.length > 1 ? (
                    <button
                      className={`rail-dot rail-dot-long ${teamOrganizationView === 'Overall' ? 'active' : ''}`}
                      onClick={() => setTeamOrganizationView('Overall')}
                      title="Project Overview"
                    >
                      <span>Project<br />Overview</span>
                    </button>
                  ) : null}
                  {teamOrganizationProjects.map((project) => (
                    <button
                      key={project}
                      className={`rail-dot ${teamOrganizationView === project ? 'active' : ''}`}
                      onClick={() => setTeamOrganizationView(project)}
                      title={project}
                    >
                      {toTitleCase(project)}
                    </button>
                  ))}
                </aside>

                <div className="calendar-main">
                  <div className="calendar-header-row team-org-header-row">
                    <h1 className="calendar-title team-org-title">{teamOrganizationView === 'Overall' ? 'Project Overview' : toTitleCase(teamOrganizationView)}</h1>
                  </div>
                  {teamOrganizationView === 'Overall' ? (
                    <div className="team-org-overall-toolbar-wrap">
                      <div className="team-org-overall-toolbar">
                        <label className="team-org-overall-search">
                          <input
                            className="search-input-icon"
                            value={teamOrgOverallSearch}
                            onChange={(e) => setTeamOrgOverallSearch(e.target.value)}
                            placeholder="Search by Project, PM, or Designer"
                            aria-label="Search team organization overview"
                          />
                        </label>
                        <button
                          type="button"
                          className="team-org-add-staff-btn"
                          onClick={() => {
                            setTeamOrgAddStaffAttempted(false)
                            setTeamOrgNewStaffName('')
                            setTeamOrgNewStaffPosition('')
                            setTeamOrgNewStaffCurrentProject('')
                            setTeamOrgAddStaffAction('move')
                            setShowTeamOrgAddStaffModal(true)
                          }}
                        >
                          Add Staff Member
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <div
                    className={`team-org-chart-wrap ${teamOrgDraggingEmployee ? 'drag-active' : ''}`}
                    onDragOverCapture={(e) => {
                      if (!canDragAcrossProjects) return
                      e.preventDefault()
                      e.dataTransfer.dropEffect = 'move'
                    }}
                    onDragOver={(e) => {
                      if (!canDragAcrossProjects) return
                      e.preventDefault()
                      const dragTarget = e.target as HTMLElement | null
                      teamOrgWasOverUnplacedRef.current = Boolean(dragTarget?.closest('.team-org-unplaced-overall'))
                      e.dataTransfer.dropEffect = 'move'
                    }}
                    onDropCapture={(e) => {
                      if (!canDragAcrossProjects) return
                      const dropEl = e.target as HTMLElement | null
                      const droppedInUnplaced = Boolean(dropEl?.closest('.team-org-unplaced-overall'))
                      if (!droppedInUnplaced) return
                      e.preventDefault()
                      const employeeName = getValidTeamOrgDropEmployee(e) || teamOrgDraggingEmployeeRef.current || teamOrgDraggingEmployee
                      if (employeeName) unassignEmployeeFromAllProjects(employeeName)
                      clearTeamOrgDragState()
                    }}
                    onDrop={(e) => {
                      if (!canDragAcrossProjects) return
                      e.preventDefault()
                      const employeeName = getValidTeamOrgDropEmployee(e) || teamOrgDraggingEmployee
                      const dropEl = e.target as HTMLElement | null
                      const droppedInUnplaced = Boolean(dropEl?.closest('.team-org-unplaced-overall'))
                      if ((teamOrgOverallDropProject === '__unplaced__' || droppedInUnplaced) && employeeName) {
                        unassignEmployeeFromAllProjects(employeeName)
                      }
                      clearTeamOrgDragState()
                    }}
                  >
                    {teamOrganizationView === 'Overall' ? (
                      <>
                        <section
                          className={`team-org-level team-org-unplaced-overall team-org-staff-not-placed ${teamOrgOverallDropProject === '__unplaced__' ? 'drop-active' : ''}`}
                          style={{ marginBottom: 12 }}
                          onDragEnter={() => {
                            if (!canDragAcrossProjects) return
                            teamOrgWasOverUnplacedRef.current = true
                            if (teamOrgOverallDropProject !== '__unplaced__') setTeamOrgOverallDropProject('__unplaced__')
                          }}
                          onDragOverCapture={(e) => {
                            if (!canDragAcrossProjects) return
                            e.preventDefault()
                            teamOrgWasOverUnplacedRef.current = true
                          }}
                          onDragOver={(e) => {
                            if (!canDragAcrossProjects) return
                            e.preventDefault()
                            teamOrgWasOverUnplacedRef.current = true
                            e.dataTransfer.dropEffect = 'move'
                            if (teamOrgOverallDropProject !== '__unplaced__') setTeamOrgOverallDropProject('__unplaced__')
                          }}
                          onDropCapture={(e) => handleDropToOverallUnplaced(e)}
                          onDrop={(e) => handleDropToOverallUnplaced(e)}
                        >
                          <div className="team-org-level-head">
                            <h3>Staff Not Placed</h3>
                            <span className="muted">{overallStaffNotPlaced.length}</span>
                          </div>
                          <p className="team-org-drop-hint">Drop employee here to unassign</p>
                          <div
                            className="team-org-level-grid"
                            onDragOver={(e) => {
                              if (!canDragAcrossProjects) return
                              e.preventDefault()
                              teamOrgWasOverUnplacedRef.current = true
                              if (teamOrgOverallDropProject !== '__unplaced__') setTeamOrgOverallDropProject('__unplaced__')
                            }}
                            onDrop={(e) => handleDropToOverallUnplaced(e)}
                          >
                            {overallStaffNotPlaced.map((row) => (
                              <div
                                key={`overall-unplaced-${row.name}`}
                                role="button"
                                tabIndex={0}
                                draggable={true}
                                onDragStart={(e) => {
                                  startTeamOrgDrag(e, row.name)
                                }}
                                onDragEnd={() => finalizeTeamOrgDrag()}
                                className={`team-org-card team-org-card-btn ${teamOrgDraggingEmployee === row.name ? 'is-dragging' : ''}`}
                                onClick={() => openTeamOrgCard(row)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    openOfficeContactCard(row)
                                  }
                                }}
                                title={`Drag ${row.name} to assign • Click for contact card`}
                              >
                                <small>{row.position || 'Role'}</small>
                                <strong>{row.name}</strong>
                                <span>
                                  {(() => {
                                    const projects = getRowProjectsLikePopup(row)
                                    return projects.length ? projects.map((p) => toTitleCase(p)).join(', ') : 'No project'
                                  })()}
                                </span>
                              </div>
                            ))}
                          </div>
                        </section>

                        <section className="team-org-overall-groups">
                          {filteredOverallProjectGroups.length === 0 ? <p className="muted">No projects match that search.</p> : null}
                          {filteredOverallProjectGroups.map((group) => {
                          const projectKey = group.project.trim().toLowerCase()
                          const overview = projectOverviewByProject[projectKey] || blankProjectOverview()
                          const currentPhaseStatus = getCurrentPhaseStatus(overview)
                          const phaseKey = currentPhaseStatus.phase
                          const phaseLabel = phaseKey === 'CC' ? 'CD' : phaseKey
                          const phaseDueIso = normalizeIsoDate(overview.phaseDeadlines[phaseKey])
                          const phaseDays = daysUntil(phaseDueIso || undefined)
                          const phaseDateText = phaseDueIso
                            ? new Date(`${phaseDueIso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                            : 'No date'
                          const phaseUrgency: 'danger' | 'warning' | 'neutral' =
                            phaseKey === 'SD' && phaseDays !== null && phaseDays <= 14
                              ? 'danger'
                              : phaseKey === 'SD' && phaseDays !== null && phaseDays <= 30
                                ? 'warning'
                                : 'neutral'
                          const hasUnassignedPm = !String(group.projectManagerLabel || '').trim()
                          const projectManagerNameSet = new Set((group.projectManagerNames || []).map((name) => name.trim().toLowerCase()))
                          const orderedRows = [...group.rows].sort((a, b) => {
                            const aKey = a.name.trim().toLowerCase()
                            const bKey = b.name.trim().toLowerCase()
                            const aIsPrincipal = group.principalNames.has(aKey) || /principal/i.test(a.position || '')
                            const bIsPrincipal = group.principalNames.has(bKey) || /principal/i.test(b.position || '')
                            const aIsPm = projectManagerNameSet.has(aKey) || /project\s*manager|manager/i.test(a.position || '')
                            const bIsPm = projectManagerNameSet.has(bKey) || /project\s*manager|manager/i.test(b.position || '')
                            const rank = (isPrincipal: boolean, isPm: boolean) => (isPrincipal ? 0 : isPm ? 1 : 2)
                            const aRank = rank(aIsPrincipal, aIsPm)
                            const bRank = rank(bIsPrincipal, bIsPm)
                            if (aRank !== bRank) return aRank - bRank
                            return a.name.localeCompare(b.name)
                          })
                          const maxVisibleOverviewChips = 5
                          const visibleRows = orderedRows.slice(0, maxVisibleOverviewChips)
                          const overflowCount = Math.max(0, orderedRows.length - visibleRows.length)
                          const draggingInitials = teamOrgDraggingEmployee
                            ? (teamOrgDraggingEmployee
                              .split(' ')
                              .filter(Boolean)
                              .slice(0, 2)
                              .map((part) => part[0]?.toUpperCase())
                              .join('') || 'NA')
                            : ''
                          return (
                          <article
                            key={group.project}
                            className={`team-org-project-group ${teamOrgDraggingEmployee ? 'dragging-active' : ''} ${teamOrgOverallDropProject === group.project ? 'drop-active' : ''}`}
                            onDragOver={(e) => {
                              if (!canDragAcrossProjects) return
                              e.preventDefault()
                              e.dataTransfer.dropEffect = 'move'
                              if (teamOrgOverallDropProject !== group.project) setTeamOrgOverallDropProject(group.project)
                            }}
                            onDrop={(e) => {
                              if (!canDragAcrossProjects) return
                              e.preventDefault()
                              e.stopPropagation()
                              const employeeName = getValidTeamOrgDropEmployee(e)
                              if (employeeName) moveEmployeeToProject(employeeName, group.project)
                              clearTeamOrgDragState()
                            }}
                          >
                            <div
                              className="team-org-project-click-zone"
                              onClick={() => {
                                if (teamOrgDraggingEmployee) return
                                if (teamOrgSuppressProjectOpenRef.current) return
                                setTeamOrganizationView(group.project)
                              }}
                            >
                              <div className="team-org-project-head team-org-project-head-line1">
                                <span className={`team-org-phase-chip ${phaseUrgency}`}>
                                  {phaseLabel} → {phaseDateText}{phaseDays !== null ? ` (${Math.abs(phaseDays)} day${Math.abs(phaseDays) === 1 ? '' : 's'}${phaseDays < 0 ? ' overdue' : ''})` : ''}
                                </span>
                                <span className={`muted team-org-assigned-count ${group.rows.length === 0 ? 'unassigned' : ''} ${teamOrgJustDroppedProject?.toLowerCase() === group.project.toLowerCase() ? 'just-dropped' : ''}`}>{group.rows.length} assigned</span>
                              </div>
                              <div className="team-org-project-head team-org-project-head-line2">
                                <button type="button" className="team-org-project-link" onClick={() => setTeamOrganizationView(group.project)}>{toTitleCase(group.project)}</button>
                              </div>
                              <div className="team-org-project-head team-org-project-head-line3-meta">
                                <span className={`project-section-row-pm ${hasUnassignedPm ? 'unassigned' : ''}`}>{hasUnassignedPm ? '⚠️ ' : ''}PM{(group.projectManagerNames?.length || 0) > 1 ? 's' : ''}: <strong>{group.projectManagerLabel || 'Unassigned'}</strong></span>
                              </div>
                            </div>
                            <div className="team-org-project-line3">
                              <div
                                className={`team-org-project-cards ${teamOrgOverallDropProject === group.project ? 'drop-active' : ''} ${teamOrgJustDroppedProject?.toLowerCase() === group.project.toLowerCase() ? 'just-dropped' : ''}`}
                                onDragOver={(e) => {
                                  if (!canDragAcrossProjects) return
                                  e.preventDefault()
                                  e.dataTransfer.dropEffect = 'move'
                                  if (teamOrgOverallDropProject !== group.project) setTeamOrgOverallDropProject(group.project)
                                }}
                                onDrop={(e) => {
                                  if (!canDragAcrossProjects) return
                                  e.preventDefault()
                                  e.stopPropagation()
                                  const employeeName = getValidTeamOrgDropEmployee(e)
                                  if (employeeName) moveEmployeeToProject(employeeName, group.project)
                                  clearTeamOrgDragState()
                                }}
                              >
                                {visibleRows.map((row) => {
                                  const initials = row.name
                                    .split(' ')
                                    .filter(Boolean)
                                    .slice(0, 2)
                                    .map((part) => part[0]?.toUpperCase())
                                    .join('') || 'NA'
                                  const isPrincipal = group.principalNames.has(row.name.trim().toLowerCase()) || /principal/i.test(row.position || '')
                                  const hoverKey = `overall-${group.project}-${row.name}`
                                  return (
                                    <div
                                      key={hoverKey}
                                      className="team-org-pill-wrap"
                                    >
                                      <div
                                        role="button"
                                        tabIndex={0}
                                        draggable={canDragAcrossProjects}
                                        onDragStart={(e) => {
                                          if (!canDragAcrossProjects) return
                                          startTeamOrgDrag(e, row.name)
                                        }}
                                        onDragEnd={() => {
                                          finalizeTeamOrgDrag()
                                        }}
                                        className={`team-org-card team-org-card-btn team-org-card-compact avatar-chip ${isPrincipal ? 'team-org-card-principal' : ''} ${teamOrgDraggingEmployee === row.name ? 'is-dragging' : ''}`}
                                        onMouseDown={() => {
                                          teamOrgSuppressProjectOpenRef.current = true
                                          window.setTimeout(() => { teamOrgSuppressProjectOpenRef.current = false }, 0)
                                        }}
                                        onClick={(e) => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          teamOrgSuppressProjectOpenRef.current = true
                                          window.setTimeout(() => { teamOrgSuppressProjectOpenRef.current = false }, 0)
                                          openTeamOrgCard(row)
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            openTeamOrgCard(row)
                                          }
                                        }}
                                        title={`Open contact card for ${row.name} • Drag to reassign`}
                                      >
                                        <strong>{initials}</strong>
                                      </div>
                                      {!teamOrgDraggingEmployee ? (
                                        <div className="team-org-card team-org-card-btn team-org-pill-hover-card" aria-hidden="true">
                                          <small>{row.position || 'Role'}</small>
                                          <strong>{row.name}</strong>
                                          <span>
                                            {(() => {
                                              const projects = getRowProjectsLikePopup(row)
                                              return projects.length ? projects.map((p) => toTitleCase(p)).join(', ') : 'No project'
                                            })()}
                                          </span>
                                        </div>
                                      ) : null}
                                    </div>
                                  )
                                })}
                                {overflowCount > 0 ? (
                                  <button
                                    type="button"
                                    className="team-org-card team-org-card-btn team-org-card-compact avatar-chip avatar-chip-more"
                                    onClick={() => setTeamOrganizationView(group.project)}
                                    title={`${overflowCount} additional employee${overflowCount === 1 ? '' : 's'}`}
                                  >
                                    <strong>+{overflowCount}</strong>
                                  </button>
                                ) : null}
                                {teamOrgDraggingEmployee && teamOrgOverallDropProject === group.project ? (
                                  <div className="team-org-card team-org-card-compact avatar-chip team-org-ghost-preview" aria-hidden="true">
                                    <strong>{draggingInitials}</strong>
                                  </div>
                                ) : null}
                                {group.rows.length === 0 ? (
                                  <div className="team-org-drop-slot-empty-wrap" aria-label={`Drop employee on ${group.project}`} title="Drop employee card here">
                                    <div className="team-org-drop-slot-empty" />
                                    <span className="team-org-drop-slot-label">+ Assign to project</span>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            {teamOrgDraggingEmployee && teamOrgOverallDropProject === group.project ? (
                              <div className="team-org-assign-label">Assign to {toTitleCase(group.project)}</div>
                            ) : null}
                          </article>
                          )
                        })}
                      </section>
                      </>
                    ) : (
                      <>
                        <section
                          className={`team-org-level team-org-staff-not-placed ${teamOrgDropTarget === 'unplaced' ? 'drop-active' : ''}`}
                          onDragOver={(e) => {
                            e.preventDefault()
                            if (teamOrgDropTarget !== 'unplaced') setTeamOrgDropTarget('unplaced')
                          }}
                          onDrop={(e) => handleDropToProjectUnplaced(e)}
                        >
                          <div className="team-org-level-head">
                            <h3>Staff Not Placed</h3>
                            <button type="button" className="team-org-add-staff-btn" onClick={() => { setTeamOrgAddStaffAttempted(false); setTeamOrgNewStaffName(''); setTeamOrgNewStaffPosition(''); setTeamOrgNewStaffCurrentProject(''); setTeamOrgAddStaffAction('move'); setShowTeamOrgAddStaffModal(true) }}>Add Staff Member</button>
                          </div>
                          <p className="team-org-drop-hint">Drag employee cards here to unassign from hierarchy</p>
                          <div className="team-org-level-grid">
                            {teamOrganizationStaffNotPlaced.length === 0 ? <p className="muted team-org-empty-note">All staff currently placed on a specific team.</p> : null}
                            {teamOrganizationStaffNotPlaced.map((row) => (
                              <div
                                key={`unplaced-${row.name}-${row.project}`}
                                role="button"
                                tabIndex={0}
                                draggable={true}
                                onDragStart={(e) => {
                                  startTeamOrgDrag(e, row.name)
                                }}
                                onDragEnd={() => clearTeamOrgDragState()}
                                className={`team-org-card team-org-card-btn ${teamOrgDraggingEmployee === row.name ? 'is-dragging' : ''}`}
                                onClick={() => openTeamOrgCard(row)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    openOfficeContactCard(row)
                                  }
                                }}
                                title={`Open contact card for ${row.name}`}
                              >
                                <small>{row.position || 'Role'}</small>
                                <strong>{row.name}</strong>
                                <span>
                                  {(() => {
                                    const projects = getRowProjectsLikePopup(row)
                                    return projects.length ? projects.map((p) => toTitleCase(p)).join(', ') : 'No project'
                                  })()}
                                </span>
                              </div>
                            ))}
                          </div>
                        </section>

                        <section className={`team-org-hierarchy-board top-down ${teamOrgConnectorAnimating ? 'connector-animating' : ''}`}>
                          {(() => {
                            const hierarchyLevels = [
                              { key: 'principal' as const, label: 'Principal' },
                              { key: 'projectManager' as const, label: 'PM' },
                              { key: 'drafter' as const, label: 'Designer' },
                            ]
                            return hierarchyLevels.map((level, index) => {
                              const levelRows = teamOrganizationHierarchy[level.key]
                                .map((name) => findTeamOrganizationRow(name))
                                .filter(Boolean) as OfficeEmployeeRow[]
                              const draggingRow = teamOrgDraggingEmployee
                                ? findTeamOrganizationRow(teamOrgDraggingEmployee)
                                : null
                              const showLevelDropPreview = Boolean(teamOrgDraggingEmployee && teamOrgDropTarget === level.key && draggingRow)
                              const currentAssigned = levelRows.length
                              const currentCount = Math.max(1, currentAssigned)
                              const prevLevel = index > 0 ? hierarchyLevels[index - 1] : null
                              const nextLevel = index < hierarchyLevels.length - 1 ? hierarchyLevels[index + 1] : null
                              const prevAssigned = prevLevel
                                ? teamOrganizationHierarchy[prevLevel.key].map((name) => findTeamOrganizationRow(name)).filter(Boolean).length
                                : 0
                              const nextAssigned = nextLevel
                                ? teamOrganizationHierarchy[nextLevel.key].map((name) => findTeamOrganizationRow(name)).filter(Boolean).length
                                : 0
                              const prevCount = Math.max(1, prevAssigned)
                              const nextCount = Math.max(1, nextAssigned)
                              const singleChainFromPrev = Boolean(prevLevel) && prevCount === 1 && currentCount === 1
                              const singleChainToNext = Boolean(nextLevel) && currentCount === 1 && nextCount === 1
                              const mergeFromMultiParent = Boolean(prevLevel) && prevCount > 1 && currentCount === 1
                              const branchFromParent = Boolean(prevLevel) && currentCount > 1
                              const branchToMultipleChildren = Boolean(nextLevel) && currentCount === 1 && nextCount > 1
                              const branchToChildGroup = Boolean(nextLevel) && currentCount > 1

                              return (
                            <article
                              key={level.key}
                              className={`team-org-hierarchy-row team-org-hierarchy-row-${level.key} ${singleChainFromPrev ? 'single-chain-from-prev' : ''} ${singleChainToNext ? 'single-chain-to-next' : ''} ${mergeFromMultiParent ? 'merge-from-multi-parent' : ''} ${branchToMultipleChildren ? 'branch-to-multi' : ''} ${teamOrgDropTarget === level.key ? 'drop-active' : ''}`}
                              onDragOver={(e) => {
                                e.preventDefault()
                                if (teamOrgDropTarget !== level.key) setTeamOrgDropTarget(level.key)
                              }}
                              onDrop={(e) => {
                                e.preventDefault()
                                const employeeName = getValidTeamOrgDropEmployee(e)
                                if (employeeName) moveEmployeeToHierarchyLevel(employeeName, level.key)
                                clearTeamOrgDragState()
                              }}
                            >
                              <h4>{level.label}</h4>
                              <div className={`team-org-level-grid team-org-level-grid-center ${currentCount > 1 ? 'multi-card' : ''} ${branchFromParent ? 'branch-from-parent' : ''} ${branchToChildGroup ? 'branch-to-child-group' : ''}`}>
                                {currentAssigned === 0 && !showLevelDropPreview ? (
                                  <div
                                    className={`team-org-card team-org-card-btn team-org-card-placeholder team-org-card-${level.key} team-org-card-single`}
                                    aria-disabled
                                    aria-label={`${level.label} unassigned placeholder`}
                                    title={`${level.label} unassigned`}
                                  >
                                    <span className="team-org-card-placeholder-label">{level.key === 'principal' ? '+ Assign Principal' : level.key === 'projectManager' ? '+ Assign PM' : '+ Add Team Member'}</span>
                                  </div>
                                ) : null}
                                {levelRows.map((row) => {
                                  return (
                                    <div
                                      key={`${level.key}-${row.name}-${row.project}`}
                                      role="button"
                                      tabIndex={0}
                                      draggable={true}
                                      onDragStart={(e) => {
                                        startTeamOrgDrag(e, row.name)
                                      }}
                                      onDragEnd={() => clearTeamOrgDragState()}
                                      className={`team-org-card team-org-card-btn team-org-card-${level.key} ${currentCount === 1 ? 'team-org-card-single' : ''} ${teamOrgDraggingEmployee === row.name ? 'is-dragging' : ''}`}
                                      onClick={() => openTeamOrgCard(row)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault()
                                          openOfficeContactCard(row)
                                        }
                                      }}
                                      title={`Open contact card for ${row.name} • Drag to reassign`}
                                    >
                                      <small>{row.position || 'Role'}</small>
                                      <strong>{row.name}</strong>
                                      <span>
                                        {(() => {
                                          const projects = getRowProjectsLikePopup(row)
                                          return projects.length ? projects.map((p) => toTitleCase(p)).join(', ') : 'No project'
                                        })()}
                                      </span>
                                    </div>
                                  )
                                })}
                                {showLevelDropPreview ? (
                                  <div className={`team-org-card team-org-card-btn team-org-card-${level.key} team-org-ghost-preview`} aria-hidden="true">
                                    <small>{draggingRow?.position || 'Role'}</small>
                                    <strong>{draggingRow?.name}</strong>
                                    <span>{draggingRow?.project || 'No project'}</span>
                                  </div>
                                ) : null}
                                {branchToChildGroup && currentCount > 1 ? <div className="team-org-multi-outgoing-stem" aria-hidden /> : null}
                              </div>

                              {index < 2 && !singleChainToNext && currentCount === 1 ? (
                                <div
                                  className={`team-org-downline ${index === 0 ? 'with-arrow' : 'no-arrow'}`}
                                  aria-hidden
                                />
                              ) : null}
                            </article>
                              )
                            })
                          })()}
                        </section>
                      </>
                    )}
                  </div>

                  <article className="dashboard-card" style={{ marginTop: 14 }}>
                    <h3>Notes</h3>
                    <textarea
                      className="team-org-notes-area"
                      rows={4}
                      placeholder="Team organization notes..."
                      value={teamOrganizationNotesByProject[teamOrganizationView] || ''}
                      onChange={(e) => setTeamOrganizationNotesByProject((prev) => ({ ...prev, [teamOrganizationView]: e.target.value }))}
                    />
                  </article>

                  {showTeamOrgAddStaffModal ? (
                    <div className="calendar-modal-backdrop" onClick={() => { setTeamOrgAddStaffAttempted(false); setShowTeamOrgAddStaffModal(false) }}>
                      <div className="calendar-popover team-org-add-staff-popover directory-contact-popover side-detail-panel" onClick={(e) => e.stopPropagation()}>
                        <h3>Add Staff Member</h3>
                        <div className="row" style={{ marginTop: 8 }}>
                          <label className="directory-popup-field">
                            <span>Team Member *</span>
                            <select
                              className={teamOrgAddStaffAttempted && !teamOrgNewStaffName.trim() ? 'required-missing' : ''}
                              value={teamOrgNewStaffName}
                              onChange={(e) => {
                                const selectedName = e.target.value
                                setTeamOrgNewStaffName(selectedName)
                                const selected = teamOrgTransferCandidates.find((candidate) => candidate.name === selectedName)
                                setTeamOrgNewStaffPosition(selected?.position || '')
                                setTeamOrgNewStaffCurrentProject(selected?.project || '')
                              }}
                            >
                              <option value="">Select team member *</option>
                              {teamOrgTransferCandidates.map((candidate) => (
                                <option key={`team-org-transfer-${candidate.name}-${candidate.project}`} value={candidate.name}>
                                  {candidate.name}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                        {teamOrgTransferCandidates.length === 0 ? <p className="muted small" style={{ margin: '6px 0 0' }}>No members available to transfer from other projects.</p> : null}
                        <div className="row" style={{ marginTop: 8 }}>
                          <label className="directory-popup-field">
                            <span>Position</span>
                            <input className={teamOrgAddStaffAttempted && !teamOrgNewStaffPosition.trim() ? 'required-missing' : ''} placeholder="Position" value={teamOrgNewStaffPosition} readOnly />
                          </label>
                        </div>
                        <div className="row" style={{ marginTop: 8 }}>
                          <label className="directory-popup-field">
                            <span>Current Project</span>
                            <input placeholder="Current Project" value={teamOrgNewStaffCurrentProject} readOnly />
                          </label>
                        </div>
                        <div className="row" style={{ marginTop: 8 }}>
                          <label className="directory-popup-field">
                            <span>Action</span>
                            <select value={teamOrgAddStaffAction} onChange={(e) => setTeamOrgAddStaffAction((e.target.value as 'move' | 'add'))}>
                              <option value="move">Move to Project</option>
                              <option value="add">Add to Project</option>
                            </select>
                          </label>
                        </div>
                        {teamOrgNewStaffName.trim() ? (
                          <p className="muted" style={{ margin: '8px 0 0', fontSize: 14, lineHeight: 1.35 }}>
                            {teamOrgAddStaffAction === 'move' ? (
                              <>
                                <strong>{teamOrgNewStaffName.trim()}</strong> will be removed from <strong>{teamOrgSelectedMemberProjects.filter((p) => p.trim().toLowerCase() !== teamOrgTargetProject.trim().toLowerCase()).map((p) => toTitleCase(p)).join(', ') || 'current project'}</strong> and added to <strong>{toTitleCase(teamOrgTargetProject)}</strong>.
                              </>
                            ) : (
                              <>
                                <strong>{teamOrgNewStaffName.trim()}</strong> will remain on current project
                                {teamOrgSelectedMemberProjects.length === 1 ? '' : 's'}
                                {teamOrgSelectedMemberProjects.length > 0
                                  ? <> (<strong>{teamOrgSelectedMemberProjects.map((p) => toTitleCase(p)).join(', ')}</strong>)</>
                                  : null}
                                {' '}and also be added to <strong>{toTitleCase(teamOrgTargetProject)}</strong>.
                              </>
                            )}
                          </p>
                        ) : null}
                        <div className="row team-org-add-staff-actions" style={{ marginTop: 8 }}>
                          <button type="button" onClick={() => { setTeamOrgAddStaffAttempted(false); setShowTeamOrgAddStaffModal(false) }}>Cancel</button>
                          <button type="button" className="directory-primary-btn team-org-add-primary-btn" onClick={applyTeamOrgAddStaffAction}>Apply</button>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {showOfficeContactCard && officeContactCardDraft ? (
                    <div className="calendar-modal-backdrop" onClick={() => setShowOfficeContactCard(false)}>
                      <div className="calendar-popover directory-contact-popover office-contact-card" onClick={(e) => e.stopPropagation()}>
                        <div className="office-contact-card-head">
                          <button className="office-contact-card-x" onClick={() => setShowOfficeContactCard(false)} aria-label="Close">×</button>
                        </div>

                        <div className="office-contact-card-main">
                          <div className="office-contact-avatar" aria-label={`${officeContactCardDraft.name} profile image`} title={officeContactCardDraft.name}>
                            {officeContactCardDraft.name
                              .split(' ')
                              .filter(Boolean)
                              .slice(0, 2)
                              .map((part) => part[0]?.toUpperCase())
                              .join('') || 'NA'}
                          </div>
                          <div className="office-contact-identity">
                            <input className={`office-contact-name ${officeContactSaveAttempted && !officeContactCardDraft.name.trim() ? 'required-missing' : ''}`} placeholder="Employee name *" value={officeContactCardDraft.name} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, name: e.target.value } : prev))} />
                            <input className="office-contact-position" placeholder="Position" value={officeContactCardDraft.position} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, position: e.target.value } : prev))} />
                          </div>
                        </div>

                        <div className="office-contact-meta">
                          <div className="office-contact-meta-row office-contact-project-row">
                            <strong>Current Projects:</strong>
                            <div className="office-contact-project-pills">
                              {(officeContactCardDraft.projects?.length ? officeContactCardDraft.projects : [officeContactCardDraft.project || '']).map((proj, idx) => (
                                <div key={'access-' + idx + '-' + (proj || 'new')} className="office-contact-project-pill">
                                  <select value={proj || ''} onChange={(e) => setOfficeContactCardDraft((prev) => {
                                    if (!prev) return prev
                                    const nextValue = e.target.value
                                    const projects = [...(prev.projects?.length ? prev.projects : [prev.project || ''])]
                                    projects[idx] = nextValue
                                    const deduped = projects.filter((x, i) => x || i === projects.length - 1).filter((x, i, arr) => !x || arr.indexOf(x) === i)
                                    const withTrailingEmpty = deduped.includes('') ? deduped : [...deduped, '']
                                    const primary = withTrailingEmpty[0] || ''
                                    return { ...prev, project: primary, projects: withTrailingEmpty, reportsTo: idx === 0 ? (getProjectPrincipal(primary) || prev.reportsTo) : prev.reportsTo }
                                  })}>
                                    <option value="">Select project</option>
                                    {projectAccessOptions.map((name) => <option key={name} value={name}>{toTitleCase(name)}</option>)}
                                  </select>
                                  {idx > 0 ? (
                                    <button type="button" className="project-access-remove-btn" onClick={() => setOfficeContactCardDraft((prev) => {
                                      if (!prev) return prev
                                      const projects = [...(prev.projects?.length ? prev.projects : [prev.project || ''])]
                                      projects.splice(idx, 1)
                                      const primary = projects[0] || ''
                                      return { ...prev, project: primary, projects }
                                    })}>×</button>
                                  ) : null}
                                </div>
                              ))}
                              <button type="button" className="project-access-add-btn" onClick={() => setOfficeContactCardDraft((prev) => {
                                if (!prev) return prev
                                const projects = [...(prev.projects?.length ? prev.projects : [prev.project || ''])]
                                if (projects.includes('')) return prev
                                return { ...prev, projects: [...projects, ''] }
                              })}>＋</button>
                            </div>
                          </div>
                          <div className="office-contact-meta-hint">Current projects control this user's app access scope.</div>
                          <div className="office-contact-meta-split">
                            <div className="office-contact-meta-left">
                              <div className="office-contact-meta-row office-contact-meta-row-half"><strong className={officeContactSaveAttempted && !officeContactCardDraft.accessTier.trim() ? 'required-missing' : ''}>Web Access *:</strong> <select className={`office-contact-access-tier ${officeContactCardDraft.accessTier ? 'has-value' : ''} ${officeContactSaveAttempted && !officeContactCardDraft.accessTier.trim() ? 'required-missing' : ''}`} value={officeContactCardDraft.accessTier} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, accessTier: e.target.value } : prev))}>
                                <option value="">Web access</option>
                                {COMPANY_TIERS.map((tier) => <option key={`office-tier-${tier}`} value={tier}>{tier}</option>)}
                              </select></div>
                              <div className="office-contact-meta-hint">Web access controls user's app access scope.</div>
                              <div className="office-contact-meta-row office-contact-meta-row-half"><strong>Location:</strong> <input placeholder="TBD" value={officeContactCardDraft.location} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, location: e.target.value } : prev))} /></div>
                              <div className="office-contact-meta-row office-contact-meta-row-half"><strong>Reports To:</strong> <input placeholder="Principal/Project Manager" value={officeContactCardDraft.reportsTo} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, reportsTo: e.target.value } : prev))} /></div>
                            </div>
                            <div className="office-contact-meta-right">
                              <label>Past Projects</label>
                              <textarea value={officeContactCardDraft.pastProjects} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, pastProjects: e.target.value } : prev))} placeholder="List past projects" />
                            </div>
                          </div>
                        </div>

                        <div className="office-contact-grid">
                          <div className="office-contact-col">
                            <div className="office-contact-field"><label><span className="office-contact-icon">✉︎</span> Email:</label><input value={officeContactCardDraft.email} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, email: e.target.value } : prev))} /></div>
                            <div className="office-contact-field"><label><span className="office-contact-icon">☎︎</span> Cell:</label><input value={officeContactCardDraft.cell} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, cell: e.target.value } : prev))} /></div>
                          </div>

                          <div className="office-contact-col office-contact-col-right">
                            <div className="office-contact-field"><label><span className="office-contact-icon">☎︎</span> Phone:</label><input value={officeContactCardDraft.phone} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, phone: e.target.value } : prev))} /></div>
                            <div className="office-contact-field"><label>Extension:</label><input value={officeContactCardDraft.extension} onChange={(e) => setOfficeContactCardDraft((prev) => (prev ? { ...prev, extension: e.target.value } : prev))} /></div>
                          </div>
                        </div>
                        <div className="row office-contact-actions" style={{ justifyContent: 'flex-end', gap: 8 }}>
                          <button onClick={() => setShowOfficeContactCard(false)}>Close</button>
                          {null}
                          <button className="modal-save-btn" onClick={saveOfficeContactCard}>Save</button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </section>
          )}

          {activeTab === 'meetingActionTracking' && (
            <section className="panel calendar-panel">
              <div className="calendar-shell">
                <aside className="calendar-left-rail">
                  {meetingTabOrder.map((tabKey, idx) => {
                    const tabType = tabKey === activeMeetingTab ? meetingType : (meetingDrafts[tabKey]?.type || '')
                    const tabLabel = abbreviateMeetingType(tabType) || `Meeting ${idx + 1}`
                    return (
                      <button key={tabKey} className={`rail-dot ${activeMeetingTab === tabKey ? 'active' : ''}`} onClick={() => switchMeetingTab(tabKey)} title={`Meeting ${idx + 1}`}>
                        {tabLabel}
                      </button>
                    )
                  })}
                  <button className="rail-dot rail-dot-plus" onClick={addMeetingTab} title="Add meeting tab">＋</button>
                </aside>
                <div className="calendar-main meeting-minutes-main">
                  <div className="calendar-header-row meeting-header-row">
                    <div className="meeting-delete-actions">
                      <button type="button" className="danger" onClick={() => setShowDeleteMeetingConfirm(true)}>Delete Sheet</button>
                    </div>
                    <h1 className="calendar-title">Meeting Minutes</h1>
                    <div className="meeting-export-actions">
                      <button type="button" onClick={exportMeetingToExcel}>Export CSV</button>
                      <button type="button" onClick={exportMeetingToPdf}>Export PDF</button>
                    </div>
                  </div>

                  {showDeleteMeetingConfirm ? (
                    <div className="calendar-modal-backdrop" onClick={() => setShowDeleteMeetingConfirm(false)}>
                      <div className="calendar-popover" onClick={(e) => e.stopPropagation()} onKeyDown={handlePopoverSubmitKey}>
                        <h3>Delete Meeting Sheet?</h3>
                        <p className="muted">This removes the current meeting sheet and all of its content.</p>
                        <div className="row">
                          <button onClick={() => setShowDeleteMeetingConfirm(false)}>Cancel</button>
                          <button className="danger" onClick={deleteActiveMeetingTab}>Yes, Delete</button>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div className="meeting-top-row">
                    <section className="meeting-meta-grid meeting-meta-with-logo">
                      <div className="meeting-meta-logo-wrap">
                        <img src={companyLogo} alt="Company logo" className="meeting-meta-logo" />
                      </div>
                      <div><strong>Project:</strong> {projectName}</div>
                      <div><strong>Date:</strong> <input className="meeting-meta-inline-input" type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} /></div>
                      <div><strong>Meeting Type:</strong> <input className="meeting-meta-inline-input" type="text" value={meetingType} placeholder="Meeting Type" onChange={(e) => setMeetingType(e.target.value)} /></div>
                      <div><strong>Prepared By:</strong> <input className="meeting-meta-inline-input" type="text" value={meetingPreparedBy} placeholder="Prepared By" onChange={(e) => setMeetingPreparedBy(e.target.value)} /></div>
                      <div><strong>Meeting Location:</strong> <input className="meeting-meta-inline-input" type="text" value={meetingLocation} placeholder="Meeting Location" onChange={(e) => setMeetingLocation(e.target.value)} /></div>
                    </section>

                    <section className="meeting-block meeting-attendees-block">
                      <div className="meeting-attendees-top">
                        <h3>Attendees</h3>
                        <button type="button" className="meeting-attendees-add-btn meeting-add-btn" onClick={openMeetingAttendeeAdd}>Add</button>
                      </div>
                      <div className="meeting-attendees-header">
                        <span>Name</span><span>Company</span><span>Present</span>
                      </div>
                      {[...meetingAttendees]
                        .sort((a, b) => (a.company || '').localeCompare(b.company || '') || a.name.localeCompare(b.name))
                        .map((attendee) => (
                          <div key={attendee.id} className={`meeting-attendees-row ${attendee.present ? '' : 'absent'}`}>
                            <div className="meeting-attendee-name-actions">
                              <button
                                type="button"
                                className="meeting-row-action-btn"
                                title="Delete attendee"
                                onClick={() => {
                                  const deleted = attendee
                                  setMeetingAttendees((prev) => prev.filter((x) => x.id !== attendee.id))
                                  showActionToast('Attendee deleted', () => setMeetingAttendees((prev) => [...prev, deleted]))
                                }}
                              >
                                🗑
                              </button>
                              <button className="meeting-inline-edit" onClick={() => openMeetingAttendeeEdit(attendee.id)}>{attendee.name}</button>
                            </div>
                            <button className="meeting-inline-edit" onClick={() => openMeetingAttendeeEdit(attendee.id)}>{attendee.company}</button>
                            <button
                              className="meeting-presence-toggle"
                              onClick={() => setMeetingAttendees((prev) => prev.map((x) => (x.id === attendee.id ? { ...x, present: !x.present } : x)))}
                              title={attendee.present ? 'Mark absent' : 'Mark present'}
                              aria-label={attendee.present ? 'Mark absent' : 'Mark present'}
                            >
                              {attendee.present ? '✓' : '✕'}
                            </button>
                          </div>
                        ))}

                      {showMeetingAttendeeEdit && (
                        <div className="calendar-modal-backdrop" onClick={() => setShowMeetingAttendeeEdit(false)}>
                          <div className="calendar-popover meeting-attendee-popover" onClick={(e) => e.stopPropagation()} onKeyDown={handlePopoverSubmitKey}>
                            <h3>{editMeetingAttendeeId ? 'Edit Attendee' : 'Add Attendee'}</h3>
                            <div className="row">
                              <input value={editMeetingAttendeeName} onChange={(e) => setEditMeetingAttendeeName(e.target.value)} placeholder="Name *" />
                              <input value={editMeetingAttendeeCompany} onChange={(e) => setEditMeetingAttendeeCompany(e.target.value)} placeholder="Company *" />
                            </div>
                            {!editMeetingAttendeeId ? (
                              <div className="row">
                                <select
                                  className="meeting-directory-select"
                                  value={selectedDirectoryContactIndex}
                                  onChange={(e) => {
                                    setSelectedDirectoryContactIndex(e.target.value)
                                    const idx = Number(e.target.value)
                                    if (!Number.isNaN(idx) && idx >= 0 && idx < directoryContacts.length) {
                                      const c = directoryContacts[idx]
                                      setEditMeetingAttendeeName(c.name || '')
                                      setEditMeetingAttendeeCompany(c.company || '')
                                    }
                                  }}
                                >
                                  <option value="" disabled>Select contact from Directory</option>
                                  {directoryContacts.map((c, i) => (
                                    <option key={`${c.name}-${c.company}-${i}`} value={String(i)}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>
                                  ))}
                                </select>
                              </div>
                            ) : null}
                            <div className="row">
                              <button onClick={() => setShowMeetingAttendeeEdit(false)}>Cancel</button>
                              {editMeetingAttendeeId ? <button className="danger" onClick={deleteMeetingAttendee}>Delete</button> : null}
                              <button onClick={saveMeetingAttendeeEdit}>{editMeetingAttendeeId ? 'Save' : 'Add'}</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </section>
                  </div>

                  <section className="meeting-block">
                    <h3>Action Items</h3>
                    <div className="meeting-action-header">
                      <span>#</span><span>Date</span><span>Item</span><span>Owner</span><span>Due</span><span>Status</span>
                    </div>
                    {meetingActionItems
                      .filter((x) => !x.parentId)
                      .map((topic, topicIndex) => {
                        const topicNumber = topic.number || `${topicIndex + 1}`
                        const children = meetingActionItems.filter((x) => x.parentId === topic.id)
                        const isCollapsed = !!collapsedMeetingTopics[topic.id]
                        return (
                          <div key={topic.id} className="meeting-topic-block">
                            <div className="meeting-topic-row-wrap">
                              <button
                                type="button"
                                className="meeting-topic-toggle-outside"
                                aria-label={isCollapsed ? `Expand topic ${topicNumber}` : `Collapse topic ${topicNumber}`}
                                title={isCollapsed ? 'Expand' : 'Collapse'}
                                onClick={() => setCollapsedMeetingTopics((prev) => ({ ...prev, [topic.id]: !prev[topic.id] }))}
                              >
                                {isCollapsed ? '+' : '−'}
                              </button>
                              <div className="meeting-action-row meeting-action-topic-row">
                                <span>{topicNumber}</span>
                              <span></span>
                              <input type="text" value={topic.item || ''} onChange={(e) => setMeetingActionItems((prev) => prev.map((x) => x.id === topic.id ? { ...x, item: e.target.value } : x))} />
                              <span></span>
                              <span></span>
                              <div className="meeting-status-actions">
                                <select value={topic.status || 'open'} onChange={(e) => setMeetingActionItems((prev) => prev.map((x) => x.id === topic.id ? { ...x, status: e.target.value } : x))}>
                                  <option value="open">Open</option>
                                  <option value="in progress">In Progress</option>
                                  <option value="closed">Closed</option>
                                </select>
                                <button
                                  type="button"
                                  className="meeting-row-action-btn"
                                  title="Delete topic"
                                  onClick={() => {
                                    const deletedTopic = topic
                                    const deletedChildren = children
                                    setMeetingActionItems((prev) => prev.filter((x) => x.id !== topic.id && x.parentId !== topic.id))
                                    showActionToast('Topic deleted', () => setMeetingActionItems((prev) => [...prev, deletedTopic, ...deletedChildren]))
                                  }}
                                >
                                  🗑
                                </button>
                              </div>
                            </div>
                            </div>

                            {!isCollapsed && children.map((action, idx) => (
                              <div key={action.id} data-meeting-action-id={action.id} className={`meeting-action-row meeting-action-sub-row ${action.status === 'closed' ? 'meeting-action-sub-closed' : ''}`}>
                                <span>{`${topicNumber}.${idx + 1}`}</span>
                                <input
                                  className={safeDateInputValue(action.date) ? 'meeting-date-input has-value' : 'meeting-date-input'}
                                  type="date"
                                  value={safeDateInputValue(action.date)}
                                  onClick={(e) => {
                                    const input = e.currentTarget as HTMLInputElement & { showPicker?: () => void }
                                    try {
                                      if (typeof input.showPicker === 'function') input.showPicker()
                                    } catch {
                                      // ignore; native picker fallback still works
                                    }
                                  }}
                                  onChange={(e) => setMeetingActionItems((prev) => prev.map((x) => x.id === action.id ? { ...x, date: e.target.value } : x))}
                                />
                                <input type="text" value={action.item || ''} onChange={(e) => setMeetingActionItems((prev) => prev.map((x) => x.id === action.id ? { ...x, item: e.target.value } : x))} />
                                <input type="text" value={action.owner || ''} onChange={(e) => setMeetingActionItems((prev) => prev.map((x) => x.id === action.id ? { ...x, owner: e.target.value } : x))} />
                                <input
                                  className={safeDateInputValue(action.due) ? 'meeting-date-input has-value' : 'meeting-date-input'}
                                  type="date"
                                  value={safeDateInputValue(action.due)}
                                  onClick={(e) => {
                                    const input = e.currentTarget as HTMLInputElement & { showPicker?: () => void }
                                    try {
                                      if (typeof input.showPicker === 'function') input.showPicker()
                                    } catch {
                                      // ignore; native picker fallback still works
                                    }
                                  }}
                                  onChange={(e) => setMeetingActionItems((prev) => prev.map((x) => x.id === action.id ? { ...x, due: e.target.value } : x))}
                                />
                                <div className="meeting-status-actions">
                                  <select value={action.status || 'open'} onChange={(e) => setMeetingActionItems((prev) => prev.map((x) => x.id === action.id ? { ...x, status: e.target.value } : x))}>
                                    <option value="open">Open</option>
                                    <option value="in progress">In Progress</option>
                                    <option value="closed">Closed</option>
                                  </select>
                                  <button
                                    type="button"
                                    className="meeting-row-action-btn"
                                    title="Delete sub-item"
                                    onClick={() => {
                                      const deleted = action
                                      setMeetingActionItems((prev) => prev.filter((x) => x.id !== action.id))
                                      showActionToast('Sub-item deleted', () => setMeetingActionItems((prev) => [...prev, deleted]))
                                    }}
                                  >
                                    🗑
                                  </button>
                                </div>
                              </div>
                            ))}

                            {!isCollapsed && (
                              <button
                                type="button"
                                className="meeting-subitem-add-btn meeting-add-btn"
                                onClick={() => setMeetingActionItems((prev) => [...prev, { id: makeId(), parentId: topic.id, date: new Date().toISOString().slice(0, 10), item: 'New discussion point', owner: 'TBD', due: new Date().toISOString().slice(0, 10), status: 'open' }])}
                              >
                                Add Sub-item
                              </button>
                            )}
                          </div>
                        )
                      })}
                    <button
                      type="button"
                      className="meeting-add-btn meeting-add-topic-btn"
                      onClick={() => {
                        const top = meetingActionItems.filter((x) => !x.parentId)
                        const nextNumber = String(Math.max(0, ...top.map((x) => Number(x.number || 0))) + 1)
                        setMeetingActionItems((prev) => [...prev, { id: makeId(), number: nextNumber, date: new Date().toISOString().slice(0, 10), item: 'New topic', owner: 'TBD', due: new Date().toISOString().slice(0, 10), status: 'open' }])
                      }}
                    >
                      Add Topic
                    </button>
                  </section>

                  <section className="meeting-block">
                    <h3>Additional Topics</h3>
                    <ul className="consultant-demo-list bullets">
                      {meetingAgendaItems.map((note, idx) => (
                        <li
                          key={`agenda-${idx}`}
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            const text = e.currentTarget.textContent?.trim() || ''
                            setMeetingAgendaItems((prev) => prev.map((x, i) => (i === idx ? text : x)).filter(Boolean))
                          }}
                        >
                          {note}
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className="meeting-add-btn"
                      onClick={() => setMeetingAgendaItems((prev) => [...prev, 'New discussion item'])}
                    >
                      Add Note
                    </button>
                  </section>
                </div>
              </div>
            </section>
          )}

          {activeTab === 'calendar' && (
            <section className="panel calendar-panel">
              <div className="calendar-shell">
                <aside className="calendar-left-rail">
                  <button className={`rail-dot rail-dot-long ${calendarView === 'list' ? 'active' : ''}`} onClick={() => setCalendarView('list')} title="Deliverables List">List</button>
                  <button className={`rail-dot ${calendarView === 'week' ? 'active' : ''}`} onClick={() => setCalendarView('week')} title="Week">Week</button>
                  <button className={`rail-dot ${calendarView === 'month' ? 'active' : ''}`} onClick={() => setCalendarView('month')} title="Month">Month</button>
                  <button className={`rail-dot ${calendarView === 'year' ? 'active' : ''}`} onClick={() => setCalendarView('year')} title="Year">Year</button>
                  <button className="rail-dot" onClick={() => { setAddCalendarItemAttempted(false); setShowAddDeadline(true) }} title="Add calendar item">✚</button>
                </aside>

                <div className="calendar-main">
                  <div className="calendar-header-row">
                    {calendarView !== 'list' ? <button className="calendar-inline-nav" onClick={() => shiftCalendarFocus(-1)} aria-label="Previous">◀</button> : <span className="calendar-inline-nav-spacer" />}
                    <h1 className={`calendar-title ${calendarView === 'list' ? 'deadline-list-title' : ''} ${calendarView === 'day' ? 'calendar-day-title' : ''}`}>
                      {calendarView === 'year' ? (
                        `${calendarFocusDate.getFullYear()}`
                      ) : calendarView === 'list' ? (
                        'Deliverables List'
                      ) : calendarView === 'day' ? (
                        <>
                          <span className="calendar-day-weekday">{calendarFocusDate.toLocaleString(undefined, { weekday: 'long' })}</span>
                          <span className="calendar-day-date">{`${calendarFocusDate.toLocaleString(undefined, { month: 'long' })} ${String(calendarFocusDate.getDate()).padStart(2, '0')}, ${calendarFocusDate.getFullYear()}`}</span>
                        </>
                      ) : calendarView === 'week' ? (
                        (() => {
                          const first = new Date(calendarFocusDate)
                          first.setDate(calendarFocusDate.getDate() - calendarFocusDate.getDay())
                          const last = new Date(first)
                          last.setDate(first.getDate() + 6)
                          return `${first.toLocaleString(undefined, { month: 'short' })} ${first.getDate()} – ${last.toLocaleString(undefined, { month: 'short' })} ${last.getDate()}, ${last.getFullYear()}`
                        })()
                      ) : (
                        `${calendarFocusDate.toLocaleString(undefined, { month: 'long' })} ${calendarFocusDate.getFullYear()}`
                      )}
                    </h1>
                    {calendarView !== 'list' ? <button className="calendar-inline-nav" onClick={() => shiftCalendarFocus(1)} aria-label="Next">▶</button> : <span className="calendar-inline-nav-spacer" />}
                    {calendarView === 'list' ? (
                      <div className="calendar-year-legend-wrap">
                        <button
                          type="button"
                          className="calendar-year-legend-btn"
                          aria-label="Show page guide"
                          onClick={() => setShowCalendarListGuide((v) => !v)}
                        >
                          <svg className="calendar-year-legend-btn-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <circle cx="12" cy="12" r="9" fill="none" />
                            <line x1="12" y1="10" x2="12" y2="16" />
                            <circle cx="12" cy="7" r="0.9" fill="currentColor" stroke="none" />
                          </svg>
                        </button>
                      </div>
                    ) : (calendarView === 'year' || calendarView === 'month' || calendarView === 'week') ? (
                      <div
                        className="calendar-year-legend-wrap"
                        onMouseEnter={() => {
                          clearCalendarYearLegendCloseTimer()
                          setShowCalendarYearLegend(true)
                        }}
                        onMouseLeave={() => {
                          clearCalendarYearLegendCloseTimer()
                          setShowCalendarYearLegend(false)
                        }}
                      >
                        <button
                          type="button"
                          className="calendar-year-legend-btn"
                          aria-label="Show legend"
                          onClick={() => {
                            setShowCalendarYearLegend(true)
                            scheduleCalendarYearLegendAutoClose()
                          }}
                        >
                          <svg className="calendar-year-legend-btn-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <circle cx="12" cy="12" r="9" fill="none" />
                            <line x1="12" y1="10" x2="12" y2="16" />
                            <circle cx="12" cy="7" r="0.9" fill="currentColor" stroke="none" />
                          </svg>
                        </button>
                        {showCalendarYearLegend ? (
                          <div className="calendar-year-legend" aria-label="Year calendar legend">
                            <span className="calendar-year-legend-item">Due (Overdue) = <span className="year-day-num deadline-ring status-overdue calendar-year-legend-icon" aria-hidden="true">15</span></span>
                            <span className="calendar-year-legend-item">Due (On Track) = <span className="year-day-num deadline-ring status-on-track calendar-year-legend-icon" aria-hidden="true">15</span></span>
                            <span className="calendar-year-legend-item">Due (Completed) = <span className="year-day-num deadline-ring status-completed calendar-year-legend-icon" aria-hidden="true">15</span></span>
                            <span className="calendar-year-legend-item">Due (Not Selected) = <span className="year-day-num deadline-ring status-not-selected calendar-year-legend-icon" aria-hidden="true">15</span></span>
                            <span className="calendar-year-legend-item">Today = <span className="today-year calendar-year-legend-today" aria-hidden="true"><span className="year-day-num calendar-year-legend-icon">15</span></span></span>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  {showAddDeadline && (
                    <div
                      className="calendar-modal-backdrop"
                      onClick={() => {
                        setAddCalendarItemAttempted(false)
                        setShowAddDeadline(false)
                        setCalendarDateInputType('text')
                      }}
                    >
                      <div className="calendar-popover calendar-add-item-popover directory-contact-popover side-detail-panel" onClick={(e) => e.stopPropagation()} onKeyDown={handlePopoverSubmitKey} style={{ display: 'flex', flexDirection: 'column', position: 'relative', paddingBottom: 58 }}>
                        <h3>Add Calendar Item</h3>
                        <div className="row add-item-row add-item-row-primary calendar-edit-row-full">
                          <label className="directory-popup-field">
                            <span>Deadline Name</span>
                            <input
                              className={addCalendarItemAttempted && !newDeadlineTitle.trim() ? 'required-missing' : ''}
                              placeholder="Deadline Name *"
                              value={newDeadlineTitle}
                              onChange={(e) => setNewDeadlineTitle(e.target.value)}
                            />
                          </label>
                        </div>
                        <div className="row add-item-row add-item-row-secondary calendar-edit-row-full">
                          <label className="directory-popup-field">
                            <span>Description</span>
                            <input
                              placeholder="Description"
                              value={newDeadlineDescription}
                              onChange={(e) => setNewDeadlineDescription(e.target.value)}
                            />
                          </label>
                        </div>
                        <div className="row add-item-row add-item-row-tertiary" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                          <label className="directory-popup-field">
                            <span>Date</span>
                            <input
                              className={`calendar-date-input ${addCalendarItemAttempted && !newDeadlineDate.trim() ? 'required-missing' : ''}`}
                              type={calendarDateInputType}
                              placeholder="Date *"
                              aria-label="Date"
                              value={newDeadlineDate}
                              onFocus={() => setCalendarDateInputType('date')}
                              onBlur={() => {
                                if (!newDeadlineDate) setCalendarDateInputType('text')
                              }}
                              onChange={(e) => setNewDeadlineDate(e.target.value)}
                            />
                          </label>
                          <label className="directory-popup-field">
                            <span>Assign To</span>
                            <input
                              placeholder="Assign to"
                              value={newDeadlineAssignTo}
                              onChange={(e) => setNewDeadlineAssignTo(e.target.value)}
                            />
                          </label>
                          <label className="directory-popup-field">
                            <span>Status</span>
                            <select
                              className={`calendar-deliverable-status calendar-edit-status status-${newDeadlineStatus}`}
                              value={newDeadlineStatus}
                              onChange={(e) => setNewDeadlineStatus(e.target.value as 'not-selected' | 'overdue' | 'on-track' | 'completed')}
                              aria-label="Deadline status"
                            >
                              <option value="not-selected">Not Selected</option>
                              <option value="overdue">Overdue</option>
                              <option value="on-track">On Track</option>
                              <option value="completed">Completed</option>
                            </select>
                          </label>
                        </div>
                        <div className="row add-item-row add-item-row-secondary calendar-edit-row-full">
                          <label className="directory-popup-field">
                            <span>Notes</span>
                            <input placeholder="Notes" value={newDeadlineNotes} onChange={(e) => setNewDeadlineNotes(e.target.value)} />
                          </label>
                        </div>
                        <div className="add-item-row-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, position: 'absolute', right: 10, bottom: 10 }}>
                          <button style={{ width: 'auto', minWidth: 92 }} onClick={() => { setAddCalendarItemAttempted(false); setShowAddDeadline(false); setCalendarDateInputType('text'); setNewDeadlineNotes('') }}>Cancel</button>
                          <button
                            className="calendar-primary-btn"
                            style={{ width: 'auto', minWidth: 92, background: 'var(--accent)', borderColor: 'var(--accent)', color: 'var(--swan-wing)' }}
                            onClick={() => {
                              setAddCalendarItemAttempted(true)
                              const title = newDeadlineTitle.trim()
                              const dateKey = newDeadlineDate.trim()
                              const description = newDeadlineDescription.trim()
                              const assignTo = newDeadlineAssignTo.trim()
                              const notes = newDeadlineNotes.trim()
                              const status = newDeadlineStatus
                              if (!title || !dateKey) {
                                showActionToast('Calendar title and date are required')
                                return
                              }
                              const stamp = now()
                              setCalendarDeadlines((prev) => [{ title, description, assignTo, notes, dateKey, source: 'Manual', status }, ...prev])
                              setCalendarLastUpdatedAt(stamp)
                              setNewDeadlineTitle('')
                              setNewDeadlineDescription('')
                              setNewDeadlineAssignTo('')
                              setNewDeadlineNotes('')
                              setNewDeadlineDate('')
                              setNewDeadlineStatus('not-selected')
                              setAddCalendarItemAttempted(false)
                              setCalendarDateInputType('text')
                              setShowAddDeadline(false)
                              setCalendarView('list')
                            }}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {editingDeadline && (
                    <div className="calendar-modal-backdrop" onClick={() => setEditingDeadline(null)}>
                      <div className="calendar-popover calendar-add-item-popover directory-contact-popover side-detail-panel" onClick={(e) => e.stopPropagation()} onKeyDown={handlePopoverSubmitKey}>
                        <h3>Edit Calendar Item</h3>
                        <div className="row add-item-row add-item-row-primary calendar-edit-row-full">
                          <label className="directory-popup-field">
                            <span>Deadline Name</span>
                            <input placeholder="Deadline Name *" value={newDeadlineTitle} onChange={(e) => setNewDeadlineTitle(e.target.value)} />
                          </label>
                        </div>
                        <div className="row add-item-row add-item-row-secondary calendar-edit-row-full">
                          <label className="directory-popup-field">
                            <span>Description</span>
                            <input placeholder="Description" value={newDeadlineDescription} onChange={(e) => setNewDeadlineDescription(e.target.value)} />
                          </label>
                        </div>
                        <div className="row add-item-row add-item-row-tertiary" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                          <label className="directory-popup-field">
                            <span>Due Date</span>
                            <input className="calendar-date-input" type="date" value={newDeadlineDate} onChange={(e) => setNewDeadlineDate(e.target.value)} />
                          </label>
                          <label className="directory-popup-field">
                            <span>Assign To</span>
                            <input placeholder="Assign to" value={newDeadlineAssignTo} onChange={(e) => setNewDeadlineAssignTo(e.target.value)} />
                          </label>
                          <label className="directory-popup-field">
                            <span>Status</span>
                            <select
                              className={`calendar-deliverable-status calendar-edit-status status-${newDeadlineStatus}`}
                              value={newDeadlineStatus}
                              onChange={(e) => setNewDeadlineStatus(e.target.value as 'not-selected' | 'overdue' | 'on-track' | 'completed')}
                              aria-label="Deadline status"
                            >
                              <option value="not-selected">Not Selected</option>
                              <option value="overdue">Overdue</option>
                              <option value="on-track">On Track</option>
                              <option value="completed">Completed</option>
                            </select>
                          </label>
                        </div>
                        <div className="row add-item-row add-item-row-secondary calendar-edit-row-full">
                          <label className="directory-popup-field">
                            <span>Notes</span>
                            <input placeholder="Notes" value={newDeadlineNotes} onChange={(e) => setNewDeadlineNotes(e.target.value)} />
                          </label>
                        </div>
                        <div className="row add-item-row add-item-row-actions">
                          <button className="danger" onClick={deleteEditedDeadline}>Delete</button>
                          <button onClick={() => setEditingDeadline(null)}>Cancel</button>
                          <button className="calendar-primary-btn" style={{ background: 'var(--accent)', borderColor: 'var(--accent)' }} onClick={saveEditedDeadline}>Save</button>
                        </div>
                      </div>
                    </div>
                  )}
                  {calendarView !== 'list' && (
                    <>
                      <div className={`calendar-today-row ${calendarView === 'year' ? 'calendar-today-row-year' : ''}`}>
                        <button className="calendar-today-btn" onClick={() => setCalendarFocusDate(new Date())}>{calendarView === 'week' ? 'This Week' : calendarView === 'month' ? 'This Month' : calendarView === 'year' ? 'This Year' : 'Jump to Today'}</button>
                      </div>
                      {calendarSyncing ? <p className="muted small" style={{ textAlign: 'center', marginTop: -20, marginBottom: 4 }}>Syncing calendar…</p> : null}
                      {calendarSyncError ? <p className="small" style={{ textAlign: 'center', marginTop: -4, marginBottom: 16, color: '#b13a3a' }}>{calendarSyncError}</p> : null}
                    </>
                  )}

                  {calendarMonthZoom ? (
                    <div
                      className={`calendar-month-zoom-overlay ${calendarMonthZoom.expanded ? 'expanded' : ''}`}
                      style={{
                        left: calendarMonthZoom.expanded ? calendarMonthZoom.end.left : calendarMonthZoom.start.left,
                        top: calendarMonthZoom.expanded ? calendarMonthZoom.end.top : calendarMonthZoom.start.top,
                        width: calendarMonthZoom.expanded ? calendarMonthZoom.end.width : calendarMonthZoom.start.width,
                        height: calendarMonthZoom.expanded ? calendarMonthZoom.end.height : calendarMonthZoom.start.height,
                      }}
                    >
                      <div className="calendar-month-zoom-preview" dangerouslySetInnerHTML={{ __html: calendarMonthZoom.html }} />
                    </div>
                  ) : null}

                  {calendarDayMorph ? (
                    <div className="calendar-day-morph-overlay" aria-hidden="true">
                      {calendarDayMorph.items.map((it, i) => (
                        <span
                          key={`${it.label}-${i}`}
                          className={`calendar-day-morph-item ${calendarDayMorph.active ? 'active' : ''}`}
                          style={{
                            left: calendarDayMorph.active ? it.endX : it.startX,
                            top: calendarDayMorph.active ? it.endY : it.startY,
                          }}
                        >
                          {it.label}
                        </span>
                      ))}
                    </div>
                  ) : null}

                  {calendarView === 'list' ? (
                    <div className="calendar-list-search-row">
                      <input
                        className="search-input-icon"
                        value={calendarListSearch}
                        onChange={(e) => setCalendarListSearch(e.target.value)}
                        placeholder="Search deliverables"
                        aria-label="Search deliverables"
                      />
                    </div>
                  ) : null}

                  <CalendarViewPanel
                    view={calendarView}
                    focusDate={calendarFocusDate}
                    setFocusDate={setCalendarFocusDate}
                    setView={setCalendarView}
                    onYearMonthSelect={animateYearMonthToMonthView}
                    onMonthDaySelect={animateMonthDayToWeekView}
                    monthEntering={calendarMonthEntering}
                    monthTransitionPhase={calendarMonthPhase}
                    weekEntering={calendarWeekEntering}
                    weekTransitionPhase={calendarWeekPhase}
                    monthDayMorphing={!!calendarDayMorph}
                    deadlines={filteredCalendarDeadlines}
                    onEditDeadline={openEditDeadline}
                    onDeleteDeadline={deleteCalendarDeadlineDirect}
                    onStatusChange={() => setCalendarLastUpdatedAt(now())}
                    companyName={siteSettings.companyName}
                    listSearch={calendarListSearch}
                    feedFilters={calendarFeedFilters}
                    onFeedFiltersChange={setCalendarFeedFilters}
                  />

                  {calendarView === 'list' && showCalendarListGuide ? (
                    <div className="calendar-list-guide-overlay" onClick={() => setShowCalendarListGuide(false)}>
                      <div className="calendar-list-guide-card">
                        <div className="calendar-list-guide-callout nav left-point" style={getGuideCalloutStyle('.calendar-left-rail', { mode: 'right-of', dx: 18, dy: 8, fallbackLeft: 280, fallbackTop: 82 })}><span className="calendar-list-guide-arrow" aria-hidden="true" /><span className="calendar-list-guide-label">Navigation Bar</span></div>
                        <div className="calendar-list-guide-callout search left-point" style={getGuideCalloutStyle('.calendar-list-search-row input', { mode: 'inside-left', dx: 14, dy: 7, fallbackLeft: 330, fallbackTop: 138 })}><span className="calendar-list-guide-arrow" aria-hidden="true" /><span className="calendar-list-guide-label">Search a Specific Deadline or Task</span></div>
                        <div className="calendar-list-guide-callout filters left-point" style={{ ...getGuideCalloutStyle('.calendar-deliverable-list .calendar-left-filter-row .calendar-left-filter-btn:nth-child(3)', { mode: 'right-of', dx: 12, dy: 0, anchor: 'middle', fallbackLeft: 520, fallbackTop: 188 }), transform: 'translateY(-50%)' }}><span className="calendar-list-guide-arrow" aria-hidden="true" /><span className="calendar-list-guide-label">Filter by Time</span></div>
                        <div className="calendar-list-guide-callout date left-point" style={getGuideCalloutStyle('.calendar-deliverable-date-badge', { mode: 'right-of', dx: 12, dy: 2, fallbackLeft: 300, fallbackTop: 250 })}><span className="calendar-list-guide-arrow" aria-hidden="true" /><span className="calendar-list-guide-label">Due Date</span></div>
                        <div className="calendar-list-guide-callout title up-point" style={{ ...getGuideCalloutStyle('.calendar-deliverable-card', { mode: 'below-center', dx: 0, dy: 10, fallbackLeft: 520, fallbackTop: 300 }), transform: 'translateX(-50%)', whiteSpace: 'nowrap', maxWidth: 'none' }}><span className="calendar-list-guide-arrow" aria-hidden="true" /><span className="calendar-list-guide-label">Select Deliverable/Task to edit and view explanation of information</span></div>
                        <div className="calendar-list-guide-callout description" style={{ ...getGuideCalloutStyle('.calendar-filter-heading strong', { mode: 'left-of', dx: 225, dy: 0, anchor: 'middle', fallbackLeft: 900, fallbackTop: 300 }), transform: 'translateY(-50%)' }}><span className="calendar-list-guide-label">Toggle on and off calendars</span><span className="calendar-list-guide-arrow" aria-hidden="true" /></div>
                        <div className="calendar-list-guide-callout notes" style={getGuideCalloutStyle('.calendar-deliverable-notes-label', { mode: 'right-of', dx: 14, dy: -2, fallbackLeft: 570, fallbackTop: 330 })}><span className="calendar-list-guide-arrow" aria-hidden="true" /><span className="calendar-list-guide-label">Notes</span></div>
                        <div className="calendar-list-guide-callout status" style={getGuideCalloutStyle('.calendar-deliverable-status-label', { mode: 'right-of', dx: 14, dy: -2, fallbackLeft: 570, fallbackTop: 356 })}><span className="calendar-list-guide-arrow" aria-hidden="true" /><span className="calendar-list-guide-label">Status</span></div>
                      </div>
                    </div>
                  ) : null}

                  <div className="staffing-last-updated muted">Last updated: {formatMonthDayTime(calendarLastUpdatedAt)}</div>

                  {calendarView === 'list' && (
                    <section className="panel calendar-upload-panel" style={{ marginTop: 12 }}>
                      <h2 style={{ marginTop: 0 }}>Calendar Uploads</h2>
                      <div className="row">
                        <input
                          type="file"
                          onChange={(e) => {
                            const f = e.target.files?.[0] || null
                            setCalendarUploadFile(f)
                            setCalendarUploadName(f?.name || '')
                          }}
                        />
                        <button
                          onClick={async () => {
                            const n = calendarUploadName.trim()
                            if (!n) return
                            setCalendarUploads([n])
                            setCalendarDeadlines([])
                            setCalendarLastUpdatedAt(now())

                            if (calendarUploadFile) {
                              try {
                                const lower = n.toLowerCase()
                                let deadlines: CalendarDeadline[] = []

                                if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
                                  showCopyToast('Excel import is temporarily disabled for security hardening. Use CSV/TXT/PDF.')
                                } else {
                                  const text = await calendarUploadFile.text()
                                  deadlines = extractDeadlinesFromText(text, n)
                                }

                                if (deadlines.length) {
                                  const withTeam = deadlines.map((x) => ({ ...x, assignTo: x.assignTo || 'Team' }))
                                  setCalendarDeadlines(withTeam)
                                  setCalendarLastUpdatedAt(now())
                                }
                              } catch {
                                // unsupported or unreadable file
                              }
                            }

                            setCalendarUploadName('')
                            setCalendarUploadFile(null)
                          }}
                        >
                          Upload
                        </button>
                      </div>
                      <div className="file-list" style={{ marginTop: 8, maxHeight: 180 }}>
                        {calendarUploads.length === 0 ? <div className="file-row muted">No uploads yet.</div> : null}
                        {calendarUploads.map((n, i) => (
                          <div key={`${n}-${i}`} className="file-row">{n}</div>
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              </div>
            </section>
          )}

          {activeTab === 'projectStandards' && (
            <section className="panel calendar-panel">
              <div className="calendar-shell">
                <aside className="calendar-left-rail consultant-left-rail">
                  <button
                    className={`rail-dot ${projectStandardsView === 'sheetIndex' ? 'active' : ''}`}
                    onClick={() => setProjectStandardsView('sheetIndex')}
                    title="Sheet Index"
                  >
                    Sheet Index
                  </button>
                  <button
                    className={`rail-dot ${projectStandardsView === 'sheetGraphics' ? 'active' : ''}`}
                    onClick={() => setProjectStandardsView('sheetGraphics')}
                    title="Sheet Graphics"
                  >
                    Sheet Graphics
                  </button>
                  <button
                    className={`rail-dot ${projectStandardsView === 'bep' ? 'active' : ''}`}
                    onClick={() => setProjectStandardsView('bep')}
                    title="BEP"
                  >
                    BEP
                  </button>
                  <button
                    className={`rail-dot ${projectStandardsView === 'uploads' ? 'active' : ''}`}
                    onClick={() => setProjectStandardsView('uploads')}
                    title="Uploads"
                  >
                    Uploads
                  </button>
                </aside>

                <div className="calendar-main project-standards-main">
                  <div className="calendar-header-row">
                    <h1 className="calendar-title">
                      {projectStandardsView === 'sheetIndex'
                        ? 'Sheet Index'
                        : projectStandardsView === 'sheetGraphics'
                          ? 'Sheet Graphics'
                          : projectStandardsView === 'bep'
                            ? 'BEP'
                            : 'Uploads'}
                    </h1>
                  </div>

                  {projectStandardsView === 'sheetIndex' ? (
                    <>
                      <div className="project-standards-input-area">
                        <h3>Sheet Index</h3>
                        <p className="muted">Upload a Sheet Index file to populate the table below.</p>

                        <div className="project-standards-upload-row">
                          <input
                            className="project-standards-file-input"
                            type="file"
                            accept=".csv,.txt,.pdf"
                            onChange={async (e) => {
                              const f = e.target.files?.[0] || null
                              if (!f) return
                              try {
                                const count = await importSheetIndexFromFile(f)
                                showCopyToast(count > 0 ? `Loaded ${count} sheet row${count === 1 ? '' : 's'}` : 'No table rows found')
                              } catch {
                                showCopyToast('Upload failed. Use CSV, TXT, or PDF.')
                              } finally {
                                e.currentTarget.value = ''
                              }
                            }}
                          />
                          <span className="muted small">{sheetIndexFileName ? `Loaded: ${sheetIndexFileName}` : 'No file loaded yet'}</span>
                        </div>

                        <div className="project-standards-upload-row">
                          <input
                            className="project-standards-add-col-input"
                            value={sheetIndexNewColumn}
                            onChange={(e) => setSheetIndexNewColumn(e.target.value)}
                            placeholder="Add column name"
                          />
                          <button
                            type="button"
                            className="project-standards-add-col-btn"
                            onClick={() => {
                              const name = sheetIndexNewColumn.trim()
                              if (!name) return
                              const normalized = name.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
                              if (sheetIndexColumns.some((c) => c.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim() === normalized)) {
                                showCopyToast('Column already exists')
                                return
                              }
                              setSheetIndexColumns((prev) => [...prev, name])
                              setSheetIndexMarkerColumns((prev) => [...prev, name])
                              setSheetIndexRows((prev) => prev.map((row) => ({ ...row, [name]: row[name] || '' })))
                              setSheetIndexDraftRow((prev) => ({ ...prev, [name]: prev[name] || '' }))
                              setSheetIndexNewColumn('')
                            }}
                          >
                            Add Column
                          </button>
                        </div>

                        <div className="project-standards-entry-row">
                          {sheetIndexColumns.map((col, idx) => {
                            const normalized = col.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
                            const isResponsibility = normalized === 'assign to' || normalized === 'responsibility'
                            const isSheetNumber = normalized === 'sheet number'
                            const isScale = normalized === 'scale'
                            const isMarkerColumn = sheetIndexMarkerColumnKeys.has(normalized)
                            const widthClass = isMarkerColumn ? 'is-25' : ((isResponsibility || isSheetNumber || isScale) ? 'is-100' : '')
                            return (
                              <div key={`draft-col-${col}-${idx}`} className={`project-standards-entry-cell ${widthClass}`}>
                                {isResponsibility ? (
                                  <select
                                    className="project-standards-cell-input project-standards-draft-input"
                                    value={sheetIndexDraftRow[col] || ''}
                                    onChange={(e) => setSheetIndexDraftRow((prev) => ({ ...prev, [col]: e.target.value }))}
                                  >
                                    <option value="">Team</option>
                                    {(staffingTeamMembers.length ? staffingTeamMembers : ['Team']).map((member) => (
                                      <option key={member} value={member}>{member}</option>
                                    ))}
                                  </select>
                                ) : isScale ? (
                                  <select
                                    className="project-standards-cell-input project-standards-draft-input"
                                    value={sheetIndexDraftRow[col] || ''}
                                    onChange={(e) => setSheetIndexDraftRow((prev) => ({ ...prev, [col]: e.target.value }))}
                                  >
                                    <option value="">Scale</option>
                                    {drawingScaleOptions.map((scale) => (
                                      <option key={scale} value={scale}>{scale}</option>
                                    ))}
                                  </select>
                                ) : isMarkerColumn ? (
                                  <select
                                    className={`project-standards-cell-input project-standards-draft-input project-standards-marker-input ${(sheetIndexDraftRow[col] || '') === '●' ? 'is-filled' : ''}`}
                                    value={sheetIndexDraftRow[col] || ''}
                                    onChange={(e) => setSheetIndexDraftRow((prev) => ({ ...prev, [col]: e.target.value }))}
                                  >
                                    <option value=""> </option>
                                    <option value="●">●</option>
                                  </select>
                                ) : (
                                  <input
                                    className="project-standards-cell-input project-standards-draft-input"
                                    value={sheetIndexDraftRow[col] || ''}
                                    placeholder={normalized === 'sheet number' ? 'Sheet Number' : normalized === 'scale' ? 'Scale' : `Enter ${col.toLowerCase()}`}
                                    onChange={(e) => setSheetIndexDraftRow((prev) => ({ ...prev, [col]: e.target.value }))}
                                  />
                                )}
                              </div>
                            )
                          })}
                          <button
                            type="button"
                            className="project-standards-add-col-btn"
                            onClick={() => {
                              const newRow = sheetIndexColumns.reduce<Record<string, string>>((acc, col) => {
                                acc[col] = (sheetIndexDraftRow[col] || '').trim()
                                return acc
                              }, {})
                              if (!Object.values(newRow).some((v) => v.length > 0)) {
                                showCopyToast('Enter at least one value to add a row')
                                return
                              }
                              setSheetIndexRows((prev) => [...prev, newRow])
                              setSheetIndexDraftRow({})
                            }}
                          >
                            Add Row
                          </button>
                        </div>
                      </div>

                      <section className="project-standards-card project-standards-table-card">
                        <div className="project-standards-table-wrap">
                          {sheetIndexColumns.length ? (
                            <table className="project-standards-table">
                              <thead>
                                <tr>
                                  {sheetIndexColumns.map((cell, i) => {
                                    const normalized = cell.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
                                    const fixed100 = normalized === 'assign to' || normalized === 'responsibility' || normalized === 'sheet number' || normalized === 'scale'
                                    const isMarkerColumn = sheetIndexMarkerColumnKeys.has(normalized)
                                    return (
                                      <th key={`${cell}-${i}`} className={isMarkerColumn ? 'project-standards-col-25' : (fixed100 ? 'project-standards-col-100' : '')}>
                                        {isMarkerColumn ? <span className="project-standards-rotated-label">{cell || `Column ${i + 1}`}</span> : (cell || `Column ${i + 1}`)}
                                      </th>
                                    )
                                  })}
                                </tr>
                              </thead>
                              <tbody>
                                {sheetIndexRows.length ? sheetIndexRows.map((row, rIdx) => (
                                  <tr key={`sheet-row-${rIdx}`}>
                                    {sheetIndexColumns.map((col, cIdx) => {
                                      const normalized = col.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
                                      const isResponsibility = normalized === 'assign to' || normalized === 'responsibility'
                                      const isSheetNumber = normalized === 'sheet number'
                                      const isSheetName = normalized === 'sheet name'
                                      const isScale = normalized === 'scale'
                                      const isMarkerColumn = sheetIndexMarkerColumnKeys.has(normalized)
                                      const fixed100 = isResponsibility || isSheetNumber || isScale
                                      return (
                                        <td key={`sheet-cell-${rIdx}-${cIdx}`} className={isMarkerColumn ? 'project-standards-col-25' : (fixed100 ? 'project-standards-col-100' : '')}>
                                          {isResponsibility ? (
                                            <select
                                              className="project-standards-cell-input"
                                              value={row[col] || ''}
                                              onChange={(e) => {
                                                const value = e.target.value
                                                setSheetIndexRows((prev) => prev.map((x, idx) => (idx === rIdx ? { ...x, [col]: value } : x)))
                                              }}
                                            >
                                              <option value="">Select team member</option>
                                              {(staffingTeamMembers.length ? staffingTeamMembers : ['Team']).map((member) => (
                                                <option key={member} value={member}>{member}</option>
                                              ))}
                                            </select>
                                          ) : isScale ? (
                                            <select
                                              className="project-standards-cell-input"
                                              value={row[col] || ''}
                                              onChange={(e) => {
                                                const value = e.target.value
                                                setSheetIndexRows((prev) => prev.map((x, idx) => (idx === rIdx ? { ...x, [col]: value } : x)))
                                              }}
                                            >
                                              <option value="">Scale</option>
                                              {drawingScaleOptions.map((scale) => (
                                                <option key={scale} value={scale}>{scale}</option>
                                              ))}
                                            </select>
                                          ) : isMarkerColumn ? (
                                            <select
                                              className={`project-standards-cell-input project-standards-marker-input ${(row[col] || '') === '●' ? 'is-filled' : ''}`}
                                              value={row[col] || ''}
                                              onChange={(e) => {
                                                const value = e.target.value
                                                setSheetIndexRows((prev) => prev.map((x, idx) => (idx === rIdx ? { ...x, [col]: value } : x)))
                                              }}
                                            >
                                              <option value=""> </option>
                                              <option value="●">●</option>
                                            </select>
                                          ) : isSheetNumber || isSheetName ? (
                                            <input
                                              className="project-standards-cell-input"
                                              value={row[col] || ''}
                                              placeholder={isSheetNumber ? 'Enter sheet number' : 'Enter sheet name'}
                                              onChange={(e) => {
                                                const value = e.target.value
                                                setSheetIndexRows((prev) => prev.map((x, idx) => (idx === rIdx ? { ...x, [col]: value } : x)))
                                              }}
                                            />
                                          ) : (
                                            row[col] || '—'
                                          )}
                                        </td>
                                      )
                                    })}
                                  </tr>
                                )) : (
                                  <tr>
                                    <td colSpan={Math.max(1, sheetIndexColumns.length)} className="muted">No data rows found in file.</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          ) : (
                            <div className="file-row muted">No sheet index columns configured.</div>
                          )}
                        </div>
                      </section>
                    </>
                  ) : null}

                  {projectStandardsView === 'sheetGraphics' ? (
                    <section className="project-standards-card">
                      <h3>Sheet Graphics</h3>
                      <p className="muted">Set title block, annotation, lineweight, and visual hierarchy standards.</p>
                    </section>
                  ) : null}

                  {projectStandardsView === 'bep' ? (
                    <section className="project-standards-card">
                      <h3>BEP</h3>
                      <p className="muted">Capture BIM Execution Plan requirements, model coordination, and exchange rules.</p>
                    </section>
                  ) : null}

                  {projectStandardsView === 'uploads' ? (
                    <section className="project-standards-card">
                      <h3>Uploads</h3>
                      <p className="muted">Track standards-related reference uploads, revisions, and source files.</p>
                    </section>
                  ) : null}
                </div>
              </div>
            </section>
          )}

          {activeTab === 'projects' && (
            <section className="panel projects-overview-panel">
              {projectCardDraft ? (
                <>
                  <div className="projects-overview-top-row">
                    <div className="projects-overview-actions" style={{ width: '100%', justifyContent: 'space-between' }}>
                      <button type="button" onClick={() => setProjectCardDraft(null)}>← Back to Projects</button>
                    </div>
                  </div>
                  <article className="projects-detail-page side-detail-panel">
                    <div className="row project-card-popup-head" style={{ alignItems: 'center', marginTop: 0, marginBottom: 8 }}>
                      <h1 className="calendar-title projects-title" style={{ margin: 0 }}>{projectCardDraft.name}</h1>
                      <button type="button" style={{ marginLeft: 'auto' }} onClick={toggleProjectPastCurrent}>
                        {pastProjectKeys[projectCardDraft.name.trim().toLowerCase()] ? 'Move to Current Projects' : 'Move to Past Projects'}
                      </button>
                    </div>
                    <div className="project-popup-columns">
                      <div className="site-settings-typography-grid">
                        <label><span>Location</span><input value={(projectOverviewByProject[projectCardDraft.name.trim().toLowerCase()] || blankProjectOverview()).location || profileOfficePlaceholder} readOnly /></label>
                        <label><span>Type</span><input value={projectCardDraft.projectType} onChange={(e) => setProjectCardDraft((prev) => (prev ? { ...prev, projectType: e.target.value } : prev))} /></label>
                        <label><span>Number of Stories</span><input value={projectCardDraft.storyCount} onChange={(e) => setProjectCardDraft((prev) => (prev ? { ...prev, storyCount: e.target.value } : prev))} /></label>
                        <label><span>Tella / Square Footage</span><input value={projectCardDraft.totalSquareFootage} onChange={(e) => setProjectCardDraft((prev) => (prev ? { ...prev, totalSquareFootage: e.target.value } : prev))} /></label>
                        <label><span>Principal</span><input value={getProjectPrincipal(projectCardDraft.name) || 'No Principal'} readOnly /></label>
                        <label><span>Client</span><input value={projectCardDraft.client} onChange={(e) => setProjectCardDraft((prev) => (prev ? { ...prev, client: e.target.value } : prev))} /></label>
                      </div>
                      <div className="site-settings-typography-grid">
                        <label><span>Concept Deadline</span><input value={(projectOverviewByProject[projectCardDraft.name.trim().toLowerCase()] || blankProjectOverview()).phaseDeadlines.Concept || '—'} readOnly /></label>
                        <label><span>Deadline</span><input value={(projectOverviewByProject[projectCardDraft.name.trim().toLowerCase()] || blankProjectOverview()).endDate || '—'} readOnly /></label>
                        <label><span>SD Deadline</span><input value={(projectOverviewByProject[projectCardDraft.name.trim().toLowerCase()] || blankProjectOverview()).phaseDeadlines.SD || '—'} readOnly /></label>
                        <label><span>DT Deadline</span><input value={(projectOverviewByProject[projectCardDraft.name.trim().toLowerCase()] || blankProjectOverview()).phaseDeadlines.DD || '—'} readOnly /></label>
                        <label><span>CD Deadline</span><input value={(projectOverviewByProject[projectCardDraft.name.trim().toLowerCase()] || blankProjectOverview()).phaseDeadlines.CC || '—'} readOnly /></label>
                        <label><span>Top Off</span><input value={projectCardDraft.topOff} onChange={(e) => setProjectCardDraft((prev) => (prev ? { ...prev, topOff: e.target.value } : prev))} /></label>
                        <label><span>TCO</span><input value={projectCardDraft.tco} onChange={(e) => setProjectCardDraft((prev) => (prev ? { ...prev, tco: e.target.value } : prev))} /></label>
                        <label><span>CO</span><input value={projectCardDraft.co} onChange={(e) => setProjectCardDraft((prev) => (prev ? { ...prev, co: e.target.value } : prev))} /></label>
                      </div>
                    </div>
                    <div className="row project-add-actions" style={{ marginTop: 12 }}>
                      <button type="button" onClick={() => setProjectCardDraft(null)}>Back</button>
                      <button type="button" className="danger" onClick={deleteProjectCardModal}>Delete</button>
                      <button type="button" className="project-add-primary-btn" onClick={saveProjectCardModal}>Save</button>
                    </div>
                  </article>
                </>
              ) : projectsSubTab === 'current' ? (
                <>
                  <div className="projects-overview-top-row">
                    <h1 className="calendar-title projects-title">Current Projects</h1>
                    <div className="projects-overview-actions">
                      <input
                        className="projects-overview-search search-input-icon"
                        placeholder="Search projects"
                        value={projectsSearch}
                        onChange={(e) => setProjectsSearch(e.target.value)}
                      />
                      <button type="button" className="projects-add-btn" onClick={() => { setAddProjectAttempted(false); setShowAddProjectModal(true) }}>Add Project</button>
                    </div>
                  </div>

                  <div className="projects-overview-grid">
                {filteredCompanyProjectsOverviewCards.map((projectCard) => {
                  const principalName = getProjectPrincipal(projectCard.name) || 'No Principal'
                  const principalInitials = principalName === 'No Principal'
                    ? 'NP'
                    : principalName
                        .split(' ')
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((part) => part[0]?.toUpperCase())
                        .join('')

                  return (
                  <article key={projectCard.name} className="projects-overview-card" onClick={() => openProjectCardModal(projectCard.name)}>
                    {(() => {
                      const projectKey = projectCard.name.trim().toLowerCase()
                      const overviewForCard = projectOverviewByProject[projectKey] || blankProjectOverview()
                      const phaseStatus = getCurrentPhaseStatus(overviewForCard)
                      const currentPhase = phaseStatus.phase
                      const phaseProgress = phaseStatus.progress
                      return (
                    <>
                    <div className="projects-overview-head">
                      <div>
                        <div className="projects-overview-name-row">
                          <div className="projects-overview-name">
                            {projectCard.name.replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())}
                          </div>
                          <span className="projects-overview-principal-wrap">
                            <span className="projects-overview-principal-initials avatar-chip avatar-chip-sm">{principalInitials}</span>
                            <span className="projects-overview-principal">{principalName}</span>
                          </span>
                        </div>
                        <p className="muted">{projectCard.location}</p>
                      </div>
                      <button
                        type="button"
                        className="projects-overview-employees"
                        title="Employees on project"
                        onClick={(e) => {
                          e.stopPropagation()
                          setTeamOrganizationView(projectCard.name)
                          setActiveTab('teamTasks')
                        }}
                      >
                        <svg className="projects-overview-employees-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                          <circle cx="12" cy="8" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.7" />
                          <path d="M5.5 19.5c0-3.2 2.9-5.4 6.5-5.4s6.5 2.2 6.5 5.4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                        <strong>{projectCard.employeeCount ?? 0}</strong>
                      </button>
                    </div>

                    <div className="projects-overview-photo-wrap">
                      {projectCard.buildingPhoto
                        ? <img src={projectCard.buildingPhoto} alt={`${projectCard.name} overview`} className="projects-overview-photo" />
                        : <div className="dashboard-overview-photo-placeholder">No project photo uploaded</div>}
                    </div>

                    <div className="projects-overview-timeline">
                      <div className="projects-overview-timeline-bar">
                        <span style={{ width: `${phaseProgress}%` }} />
                      </div>
                      <small>{phaseProgress}% of {currentPhase === 'CC' ? 'CD' : currentPhase}</small>
                    </div>

                    <div className="projects-overview-phases muted">
                      <span>{currentPhase === 'SD' ? <strong>SD</strong> : 'SD'}: {projectCard.phaseDeadlines.SD || '—'}</span>
                      <span>{currentPhase === 'DD' ? <strong>DD</strong> : 'DD'}: {projectCard.phaseDeadlines.DD || '—'}</span>
                      <span>{currentPhase === 'CC' ? <strong>CD</strong> : 'CD'}: {projectCard.phaseDeadlines.CC || '—'}</span>
                    </div>
                  </>
                      )
                    })()}
                  </article>
                  )
                })}
              </div>

                  {showAddProjectModal ? (
                    <div className="calendar-modal-backdrop" onClick={() => { setAddProjectAttempted(false); setShowAddProjectModal(false) }}>
                      <div className="calendar-popover project-add-popover" onClick={(e) => e.stopPropagation()}>
                        <h3>Add Project</h3>
                        <div className="row" style={{ marginTop: 8 }}>
                          <input className={addProjectAttempted && !newProjectName.trim() ? 'required-missing' : ''} placeholder="Project name *" value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} />
                        </div>
                        <div className="row" style={{ marginTop: 8 }}>
                          <input className={addProjectAttempted && !newProjectLocation.trim() ? 'required-missing' : ''} placeholder="Project location *" value={newProjectLocation} onChange={(e) => setNewProjectLocation(e.target.value)} />
                        </div>
                        <div className="row project-add-actions" style={{ marginTop: 8 }}>
                          <button type="button" onClick={() => { setAddProjectAttempted(false); setShowAddProjectModal(false) }}>Cancel</button>
                          <button type="button" className="project-add-primary-btn" onClick={addProjectsCard}>Add</button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <div className="projects-overview-top-row">
                    <h1 className="calendar-title projects-title">Past Projects</h1>
                  </div>
                  <div className="projects-overview-grid">
                    {pastProjectsOverviewCards.map((projectCard) => {
                      const principalName = getProjectPrincipal(projectCard.name) || 'No Principal'
                      const principalInitials = principalName === 'No Principal'
                        ? 'NP'
                        : principalName
                            .split(' ')
                            .filter(Boolean)
                            .slice(0, 2)
                            .map((part) => part[0]?.toUpperCase())
                            .join('')
                      return (
                        <article key={`past-${projectCard.name}`} className="projects-overview-card" onClick={() => openProjectCardModal(projectCard.name)}>
                          <div className="projects-overview-head">
                            <div>
                              <div className="projects-overview-name-row">
                                <div className="projects-overview-name">{projectCard.name.replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())}</div>
                                <span className="projects-overview-principal-wrap">
                                  <span className="projects-overview-principal-initials avatar-chip avatar-chip-sm">{principalInitials}</span>
                                  <span className="projects-overview-principal">{principalName}</span>
                                </span>
                              </div>
                              <p className="muted">{projectCard.location}</p>
                            </div>
                          </div>
                        </article>
                      )
                    })}
                    {pastProjectsOverviewCards.length === 0 ? <p className="muted">No past projects yet.</p> : null}
                  </div>
                </>
              )}

            </section>
          )}

          {(activeTab === 'directory' || activeTab === 'consultantList') && (
            <section className={`panel directory-panel ${activeTab === 'consultantList' ? 'consultant-list-main' : ''}`}>
              <div className="directory-header-row">
                <h1 className="calendar-title directory-title">{activeTab === 'consultantList' ? 'Consultant List' : 'Directory'}</h1>
                {activeTab === 'consultantList' ? (
                  <p className="consultant-list-disclaimer muted">
                    Consultants shown here are generated automatically from the Project Directory contacts.
                  </p>
                ) : null}
              </div>
              <div className="directory-toolbar-search row directory-search-row">
                <input
                  ref={directorySearchInputRef}
                  className="search-input-icon"
                  placeholder={activeTab === 'consultantList' ? 'Search discipline or company' : 'Search Name, Role, or Company'}
                  value={directorySearch}
                  onChange={(e) => setDirectorySearch(e.target.value)}
                  title={activeTab === 'directory' ? 'Shortcut: Ctrl/Cmd + K' : undefined}
                />
                {activeTab === 'directory' ? (
                  <div className="row directory-header-actions">
                    <button onClick={() => { resetContactForm(); setAddContactAttempted(false); setShowAddContact(true) }}>Add Contact</button>
                    <button onClick={() => contactUploadRef.current?.click()}>Upload</button>
                  </div>
                ) : null}
                {activeTab === 'consultantList' ? (
                  <div className="row directory-header-actions">
                    <button onClick={() => { setNewConsultantType(''); setNewConsultantCompany(''); setShowAddConsultantCompany(true) }}>Add Consultant</button>
                  </div>
                ) : null}
              </div>
              {activeTab === 'directory' ? (
                <>
                <div className="directory-contact-filter-row" aria-label="Directory filters">
                  <button
                    type="button"
                    className={`directory-filter-btn ${directoryContactFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setDirectoryContactFilter('all')}
                  >
                    All Contacts
                  </button>
                  <button
                    type="button"
                    className={`directory-filter-btn ${directoryContactFilter === 'key' ? 'active' : ''}`}
                    onClick={() => setDirectoryContactFilter('key')}
                  >
                    Primary Contacts
                  </button>
                  <button
                    type="button"
                    className={`directory-filter-btn ${directoryContactFilter === 'archived' ? 'active' : ''}`}
                    onClick={() => setDirectoryContactFilter('archived')}
                  >
                    Archived
                  </button>
                  <label className="directory-filter-btn directory-sort-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span className="directory-sort-label">Sort:</span>
                    <select
                      className="directory-sort-select"
                      value={directorySortBy}
                      onChange={(e) => setDirectorySortBy(e.target.value as 'company' | 'consultantType')}
                    >
                      <option value="company">Company Name</option>
                      <option value="consultantType">Consultant Type</option>
                    </select>
                  </label>
                  <select
                    className={`directory-inline-input directory-group-select ${activeDirectoryViewId ? 'active' : ''}`}
                    style={{ maxWidth: 210 }}
                    value={activeDirectoryViewId || ''}
                    onChange={(e) => applyDirectorySavedView(e.target.value)}
                  >
                    <option value="">Select a Contact Group</option>
                    {directorySavedViews.map((view) => (
                      <option key={view.id} value={view.id}>{view.name}</option>
                    ))}
                  </select>
                  <div className="directory-group-actions">
                    <div className="directory-save-group-anchor">
                      <button
                        type="button"
                        className="directory-filter-btn"
                        onClick={() => {
                          setDirectoryViewNameDraft('')
                          setShowDirectorySaveGroupPopover((prev) => !prev)
                        }}
                      >
                        Save Group
                      </button>
                      {showDirectorySaveGroupPopover ? (
                        <div className="directory-save-group-popover" onClick={(e) => e.stopPropagation()}>
                          <label className="directory-save-group-field-label">Name Contact Group *</label>
                          <input
                            className="directory-inline-input"
                            value={directoryViewNameDraft}
                            onChange={(e) => setDirectoryViewNameDraft(e.target.value)}
                            placeholder="Required"
                            autoFocus
                          />
                          <div className="directory-save-group-popover-actions">
                            <button type="button" className="directory-filter-btn" onClick={() => { setShowDirectorySaveGroupPopover(false); setDirectoryViewNameDraft('') }}>Cancel</button>
                            <button type="button" className="directory-filter-btn" onClick={saveCurrentDirectoryView} disabled={!directoryViewNameDraft.trim()}>Save</button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <button type="button" className="directory-filter-btn" onClick={updateActiveDirectoryView} disabled={!activeDirectoryViewId}>Update Group</button>
                    <button type="button" className="directory-filter-btn" onClick={deleteActiveDirectoryGroup} disabled={!activeDirectoryViewId}>Delete Group</button>
                  </div>
                  <button
                    type="button"
                    className="directory-filter-btn"
                    style={{ marginLeft: 'auto', minWidth: 196, textAlign: 'center' }}
                    onClick={() => {
                      const allShownSelected = directoryVisibleIndices.length > 0 && directoryVisibleIndices.every((idx) => directoryBulkSelection.has(idx))
                      setDirectoryBulkSelection((prev) => {
                        const next = new Set(prev)
                        if (allShownSelected) directoryVisibleIndices.forEach((idx) => next.delete(idx))
                        else directoryVisibleIndices.forEach((idx) => next.add(idx))
                        return next
                      })
                    }}
                  >
                    {directoryVisibleIndices.length > 0 && directoryVisibleIndices.every((idx) => directoryBulkSelection.has(idx)) ? 'Deselect All Shown' : 'Select All Shown'}
                  </button>
                </div>
                {directoryDuplicateGroups.length > 0 ? (
                  <div className="directory-contact-filter-row directory-duplicate-banner" style={{ marginTop: 8, marginBottom: 8 }}>
                    <span className="directory-filter-btn active">{directoryDuplicateGroups.length} duplicate group{directoryDuplicateGroups.length === 1 ? '' : 's'} detected</span>
                    <button type="button" className="directory-filter-btn" onClick={mergeFirstDirectoryDuplicateGroup}>Merge first group</button>
                  </div>
                ) : null}
                </>
              ) : null}

              {activeTab === 'directory' && directoryBulkSelection.size > 0 ? (
                <div className="directory-contact-filter-row directory-bulk-actions" style={{ marginBottom: 8, flexWrap: 'wrap' }}>
                  <span className="directory-filter-btn active">{directoryBulkSelection.size} selected</span>
                  <input
                    className="directory-inline-input"
                    style={{ maxWidth: 180 }}
                    value={bulkAssignTeamValue}
                    onChange={(e) => setBulkAssignTeamValue(e.target.value)}
                    placeholder="Assign Team"
                  />
                  <button type="button" className="directory-filter-btn" onClick={applyBulkAssignTeam}>Apply Team</button>
                  <input
                    className="directory-inline-input"
                    style={{ maxWidth: 180 }}
                    value={bulkAssignDisciplineValue}
                    onChange={(e) => setBulkAssignDisciplineValue(e.target.value)}
                    placeholder="Tag Discipline"
                  />
                  <button type="button" className="directory-filter-btn" onClick={applyBulkAssignDiscipline}>Apply Discipline</button>
                  <select
                    className="directory-inline-input"
                    style={{ maxWidth: 170 }}
                    value={directoryExportFormat}
                    onChange={(e) => { void handleDirectoryExportFormatChange(e.target.value as 'excel' | 'pdf' | '') }}
                  >
                    <option value="">Export Selected…</option>
                    <option value="excel">CSV (.csv)</option>
                    <option value="pdf">PDF</option>
                  </select>
                  <button type="button" className="directory-filter-btn" onClick={() => void copyEmailsFromSelectedDirectoryContacts()}>Copy Emails</button>
                  {directoryContactFilter === 'archived'
                    ? <button type="button" className="directory-filter-btn" onClick={restoreSelectedDirectoryContacts}>Restore Selected</button>
                    : <button type="button" className="directory-filter-btn" onClick={archiveSelectedDirectoryContacts}>Archive Selected</button>}
                  <button type="button" className="directory-filter-btn" onClick={() => setDirectoryBulkSelection(new Set())}>Clear</button>
                </div>
              ) : null}

              {activeTab === 'directory' ? (
                <div className="directory-toolbar" style={{ marginTop: 6 }}>
                  <input
                    ref={contactUploadRef}
                    type="file"
                    accept=".csv,.txt,.pdf"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const f = e.target.files?.[0] || null
                      if (!f) return
                      try {
                        const count = await importContactsFromFile(f)
                        showCopyToast(count > 0 ? `Imported ${count} contact${count === 1 ? '' : 's'}` : 'No contacts found')
                      } catch {
                        showCopyToast('Import failed')
                      } finally {
                        e.currentTarget.value = ''
                      }
                    }}
                  />
                </div>
              ) : null}

              {activeTab === 'directory' ? (() => {
                const chips: Array<{ key: string; label: string; onClear: () => void }> = []
                const searchTrim = directorySearch.trim()
                if (searchTrim) chips.push({ key: 'search', label: `Search: ${searchTrim}`, onClear: () => setDirectorySearch('') })
                if (directoryContactFilter !== 'all') {
                  const label = directoryContactFilter === 'key' ? 'Primary Contacts' : 'Archived'
                  chips.push({ key: 'contactFilter', label: `Status: ${label}`, onClear: () => setDirectoryContactFilter('all') })
                }
                if (directorySortBy !== 'company') {
                  chips.push({ key: 'sort', label: 'Sort: Consultant Type', onClear: () => setDirectorySortBy('company') })
                }
                if (activeDirectoryViewId) {
                  const selectedView = directorySavedViews.find((v) => v.id === activeDirectoryViewId)
                  chips.push({ key: 'savedView', label: `Group: ${selectedView?.name || 'Selected'}`, onClear: () => applyDirectorySavedView('') })
                }
                if (!chips.length) return null
                return (
                  <div className="directory-active-chips" aria-label="Active filters">
                    {chips.map((chip) => (
                      <button key={chip.key} type="button" className="directory-chip" onClick={chip.onClear} title="Clear this filter">
                        <span>{chip.label}</span>
                        <span aria-hidden="true">×</span>
                      </button>
                    ))}
                    <button
                      type="button"
                      className="directory-chip directory-chip-clear-all"
                      onClick={() => {
                        setDirectorySearch('')
                        setDirectoryContactFilter('all')
                        setDirectorySortBy('company')
                        applyDirectorySavedView('')
                      }}
                    >
                      Clear all filters
                    </button>
                  </div>
                )
              })() : null}

              <div className={`file-list ${activeTab === 'consultantList' ? 'consultant-list-container' : ''}`} style={{ marginTop: activeTab === 'directory' ? 6 : 12, maxHeight: 'none' }}>
                <div className="file-row directory-column-headers">
                  {activeTab === 'directory' ? (
                    <>
                      <div />
                      <div className="directory-contact-actions" />
                    </>
                  ) : (
                    <>
                      <div />
                      <div />
                    </>
                  )}
                </div>
                {(() => {
                  if (activeTab === 'consultantList') {
                    const q = directorySearch.trim().toLowerCase()
                    const byCompany = new Map<string, { company: string; types: string[]; keys: string[] }>()
                    consultantTypeSections.forEach((section) => {
                      section.companies.forEach((company) => {
                        const companyKey = `${section.type}::${company}`
                        const id = company.trim().toLowerCase()
                        const current = byCompany.get(id) || { company, types: [], keys: [] }
                        if (!current.types.includes(section.type)) current.types.push(section.type)
                        current.keys.push(companyKey)
                        byCompany.set(id, current)
                      })
                    })

                    return Array.from(byCompany.values())
                      .sort((a, b) => a.company.localeCompare(b.company))
                      .filter((entry) => {
                        if (!entry.keys.some((k) => !hiddenConsultantCompanies[k])) return false
                        if (!q) return true
                        const typeMatches = entry.types.some((t) => t.toLowerCase().includes(q))
                        const projectMatches = entry.keys.some((k) => (consultantProjectsByCompany[k] || []).some((project) => project.toLowerCase().includes(q)))
                        return entry.company.toLowerCase().includes(q) || typeMatches || projectMatches
                      })
                      .sort((a, b) => a.company.localeCompare(b.company))
                      .map((entry) => {
                        const visibleKeys = entry.keys.filter((k) => !hiddenConsultantCompanies[k])
                        const primaryKey = visibleKeys[0] || entry.keys[0]
                        const primaryType = (primaryKey.split('::')[0] || '').trim()
                        const reviews = visibleKeys.flatMap((k) => consultantCompanyReviews[k] || [])
                        const rating = reviews.length ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length) : 0
                        const assignedProjects = Array.from(new Set(visibleKeys.flatMap((k) => consultantProjectsByCompany[k] || [])))
                        const responsivenessLevel = rating >= 4 ? 'success' : rating >= 3 ? 'warning' : 'danger'
                        const responsivenessLabel = rating >= 4 ? 'Responsive' : rating >= 3 ? 'Watch' : 'At risk'

                        return (
                          <button
                            key={entry.company}
                            type="button"
                            className="file-row consultant-company-row"
                            onClick={() => setSelectedConsultantCompany({ type: primaryType, company: entry.company })}
                          >
                            <span className="consultant-company-row-main">
                              <span className="consultant-company-row-name">{entry.company}</span>
                              <span className="consultant-company-row-meta muted">
                                {assignedProjects.length} project{assignedProjects.length === 1 ? '' : 's'} assigned
                              </span>
                            </span>
                            <span className="consultant-company-row-right">
                              <span className={`status-chip ${reviews.length ? responsivenessLevel : 'info'}`}>{reviews.length ? responsivenessLabel : 'No rating'}</span>
                              <span className="consultant-company-rating" style={{ display: 'flex', gap: 4, alignItems: 'center', minWidth: 140, justifyContent: 'flex-start' }}>
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <span
                                    key={`${entry.company}-${star}`}
                                    aria-label={`${entry.company} average rating ${rating.toFixed(1)} / 5`}
                                    title={`${rating.toFixed(1)} / 5 (${reviews.length} review${reviews.length === 1 ? '' : 's'})`}
                                    style={{ color: 'var(--quicksand)', fontSize: '1.25em', lineHeight: 1 }}
                                  >
                                    {star <= Math.round(rating) ? '★' : '☆'}
                                  </span>
                                ))}
                              </span>
                            </span>
                          </button>
                        )
                      })
                  }

                  const q = directorySearch.trim().toLowerCase()
                  const contactIndexMap = new Map<DirectoryContact, number>()
                  directoryContacts.forEach((contact, index) => { contactIndexMap.set(contact, index) })
                  const grouped = directoryContacts
                    .filter((c) => directoryTeamFilter === 'All Teams' || c.team === directoryTeamFilter)
                    .filter((c) => !activeDirectoryGroupKeySet || activeDirectoryGroupKeySet.has(directoryContactKey(c)))
                    .filter((c) => {
                      if (directoryContactFilter === 'archived') return !!c.archived
                      if (c.archived) return false
                      return directoryContactFilter === 'key' ? !!c.primaryContact : true
                    })
                    .filter((c) => {
                      if (!q) return true
                      const haystack = [c.name, c.team, c.role, c.company, c.email, c.phone, c.altPhone]
                        .map((x) => (x || '').toLowerCase())
                        .join(' ')
                      return haystack.includes(q)
                    })
                    .reduce<Record<string, DirectoryContact[]>>((acc, c) => {
                      // Container header label should reflect Consultant Type from the contact card.
                      // In this schema, consultant type is stored in team.
                      const key = (c.team || 'Unassigned Consultant Type')
                      if (!acc[key]) acc[key] = []
                      acc[key].push(c)
                      return acc
                    }, {})

                  const entries = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                  const sortedContactsByTeam = new Map<string, DirectoryContact[]>()
                  const byText = (x: string, y: string) => x.localeCompare(y, undefined, { sensitivity: 'base' })
                  entries.forEach(([team, contacts]) => {
                    const sorted = [...contacts].sort((a, b) => {
                      const roleA = (a.role || '').trim()
                      const roleB = (b.role || '').trim()
                      const companyA = (a.company || '').trim()
                      const companyB = (b.company || '').trim()
                      const nameA = (a.name || '').trim()
                      const nameB = (b.name || '').trim()

                      let baseCmp = 0
                      if (directorySortBy === 'consultantType') {
                        baseCmp = byText(roleA, roleB) || byText(companyA, companyB)
                      } else {
                        baseCmp = byText(companyA, companyB) || byText(roleA, roleB)
                      }
                      if (baseCmp === 0) baseCmp = byText(nameA, nameB)
                      return baseCmp
                    })
                    sortedContactsByTeam.set(team, sorted)
                  })

                  return entries.map(([team, contacts]) => (
                  <div key={team} className="directory-type-container">
                    <div className="file-row directory-team-header">
                      <div>
                        <strong className="directory-type-badge">{team}</strong>
                        {(() => {
                          const companies = Array.from(new Set(contacts.map((x) => (x.company || '').trim()).filter(Boolean)))
                          const companyLabel = companies[0] || 'Unassigned Company'
                          const consultantTypeLabel = (team || '').trim().toLowerCase()
                          const companyKey = companyLabel.trim().toLowerCase()
                          if (consultantTypeLabel && companyKey === consultantTypeLabel) return null
                          return <span className="directory-company-text"> • {companyLabel}</span>
                        })()}
                      </div>
                      <div style={{ marginLeft: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
                        {(() => {
                          const containerIndices = contacts
                            .map((contact) => contactIndexMap.get(contact) ?? -1)
                            .filter((idx) => idx >= 0)
                          const allSelected = containerIndices.length > 0 && containerIndices.every((idx) => directoryBulkSelection.has(idx))
                          return (
                            <button
                              type="button"
                              className="directory-filter-btn"
                              onClick={() => {
                                setDirectoryBulkSelection((prev) => {
                                  const next = new Set(prev)
                                  if (allSelected) containerIndices.forEach((idx) => next.delete(idx))
                                  else containerIndices.forEach((idx) => next.add(idx))
                                  return next
                                })
                              }}
                            >
                              {allSelected ? 'Deselect All' : 'Select All'}
                            </button>
                          )
                        })()}
                      </div>
                    </div>
                    {(sortedContactsByTeam.get(team) || contacts)
                      .map((c, i) => {
                      const idx = contactIndexMap.get(c) ?? -1
                      return (
                      <div
                        key={`${team}-${c.name}-${i}`}
                        className="file-row directory-contact-row"
                        onClick={() => {
                          if (idx < 0) return
                          openEditContactByIndex(idx)
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            if (idx < 0) return
                            openEditContactByIndex(idx)
                          }
                        }}
                      >
                        <div className="directory-contact-main">
                          <input
                            type="checkbox"
                            checked={directoryBulkSelection.has(idx)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              if (idx < 0) return
                              toggleDirectoryBulkIndex(idx, e.target.checked)
                            }}
                            aria-label="Select contact"
                          />
                          <div className="directory-contact-avatar" aria-hidden="true">
                            {c.name
                              .split(/\s+/)
                              .filter(Boolean)
                              .slice(0, 2)
                              .map((part) => part[0]?.toUpperCase() || '')
                              .join('') || '?'}
                          </div>
                          <div>
                            <div className="directory-name-row">
                              <strong>{c.name}</strong>
                              <button
                                type="button"
                                className={`directory-primary-star ${c.primaryContact ? 'active' : ''}`}
                                title={c.primaryContact ? 'Remove primary contact' : 'Mark as primary contact'}
                                aria-label={c.primaryContact ? 'Remove primary contact' : 'Mark as primary contact'}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (idx < 0) return
                                  togglePrimaryContact(idx)
                                }}
                              >
                                {c.primaryContact ? '★' : '☆'}
                              </button>
                              {c.primaryContact ? <span className="directory-primary-label">Primary</span> : null}
                            </div>
                            <div className="muted small directory-role-text">
                              <span>{c.role}</span>
                            </div>
                          </div>
                        </div>
                        <div className="directory-contact-actions" onClick={(e) => e.stopPropagation()}>
                          {(() => {
                            const isEditing = inlineDirectoryEdit?.idx === idx && inlineDirectoryEdit?.field === 'email'
                            return isEditing ? (
                              <input
                                className="desktop-only desktop-email directory-inline-input"
                                autoFocus
                                value={inlineDirectoryValue}
                                onChange={(e) => setInlineDirectoryValue(e.target.value)}
                                onBlur={saveInlineDirectoryEdit}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveInlineDirectoryEdit()
                                  if (e.key === 'Escape') { setInlineDirectoryEdit(null); setInlineDirectoryValue('') }
                                }}
                              />
                            ) : (
                              <span
                                className="desktop-only desktop-email directory-inline-text"
                                title={c.email ? 'Copy email' : 'Open contact'}
                                onClick={async () => {
                                  if (idx < 0) return
                                  const email = (c.email || '').trim()
                                  if (email) {
                                    const ok = await copyText(email)
                                    showCopyToast(ok ? 'Copied' : 'Copy failed')
                                    return
                                  }
                                  openEditContactByIndex(idx)
                                }}
                              >
                                {c.email || '—'}
                              </span>
                            )
                          })()}
                          <button
                            className="mobile-info-btn mobile-only"
                            title={c.email ? 'Show email' : 'No email listed'}
                            onClick={() => {
                              const email = (c.email || '').trim()
                              setContactInfoTitle('Email')
                              setContactInfoValue(email || 'No email listed')
                              setContactInfoActionLabel(email ? 'Open Email App' : '')
                              setContactInfoActionHref(email ? `mailto:${email}` : '')
                              setShowContactInfo(true)
                            }}
                          >
                            <svg className="info-icon-svg" viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M3 6h18v12H3z" />
                              <path d="M3 7l9 7 9-7" />
                            </svg>
                          </button>

                          {(() => {
                            const office = c.phoneType === 'Office' ? c.phone : c.altPhoneType === 'Office' ? c.altPhone : ''
                            const cell = c.phoneType === 'Cell' ? c.phone : c.altPhoneType === 'Cell' ? c.altPhone : ''
                            const editingOffice = inlineDirectoryEdit?.idx === idx && inlineDirectoryEdit?.field === 'office'
                            const editingCell = inlineDirectoryEdit?.idx === idx && inlineDirectoryEdit?.field === 'cell'
                            return (
                              <span className="desktop-only desktop-phone">
                                <span className="phone-stack">
                                  {editingOffice ? (
                                    <input
                                      className="directory-inline-input"
                                      autoFocus
                                      value={inlineDirectoryValue}
                                      onChange={(e) => setInlineDirectoryValue(formatPhoneInput(e.target.value))}
                                      onBlur={saveInlineDirectoryEdit}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveInlineDirectoryEdit()
                                        if (e.key === 'Escape') { setInlineDirectoryEdit(null); setInlineDirectoryValue('') }
                                      }}
                                    />
                                  ) : office ? (
                                    <span
                                      className="directory-inline-text"
                                      title="Open contact"
                                      onClick={() => {
                                        if (idx < 0) return
                                        openEditContactByIndex(idx)
                                      }}
                                    >
                                      O: {`${formatPhone(office)}${c.officeExtension ? ` x${c.officeExtension}` : ''}`}
                                    </span>
                                  ) : null}
                                  {editingCell ? (
                                    <input
                                      className="directory-inline-input"
                                      autoFocus
                                      value={inlineDirectoryValue}
                                      onChange={(e) => setInlineDirectoryValue(formatPhoneInput(e.target.value))}
                                      onBlur={saveInlineDirectoryEdit}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveInlineDirectoryEdit()
                                        if (e.key === 'Escape') { setInlineDirectoryEdit(null); setInlineDirectoryValue('') }
                                      }}
                                    />
                                  ) : cell ? (
                                    <span
                                      className="directory-inline-text"
                                      title="Open contact"
                                      onClick={() => {
                                        if (idx < 0) return
                                        openEditContactByIndex(idx)
                                      }}
                                    >
                                      C: {formatPhone(cell)}
                                    </span>
                                  ) : null}
                                  {!editingOffice && !editingCell && !office && !cell ? (
                                    <span className="directory-inline-text">—</span>
                                  ) : null}
                                </span>
                              </span>
                            )
                          })()}
                          <button
                            className="mobile-info-btn mobile-only"
                            title="Show phone"
                            onClick={() => {
                              const office = c.phoneType === 'Office' ? c.phone : c.altPhoneType === 'Office' ? c.altPhone : ''
                              const cell = c.phoneType === 'Cell' ? c.phone : c.altPhoneType === 'Cell' ? c.altPhone : ''
                              const officeLine = office ? `O: ${formatPhone(office)}${c.officeExtension ? ` x${c.officeExtension}` : ''}` : ''
                              const phoneText = `${officeLine}${officeLine && cell ? '\n' : ''}${cell ? `C: ${formatPhone(cell)}` : ''}`.trim()
                              const rawPhone = (cell || office || '').trim()
                              const dial = rawPhone ? rawPhone.replace(/[^\d+]/g, '') : ''
                              setContactInfoTitle('Phone')
                              setContactInfoValue(phoneText || 'No phone listed')
                              setContactInfoActionLabel(dial ? 'Call Number' : '')
                              setContactInfoActionHref(dial ? `tel:${dial}` : '')
                              setShowContactInfo(true)
                            }}
                          >
                            <svg className="info-icon-svg" viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M6.6 3.6l3.1-.6 1.5 4.1-2.1 1.7a14 14 0 0 0 6.1 6.1l1.7-2.1 4.1 1.5-.6 3.1a2 2 0 0 1-2 1.6C10.8 19 5 13.2 5 5.6a2 2 0 0 1 1.6-2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )
                    })}
                  </div>
                ))
                })()}
              </div>

              {activeTab === 'consultantList' && showAddConsultantCompany ? (
                <div className="calendar-modal-backdrop" onClick={() => setShowAddConsultantCompany(false)}>
                  <div className="calendar-popover" onClick={(e) => e.stopPropagation()} onKeyDown={handlePopoverSubmitKey} style={{ width: 'min(720px, calc(100vw - 40px))' }}>
                    <h3>New Consultant Company Profile</h3>
                    <div className="site-settings-typography-grid" style={{ marginTop: 10 }}>
                      <label>
                        <span>Consultant Type *</span>
                        <input value={newConsultantType} onChange={(e) => setNewConsultantType(e.target.value)} placeholder="e.g., Structural" />
                      </label>
                      <label>
                        <span>Company Name *</span>
                        <input value={newConsultantCompany} onChange={(e) => setNewConsultantCompany(e.target.value)} placeholder="e.g., Acme Engineering" />
                      </label>
                    </div>
                    <p className="muted" style={{ marginTop: 10 }}>After creating, you can open the company card to add contact info, location, notes, and reviews.</p>
                    <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end', gap: 8 }}>
                      <button onClick={() => setShowAddConsultantCompany(false)}>Cancel</button>
                      <button
                        className="directory-primary-btn"
                        onClick={() => {
                          const type = newConsultantType.trim()
                          const company = newConsultantCompany.trim()
                          if (!type || !company) {
                            showActionToast('Consultant type and company name are required')
                            return
                          }
                          setCustomConsultantCompanies((prev) => {
                            const exists = prev.some((x) => x.type.toLowerCase() === type.toLowerCase() && x.company.toLowerCase() === company.toLowerCase())
                            return exists ? prev : [...prev, { type, company }]
                          })
                          setShowAddConsultantCompany(false)
                          setSelectedConsultantCompany({ type, company })
                        }}
                      >
                        Create Profile
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {activeTab === 'consultantList' && selectedConsultantCompany && (() => {
                const companyKey = `${selectedConsultantCompany.type}::${selectedConsultantCompany.company}`
                const projects = consultantProjectsByCompany[companyKey] || []
                const reviews = consultantCompanyReviews[companyKey] || []
                const rating = reviews.length ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length) : 0
                const photo = consultantCompanyPhotos[companyKey] || ''
                const details = consultantCompanyDetails[companyKey] || {
                  location: '',
                  yearsWorkingTogether: '',
                  primaryContactName: '',
                  primaryContactEmail: '',
                  primaryContactPhone: '',
                }

                return (
                  <div className="calendar-modal-backdrop" onClick={() => setSelectedConsultantCompany(null)}>
                    <div className="calendar-popover consultant-company-popover side-detail-panel" onClick={(e) => e.stopPropagation()} onKeyDown={handlePopoverSubmitKey} style={{ width: 'min(720px, calc(100vw - 40px))', position: 'relative', background: 'var(--swan-wing)' }}>
                      <div className="row" style={{ alignItems: 'flex-start', marginTop: 6 }}>
                        <div style={{ width: 120, flex: '0 0 120px' }}>
                          {photo ? (
                            <img src={photo} alt={`${selectedConsultantCompany.company} profile`} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--line)' }} />
                          ) : (
                            <div style={{ width: 120, height: 120, borderRadius: 8, border: '1px dashed var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="muted small">No photo</div>
                          )}
                          <label style={{ marginTop: 8, width: '100%', display: 'inline-block' }}>
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const f = e.target.files?.[0]
                                if (!f) return
                                const fr = new FileReader()
                                fr.onload = () => {
                                  const data = typeof fr.result === 'string' ? fr.result : ''
                                  if (!data) return
                                  setConsultantCompanyPhotos((prev) => ({ ...prev, [companyKey]: data }))
                                }
                                fr.readAsDataURL(f)
                              }}
                            />
                            <span style={{ display: 'inline-block', width: '100%', fontSize: 11, textAlign: 'center', padding: '6px 8px', border: '1px solid var(--line)', borderRadius: 6, cursor: 'pointer' }}>Upload Photo</span>
                          </label>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                            <div style={{ fontSize: 25, fontWeight: 700, lineHeight: 1.1, textAlign: 'left' }}>{selectedConsultantCompany.company}</div>
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                              {[1, 2, 3, 4, 5].map((star) => (
                                <span key={`${companyKey}-modal-top-${star}`} style={{ color: 'var(--quicksand)', fontSize: '1.5em', lineHeight: 1 }}>
                                  {star <= Math.round(rating) ? '★' : '☆'}
                                </span>
                              ))}
                              <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>{rating ? `${rating.toFixed(1)} (${reviews.length})` : 'No reviews'}</span>
                            </div>
                          </div>
                          <div className="muted" style={{ marginBottom: 8, fontSize: 15, lineHeight: 1.1, textAlign: 'left' }}>{selectedConsultantCompany.type}</div>

                          <div style={{ marginBottom: 8, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, alignItems: 'start' }}>
                            <div style={{ display: 'grid', gridTemplateRows: 'auto auto', alignContent: 'start' }}>
                              <strong><span style={{ color: 'var(--quicksand)', marginRight: 6 }}>▣</span>Projects</strong>
                              <div style={{ marginTop: 6 }}>
                                <ul style={{ margin: '0 0 0 18px' }}>
                                  {projects.length ? projects.map((project) => <li key={project}>{project}</li>) : <li className="muted">No projects listed</li>}
                                </ul>
                              </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateRows: 'auto auto', alignContent: 'start' }}>
                              <strong><span style={{ color: 'var(--quicksand)', marginRight: 6 }}>⌖</span>Office Location</strong>
                              <input
                                placeholder="Location"
                                value={details.location}
                                onChange={(e) => setConsultantCompanyDetails((prev) => ({
                                  ...prev,
                                  [companyKey]: { ...details, location: e.target.value },
                                }))}
                                style={{ width: 'calc(100% - 18px)', marginTop: 6, marginLeft: 18, border: 'none', outline: 'none', background: 'transparent' }}
                              />
                            </div>
                            <div style={{ display: 'grid', gridTemplateRows: 'auto auto', alignContent: 'start' }}>
                              <strong style={{ whiteSpace: 'nowrap' }}><span style={{ color: 'var(--quicksand)', marginRight: 6 }}>◔</span>Years Work Together</strong>
                              <input
                                placeholder="Years Work Together"
                                value={details.yearsWorkingTogether}
                                onChange={(e) => setConsultantCompanyDetails((prev) => ({
                                  ...prev,
                                  [companyKey]: { ...details, yearsWorkingTogether: e.target.value },
                                }))}
                                style={{ width: 'calc(100% - 18px)', marginTop: 6, marginLeft: 18, border: 'none', outline: 'none', background: 'transparent' }}
                              />
                            </div>
                          </div>

                          <div style={{ marginBottom: 8 }}>
                            <strong>Primary Contact</strong>
                            <div style={{ marginTop: 6, marginLeft: 14, display: 'grid', gap: 6 }}>
                              <input
                                placeholder="Name"
                                value={details.primaryContactName}
                                onChange={(e) => setConsultantCompanyDetails((prev) => ({
                                  ...prev,
                                  [companyKey]: { ...details, primaryContactName: e.target.value },
                                }))}
                                style={{ border: 'none', outline: 'none', background: 'transparent' }}
                              />
                              <input
                                placeholder="Email"
                                value={details.primaryContactEmail}
                                onChange={(e) => setConsultantCompanyDetails((prev) => ({
                                  ...prev,
                                  [companyKey]: { ...details, primaryContactEmail: e.target.value },
                                }))}
                                style={{ border: 'none', outline: 'none', background: 'transparent' }}
                              />
                              <input
                                placeholder="Phone Number"
                                value={details.primaryContactPhone}
                                onChange={(e) => setConsultantCompanyDetails((prev) => ({
                                  ...prev,
                                  [companyKey]: { ...details, primaryContactPhone: e.target.value },
                                }))}
                                style={{ border: 'none', outline: 'none', background: 'transparent' }}
                              />
                            </div>
                          </div>

                          <div style={{ marginTop: 10 }}>
                            <strong>Reviews</strong>
                            <div style={{ marginTop: 8, border: '1px solid var(--line)', borderRadius: 8, padding: 10, background: 'var(--royal-blue)' }}>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <button
                                    key={`${companyKey}-draft-star-${star}`}
                                    type="button"
                                    className="contact-edit-btn"
                                    style={{ color: 'var(--quicksand)', borderColor: 'var(--quicksand)', borderWidth: 1, fontSize: '1.2em', lineHeight: 1 }}
                                    onClick={() => setConsultantReviewDraftRating(star)}
                                  >
                                    {star <= consultantReviewDraftRating ? '★' : '☆'}
                                  </button>
                                ))}
                              </div>
                              <textarea
                                value={consultantReviewDraftNotes}
                                onChange={(e) => setConsultantReviewDraftNotes(e.target.value)}
                                placeholder="Leave a review note"
                                style={{ width: '100%', minHeight: 80, border: 'none', outline: 'none', background: 'transparent', borderRadius: 0 }}
                              />
                              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, gap: 8 }}>
                                {editingConsultantReviewId ? <button onClick={() => { setEditingConsultantReviewId(null); setConsultantReviewDraftNotes(''); setConsultantReviewDraftRating(0) }}>Cancel</button> : null}
                                <button
                                  style={{ background: 'var(--quicksand)', borderColor: 'var(--quicksand)', color: 'var(--royal-blue)' }}
                                  onClick={() => {
                                    if (!consultantReviewDraftRating) {
                                      showActionToast('Star rating is required')
                                      return
                                    }
                                    const writerName = (currentUsername || 'Unknown User').trim()
                                    const nextNow = new Date().toISOString()
                                    setConsultantCompanyReviews((prev) => {
                                      const list = prev[companyKey] || []
                                      if (editingConsultantReviewId) {
                                        return {
                                          ...prev,
                                          [companyKey]: list.map((r) => r.id === editingConsultantReviewId ? {
                                            ...r,
                                            rating: consultantReviewDraftRating,
                                            notes: consultantReviewDraftNotes.trim(),
                                            updatedAt: nextNow,
                                          } : r),
                                        }
                                      }
                                      const nextReview: ConsultantReview = {
                                        id: makeId(),
                                        authorName: writerName,
                                        authorUsername: currentUsername || writerName,
                                        rating: consultantReviewDraftRating,
                                        notes: consultantReviewDraftNotes.trim(),
                                        createdAt: nextNow,
                                        updatedAt: nextNow,
                                      }
                                      return { ...prev, [companyKey]: [nextReview, ...list] }
                                    })
                                    setEditingConsultantReviewId(null)
                                    setConsultantReviewDraftNotes('')
                                    setConsultantReviewDraftRating(0)
                                    showCenterToast('Saved')
                                  }}
                                >
                                  {editingConsultantReviewId ? 'Update Review' : 'Post Review'}
                                </button>
                              </div>
                            </div>

                            <div style={{ display: 'grid', gap: 8, marginTop: 8, maxHeight: 260, overflowY: 'auto', paddingRight: 4 }}>
                              {reviews.length === 0 ? <div className="muted">No reviews yet</div> : reviews.map((review) => {
                                const canModifyReview = review.authorUsername === currentUsername || effectiveRole === 'Executive'
                                return (
                                  <div key={review.id} style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 8 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <strong style={{ fontSize: 13 }}>{review.authorName}</strong>
                                        <span className="muted" style={{ fontSize: 11 }}>{new Date(review.createdAt).toLocaleDateString()}</span>
                                      </div>
                                      <div style={{ color: 'var(--quicksand)' }}>{'★'.repeat(Math.max(0, Math.min(5, review.rating)))}{'☆'.repeat(5 - Math.max(0, Math.min(5, review.rating)))}</div>
                                    </div>
                                    <div
                                      style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}
                                      contentEditable={canModifyReview}
                                      suppressContentEditableWarning
                                      onBlur={(e) => {
                                        if (!canModifyReview) return
                                        const nextNotes = (e.currentTarget.textContent || '').trim()
                                        if (nextNotes === (review.notes || '')) return
                                        setConsultantCompanyReviews((prev) => ({
                                          ...prev,
                                          [companyKey]: (prev[companyKey] || []).map((r) => r.id === review.id ? { ...r, notes: nextNotes, updatedAt: now() } : r),
                                        }))
                                        showCenterToast('Saved')
                                      }}
                                      title={canModifyReview ? 'Click to edit review text' : undefined}
                                    >
                                      {review.notes || 'No notes'}
                                    </div>
                                    {canModifyReview ? (
                                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                                        <button
                                          className="consultant-review-delete-btn"
                                          title="Delete review"
                                          aria-label="Delete review"
                                          onClick={() => {
                                            setConsultantCompanyReviews((prev) => ({
                                              ...prev,
                                              [companyKey]: (prev[companyKey] || []).filter((r) => r.id !== review.id),
                                            }))
                                            showCenterToast('Deleted')
                                          }}
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    ) : null}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="row" style={{ marginTop: 10, justifyContent: 'flex-end', gap: 8 }}>
                        <button onClick={() => setSelectedConsultantCompany(null)}>Close</button>
                        <button
                          className="danger"
                          onClick={() => {
                            const deleted = selectedConsultantCompany
                            if (!deleted) return
                            const deletedKey = `${deleted.type}::${deleted.company}`
                            setHiddenConsultantCompanies((prev) => ({ ...prev, [deletedKey]: true }))
                            setSelectedConsultantCompany(null)
                            setActionToast({
                              message: '',
                              undo: () => {
                                setHiddenConsultantCompanies((prev) => {
                                  const next = { ...prev }
                                  delete next[deletedKey]
                                  return next
                                })
                              },
                            })
                            window.setTimeout(() => setActionToast(null), 3000)
                          }}
                        >
                          Delete
                        </button>
                        <button
                          style={{ background: 'var(--quicksand)', borderColor: 'var(--quicksand)', color: 'var(--royal-blue)' }}
                          onClick={() => {
                            showCenterToast('Saved')
                          }}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })()}

              {showContactInfo && (
                <div className="calendar-modal-backdrop" onClick={() => setShowContactInfo(false)}>
                  <div
                    className="calendar-popover meeting-attendee-popover"
                    onClick={(e) => e.stopPropagation()} onKeyDown={handlePopoverSubmitKey}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    <h3>{contactInfoTitle}</h3>
                    <div className="file-row" style={{ whiteSpace: 'pre-line' }}>{contactInfoValue || '-'}</div>
                    <div className="row contact-action-row" style={{ marginTop: 8 }}>
                      {contactInfoActionHref ? (
                        <a
                          className="contact-action-btn"
                          href={contactInfoActionHref}
                          onClick={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                        >
                          {contactInfoActionLabel}
                        </a>
                      ) : null}
                      <button
                        type="button"
                        className="contact-action-btn contact-action-copy"
                        onClick={async (e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          const ok = await copyText(contactInfoValue || '')
                          showCopyToast(ok ? 'Copied' : 'Copy failed')
                        }}
                        onTouchStart={(e) => e.stopPropagation()}
                      >
                        Copy
                      </button>
                      <button
                        type="button"
                        className="contact-action-btn"
                        onClick={(e) => {
                          e.stopPropagation()
                          setShowContactInfo(false)
                        }}
                        onTouchStart={(e) => e.stopPropagation()}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'directory' && showAddContact && (
                <div className="calendar-modal-backdrop" onClick={() => { setAddContactAttempted(false); setShowAddContact(false) }}>
                  <div className="calendar-popover directory-contact-popover side-detail-panel" onClick={(e) => e.stopPropagation()} onKeyDown={handlePopoverSubmitKey}>
                    <h3>Add Contact</h3>
                    <div className="row directory-phone-row">
                      <label className="directory-popup-field">
                        <span>Company *</span>
                        <input className={addContactAttempted && !newContactCompany.trim() ? 'required-missing' : ''} placeholder="Company *" value={newContactCompany} onChange={(e) => setNewContactCompany(e.target.value)} />
                      </label>
                      <label className="directory-popup-field">
                        <span>Abbrev.</span>
                        <input placeholder="Abbrev." value={newContactCompanyAbbreviation} onChange={(e) => setNewContactCompanyAbbreviation(e.target.value)} />
                      </label>
                      <label className="directory-popup-field">
                        <span>Consultant Type *</span>
                        <input className={addContactAttempted && !newContactTeam.trim() ? 'required-missing' : ''} placeholder="Consultant Type *" value={newContactTeam} onChange={(e) => setNewContactTeam(e.target.value)} />
                      </label>
                    </div>
                    <div className="row">
                      <label className="directory-popup-field">
                        <span>Name *</span>
                        <input className={addContactAttempted && !newContactName.trim() ? 'required-missing' : ''} placeholder="Name *" value={newContactName} onChange={(e) => setNewContactName(e.target.value)} />
                      </label>
                      <label className="directory-popup-field">
                        <span>Role</span>
                        <input placeholder="Role" value={newContactRole} onChange={(e) => setNewContactRole(e.target.value)} />
                      </label>
                    </div>
                    <div className="row">
                      <label className="directory-popup-field">
                        <span>Email</span>
                        <input className="directory-email-input" style={{ fontSize: 15 }} placeholder="Email" value={newContactEmail} onChange={(e) => setNewContactEmail(e.target.value)} />
                      </label>
                    </div>
                    <div className="row directory-phone-row">
                      <label className="directory-popup-field">
                        <span>Office</span>
                        <input placeholder="Office" value={newContactAltPhone} onChange={(e) => setNewContactAltPhone(formatPhoneInput(e.target.value))} />
                      </label>
                      <label className="directory-popup-field">
                        <span>EXT</span>
                        <input placeholder="EXT" value={newContactOfficeExtension} onChange={(e) => setNewContactOfficeExtension(e.target.value.replace(/\D/g, ''))} />
                      </label>
                      <label className="directory-popup-field">
                        <span>Cell</span>
                        <input placeholder="Cell" value={newContactPhone} onChange={(e) => setNewContactPhone(formatPhoneInput(e.target.value))} />
                      </label>
                    </div>
                    <div className="row">
                      <label className="directory-popup-field">
                        <span>Notes</span>
                        <input placeholder="Notes" value={newContactNotes} onChange={(e) => setNewContactNotes(e.target.value)} />
                      </label>
                    </div>
                    <div className="row directory-popover-actions">
                      <button onClick={() => { setAddContactAttempted(false); setShowAddContact(false) }}>Cancel</button>
                      <button
                        className="directory-primary-btn"
                        onClick={() => {
                          setAddContactAttempted(true)
                          const name = newContactName.trim()
                          const role = newContactRole.trim() || 'Role'
                          const team = newContactTeam.trim()
                          const company = newContactCompany.trim()
                          const companyAbbreviation = newContactCompanyAbbreviation.trim()
                          const phone = newContactPhone.trim()
                          const altPhone = newContactAltPhone.trim()
                          const officeExtension = newContactOfficeExtension.trim()
                          const notes = newContactNotes.trim()
                          const email = newContactEmail.trim()
                          const missingRequired: string[] = []
                          if (!team) missingRequired.push('Consultant Type')
                          if (!company) missingRequired.push('Company')
                          if (!name) missingRequired.push('Name')
                          if (missingRequired.length > 0) {
                            showActionToast(`${missingRequired.join(', ')} ${missingRequired.length === 1 ? 'is' : 'are'} required`)
                            return
                          }
                          setDirectoryContacts((prev) => [{
                            name,
                            role,
                            team,
                            company,
                            companyAbbreviation,
                            phone,
                            phoneType: 'Cell',
                            altPhone,
                            altPhoneType: 'Office',
                            officeExtension,
                            notes,
                            email,
                          }, ...prev])
                          setNewContactName('')
                          setNewContactRole('')
                          setNewContactTeam('')
                          setNewContactCompany('')
                          setNewContactCompanyAbbreviation('')
                          setNewContactPhone('')
                          setNewContactPhoneType('Cell')
                          setNewContactAltPhone('')
                          setNewContactAltPhoneType('Office')
                          setNewContactOfficeExtension('')
                          setNewContactNotes('')
                          setNewContactEmail('')
                          setAddContactAttempted(false)
                          setShowAddContact(false)
                        }}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {showEditContact && editContactIndex !== null && (
                <div className="calendar-modal-backdrop" onClick={() => setShowEditContact(false)}>
                  <div className="calendar-popover directory-contact-popover side-detail-panel" onClick={(e) => e.stopPropagation()} onKeyDown={handlePopoverSubmitKey}>
                    <h3>Edit Contact</h3>
                    <div className="row directory-phone-row">
                      <label className="directory-popup-field">
                        <span>Company</span>
                        <input placeholder="Company" value={newContactCompany} onChange={(e) => setNewContactCompany(e.target.value)} />
                      </label>
                      <label className="directory-popup-field">
                        <span>Abbrev.</span>
                        <input placeholder="Abbrev." value={newContactCompanyAbbreviation} onChange={(e) => setNewContactCompanyAbbreviation(e.target.value)} />
                      </label>
                      <label className="directory-popup-field">
                        <span>Consultant Type</span>
                        <input placeholder="Consultant Type" value={newContactTeam} onChange={(e) => setNewContactTeam(e.target.value)} />
                      </label>
                    </div>
                    <div className="row">
                      <label className="directory-popup-field">
                        <span>Name *</span>
                        <input placeholder="Name *" value={newContactName} onChange={(e) => setNewContactName(e.target.value)} />
                      </label>
                      <label className="directory-popup-field">
                        <span>Role</span>
                        <input placeholder="Role" value={newContactRole} onChange={(e) => setNewContactRole(e.target.value)} />
                      </label>
                    </div>
                    <div className="row">
                      <label className="directory-popup-field">
                        <span>Email</span>
                        <input className="directory-email-input" style={{ fontSize: 15 }} placeholder="Email" value={newContactEmail} onChange={(e) => setNewContactEmail(e.target.value)} />
                      </label>
                    </div>
                    <div className="row directory-phone-row">
                      <label className="directory-popup-field">
                        <span>Office</span>
                        <input placeholder="Office" value={newContactAltPhone} onChange={(e) => setNewContactAltPhone(formatPhoneInput(e.target.value))} />
                      </label>
                      <label className="directory-popup-field">
                        <span>EXT</span>
                        <input placeholder="EXT" value={newContactOfficeExtension} onChange={(e) => setNewContactOfficeExtension(e.target.value.replace(/\D/g, ''))} />
                      </label>
                      <label className="directory-popup-field">
                        <span>Cell</span>
                        <input placeholder="Cell" value={newContactPhone} onChange={(e) => setNewContactPhone(formatPhoneInput(e.target.value))} />
                      </label>
                    </div>
                    <div className="row">
                      <label className="directory-popup-field">
                        <span>Notes</span>
                        <input placeholder="Notes" value={newContactNotes} onChange={(e) => setNewContactNotes(e.target.value)} />
                      </label>
                    </div>
                    <div className="row directory-popover-actions">
                      <button
                        className="danger"
                        onClick={() => {
                          if (editContactIndex === null) return
                          const current = directoryContacts[editContactIndex]
                          if (!current) return

                          if (current.archived) {
                            setDirectoryContacts((prev) => prev.map((c, i) => (i === editContactIndex ? { ...c, archived: false } : c)))
                            showActionToast('Contact restored from Archived')
                          } else {
                            setDirectoryContacts((prev) => prev.map((c, i) => (i === editContactIndex ? { ...c, archived: true } : c)))
                            showActionToast('Contact archived', () => {
                              setDirectoryContacts((prev) => prev.map((c, i) => (i === editContactIndex ? { ...c, archived: false } : c)))
                            })
                          }

                          setShowEditContact(false)
                          setEditContactIndex(null)
                        }}
                      >
                        {editContactIndex !== null && directoryContacts[editContactIndex]?.archived ? 'Restore' : 'Archive'}
                      </button>
                      <button onClick={() => setShowEditContact(false)}>Cancel</button>
                      <button
                        className="directory-primary-btn"
                        onClick={() => {
                          if (editContactIndex === null) return
                          const name = newContactName.trim()
                          const role = newContactRole.trim() || 'Role'
                          const team = newContactTeam.trim()
                          const company = newContactCompany.trim()
                          const companyAbbreviation = newContactCompanyAbbreviation.trim()
                          const phone = newContactPhone.trim()
                          const altPhone = newContactAltPhone.trim()
                          const officeExtension = newContactOfficeExtension.trim()
                          const notes = newContactNotes.trim()
                          const email = newContactEmail.trim()
                          if (!team) {
                            showActionToast('Consultant Type is required')
                            return
                          }
                          if (!name) {
                            showActionToast('Name is required')
                            return
                          }
                          setDirectoryContacts((prev) => prev.map((x, i) => i === editContactIndex ? {
                            name,
                            role,
                            team,
                            company,
                            companyAbbreviation,
                            phone,
                            phoneType: 'Cell',
                            altPhone,
                            altPhoneType: 'Office',
                            officeExtension,
                            notes,
                            email,
                          } : x))
                          showCenterToast('Saved')
                          setShowEditContact(false)
                          setEditContactIndex(null)
                        }}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}
          {isAdminRole && showSiteSettings ? (
            <div className="calendar-modal-backdrop" onClick={() => setShowSiteSettings(false)}>
              <div className="calendar-popover site-settings-popover" onClick={(e) => e.stopPropagation()} onKeyDown={handlePopoverSubmitKey}>
                <h3>Website Settings</h3>
                <div className="site-settings-theme-row">
                  <label className="site-color-item">
                    <span>Main Background</span>
                    <small>Main page and panel background</small>
                    <input type="color" value={siteSettings.theme.royalBlue} onChange={(e) => setSiteSettings((prev) => ({ ...prev, theme: { ...prev.theme, royalBlue: e.target.value } }))} />
                  </label>
                  <label className="site-color-item">
                    <span>Container Background</span>
                    <small>Cards, chips, soft fills</small>
                    <input type="color" value={siteSettings.theme.swanWing} onChange={(e) => setSiteSettings((prev) => ({ ...prev, theme: { ...prev.theme, swanWing: e.target.value } }))} />
                  </label>
                  <label className="site-color-item">
                    <span>Primary Accent</span>
                    <small>Selected states, active nav, buttons, positive signals</small>
                    <input type="color" value={siteSettings.theme.primaryAccent || siteSettings.theme.quicksand} onChange={(e) => setSiteSettings((prev) => ({ ...prev, theme: { ...prev.theme, primaryAccent: e.target.value } }))} />
                  </label>
                  <label className="site-color-item">
                    <span>Secondary Accent</span>
                    <small>Charts, secondary emphasis, neutral info states</small>
                    <input type="color" value={siteSettings.theme.secondaryAccent} onChange={(e) => setSiteSettings((prev) => ({ ...prev, theme: { ...prev.theme, secondaryAccent: e.target.value } }))} />
                  </label>
                  <label className="site-color-item">
                    <span>Accent Color</span>
                    <small>Use sparingly in charts, tags, subtle surfaces</small>
                    <input type="color" value={siteSettings.theme.quicksand} onChange={(e) => setSiteSettings((prev) => ({ ...prev, theme: { ...prev.theme, quicksand: e.target.value } }))} />
                  </label>
                  <label className="site-color-item">
                    <span>Gray Accents</span>
                    <small>Borders, rules, secondary text</small>
                    <input type="color" value={siteSettings.theme.line} onChange={(e) => setSiteSettings((prev) => ({ ...prev, theme: { ...prev.theme, line: e.target.value } }))} />
                  </label>
                  <label className="site-color-item">
                    <span>Main Text</span>
                    <small>Primary paragraph text color</small>
                    <input type="color" value={siteSettings.theme.bodyText} onChange={(e) => setSiteSettings((prev) => ({ ...prev, theme: { ...prev.theme, bodyText: e.target.value } }))} />
                  </label>
                  <label className="site-color-item">
                    <span>Secondary Text</span>
                    <small>Muted labels/help text</small>
                    <input type="color" value={siteSettings.theme.textSoft} onChange={(e) => setSiteSettings((prev) => ({ ...prev, theme: { ...prev.theme, textSoft: e.target.value } }))} />
                  </label>
                </div>
                <h4 style={{ margin: '12px 0 6px' }}>Status Colors</h4>
                <div className="site-settings-theme-row">
                  <label className="site-color-item">
                    <span>Success / On Track — Text & Icon</span>
                    <small>Foreground for success states</small>
                    <input type="color" value={siteSettings.theme.statusSuccessFg || siteSettings.theme.statusSuccess} onChange={(e) => setSiteSettings((prev) => ({ ...prev, theme: { ...prev.theme, statusSuccess: e.target.value, statusSuccessFg: e.target.value } }))} />
                  </label>
                  <label className="site-color-item">
                    <span>Success / On Track — Background</span>
                    <small>Background for success states</small>
                    <input type="color" value={siteSettings.theme.statusSuccessBg || '#E8F1EB'} onChange={(e) => setSiteSettings((prev) => ({ ...prev, theme: { ...prev.theme, statusSuccessBg: e.target.value } }))} />
                  </label>
                  <label className="site-color-item">
                    <span>Warning / At Risk — Text & Icon</span>
                    <small>Foreground for warning states</small>
                    <input type="color" value={siteSettings.theme.statusWarningFg || siteSettings.theme.statusWarning} onChange={(e) => setSiteSettings((prev) => ({ ...prev, theme: { ...prev.theme, statusWarning: e.target.value, statusWarningFg: e.target.value } }))} />
                  </label>
                  <label className="site-color-item">
                    <span>Warning / At Risk — Background</span>
                    <small>Background for warning states</small>
                    <input type="color" value={siteSettings.theme.statusWarningBg || '#F7EEDF'} onChange={(e) => setSiteSettings((prev) => ({ ...prev, theme: { ...prev.theme, statusWarningBg: e.target.value } }))} />
                  </label>
                  <label className="site-color-item">
                    <span>Danger / Overloaded / Late — Text & Icon</span>
                    <small>Foreground for danger states</small>
                    <input type="color" value={siteSettings.theme.statusDangerFg || siteSettings.theme.statusDanger} onChange={(e) => setSiteSettings((prev) => ({ ...prev, theme: { ...prev.theme, statusDanger: e.target.value, statusDangerFg: e.target.value } }))} />
                  </label>
                  <label className="site-color-item">
                    <span>Danger / Overloaded / Late — Background</span>
                    <small>Background for danger states</small>
                    <input type="color" value={siteSettings.theme.statusDangerBg || '#F6E8E5'} onChange={(e) => setSiteSettings((prev) => ({ ...prev, theme: { ...prev.theme, statusDangerBg: e.target.value } }))} />
                  </label>
                  <label className="site-color-item">
                    <span>Info / Neutral — Text & Icon</span>
                    <small>Foreground for neutral states</small>
                    <input type="color" value={siteSettings.theme.statusInfoFg || siteSettings.theme.statusInfo} onChange={(e) => setSiteSettings((prev) => ({ ...prev, theme: { ...prev.theme, statusInfo: e.target.value, statusInfoFg: e.target.value } }))} />
                  </label>
                  <label className="site-color-item">
                    <span>Info / Neutral — Background</span>
                    <small>Background for neutral states</small>
                    <input type="color" value={siteSettings.theme.statusInfoBg || '#EAF0F4'} onChange={(e) => setSiteSettings((prev) => ({ ...prev, theme: { ...prev.theme, statusInfoBg: e.target.value } }))} />
                  </label>
                </div>
                <div className="site-settings-typography-grid" style={{ marginTop: 10 }}>
                  <label>
                    <span>Company Logo</span>
                    <div className="company-logo-upload-wrap">
                      {companyLogo ? (
                        <button type="button" className="company-image-preview-btn" onClick={() => openImageCropModal('companyLogo', companyLogoOriginal)}>
                          <img src={companyLogoOriginal} alt="Company logo" className="company-logo-upload-preview" />
                        </button>
                      ) : <div className="muted">No logo uploaded</div>}
                      {isAdminRole || effectiveRole === 'Executive' ? (
                        <>
                          <button type="button" onClick={() => companyLogoUploadRef.current?.click()}>Upload Logo</button>
                          <input
                            ref={companyLogoUploadRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files?.[0]
                              if (!file) return
                              const reader = new FileReader()
                              reader.onload = () => {
                                const result = typeof reader.result === 'string' ? reader.result : ''
                                if (!result) return
                                setSiteSettings((prev) => ({ ...prev, companyLogo: result, companyLogoCropped: '' }));
                                                setSavedDefaultSiteSettings((prev) => ({ ...prev, companyLogo: result, companyLogoCropped: '' }))
                                showCenterToast('Saved')
                              }
                              reader.readAsDataURL(file)
                              e.currentTarget.value = ''
                            }}
                          />
                        </>
                      ) : null}
                    </div>
                  </label>
                  <label>
                    <span>Company Icon</span>
                    <div className="company-logo-upload-wrap">
                      {companyIcon ? (
                        <button type="button" className="company-image-preview-btn" onClick={() => openImageCropModal('companyIcon', companyIconOriginal)}>
                          <img src={companyIconOriginal} alt="Company icon" className="company-logo-upload-preview" />
                        </button>
                      ) : <div className="muted">No icon uploaded</div>}
                      {isAdminRole || effectiveRole === 'Executive' ? (
                        <>
                          <button type="button" onClick={() => companyIconUploadRef.current?.click()}>Upload Icon</button>
                          <input
                            ref={companyIconUploadRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files?.[0]
                              if (!file) return
                              const reader = new FileReader()
                              reader.onload = () => {
                                const result = typeof reader.result === 'string' ? reader.result : ''
                                if (!result) return
                                setSiteSettings((prev) => ({ ...prev, companyIcon: result, companyIconCropped: '' }));
                                                setSavedDefaultSiteSettings((prev) => ({ ...prev, companyIcon: result, companyIconCropped: '' }))
                                showCenterToast('Saved')
                              }
                              reader.readAsDataURL(file)
                              e.currentTarget.value = ''
                            }}
                          />
                        </>
                      ) : null}
                    </div>
                  </label>
                </div>
                <div className="site-settings-typography-grid">
                  <label>
                    <span>Primary UI Font</span>
                    <select
                      value={extractPrimaryFamily(siteSettings.typography.uiFont)}
                      onChange={(e) => setSiteSettings((prev) => ({ ...prev, typography: { ...prev.typography, uiFont: `${e.target.value}, system-ui, sans-serif` } }))}
                    >
                      {Array.from(new Set([
                        extractPrimaryFamily(siteSettings.typography.uiFont),
                        ...localFontFamilies,
                        'Inter',
                        'Arial',
                        'Segoe UI',
                        'Helvetica Neue',
                        'Georgia',
                      ])).filter(Boolean).map((font) => (
                        <option key={`ui-font-${font}`} value={font}>{font}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Technical / IDs Font</span>
                    <select
                      value={extractPrimaryFamily(siteSettings.typography.technicalFont)}
                      onChange={(e) => setSiteSettings((prev) => ({ ...prev, typography: { ...prev.typography, technicalFont: `${e.target.value}, monospace` } }))}
                    >
                      {Array.from(new Set([
                        extractPrimaryFamily(siteSettings.typography.technicalFont),
                        ...localFontFamilies,
                        'IBM Plex Mono',
                        'Courier New',
                        'Consolas',
                        'Lucida Console',
                      ])).filter(Boolean).map((font) => (
                        <option key={`tech-font-${font}`} value={font}>{font}</option>
                      ))}
                    </select>
                  </label>
                  <label><span>Page Title (28–32px)</span><input type="number" min={28} max={32} value={siteSettings.typography.pageTitle} onChange={(e) => setSiteSettings((prev) => ({ ...prev, typography: { ...prev.typography, pageTitle: Number(e.target.value) || 32 } }))} /></label>
                  <label><span>Section Title (20–24px)</span><input type="number" min={20} max={24} value={siteSettings.typography.sectionTitle} onChange={(e) => setSiteSettings((prev) => ({ ...prev, typography: { ...prev.typography, sectionTitle: Number(e.target.value) || 22 } }))} /></label>
                  <label><span>Card Title (18px)</span><input type="number" min={16} max={22} value={siteSettings.typography.cardTitle} onChange={(e) => setSiteSettings((prev) => ({ ...prev, typography: { ...prev.typography, cardTitle: Number(e.target.value) || 18 } }))} /></label>
                  <label><span>Body Text (14–16px)</span><input type="number" min={14} max={16} value={siteSettings.typography.bodyText} onChange={(e) => setSiteSettings((prev) => ({ ...prev, typography: { ...prev.typography, bodyText: Number(e.target.value) || 15 } }))} /></label>
                  <label><span>Tables (13–14px)</span><input type="number" min={13} max={14} value={siteSettings.typography.tableText} onChange={(e) => setSiteSettings((prev) => ({ ...prev, typography: { ...prev.typography, tableText: Number(e.target.value) || 13 } }))} /></label>
                  <label><span>Labels (12px)</span><input type="number" min={11} max={14} value={siteSettings.typography.labels} onChange={(e) => setSiteSettings((prev) => ({ ...prev, typography: { ...prev.typography, labels: Number(e.target.value) || 12 } }))} /></label>
                </div>

                <div className="row site-settings-footer-actions">
                  <button
                    onClick={async () => {
                      setSavedDefaultSiteSettings(siteSettings)
                      setHasSavedDefaultSiteSettings(true)
                      try {
                        const res = await apiFetch(`${apiBase}/api/site-settings`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            settings: {
                              schemaVersion: 3,
                              theme: siteSettings.theme,
                              typography: siteSettings.typography,
                            },
                          }),
                        })
                        if (!res.ok) throw new Error('save failed')
                        showCenterToast('Saved')
                      } catch {
                        showCenterToast('Save failed — check login/session and retry')
                      }
                    }}
                  >
                    Save Current as Default
                  </button>
                  <button onClick={() => setSiteSettings(savedDefaultSiteSettings)}>Reset to Saved Default</button>
                  <button className="site-settings-done-btn" onClick={() => setShowSiteSettings(false)}>Done</button>
                </div>
              </div>
            </div>
          ) : null}
          {showLogoutConfirm ? (
            <div className="calendar-modal-backdrop" onClick={() => setShowLogoutConfirm(false)}>
              <div className="calendar-popover site-settings-popover logout-confirm-popover" onClick={(e) => e.stopPropagation()}>
                <h3>Confirm Logout</h3>
                <p className="muted">Do you want to log out?</p>
                <div className="row" style={{ justifyContent: 'flex-end' }}>
                  <button onClick={() => setShowLogoutConfirm(false)}>Cancel</button>
                  <button
                    className="modal-save-btn"
                    onClick={() => {
                      setShowLogoutConfirm(false)
                      clearAuth()
                      setShowProfileSettings(false)
                      setProfileSettingsTab(null)
                      navigateTo('login')
                    }}
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          ) : null}
          {showChangePasswordModal ? (
            <div className="calendar-modal-backdrop" onClick={() => { if (!mustChangePassword) setShowChangePasswordModal(false) }}>
              <div className="calendar-popover site-settings-popover" onClick={(e) => e.stopPropagation()}>
                <h3>Change Password</h3>
                {mustChangePassword ? <p className="muted" style={{ color: '#b13a3a' }}>Password update required before continuing.</p> : null}
                <div className="site-settings-typography-grid">
                  <label><span>Current Password</span><input type="password" value={currentPasswordInput} onChange={(e) => setCurrentPasswordInput(e.target.value)} /></label>
                  <label><span>New Password</span><input type="password" value={newPasswordInput} onChange={(e) => setNewPasswordInput(e.target.value)} /></label>
                  <label><span>Confirm New Password</span><input type="password" value={confirmPasswordInput} onChange={(e) => setConfirmPasswordInput(e.target.value)} /></label>
                </div>
                <p className="muted">Password must be at least 8 characters and include uppercase, lowercase, and a number.</p>
                {passwordUpdateError ? <p className="muted" style={{ color: '#c0392b' }}>{passwordUpdateError}</p> : null}
                <div className="row" style={{ justifyContent: 'flex-end' }}>
                  <button
                    className="modal-save-btn"
                    onClick={async () => {
                      setPasswordUpdateError('')
                      if (!isStrongPassword(newPasswordInput)) {
                        setPasswordUpdateError('New password does not meet complexity requirements.')
                        return
                      }
                      if (newPasswordInput !== confirmPasswordInput) {
                        setPasswordUpdateError('New password entries do not match.')
                        return
                      }
                      try {
                        const res = await apiFetch(`${apiBase}/api/auth/change-password`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ currentPassword: currentPasswordInput, newPassword: newPasswordInput }),
                        })
                        const data = await res.json()
                        if (!res.ok || !data?.ok) {
                          setPasswordUpdateError(data?.error || 'Unable to update password.')
                          return
                        }
                        setCurrentPasswordInput('')
                        setNewPasswordInput('')
                        setConfirmPasswordInput('')
                        setMustChangePassword(false)
                        setShowChangePasswordModal(false)
                        showCenterToast('Password updated')
                      } catch {
                        setPasswordUpdateError('Password service unavailable.')
                      }
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          ) : null}
          {showTwoFactorModal ? (
            <div className="calendar-modal-backdrop" onClick={() => setShowTwoFactorModal(false)}>
              <div className="calendar-popover site-settings-popover" onClick={(e) => e.stopPropagation()}>
                <h3>Two-Factor Authentication</h3>
                {twoFactorChallengeId ? (
                  <>
                    <p className="muted">Enter your authenticator code. If you lost access, use a recovery code.</p>
                    <div className="site-settings-typography-grid">
                      <label><span>Authenticator Code</span><input value={twoFactorCodeInput} onChange={(e) => setTwoFactorCodeInput(e.target.value)} placeholder="123456" /></label>
                      <label><span>Recovery Code (optional)</span><input value={twoFactorRecoveryInput} onChange={(e) => setTwoFactorRecoveryInput(e.target.value)} placeholder="ABCDE12345" /></label>
                    </div>
                    {twoFactorError ? <p className="muted" style={{ color: '#c0392b' }}>{twoFactorError}</p> : null}
                    <div className="row" style={{ justifyContent: 'flex-end' }}>
                      <button className="modal-save-btn" onClick={() => void completeTwoFactorLogin()} disabled={loginPending}>{loginPending ? 'Verifying…' : 'Verify & Login'}</button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="muted">Scan this QR code in your authenticator app, then verify with a 6-digit code.</p>
                    {twoFactorEnrollmentQr ? <img src={twoFactorEnrollmentQr} alt="2FA QR" style={{ width: 200, height: 200, borderRadius: 8, border: '1px solid #ddd' }} /> : <p className="muted">Preparing QR code…</p>}
                    {twoFactorEnrollmentSecret ? <p className="muted">Manual key: <code>{twoFactorEnrollmentSecret}</code></p> : null}
                    <label><span>Verification Code</span><input value={twoFactorEnrollmentCode} onChange={(e) => setTwoFactorEnrollmentCode(e.target.value)} placeholder="123456" /></label>
                    {twoFactorRecoveryCodes.length ? <p className="muted">Recovery codes: {twoFactorRecoveryCodes.join(', ')}</p> : null}
                    {twoFactorError ? <p className="muted" style={{ color: '#c0392b' }}>{twoFactorError}</p> : null}
                    <div className="row" style={{ justifyContent: 'flex-end' }}>
                      <button className="modal-save-btn" onClick={() => void verifyTwoFactorEnrollment()}>Enable 2FA</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : null}
          {showInviteUserModal ? (
            <div className="calendar-modal-backdrop" onClick={() => setShowInviteUserModal(false)}>
              <div className="calendar-popover site-settings-popover" onClick={(e) => e.stopPropagation()}>
                <h3>New Employee Contact Card</h3>
                <div className="site-settings-typography-grid">
                  <label><span>Name *</span><input value={inviteUserDraft.name} onChange={(e) => setInviteUserDraft((p) => ({ ...p, name: e.target.value }))} /></label>
                  <label><span>Role / Title</span><input value={inviteUserDraft.position} onChange={(e) => setInviteUserDraft((p) => ({ ...p, position: e.target.value }))} /></label>
                  <label><span>Project</span><select value={inviteUserDraft.project} onChange={(e) => setInviteUserDraft((p) => ({ ...p, project: e.target.value }))}><option value="">Select project</option>{projectAccessOptions.map((project) => <option key={`invite-project-${project}`} value={project}>{project}</option>)}</select></label>
                  <label><span>Web Access Level *</span><select value={inviteUserDraft.accessTier} onChange={(e) => setInviteUserDraft((p) => ({ ...p, accessTier: e.target.value }))}>{COMPANY_TIERS.map((tier) => <option key={`invite-tier-${tier}`} value={tier}>{tier}</option>)}</select></label>
                  <label><span>Office Location</span><input value={inviteUserDraft.phone} onChange={(e) => setInviteUserDraft((p) => ({ ...p, phone: e.target.value }))} /></label>
                  <label><span>Email *</span><input value={inviteUserDraft.email} onChange={(e) => setInviteUserDraft((p) => ({ ...p, email: e.target.value }))} /></label>
                  <label><span>Username</span><input value={usernameFromEmail(inviteUserDraft.email)} readOnly /></label>
                  <label><span>Cell</span><input value={inviteUserDraft.cell} onChange={(e) => setInviteUserDraft((p) => ({ ...p, cell: e.target.value }))} /></label>
                  <label><span>Initial Password *</span><input type="password" value={inviteUserPassword} onChange={(e) => setInviteUserPassword(e.target.value)} /></label>
                  <label><span>Confirm Password *</span><input type="password" value={inviteUserPasswordConfirm} onChange={(e) => setInviteUserPasswordConfirm(e.target.value)} /></label>
                </div>
                <div className="row" style={{ justifyContent: 'flex-end' }}>
                  <button
                    className="modal-save-btn"
                    onClick={() => {
                      const name = inviteUserDraft.name.trim()
                      const email = inviteUserDraft.email.trim().toLowerCase()
                      const username = usernameFromEmail(email)
                      const tier = inviteUserDraft.accessTier.trim()
                      if (!name) {
                        showActionToast('Name is required')
                        return
                      }
                      if (!tier) {
                        showActionToast('Tier is required')
                        return
                      }
                      if (!email || !email.includes('@')) {
                        showActionToast('Valid email is required')
                        return
                      }
                      if (!username) {
                        showActionToast('Username could not be derived from email')
                        return
                      }
                      if (!isStrongPassword(inviteUserPassword)) {
                        showActionToast('Password must include 8+ chars, uppercase, lowercase, and number')
                        return
                      }
                      if (inviteUserPassword !== inviteUserPasswordConfirm) {
                        showActionToast('Password confirmation does not match')
                        return
                      }
                      if (profileAccounts.some((a) => a.email.toLowerCase() === email || a.username.toLowerCase() === username)) {
                        showActionToast('Email/username already exists')
                        return
                      }

                      const key = officeContactKey(name)
                      const project = inviteUserDraft.project.trim() || projectName
                      setOfficeCustomEmployees((prev) => [{ name, position: inviteUserDraft.position || inviteUserDraft.accessTier || 'Staff', project, location: inviteUserDraft.location || profileOfficePlaceholder }, ...prev])
                      setOfficeContactOverrides((prev) => ({
                        ...prev,
                        [key]: {
                          ...inviteUserDraft,
                          name,
                          project,
                          projects: [project],
                        },
                      }))
                      setOfficeRemovedEmployees((prev) => ({ ...prev, [key]: false }))
                      setDirectoryContacts((prev) => [{
                        name,
                        role: inviteUserDraft.position || inviteUserDraft.accessTier || 'Staff',
                        team: 'Core Team',
                        company: companyName,
                        phone: inviteUserDraft.cell || '',
                        phoneType: 'Cell',
                        altPhone: inviteUserDraft.phone || '',
                        altPhoneType: 'Office',
                        officeExtension: inviteUserDraft.extension || '',
                        email,
                      }, ...prev])
                      setProfileAccounts((prev) => [{ username, email, tier, name, project, active: true }, ...prev])
                      setShowInviteUserModal(false)
                      setInviteUserPassword('')
                      setInviteUserPasswordConfirm('')
                      setInviteUserDraft({ name: '', position: '', accessTier: 'Drafter', project: '', projects: [''], location: '', reportsTo: '', pastProjects: '', email: '', phone: '', extension: '', cell: '' })
                      showCenterToast('Saved')
                    }}
                  >Save</button>
                </div>
              </div>
            </div>
          ) : null}
          {showDeactivateUserModal ? (
            <div className="calendar-modal-backdrop" onClick={() => setShowDeactivateUserModal(false)}>
              <div className="calendar-popover site-settings-popover user-management-popover" onClick={(e) => e.stopPropagation()}>
                <h3>Delete User</h3>
                <div className="site-settings-typography-grid">
                  <label>
                    <span>Company Employee List</span>
                    <select value={deactivateUserTarget} onChange={(e) => setDeactivateUserTarget(e.target.value)}>
                      <option value="">Select employee</option>
                      {officeEmployeesAll.map((emp) => <option key={`deactivate-${emp.name}`} value={emp.name}>{emp.name}</option>)}
                    </select>
                  </label>
                </div>
                <div className="row" style={{ justifyContent: 'flex-end' }}>
                  <button
                    className="modal-save-btn"
                    onClick={() => {
                      const target = deactivateUserTarget.trim()
                      if (!target) return
                      const key = officeContactKey(target)
                      setOfficeRemovedEmployees((prev) => ({ ...prev, [key]: true }))
                      setOfficeCustomEmployees((prev) => prev.filter((x) => officeContactKey(x.name) !== key))
                      setDirectoryContacts((prev) => prev.filter((x) => officeContactKey(x.name) !== key))
                      setShowDeactivateUserModal(false)
                      setDeactivateUserTarget('')
                      showCenterToast('Deleted')
                    }}
                  >Delete</button>
                </div>
              </div>
            </div>
          ) : null}
          {showImageCropModal ? (
            <div className="calendar-modal-backdrop" onClick={() => setShowImageCropModal(false)}>
              <div className="calendar-popover image-crop-popover" onClick={(e) => e.stopPropagation()} onKeyDown={handlePopoverSubmitKey}>
                <h3>Crop Image</h3>
                <div className={`image-crop-preview ${imageCropTarget === 'companyIcon' ? 'icon' : 'logo'}`}>
                  <div className="image-crop-stage">
                    <img
                      src={imageCropSource}
                      alt="Crop preview"
                      onLoad={() => {
                        const cropAspect = imageCropTarget === 'companyIcon' ? 1 : 2
                        const initialW = Math.min(90, 100)
                        const initialH = initialW / cropAspect
                        const w = initialH > 100 ? 100 * cropAspect : initialW
                        const h = w / cropAspect
                        setImageCropRect({ x: (100 - w) / 2, y: (100 - h) / 2, w })
                      }}
                    />
                    <div
                      className="image-crop-rect"
                      style={{
                        left: `${imageCropRect.x}%`,
                        top: `${imageCropRect.y}%`,
                        width: `${imageCropRect.w}%`,
                        height: `${imageCropRect.w / (imageCropTarget === 'companyIcon' ? 1 : 2)}%`,
                      }}
                      onMouseDown={(e) => startImageCropDrag('move', e)}
                    >
                      <button type="button" className="image-crop-handle nw" onMouseDown={(e) => startImageCropDrag('nw', e)} aria-label="Resize from top left" />
                      <button type="button" className="image-crop-handle ne" onMouseDown={(e) => startImageCropDrag('ne', e)} aria-label="Resize from top right" />
                      <button type="button" className="image-crop-handle sw" onMouseDown={(e) => startImageCropDrag('sw', e)} aria-label="Resize from bottom left" />
                      <button type="button" className="image-crop-handle se" onMouseDown={(e) => startImageCropDrag('se', e)} aria-label="Resize from bottom right" />
                    </div>
                  </div>
                </div>
                <div className="row directory-popover-actions" style={{ marginTop: 12 }}>
                  <button type="button" onClick={() => setShowImageCropModal(false)}>Cancel</button>
                  <button type="button" className="directory-primary-btn" onClick={applyImageCrop}>Apply Crop</button>
                </div>
              </div>
            </div>
          ) : null}
          {actionToast ? (
            <div className="action-toast">
              {actionToast.message ? <span style={{ color: 'var(--swan-wing)' }}>{actionToast.message}</span> : null}
              {actionToast.undo ? (
                <button type="button" className="toast-undo-btn" onClick={() => { actionToast.undo?.(); setActionToast(null) }}>
                  {String(actionToast.message || '').toLowerCase().includes('archiv') ? 'Undo Archive' : 'Undo'}
                </button>
              ) : null}
            </div>
          ) : null}
        </section>
      </div>
      {showContactInfo && activeTab !== 'directory' && activeTab !== 'consultantList' ? (
        <div className="calendar-modal-backdrop" onClick={() => setShowContactInfo(false)}>
          <div
            className="calendar-popover meeting-attendee-popover"
            onClick={(e) => e.stopPropagation()} onKeyDown={handlePopoverSubmitKey}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <h3>{contactInfoTitle}</h3>
            <div className="file-row" style={{ whiteSpace: 'pre-line' }}>{contactInfoValue || '-'}</div>
            <div className="row contact-action-row" style={{ marginTop: 8 }}>
              {contactInfoActionHref ? (
                <a
                  className="contact-action-btn"
                  href={contactInfoActionHref}
                  onClick={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  {contactInfoActionLabel}
                </a>
              ) : null}
              <button
                type="button"
                className="contact-action-btn contact-action-copy"
                onClick={async (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  const ok = await copyText(contactInfoValue || '')
                  showCopyToast(ok ? 'Copied' : 'Copy failed')
                }}
                onTouchStart={(e) => e.stopPropagation()}
              >
                Copy
              </button>
              <button
                type="button"
                className="contact-action-btn"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowContactInfo(false)
                }}
                onTouchStart={(e) => e.stopPropagation()}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {showEditContact && editContactIndex !== null && activeTab !== 'directory' && activeTab !== 'consultantList' ? (
        <div className="calendar-modal-backdrop" onClick={() => setShowEditContact(false)}>
          <div className="calendar-popover directory-contact-popover side-detail-panel" onClick={(e) => e.stopPropagation()} onKeyDown={handlePopoverSubmitKey}>
            <h3>Edit Contact</h3>
            <div className="row directory-phone-row">
              <label className="directory-popup-field">
                <span>Company</span>
                <input placeholder="Company" value={newContactCompany} onChange={(e) => setNewContactCompany(e.target.value)} />
              </label>
              <label className="directory-popup-field">
                <span>Abbrev.</span>
                <input placeholder="Abbrev." value={newContactCompanyAbbreviation} onChange={(e) => setNewContactCompanyAbbreviation(e.target.value)} />
              </label>
              <label className="directory-popup-field">
                <span>Consultant Type</span>
                <input placeholder="Consultant Type" value={newContactTeam} onChange={(e) => setNewContactTeam(e.target.value)} />
              </label>
            </div>
            <div className="row">
              <label className="directory-popup-field">
                <span>Name *</span>
                <input placeholder="Name *" value={newContactName} onChange={(e) => setNewContactName(e.target.value)} />
              </label>
              <label className="directory-popup-field">
                <span>Role</span>
                <input placeholder="Role" value={newContactRole} onChange={(e) => setNewContactRole(e.target.value)} />
              </label>
            </div>
            <div className="row">
              <label className="directory-popup-field">
                <span>Email</span>
                <input className="directory-email-input" style={{ fontSize: 15 }} placeholder="Email" value={newContactEmail} onChange={(e) => setNewContactEmail(e.target.value)} />
              </label>
            </div>
            <div className="row directory-phone-row">
              <label className="directory-popup-field">
                <span>Office</span>
                <input placeholder="Office" value={newContactAltPhone} onChange={(e) => setNewContactAltPhone(formatPhoneInput(e.target.value))} />
              </label>
              <label className="directory-popup-field">
                <span>EXT</span>
                <input placeholder="EXT" value={newContactOfficeExtension} onChange={(e) => setNewContactOfficeExtension(e.target.value.replace(/\D/g, ''))} />
              </label>
              <label className="directory-popup-field">
                <span>Cell</span>
                <input placeholder="Cell" value={newContactPhone} onChange={(e) => setNewContactPhone(formatPhoneInput(e.target.value))} />
              </label>
            </div>
            <div className="row">
              <label className="directory-popup-field">
                <span>Notes</span>
                <input placeholder="Notes" value={newContactNotes} onChange={(e) => setNewContactNotes(e.target.value)} />
              </label>
            </div>
            <div className="row directory-popover-actions">
              <button
                className="danger"
                onClick={() => {
                  if (editContactIndex === null) return
                  const current = directoryContacts[editContactIndex]
                  if (!current) return

                  if (current.archived) {
                    setDirectoryContacts((prev) => prev.map((c, i) => (i === editContactIndex ? { ...c, archived: false } : c)))
                    showActionToast('Contact restored from Archived')
                  } else {
                    setDirectoryContacts((prev) => prev.map((c, i) => (i === editContactIndex ? { ...c, archived: true } : c)))
                    showActionToast('Contact archived', () => {
                      setDirectoryContacts((prev) => prev.map((c, i) => (i === editContactIndex ? { ...c, archived: false } : c)))
                    })
                  }

                  setShowEditContact(false)
                  setEditContactIndex(null)
                }}
              >
                {editContactIndex !== null && directoryContacts[editContactIndex]?.archived ? 'Restore' : 'Archive'}
              </button>
              <button onClick={() => setShowEditContact(false)}>Cancel</button>
              <button
                className="directory-primary-btn"
                onClick={() => {
                  if (editContactIndex === null) return
                  const name = newContactName.trim()
                  const role = newContactRole.trim() || 'Role'
                  const team = newContactTeam.trim()
                  const company = newContactCompany.trim()
                  const companyAbbreviation = newContactCompanyAbbreviation.trim()
                  const phone = newContactPhone.trim()
                  const altPhone = newContactAltPhone.trim()
                  const officeExtension = newContactOfficeExtension.trim()
                  const notes = newContactNotes.trim()
                  const email = newContactEmail.trim()
                  if (!team) {
                    showActionToast('Consultant Type is required')
                    return
                  }
                  if (!name) {
                    showActionToast('Name is required')
                    return
                  }
                  setDirectoryContacts((prev) => prev.map((x, i) => i === editContactIndex ? {
                    name,
                    role,
                    team,
                    company,
                    companyAbbreviation,
                    phone,
                    phoneType: 'Cell',
                    altPhone,
                    altPhoneType: 'Office',
                    officeExtension,
                    notes,
                    email,
                  } : x))
                  showCenterToast('Saved')
                  setShowEditContact(false)
                  setEditContactIndex(null)
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {centerToastMessage ? <div className="saved-center-toast">{centerToastMessage}</div> : null}
      {copyToast ? <div className="copy-toast">{copyToast}</div> : null}
    </div>
  )
}

function CalendarViewPanel({
  view,
  focusDate,
  setFocusDate,
  setView,
  onYearMonthSelect,
  onMonthDaySelect,
  monthEntering,
  monthTransitionPhase,
  weekEntering,
  weekTransitionPhase,
  monthDayMorphing,
  deadlines,
  onEditDeadline,
  onDeleteDeadline,
  onStatusChange,
  companyName,
  listSearch,
  feedFilters,
  onFeedFiltersChange,
}: {
  view: 'day' | 'week' | 'month' | 'year' | 'list'
  focusDate: Date
  setFocusDate: (d: Date) => void
  setView: (v: 'day' | 'week' | 'month' | 'year' | 'list') => void
  onYearMonthSelect?: (
    monthDate: Date,
    sourceEl?: HTMLElement | null,
    sourceDayPoints?: Array<{ dateKey: string; label: string; x: number; y: number }>,
  ) => void
  onMonthDaySelect?: (
    dayDate: Date,
    sourceDayPoints?: Array<{ dateKey: string; label: string; x: number; y: number }>,
  ) => void
  monthEntering?: boolean
  monthTransitionPhase?: number
  weekEntering?: boolean
  weekTransitionPhase?: number
  monthDayMorphing?: boolean
  deadlines: CalendarDeadline[]
  onEditDeadline: (item: CalendarDeadline) => void
  onDeleteDeadline: (item: CalendarDeadline) => void
  onStatusChange?: () => void
  companyName: string
  listSearch?: string
  feedFilters?: {
    deliverables: boolean
    teamTasks: boolean
    myTasks: boolean
    consultantTasks: boolean
  }
  onFeedFiltersChange?: (next: { deliverables: boolean; teamTasks: boolean; myTasks: boolean; consultantTasks: boolean }) => void
}) {
  const year = focusDate.getFullYear()
  const month = focusDate.getMonth()
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const dateKey = (d: Date) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  const normalizeDeadline = (d: CalendarDeadline) => {
    const assignMatch = d.title.match(/\(Assign to:\s*([^)]*)\)$/i)
    const assignFromTitle = (assignMatch?.[1] || '').trim()
    const baseTitle = d.title.replace(/\s*\(Assign to:[^)]*\)\s*$/i, '').trim()
    const [mainTitle, ...descParts] = baseTitle.split(' — ')
    return {
      mainTitle: (mainTitle || d.title || '').trim(),
      description: (d.description || descParts.join(' — ') || '').trim(),
      assignTo: (d.assignTo || assignFromTitle || '').trim(),
    }
  }

  const [deliverableStatusByKey, setDeliverableStatusByKey] = useState<Record<string, 'not-selected' | 'overdue' | 'on-track' | 'completed'>>({})
  const [deliverableListFilter, setDeliverableListFilter] = useState<'all' | 'overdue' | 'this-week'>('all')

  const deliverableStatusKey = (d: CalendarDeadline, parsed: { mainTitle: string; description: string; assignTo: string }) => {
    return `${d.dateKey}|${parsed.mainTitle}|${parsed.description}|${parsed.assignTo}`
  }

  const activeFeedFilters = feedFilters || { deliverables: true, teamTasks: false, myTasks: false, consultantTasks: false }
  const toggleFeedFilter = (key: 'deliverables' | 'teamTasks' | 'myTasks' | 'consultantTasks') => {
    onFeedFiltersChange?.({ ...activeFeedFilters, [key]: !activeFeedFilters[key] })
  }
  const calendarFeedToggleRow = (
    <div className="calendar-left-filter-row" aria-label="Calendar source filters">
      <button type="button" className={`calendar-left-filter-btn ${activeFeedFilters.deliverables ? 'active' : ''}`} onClick={() => toggleFeedFilter('deliverables')}>Deliverables</button>
      <button type="button" className={`calendar-left-filter-btn ${activeFeedFilters.teamTasks ? 'active' : ''}`} onClick={() => toggleFeedFilter('teamTasks')}>Team Task</button>
      <button type="button" className={`calendar-left-filter-btn ${activeFeedFilters.myTasks ? 'active' : ''}`} onClick={() => toggleFeedFilter('myTasks')}>My Task</button>
      <button type="button" className={`calendar-left-filter-btn ${activeFeedFilters.consultantTasks ? 'active' : ''}`} onClick={() => toggleFeedFilter('consultantTasks')}>Consultant Task</button>
    </div>
  )

  const dayHoverSummary = (d: Date) => {
    const items = deadlines.filter((x) => x.dateKey === dateKey(d))
    if (!items.length) return ''
    const dateLabel = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
    const firstTitle = normalizeDeadline(items[0]).mainTitle
    return `${dateLabel}\n• ${items.length} ${items.length === 1 ? 'task' : 'tasks'} due\n• ${firstTitle}`
  }

  const formatAssigneeTag = (assignTo?: string) => {
    const cleaned = (assignTo || '').trim()
    if (!cleaned) return ''
    if (/^team$/i.test(cleaned)) return 'Team'
    const initials = cleaned
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('')
      .slice(0, 3)
    return initials || 'Team'
  }

  const startOfWeek = (d: Date) => {
    const x = new Date(d)
    x.setDate(x.getDate() - x.getDay())
    return x
  }

  const weekDates = (d: Date) => {
    const start = startOfWeek(d)
    return Array.from({ length: 7 }).map((_, i) => {
      const x = new Date(start)
      x.setDate(start.getDate() + i)
      return x
    })
  }

  const monthWeeks = (y: number, m: number) => {
    const first = new Date(y, m, 1)
    const start = startOfWeek(first)
    const last = new Date(y, m + 1, 0)
    const end = startOfWeek(new Date(last.getFullYear(), last.getMonth(), last.getDate() + (6 - last.getDay())))

    const rows: Date[][] = []
    const cur = new Date(start)
    while (cur <= end) {
      const row = Array.from({ length: 7 }).map((_, i) => {
        const d = new Date(cur)
        d.setDate(cur.getDate() + i)
        return d
      })
      rows.push(row)
      cur.setDate(cur.getDate() + 7)
    }
    return rows
  }

  const deadlineRingStatus = (d: Date): 'not-selected' | 'overdue' | 'on-track' | 'completed' | null => {
    const dayItems = deadlines.filter((x) => x.dateKey === dateKey(d))
    if (!dayItems.length) return null
    const statuses = dayItems.map((item) => {
      const parsed = normalizeDeadline(item)
      const key = deliverableStatusKey(item, parsed)
      return deliverableStatusByKey[key] || item.status || 'not-selected'
    })
    if (statuses.includes('overdue')) return 'overdue'
    if (statuses.includes('on-track')) return 'on-track'
    if (statuses.includes('completed')) return 'completed'
    return 'not-selected'
  }

  if (view === 'day') {
    const items = deadlines.filter((d) => d.dateKey === dateKey(focusDate))
    const grouped = items.reduce<Record<string, typeof items>>((acc, item) => {
      const assignTo = normalizeDeadline(item).assignTo || 'Unassigned'
      if (!acc[assignTo]) acc[assignTo] = []
      acc[assignTo].push(item)
      return acc
    }, {})

    const companyPattern = companyName.trim() ? new RegExp(companyName.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i') : null
    const groupedEntries = Object.entries(grouped).sort(([a], [b]) => {
      const isPriorityAssignee = (name: string) => /aor|odp/i.test(name) || (companyPattern ? companyPattern.test(name) : false)
      const aPriority = isPriorityAssignee(a) ? 0 : 1
      const bPriority = isPriorityAssignee(b) ? 0 : 1
      if (aPriority !== bPriority) return aPriority - bPriority
      return a.localeCompare(b)
    })

    const miniWeeks = monthWeeks(year, month)
    return (
      <div className="day-view-layout">
        <div className="file-list day-deadline-list" style={{ maxHeight: 'none' }}>
          {items.length > 0 ? <div className="file-row tasks-today-divider"><strong>Tasks for Today</strong></div> : null}
          {items.length === 0 ? <div className="file-row muted">No deliverables on this day.</div> : null}
          {groupedEntries.map(([assignee, group]) => (
            <div key={assignee}>
              <div className="file-row"><span className="day-item-assign">{assignee}</span></div>
              {group.map((d, i) => {
                const parsed = normalizeDeadline(d)
                const itemKey = deliverableStatusKey(d, parsed)
                const status = deliverableStatusByKey[itemKey] || d.status || 'not-selected'
                return (
                  <div key={`${d.title}-${i}`} className="file-row day-item-row">
                    <button className="day-item-edit-btn" onClick={() => onEditDeadline(d)} aria-label={`Edit ${parsed.mainTitle}`} title="Edit">✎</button>
                    <button className="day-item-edit-btn" onClick={() => onDeleteDeadline(d)} aria-label={`Delete ${parsed.mainTitle}`} title="Delete">🗑</button>
                    <div className="day-item-main">
                      <span className="day-item-title">{parsed.mainTitle}</span>
                      {parsed.description ? <span className="day-item-description"> — {parsed.description}</span> : null}
                    </div>
                    <select
                      className={`calendar-deliverable-status day-item-status status-${status}`}
                      value={status}
                      onChange={(e) => {
                        const nextStatus = e.target.value as 'not-selected' | 'overdue' | 'on-track' | 'completed'
                        setDeliverableStatusByKey((prev) => ({ ...prev, [itemKey]: nextStatus }))
                        onStatusChange?.()
                      }}
                      aria-label={`Status for ${parsed.mainTitle}`}
                    >
                      <option value="not-selected">Not Selected</option>
                      <option value="overdue">Overdue</option>
                      <option value="on-track">On Track</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                )
              })}
            </div>
          ))}
        </div>

        <div className="day-mini-month">
          <strong className="year-month-title">{focusDate.toLocaleString(undefined, { month: 'long' })} {year}</strong>
          <div className="year-month-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginTop: 8 }}>
            {weekdays.map((d) => (
              <span key={`day-mini-${d}`} className="muted small">{d[0]}</span>
            ))}
            {miniWeeks.flatMap((row, rowIndex) =>
              row.map((d, colIndex) => {
                const inMonth = d.getMonth() === month
                const isToday = d.toDateString() === new Date().toDateString()
                const isSelected = d.toDateString() === focusDate.toDateString()
                const ringStatus = deadlineRingStatus(d)
                return (
                  <button
                    key={`day-mini-${rowIndex}-${colIndex}`}
                    className={`file-row year-day-cell ${isToday ? 'today-year' : ''} ${isSelected ? 'day-mini-selected' : ''}`}
                    style={{ minHeight: 28, padding: '4px 6px', opacity: inMonth ? 1 : 0.225 }}
                    onClick={() => setFocusDate(d)}
                  >
                    <span className={`year-day-num ${ringStatus ? `deadline-ring status-${ringStatus}` : ''}`}>{String(d.getDate()).padStart(2, '0')}</span>
                  </button>
                )
              }),
            )}
          </div>
        </div>
      </div>
    )
  }

  if (view === 'list') {
    const query = (listSearch || '').trim().toLowerCase()
    const searchedDeadlines = !query
      ? deadlines
      : deadlines.filter((d) => {
          const parsed = normalizeDeadline(d)
          return [d.dateKey, parsed.mainTitle, parsed.description, parsed.assignTo, d.notes || '']
            .join(' ')
            .toLowerCase()
            .includes(query)
        })

    const today = new Date()
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    const nextWeek = new Date(today)
    nextWeek.setDate(today.getDate() + 7)
    const nextWeekKey = `${nextWeek.getFullYear()}-${String(nextWeek.getMonth() + 1).padStart(2, '0')}-${String(nextWeek.getDate()).padStart(2, '0')}`

    const withStatus = deadlines.map((d) => {
      const parsed = normalizeDeadline(d)
      const itemKey = deliverableStatusKey(d, parsed)
      const status = deliverableStatusByKey[itemKey] || d.status || 'not-selected'
      return { d, parsed, status }
    })

    const filteredSearchedDeadlines = searchedDeadlines.filter((d) => {
      const parsed = normalizeDeadline(d)
      const itemKey = deliverableStatusKey(d, parsed)
      const status = deliverableStatusByKey[itemKey] || d.status || 'not-selected'
      if (deliverableListFilter === 'overdue') return status === 'overdue' || d.dateKey < todayKey
      if (deliverableListFilter === 'this-week') return d.dateKey >= todayKey && d.dateKey <= nextWeekKey && status !== 'completed'
      return true
    })

    const sourceBucket = (d: CalendarDeadline) => {
      if (d.source === 'Team Task') return 'teamTasks'
      if (d.source === 'My Task') return 'myTasks'
      if (d.source === 'Consultant Task') return 'consultantTasks'
      return 'deliverables'
    }

    const sourceCounts = {
      deliverables: deadlines.filter((d) => sourceBucket(d) === 'deliverables').length,
      teamTasks: deadlines.filter((d) => sourceBucket(d) === 'teamTasks').length,
      myTasks: deadlines.filter((d) => sourceBucket(d) === 'myTasks').length,
      consultantTasks: deadlines.filter((d) => sourceBucket(d) === 'consultantTasks').length,
    }

    const sourceLabel = (bucket: 'deliverables' | 'teamTasks' | 'myTasks' | 'consultantTasks') => {
      if (bucket === 'teamTasks') return 'TEAM'
      if (bucket === 'myTasks') return 'MINE'
      if (bucket === 'consultantTasks') return 'CONS'
      return 'DEL'
    }

    const overdueCount = withStatus.filter(({ d, status }) => status === 'overdue' || d.dateKey < todayKey).length
    const dueThisWeekCount = withStatus.filter(({ d, status }) => d.dateKey >= todayKey && d.dateKey <= nextWeekKey && status !== 'completed').length

    const recentOverdue = withStatus
      .filter(({ d, status }) => status === 'overdue' || d.dateKey < todayKey)
      .slice()
      .sort((a, b) => b.d.dateKey.localeCompare(a.d.dateKey))
      .slice(0, 3)

    const formatShortDate = (dateKey: string) => {
      const asDate = new Date(`${dateKey}T00:00:00`)
      return asDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit' })
    }

    return (
      <div className="calendar-deliverable-layout">
        <div className="file-list calendar-deliverable-list" style={{ maxHeight: 'none' }}>
          <div className="calendar-left-filter-row" aria-label="Deliverable list filters">
            <button type="button" className={`calendar-left-filter-btn ${deliverableListFilter === 'all' ? 'active' : ''}`} onClick={() => setDeliverableListFilter('all')}>All</button>
            <button type="button" className={`calendar-left-filter-btn ${deliverableListFilter === 'overdue' ? 'active' : ''}`} onClick={() => setDeliverableListFilter('overdue')}>Overdue</button>
            <button type="button" className={`calendar-left-filter-btn ${deliverableListFilter === 'this-week' ? 'active' : ''}`} onClick={() => setDeliverableListFilter('this-week')}>This Week</button>
          </div>
          {filteredSearchedDeadlines.length === 0 ? <div className="file-row muted">No matching deliverables.</div> : null}
          {Object.entries(
            filteredSearchedDeadlines
              .slice()
              .sort((a, b) => a.dateKey.localeCompare(b.dateKey))
              .reduce<Record<string, CalendarDeadline[]>>((acc, d) => {
                if (!acc[d.dateKey]) acc[d.dateKey] = []
                acc[d.dateKey].push(d)
                return acc
              }, {}),
          ).map(([dateKey, group]) => {
            const asDate = new Date(`${dateKey}T00:00:00`)
            const monthShort = asDate.toLocaleString(undefined, { month: 'short' })
            const dayShort = String(asDate.getDate()).padStart(2, '0')
            const groupStatuses = group.map((d) => {
              const parsed = normalizeDeadline(d)
              const itemKey = deliverableStatusKey(d, parsed)
              return deliverableStatusByKey[itemKey] || d.status || 'not-selected'
            })
            const badgeStatus = groupStatuses.includes('overdue')
              ? 'overdue'
              : groupStatuses.includes('on-track')
                ? 'on-track'
                : groupStatuses.includes('completed')
                  ? 'completed'
                  : 'not-selected'
            return (
              <article key={dateKey} className="calendar-deliverable-card">
                <div className={`calendar-deliverable-date-badge status-${badgeStatus}`} aria-hidden="true">
                  <span>{monthShort}</span>
                  <span>{dayShort}</span>
                </div>
                <div className="calendar-deliverable-content">
                  {group.map((d, i) => {
                    const parsed = normalizeDeadline(d)
                    const itemKey = deliverableStatusKey(d, parsed)
                    const status = deliverableStatusByKey[itemKey] || d.status || 'not-selected'
                    const source = sourceBucket(d)
                    const statusText = status === 'not-selected' ? 'Not Selected' : status === 'on-track' ? 'On Track' : status === 'overdue' ? 'Overdue' : 'Completed'
                    return (
                      <div key={`${d.title}-${i}`} className="calendar-deliverable-item-row" onClick={() => onEditDeadline(d)}>
                        <div className="calendar-deliverable-item-header-row">
                          <div className="calendar-deliverable-item-header">
                            <span className={`calendar-source-chip source-${source}`}>{sourceLabel(source)}</span>
                            <strong className={`status-${status}`}>{parsed.mainTitle}</strong>
                            {parsed.assignTo ? <span className="calendar-deliverable-top-assign"> • {parsed.assignTo}</span> : null}
                          </div>
                        </div>
                        {parsed.description ? <div className="calendar-deliverable-description">{parsed.description}</div> : null}
                        {d.notes ? <div className="calendar-deliverable-notes"><span className="calendar-deliverable-notes-label">Notes</span>: {d.notes}</div> : null}
                        <div className="calendar-deliverable-meta"><span className="calendar-deliverable-status-label">Status</span>: <span className={`calendar-deliverable-status-text status-${status}`}>{statusText}</span></div>
                      </div>
                    )
                  })}
                </div>
              </article>
            )
          })}
        </div>
        <aside className="calendar-deliverable-side-container" aria-label="Deliverables side container">
          <div className="calendar-side-section calendar-filter-side-section">
            <div className="file-row calendar-filter-heading"><strong>Calendars</strong></div>
            <div className="calendar-filter-inline-list" aria-label="Toggle calendars">
              <label className="calendar-filter-inline-item">
                <span className="calendar-source-chip source-deliverables">Deliverables</span>
                <input type="checkbox" checked={feedFilters?.deliverables ?? true} onChange={(e) => onFeedFiltersChange?.({ ...(feedFilters || { deliverables: true, teamTasks: false, myTasks: false, consultantTasks: false }), deliverables: e.target.checked })} />
              </label>
              <label className="calendar-filter-inline-item">
                <span className="calendar-source-chip source-teamTasks">Team Task</span>
                <input type="checkbox" checked={feedFilters?.teamTasks ?? false} onChange={(e) => onFeedFiltersChange?.({ ...(feedFilters || { deliverables: true, teamTasks: false, myTasks: false, consultantTasks: false }), teamTasks: e.target.checked })} />
              </label>
              <label className="calendar-filter-inline-item">
                <span className="calendar-source-chip source-myTasks">My Task</span>
                <input type="checkbox" checked={feedFilters?.myTasks ?? false} onChange={(e) => onFeedFiltersChange?.({ ...(feedFilters || { deliverables: true, teamTasks: false, myTasks: false, consultantTasks: false }), myTasks: e.target.checked })} />
              </label>
              <label className="calendar-filter-inline-item">
                <span className="calendar-source-chip source-consultantTasks">Consultant Task</span>
                <input type="checkbox" checked={feedFilters?.consultantTasks ?? false} onChange={(e) => onFeedFiltersChange?.({ ...(feedFilters || { deliverables: true, teamTasks: false, myTasks: false, consultantTasks: false }), consultantTasks: e.target.checked })} />
              </label>
            </div>
          </div>

          <div className="calendar-side-section calendar-summary-section">
            {(feedFilters?.deliverables ?? true) ? <div className="file-row calendar-summary-item"><strong>{sourceCounts.deliverables} Deliverables</strong></div> : null}
            {feedFilters?.teamTasks ? <div className="file-row calendar-summary-item"><strong>{sourceCounts.teamTasks} Team Task</strong></div> : null}
            {feedFilters?.myTasks ? <div className="file-row calendar-summary-item"><strong>{sourceCounts.myTasks} My Task</strong></div> : null}
            {feedFilters?.consultantTasks ? <div className="file-row calendar-summary-item"><strong>{sourceCounts.consultantTasks} Consultant Task</strong></div> : null}
            <div className="file-row calendar-summary-item"><strong>{dueThisWeekCount} Due this week</strong></div>
            <div className="file-row calendar-summary-item calendar-overdue-text"><strong>{overdueCount} Overdue</strong></div>
          </div>

          <div className="calendar-side-section calendar-overdue-section">
            <div className="file-row calendar-most-recent-title calendar-overdue-text"><strong>Most Recent Overdue</strong></div>
            {recentOverdue.length === 0 ? <div className="file-row muted">No overdue deadlines.</div> : null}
            {recentOverdue.map(({ d, parsed }, idx) => (
              <div key={`${d.title}-${d.dateKey}-${idx}`} className="file-row overdue-item-row">
                <span className="overdue-item-date">{formatShortDate(d.dateKey)}</span>
                <span className="overdue-item-title">{parsed.mainTitle}</span>
              </div>
            ))}
          </div>

          <div className="calendar-side-section calendar-key-section" aria-label="Calendar keys">
            <div className="file-row calendar-key-heading"><strong>Key</strong></div>
            <div className="calendar-key-group">
              <span className="calendar-key-title">Calendars</span>
              <div className="calendar-source-legend" aria-label="Calendar source legend">
                <span className="calendar-source-chip source-deliverables">Deliverables</span>
                <span className="calendar-source-chip source-teamTasks">Team Task</span>
                <span className="calendar-source-chip source-myTasks">My Task</span>
                <span className="calendar-source-chip source-consultantTasks">Consultant Task</span>
              </div>
            </div>
            <div className="calendar-key-group">
              <span className="calendar-key-title">Status</span>
              <div className="calendar-status-legend" aria-label="Status color legend">
                <span className="calendar-status-key status-overdue">Overdue</span>
                <span className="calendar-status-key status-on-track">On Track</span>
                <span className="calendar-status-key status-completed">Completed</span>
                <span className="calendar-status-key status-not-selected">Not Selected</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    )
  }

  if (view === 'week') {
    const days = weekDates(focusDate)
    const selectedDateKey = dateKey(focusDate)
    const selectedDayItems = deadlines.filter((x) => x.dateKey === selectedDateKey)
    const miniWeeks = monthWeeks(focusDate.getFullYear(), focusDate.getMonth())

    return (
      <div className="week-view-shell">
        {calendarFeedToggleRow}
        <div className={`calendar-grid-week ${weekEntering ? `week-entering week-phase-${weekTransitionPhase ?? 4}` : ''} ${monthDayMorphing ? 'month-day-morphing' : ''}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0 }}>
          {days.map((d) => {
            const dayItems = deadlines.filter((x) => x.dateKey === dateKey(d))
            const isToday = d.toDateString() === new Date().toDateString()
            const cappedDayItems = dayItems.slice(0, 2)
            const remainingDayCount = Math.max(0, dayItems.length - cappedDayItems.length)
          return (
              <button
                key={d.toISOString()}
                className={`file-row calendar-cell ${isToday ? 'today-week' : ''} ${dateKey(d) === selectedDateKey ? 'selected' : ''}`}
                data-date={dateKey(d)}
                style={{ textAlign: 'left' }}
                onClick={() => {
                  setFocusDate(d)
                  setView('week')
                }}
              >
                <div className="week-day-header">
                  <span className="week-day-short">{d.toLocaleString(undefined, { weekday: 'short' })}</span>
                  <span className="week-day-rest">{d.toLocaleString(undefined, { weekday: 'long' }).slice(3)}</span>
                </div>
                <div className="week-day-divider" aria-hidden="true" />
                <span className="day-num">{String(d.getDate()).padStart(2, '0')}</span>
                {dayItems.length > 0 ? (
                  <div className="week-deadline-list">
                    {cappedDayItems.map((item, idx) => {
                      const parsed = normalizeDeadline(item)
                      const itemKey = deliverableStatusKey(item, parsed)
                      const status = deliverableStatusByKey[itemKey] || item.status || 'on-track'
                      const assignee = formatAssigneeTag(parsed.assignTo)
                      return (
                        <button
                          key={`${item.title}-${idx}`}
                          className={`week-deadline-item status-${status}`}
                          title={parsed.mainTitle}
                          onClick={(e) => {
                            e.stopPropagation()
                            onEditDeadline(item)
                          }}
                        >
                          <span className={`week-deadline-dot status-${status}`} aria-hidden="true" />
                          <span className="week-deadline-main">
                            <span className="week-deadline-title">{parsed.mainTitle}</span>
                            {assignee ? <span className="week-deadline-assignee">{assignee}</span> : null}
                          </span>
                        </button>
                      )
                    })}
                    {remainingDayCount > 0 ? (
                      <button
                        type="button"
                        className="week-deadline-item week-deadline-more"
                        onClick={(e) => {
                          e.stopPropagation()
                          setFocusDate(d)
                        }}
                      >
                        {remainingDayCount} more — click to view
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </button>
            )
          })}
        </div>

        <div className="week-detail-row">
          <section className="week-day-detail panel">
            <h3>{focusDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</h3>
            {selectedDayItems.length ? (
              <div className="week-day-detail-list">
                {selectedDayItems.map((item, idx) => {
                  const parsed = normalizeDeadline(item)
                  const itemKey = deliverableStatusKey(item, parsed)
                  const status = deliverableStatusByKey[itemKey] || item.status || 'on-track'
                  const assignee = (parsed.assignTo || 'Team').trim() || 'Team'
                  return (
                    <button key={`${item.title}-${idx}`} className={`week-detail-item status-${status}`} onClick={() => onEditDeadline(item)}>
                      <span className={`week-deadline-dot status-${status}`} aria-hidden="true" />
                      <span className="week-detail-main">
                        <strong className="week-detail-title">{parsed.mainTitle}</strong>
                        <span className="week-detail-subline">
                          {parsed.description ? <span className="week-detail-desc">{parsed.description}</span> : null}
                          <span className="week-deadline-assignee"> ({assignee})</span>
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
            ) : (
              <p className="muted" style={{ fontSize: '12px' }}>No deadlines for this day.</p>
            )}
          </section>

          <aside className="week-mini-calendar panel" aria-label="Mini calendar">
            <h3>{focusDate.toLocaleString(undefined, { month: 'long' })}</h3>
            <div className="week-mini-grid">
              {weekdays.map((w) => <span key={`mini-${w}`} className="muted small">{w[0]}</span>)}
              {miniWeeks.flatMap((row, ri) => row.map((d, ci) => {
                const inMonth = d.getMonth() === focusDate.getMonth()
                const sel = dateKey(d) === selectedDateKey
                const isToday = d.toDateString() === new Date().toDateString()
                const ringStatus = deadlineRingStatus(d)
                return (
                  <button
                    key={`mini-${ri}-${ci}-${d.toISOString()}`}
                    className={`week-mini-day ${isToday ? 'today-mini' : ''} ${sel ? 'selected' : ''} ${ringStatus ? `deadline-ring status-${ringStatus}` : ''}`}
                    style={{ opacity: inMonth ? 1 : 0.3 }}
                    onClick={() => setFocusDate(d)}
                  >
                    {d.getDate()}
                  </button>
                )
              }))}
            </div>
          </aside>
        </div>
      </div>
    )
  }

  if (view === 'month') {
    const weeks = monthWeeks(year, month)
    return (
      <>
        {calendarFeedToggleRow}
        <div className={`calendar-grid-month ${monthEntering ? `month-entering month-phase-${monthTransitionPhase ?? 4}` : ''} ${monthDayMorphing ? 'month-day-morphing' : ''}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
          {weekdays.map((d) => (
            <strong key={d} className="month-weekday">
              <span className="month-weekday-first">{d[0]}</span>
              <span className="month-weekday-rest">{d.slice(1)}</span>
            </strong>
          ))}
          {weeks.flatMap((row, rowIndex) =>
            row.map((d, colIndex) => {
              const inMonth = d.getMonth() === month
              const isToday = d.toDateString() === new Date().toDateString()
              const dayItems = deadlines.filter((x) => x.dateKey === dateKey(d))
              return (
                <button
                  key={`${rowIndex}-${colIndex}-${d.toISOString()}`}
                  className={`file-row calendar-cell ${isToday ? 'today' : ''}`}
                  data-date={dateKey(d)}
                  style={{ minHeight: 96, opacity: inMonth ? 1 : 0.45 }}
                  title={dayHoverSummary(d)}
                  onClick={() => {
                    if (onMonthDaySelect) {
                      const week = weekDates(d)
                      const sourceDayPoints = week
                        .map((wd) => {
                          const cell = document.querySelector(`.calendar-grid-month .calendar-cell[data-date='${dateKey(wd)}'] .day-num`) as HTMLElement | null
                          const rect = cell?.getBoundingClientRect()
                          return rect
                            ? {
                                dateKey: dateKey(wd),
                                label: String(wd.getDate()).padStart(2, '0'),
                                x: rect.left + rect.width / 2,
                                y: rect.top + rect.height / 2,
                              }
                            : null
                        })
                        .filter((x): x is { dateKey: string; label: string; x: number; y: number } => !!x)
                      onMonthDaySelect(d, sourceDayPoints)
                    } else {
                      setFocusDate(d)
                      setView('week')
                    }
                  }}
                >
                  <span className="day-num">{String(d.getDate()).padStart(2, '0')}</span>
                  {dayItems.length > 0 ? (
                    <div className="month-deadline-list">
                      {dayItems.length > 2 ? (
                        <div className="month-deadline-item month-deadline-count">{dayItems.length} deadlines</div>
                      ) : (
                        dayItems.map((item, idx) => {
                          const parsed = normalizeDeadline(item)
                          const itemKey = deliverableStatusKey(item, parsed)
                          const status = deliverableStatusByKey[itemKey] || item.status || 'on-track'
                          return <div key={`${item.title}-${idx}`} className={`month-deadline-item status-${status}`}>{parsed.mainTitle}</div>
                        })
                      )}
                    </div>
                  ) : null}
                </button>
              )
            }),
          )}
        </div>
      </>
    )
  }

  return (
    <>
      {calendarFeedToggleRow}
      <div className="calendar-grid-year" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        {Array.from({ length: 12 }, (_, m) => {
        const monthLabel = new Date(year, m, 1).toLocaleString(undefined, { month: 'long' })
        const weeks = monthWeeks(year, m)
        return (
          <button
            key={monthLabel}
            className="year-month-card"
            style={{ padding: 10, textAlign: 'left' }}
            onClick={(e) => {
              const monthDate = new Date(year, m, 1)
              if (onYearMonthSelect) {
                const sourceDayPoints = Array.from(e.currentTarget.querySelectorAll('.year-day-cell .year-day-num'))
                  .map((el) => {
                    const rect = (el as HTMLElement).getBoundingClientRect()
                    const parent = (el as HTMLElement).closest('.year-day-cell') as HTMLElement | null
                    return {
                      dateKey: parent?.dataset.date || '',
                      label: ((el as HTMLElement).textContent || '').trim(),
                      x: rect.left + rect.width / 2,
                      y: rect.top + rect.height / 2,
                    }
                  })
                  .filter((x) => !!x.dateKey)
                onYearMonthSelect(monthDate, e.currentTarget, sourceDayPoints)
              } else {
                setFocusDate(monthDate)
                setView('month')
              }
            }}
          >
            <strong className="year-month-title">{monthLabel}</strong>
            <div className="year-month-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginTop: 8 }}>
              {weekdays.map((d) => (
                <span key={`${monthLabel}-${d}`} className="muted small">{d[0]}</span>
              ))}
              {weeks.flatMap((row, rowIndex) =>
                row.map((d, colIndex) => {
                  const inMonth = d.getMonth() === m
                  const isToday = d.toDateString() === new Date().toDateString()
                  const ringStatus = deadlineRingStatus(d)
                  return (
                    <div
                      key={`${monthLabel}-${rowIndex}-${colIndex}`}
                      className={`file-row year-day-cell ${isToday ? 'today-year' : ''}`}
                      data-date={dateKey(d)}
                      style={{ minHeight: 28, padding: '4px 6px', opacity: inMonth ? 1 : 0.225 }}
                      title={dayHoverSummary(d)}
                    >
                      <span className={`year-day-num ${ringStatus ? `deadline-ring status-${ringStatus}` : ''}`}>{String(d.getDate()).padStart(2, '0')}</span>
                    </div>
                  )
                }),
              )}
            </div>
          </button>
        )
        })}
      </div>
    </>
  )
}

export default App
